/**
 * Every user-visible string in the Waslo AI section lives here — English and Arabic,
 * side by side, in one file.
 *
 * WHY NOT `i18n/locale/<lang>/settings.json`: those are tracked upstream files that gain keys
 * on almost every Chatwoot bump, so an entry there conflicts on every merge. This module
 * is fork-owned and upstream cannot reach it. THE PLAN, §6 R-1.
 *
 * HOW COMPONENTS READ IT: `useWasloI18n()` returns a vue-i18n composer scoped to THIS
 * message tree (`useScope: 'local'`). It inherits `locale` from the global composer, so
 * switching the account to Arabic switches these strings too, with no registration step
 * and no edit to a core file.
 *
 * HOW `nav.js` READS IT: the sidebar is rendered by a core component that hands us its
 * own global `t` AND, since the labels have to follow the account's language, its own
 * `locale`. A plain module cannot obtain the composer for itself — `useI18n()` needs a
 * setup context and `wasloSidebarItem()` is called from inside a `computed` during
 * render — so both are passed in, and `wasloT()` asks the global `t` first (it wins if
 * these keys are ever added to a locale file) and otherwise reads the table for that
 * locale, falling back to English.
 *
 * WHY THE LOCALE IS AN ARGUMENT AND NOT A LOOKUP. The two alternatives were both worse.
 * Reading `getCurrentInstance()?.appContext.config.globalProperties.$i18n` from in here
 * happens to work while the sidebar is rendering and returns `null` the moment anything
 * else evaluates that computed — a language that is correct only by luck of the call
 * stack. Reading the account's locale out of vuex would be a SECOND source of truth: the
 * rendered language is `uiSettings.locale || account.locale` (`App.vue:84,116`), so a
 * user whose own language differs from her account's would get an Arabic nav above an
 * English page. The locale that is passed in is the one the rest of the screen is
 * actually using, by construction. THE PLAN, §6 R-1: one more argument on a line that is
 * already fenced, and nothing new that upstream can reach.
 *
 * VOCABULARY IS ENFORCED: `deploy/branding/checks/waslo-lint.mjs` fails the image build
 * if any string in this file uses the internal words this product deliberately dropped
 * (see THE PLAN, §3 P3). Owner language only.
 */

import { useI18n } from 'vue-i18n';

import { COPY_FACTS } from './helpers/copy-facts.js';
import { arabicChoice, formatCount, localisedArgs } from './helpers/plural.js';

const en = {
  WASLO: {
    // P-10.26 and P-2.10: the seven channels, by their own names.
    CHANNEL_NAMES: {
      WHATSAPP: 'WhatsApp',
      INSTAGRAM: 'Instagram',
      MESSENGER: 'Messenger',
      TELEGRAM: 'Telegram',
      WEBSITE: 'Website chat',
      EMAIL: 'Email',
      SMS: 'SMS',
    },
    // P-5.12: one confirm for every AI action the owner starts. No string here holds a
    // digit: every amount is {credits} or {balance}, filled from the server through CREDITS.
    AI_CONFIRM: {
      CREDITS: '{count} credit | {count} credits',
      TITLE: 'This uses AI',
      BODY: 'This uses AI and costs {credits}. You have {balance} left.',
      TICK: 'I understand this is charged',
      CONFIRM: 'Confirm',
      CANCEL: 'Cancel',
      PRICED: '{action} · {credits}',
      PRICE_CHANGED: 'The price changed to {credits}. Confirm again.',
      PRICE_KEEPS_CHANGING:
        'The price kept changing, so nothing ran and nothing was charged. Try again.',
      RECEIPT: 'Used {credits}.',
      NOTHING_CHARGED: 'Nothing was charged.',
      // O-11: Test it and meaning search ask once per visit, in these words.
      SESSION: {
        TEST_MESSAGE_BODY:
          'Each test message costs {credits}. You have {balance} left.',
        TEST_MESSAGE_TICK: 'I understand each test message costs {credits}',
        SEARCH_BY_MEANING_BODY:
          'Each search by meaning costs {credits}. You have {balance} left.',
        SEARCH_BY_MEANING_TICK:
          'I understand each search by meaning costs {credits}',
      },
    },
    BUY_CREDITS: {
      OPEN: 'Buy credits',
      TITLE: 'Buy credits',
      INTRO:
        'Pick a pack. You pay on a secure page and come straight back here.',
      PRICE: '{amount} KWD',
      PAY_KNET: 'Pay with KNET',
      PAY_CARD: 'Pay by card',
      TEST_MODE: 'Test payments: no real money moves on this workspace.',
      UNAVAILABLE:
        "Paying online isn't open for this workspace yet. Ask us and we'll add credits for you.",
      LOAD_FAILED: "We couldn't load the packs just now. Try again.",
      START_FAILED:
        "We couldn't open the payment page just now. Nothing was charged. Try again.",
      RETURN: {
        TITLE: 'Your payment',
        WAITING: 'Waiting for the bank to confirm your payment…',
        STALLED:
          'This is taking longer than usual. Your credits arrive on their own once the bank confirms.',
        CHECK_AGAIN: 'Check again',
        PAID: '{credits} were added to your balance.',
        NOT_PAID: "The payment didn't go through.",
        CHECK:
          "We received this payment and need to check it by hand. We'll be in touch.",
        MISSING: "We couldn't find that payment.",
        LOAD_FAILED: "We couldn't check your payment just now.",
        BACK: 'Back to overview',
        TRY_AGAIN: 'Try again',
      },
    },
    // P-7.1 and P-2.10: one or two languages. The warning a Both account sees when a field
    // is written in one language only, and "Translate them for me". Counts are plural choices.
    LANGUAGES: {
      OPTIONS_MISSING_AR:
        'One option has no Arabic words yet. Arabic customers will see the English. | {count} options have no Arabic words yet. Arabic customers will see the English.',
      OPTIONS_MISSING_EN:
        'One option has no English words yet. English customers will see the Arabic. | {count} options have no English words yet. English customers will see the Arabic.',
      OTHERS_MISSING_AR:
        'One more place has no Arabic words yet. | {count} more places have no Arabic words yet.',
      OTHERS_MISSING_EN:
        'One more place has no English words yet. | {count} more places have no English words yet.',
      TRANSLATE: 'Translate them for me',
      USE_ALL: 'Use them all',
      TOO_LONG:
        'Too long for this place ({length} of {limit} characters). Write it yourself.',
      CUT_OFF: 'This one did not come back. Write it yourself, or try again.',
      FAILED: 'That did not work. Nothing was changed. Try again.',
      WHAT: {
        QUESTION: 'Question: {name}',
        OPTION: 'Option: {name}',
        MESSAGE: 'Message: {name}',
        FORM_BUTTON: 'The button that sends the form',
        MENU_BODY: 'The menu message',
        MENU_BUTTON: 'The menu button',
        HANDOVER: 'The message when a person takes over',
        HANDOVER_CLOSED: 'The message when you’re closed',
      },
    },
    // P-6.11: undo. "Restore how it was" lists the saves of one thing; "Recently removed" keeps
    // what was removed for 30 days. Who did it is one of four words the BFF picks, never an id.
    UNDO: {
      WHO: {
        YOU: 'you',
        TEAMMATE: 'someone on your team',
        CLAUDE: 'Claude',
        WASLO: 'Waslo',
      },
      HISTORY: {
        HEADING: 'Restore how it was',
        DESCRIPTION:
          'Every save is kept for {period}. Restoring brings back how it was just before that save, and you can restore again afterwards.',
        ROW: 'Before the change on {when}, by {who}',
        RESTORE: 'Restore',
        RESTORED: 'Restored. Your assistant uses it from the next message.',
        RESTORED_OFF: 'Restored and switched off. Check it, then switch it on.',
        CONFLICT:
          'This changed since then, so it could not be restored that way. Reload the page and try again.',
        FAILED: 'We could not restore that. Nothing was changed. Try again.',
      },
      REMOVED: {
        HEADING: 'Recently removed',
        DESCRIPTION:
          'What you removed in the last {period}. Putting one back costs nothing, and it works again straight away.',
        ROW: 'Removed {when} by {who}',
        JUST_NOW: 'just now',
        PUT_BACK: 'Put it back',
        PUT_BACK_DONE:
          'Put back. Your assistant uses it from the next message.',
        PUT_BACK_OFF: 'Put back and switched off. Check it, then switch it on.',
        PUT_BACK_RENAMED:
          'Put back and switched off, with “(restored)” added to its name, because another one has that name now.',
        CONFLICT:
          'That is already back, or what it belonged to was removed. Reload the page.',
        FAILED: 'We could not put that back. Nothing was changed. Try again.',
        KIND: {
          SOURCE: 'Source',
          ANSWER: 'Answer',
          TASK: 'Task',
          MENU: 'Menu',
          CONNECTION: 'Connection',
          NOTICE: 'Note',
        },
      },
    },
    // ── Ease C3 (spec §6): knowledge the owner can fix. No AI word, no engine word. ─────
    // P-6.6: how often an unanswered question came up, and in how many wordings.
    GAPS: {
      WAYS: 'in {count} different way | in {count} different ways',
    },
    // P-6.1 to P-6.4: the one dialog that fixes an answer, from anywhere it shows.
    FIX: {
      TITLE_FIX: 'Fix this answer',
      TITLE_EDIT: 'Edit this answer',
      BODY_FIX:
        'Write the answer your assistant should give. It uses it from its next message.',
      BODY_EDIT:
        'Change the words your assistant uses. It uses the new words from its next message.',
      IT_SAID: 'It said',
      NOTHING_SAID: 'Nothing. It did not know.',
      SAVED: 'Saved. Your assistant uses it from its next message.',
      ALSO_SAYS: '“{source}” also says: {answer}',
      REPLACES: 'This replaces the answer from “{source}”.',
      USE_MINE: 'Use mine instead',
      KEEP_BOTH: 'Keep both',
      USED_MINE: 'Done. Only your answer is used now.',
      CHECK: 'Check it now',
      BEFORE: 'Before',
      NOW: 'Now',
      NOW_HANDOFF: 'It would pass this chat to your team.',
      CHECK_FAILED: 'We could not check it just now. Your answer is saved.',
      DONE: 'Done',
      VERB: 'Fix it',
      WRONG: 'Wrong? Fix it',
      FAILED:
        'We could not save that just now. Nothing was changed. Try again.',
    },
    // P-6.1: every answer it knows, in one list.
    ALL_ANSWERS: {
      HEADING: 'All answers',
      DESCRIPTION:
        'Everything your assistant knows, from every source and every answer you wrote. Fix one and it is used from the next message.',
      SEARCH_LABEL: 'Search answers',
      SEARCH_PLACEHOLDER: 'Type a word from the question or the answer',
      SEARCH_WORDS: 'Search',
      SEARCH_MEANING: 'Search by meaning',
      CLEAR: 'Clear',
      FILTER: {
        ALL: 'All',
        YOURS: 'Written by you',
        SOURCES: 'From your sources',
        OFF: 'Switched off',
      },
      YOURS: 'Yours',
      OFF: 'Off',
      REPLACED: 'Replaced by your answer',
      FROM: 'From “{source}”',
      EDIT: 'Edit',
      SWITCH_OFF: 'Switch off',
      SWITCH_ON: 'Switch on',
      REMOVE: 'Remove',
      REMOVED:
        'Removed. You can put it back from Recently removed for {period}.',
      HISTORY: 'Earlier versions',
      NONE: 'No answers match that.',
      EMPTY: 'Your assistant has no answers yet. Add a source or write one.',
      UNAVAILABLE: 'We could not read the answers just now.',
      SHOWING: 'Showing {from} to {to} of {total}.',
      PREVIOUS: 'Previous',
      NEXT: 'Next',
      FAILED:
        'We could not change that just now. Nothing was changed. Try again.',
    },
    // P-6.9: something true for a while, which removes itself.
    NOTICES: {
      HEADING: 'For now',
      DESCRIPTION:
        'Something true for a while, like a holiday or a closed branch. Your assistant uses it until the day you pick, then it removes itself.',
      TEXT_LABEL: 'What is true for now',
      TEXT_PLACEHOLDER: 'We are closed for Eid from Tuesday to Thursday.',
      ENDS_LABEL: 'Last day',
      ADD: 'Add',
      ENDS: 'Removes itself after {date}',
      EXTEND: 'Extend',
      SAVE: 'Save',
      CANCEL: 'Cancel',
      REMOVE: 'Remove',
      FULL: 'You can keep up to ten notes at a time. Remove one first.',
      FAILED:
        'We could not save that just now. Nothing was changed. Try again.',
      UNAVAILABLE: 'We could not read your notes just now.',
    },
    // P-6.5: answers the team already gives, found in its own replies.
    SCAN: {
      START: 'Look through my team’s replies',
      RUNNING:
        'Looking through your team’s replies. This can take a few minutes.',
      DONE: 'Found {count} answer your team gives. | Found {count} answers your team gives.',
      NOTHING:
        'Nothing new this time. Your team’s replies are already covered.',
      FAILED: 'That did not finish. Nothing was charged. You can try again.',
      TOMORROW: 'You can look again tomorrow.',
      CAME_UP: 'Came up in {count} chat | Came up in {count} chats',
      FROM_REPLIES: 'From your team’s replies',
    },
    // P-6.7: photos of a menu or price list, and what was read from them.
    PHOTOS: {
      CHOICE: 'Photos',
      CHOICE_HINT: 'Up to ten photos of a menu, a price list or a page.',
      LABEL: 'Photos',
      HINT: 'JPG, PNG or WebP, up to {size} MB each.',
      TOO_MANY: 'Choose up to ten photos.',
      TOO_BIG: 'Each photo can be up to {size} MB.',
      NOT_PHOTO: 'Choose JPG, PNG or WebP photos.',
    },
    RECEIPT: {
      PAGES: 'Read {count} page. | Read {count} pages.',
      LEARNED: 'Learned {count} answer. | Learned {count} answers.',
      LINES: 'Kept {count} line as it is. | Kept {count} lines as they are.',
      PAGES_UNREADABLE: 'Could not read page {pages}.',
      LINES_UNREADABLE: 'Could not read these lines:',
    },
    // P-6.10: opening hours, when it answers, and chats it leaves to the team.
    HOURS: {
      HEADING: 'Opening hours',
      DESCRIPTION:
        'Your assistant tells customers when you are open. Add a second time if you close in the middle of the day.',
      TIMEZONE: 'Time zone',
      CLOSED: 'Closed',
      ADD_TIME: 'Add a time',
      REMOVE_TIME: 'Remove',
      FROM: 'From',
      TO: 'To',
      NEXT_DAY: '(next day)',
      DAYS: {
        MON: 'Monday',
        TUE: 'Tuesday',
        WED: 'Wednesday',
        THU: 'Thursday',
        FRI: 'Friday',
        SAT: 'Saturday',
        SUN: 'Sunday',
      },
      NEEDS_HOURS: 'Add your opening hours first.',
    },
    ANSWER_WHEN: {
      HEADING: 'When it answers',
      ALWAYS: 'All the time',
      ALWAYS_HINT: 'It answers every message, day and night.',
      CLOSED_ONLY: 'Only when you’re closed',
      CLOSED_ONLY_HINT:
        'While you are open, chats go to your team. It answers when you close.',
      TEST_NOTE:
        'Test it always answers, even while you are open, so you can try it any time.',
    },
    // F3.8.
    TYPING: {
      HEADING: 'Show that it is typing',
      ON: 'Show it',
      ON_HINT:
        'On WhatsApp, your customer sees "typing" while it writes the answer, and their message is marked as seen.',
      OFF: 'Do not show it',
      OFF_HINT: 'Your customer sees nothing until the answer arrives.',
    },
    // Field report G2.1.
    SENDING_HOURS: {
      HEADING: 'When it may start a message',
      DESCRIPTION:
        'Follow-ups and broadcasts go out only inside these hours. A reply to a customer who just wrote always goes straight away.',
      WEEK: {
        OPENING: 'Your opening hours',
        OPENING_HINT: 'It starts messages only while you are open.',
        OPENING_NONE_HINT:
          'You have not set opening hours, so it can start messages at any time.',
        OWN: 'Other hours',
        OWN_HINT: 'Choose the hours it may send in.',
        HEADING: 'Hours it may send in',
      },
      PRAYER: {
        LABEL: 'Wait during Friday prayer',
        FROM: 'From',
        TO: 'To',
      },
      SEASONS: {
        HEADING: 'Seasons with other hours',
        DESCRIPTION:
          'For example Ramadan: enter its dates each year, with the hours for those days.',
        ADD: 'Add a season',
        NAME: 'Name',
        NAME_PLACEHOLDER: 'Ramadan',
        FROM: 'First day',
        TO: 'Last day',
        WEEK: 'Hours in this season',
        WEEK_HINT: 'The hours it may send in on these days.',
        REMOVE: 'Remove',
      },
      PAUSES: {
        HEADING: 'Days it sends nothing',
        DESCRIPTION:
          'For example Eid: nothing it starts goes out on these days.',
        ADD: 'Add days off',
        NAME: 'Name',
        NAME_PLACEHOLDER: 'Eid',
        FROM: 'First day',
        TO: 'Last day',
        REMOVE: 'Remove',
      },
      BROADCASTS: 'Broadcasts wait for these hours too',
      BROADCASTS_HINT:
        'A broadcast that reaches the end of these hours stops, and carries on by itself when they open.',
      WEBSITE_NOTE:
        'Messages your own website sends go out straight away, unless it asks to wait.',
      ERRORS: {
        GAP_ORDER: 'The prayer pause must end after it starts.',
        DATES:
          'Give every season and every set of days off a name, a first day and a last day.',
        CHECK: 'Check the dates and times in this section.',
      },
    },
    // Field report G2.2.
    FOLLOW_UPS: {
      HEADING: 'Follow up when a customer goes quiet',
      DESCRIPTION:
        'If a customer stops answering in the middle of a task, it can ask again. Each follow-up is a WhatsApp message, and WhatsApp may charge you for it.',
      OFF: 'Do not follow up',
      OFF_HINT: 'It waits for the customer to write again.',
      ON: 'Follow up',
      ON_HINT:
        'It asks again, at most twice, and never once a person has taken the chat.',
      TIMES: 'How many times',
      ONCE: 'Once',
      TWICE: 'Twice',
      FIRST: 'First follow-up',
      SECOND: 'Second follow-up',
      FIRST_AFTER: {
        M30: 'After 30 minutes',
        M60: 'After 1 hour',
        M180: 'After 3 hours',
      },
      SECOND_AFTER: {
        M180: '3 hours after the first',
        M360: '6 hours after the first',
        M720: '12 hours after the first',
      },
      WORDING: {
        QUESTION: 'Ask the same question again',
        QUESTION_HINT:
          'It sends the question exactly as you wrote it in the task.',
        OWN: 'Send my own words',
        OWN_HINT: 'It sends the words you write below.',
      },
      WORDS_EN: 'Your words in English',
      WORDS_AR: 'Your words in Arabic',
      WORDS_HINT: 'Also sent when a question cannot be sent as written.',
      WORDS_PLACEHOLDER_EN:
        'Are you still there? Just reply and we will carry on.',
      WORDS_PLACEHOLDER_AR: 'حاب تكمل؟ رد علينا ونكمل.',
      RULES:
        'It never follows up after a person replies, after the chat goes to your team, or once the customer’s last message is almost a day old. It sends only inside the hours you chose above.',
      ERRORS: {
        NEEDS_WORDS: 'Write the words it sends, in at least one language.',
        CHECK: 'Check the follow-up settings.',
      },
    },
    SKIP_LABELS: {
      HEADING: 'Chats it leaves to your team',
      DESCRIPTION:
        'Add a label to a chat or a contact in your inbox and your assistant leaves that chat to your team.',
      LABEL: 'Labels',
      PLACEHOLDER: 'Choose labels',
      NONE: 'No labels yet. Create them in your inbox settings.',
    },
    // Task review item 11: the labels a handover adds, on a task's step and on Personality.
    HAND_LABELS: {
      HEADING: 'Add labels',
      DESCRIPTION:
        'Put on the chat when it is passed to your team, beside needs-a-person. Your inbox rules and filters can use them.',
      NONE: 'No labels yet. Create them in your inbox settings.',
      GONE: 'A label marked here is no longer in your inbox settings. Tap it to take it off.',
      FULL: 'You can add up to {max} labels.',
    },
    NAV: {
      SECTION: 'Waslo AI',
      OVERVIEW: 'Overview',
      ANSWERS: 'Answers',
      PERSONALITY: 'Personality',
      TASKS: 'Tasks',
      MENUS: 'Menus',
      // NOT `Templates`: Chatwoot already spends that exact word on its own read-only
      // Templates page in this same rail, and two rows carrying one word is how an owner
      // opens the wrong screen — the rule `specs/vocabulary.spec.js` enforces. It is also
      // what the Outbound API screens have called them since they shipped.
      TEMPLATES: 'Approved messages',
      // `Forms`, not `Flows`. Meta's own word for these is Flow, and `vocabulary.spec.js`
      // would not have caught it — but an owner has no idea what a flow is, and the Arabic
      // transliteration names a mechanism. See the FORMS block for the full argument.
      FORMS: 'Forms',
      TEST: 'Test it',
      ACTIVITY: 'Activity',
      // Under "For developers" since rebuild 2. `Connections` named nothing an owner
      // recognises, and `الاتصالات` read as TELECOM while sitting beside Chatwoot's own
      // `جهات الاتصال` (Contacts). These are her own systems, which the assistant calls.
      CONNECTIONS: 'Your systems',
      // The second sub-group, a sibling of Settings. It was "Outbound API", which named
      // the protocol rather than the reader — and the vocabulary lint that bans
      // `webhook` and `endpoint` as developer-speak never had `api` on its list, which
      // is the only reason that label shipped. It now names the reader, so an owner can
      // tell in one word that this corner is not addressed to her. The children are the
      // plain nouns for what is behind each one.
      OUTBOUND: 'For developers',
      OUTBOUND_SETUP: 'Keys & limits',
      OUTBOUND_LOG: 'What was sent',
      // NOT under the caption above any more — it moved to Campaigns, beside the other
      // things that go out. It is an owner's list, not a developer's, and leaving it
      // there was what stopped the caption being collapsible. See `wasloai/nav.js`.
      OUTBOUND_NUMBERS: "Numbers we don't message",
      // NOT a child of the Waslo AI group. This label sits in Chatwoot's own Campaigns
      // group — sending marketing messages you pay for per person shares no engine with
      // authoring an assistant, and it was the one tab in the old UI that had nothing to
      // do with the other six. THE PLAN, §2.
      BROADCASTS: 'WhatsApp broadcasts',
    },
    COMMON: {
      RETRY: 'Try again',
      ERROR_TITLE: "We couldn't load this just now",
      ERROR_BODY:
        'Nothing was changed. Check your connection and try again — if it keeps happening, contact us.',
      // Shown when the session itself is the problem, so "check your connection" would be
      // the wrong advice a second time.
      ERROR_SESSION:
        'Your sign-in has expired. Reload the page to sign in again — nothing was changed.',
      // Not an error. This workspace simply has no assistant yet, which is the state every
      // account is in before it is set up. It must never read as a failure, and it must
      // never suggest retrying: nothing the customer does on this page can change it.
      NOT_PROVISIONED_TITLE: 'No assistant on this workspace yet',
      NOT_PROVISIONED_BODY:
        'This workspace has not been set up with an assistant. Contact us and we will connect one to your WhatsApp number — everything on these pages appears once it is running.',
      LOADING: 'Loading',
      // Joining a list of names is a locale decision, not a code one: Arabic separates
      // with an Arabic comma. `Intl.ListFormat` would do it too, but it is one more
      // runtime behaviour to reason about for a string this short.
      LIST_SEPARATOR: ', ',
      // ── THE TWO LANGUAGES, NAMED TWICE, AND THE DIFFERENCE MATTERS ──────────────────
      // LANG_TAG tags a BOX with the language of the words inside it, so it is the
      // language's own name for itself and reads the same to everybody — the same
      // convention a passport uses. It is identical in both tables on purpose, and
      // `specs/vocabulary.spec.js` carries both keys with that reason written down.
      LANG_TAG: { EN: 'English', AR: 'العربية' },
      // LANG_NAME goes inside a SENTENCE the owner is reading, so it is in the language
      // she is reading it in: "Arabic customers…" / "…العملاء الذين يكتبون بالعربية".
      LANG_NAME: { EN: 'English', AR: 'Arabic' },
      // A number with its noun, as one phrase, for the facts a sentence states
      // (`helpers/copy-facts.js`, spec §7 P-7.3). The number is the formatter's.
      COUNT: {
        DAYS: '{count} day | {count} days',
        HOURS: '{count} hour | {count} hours',
        MINUTES: '{count} minute | {count} minutes',
        CHARACTERS: '{count} character | {count} characters',
        OPTIONS: '{count} option | {count} options',
        VALUES: '{count} value | {count} values',
        TIMES: 'once | {count} times',
      },
    },
    // ONE TRUTHFUL STATUS (ease package P-5.1). One block per state code the sidecar can
    // send, and every screen that talks about the assistant reads THIS block: the Overview
    // headline, every channel row, the in-chat panel and the Test it header. TITLE is the
    // sentence, BODY what it means and CHIP the two words on a row.
    // `services/authoring-web/test/status-parity.test.ts` fails if a code has no block.
    STATUS: {
      NOT_SET_UP: {
        TITLE: 'Your assistant is not set up yet.',
        BODY: 'We are still connecting it. Every message waits for your team until then.',
        CHIP: 'Not set up',
      },
      NOT_CONNECTED: {
        TITLE: 'Your assistant is not on this channel.',
        BODY: 'Messages arriving here wait for your team. We can put your assistant on it.',
        CHIP: 'Not connected',
      },
      CHANNEL_OFF: {
        TITLE: 'Replies are off on this channel.',
        BODY: 'Your assistant is on this channel but not answering here, so every message waits for your team.',
        CHIP: 'Off here',
      },
      PAUSED_BY_US: {
        TITLE: 'We paused replies.',
        BODY: 'We paused your assistant and will contact you. Every message waits for your team until then.',
        CHIP: 'Paused by us',
      },
      PLATFORM_LIMIT: {
        TITLE: 'Your assistant has stopped answering.',
        BODY: "Replies are paused by a Waslo-wide limit. We're on it, and nothing on your side needs fixing.",
        CHIP: 'Stopped',
      },
      OUT_OF_CREDITS: {
        TITLE: 'Your assistant has stopped answering.',
        BODY: 'You have run out of credits. It starts again the moment you top up.',
        CHIP: 'Out of credits',
      },
      MONTH_LIMIT: {
        TITLE: 'Your assistant has stopped answering.',
        BODY: "It reached this month's spending limit. We can raise it for you.",
        CHIP: 'Month limit reached',
      },
      DAY_LIMIT: {
        TITLE: 'Your assistant has stopped answering.',
        BODY: "It reached today's spending limit and starts again tomorrow. We can raise the limit for you.",
        CHIP: 'Day limit reached',
      },
      SWITCHED_OFF: {
        TITLE: 'Your assistant is off.',
        BODY: 'Nothing is answered automatically. Every new chat goes to your team.',
        CHIP: 'Replies off',
      },
      PRACTICE: {
        TITLE: 'Your assistant is practising.',
        BODY: 'It answers only your test numbers and the Test it page. Every other chat goes to your team.',
        CHIP: 'Practising',
      },
      ANSWERING: {
        TITLE: 'Your assistant is answering customers on its own.',
        BODY: 'Messages arriving here are answered by your assistant.',
        CHIP: 'Answering',
      },
      UNKNOWN: "We couldn't check your assistant just now.",
      RESUMES: 'It starts again {when}.',
      CUSTOMERS: 'Customers right now: {state}',
      CHANNEL_NAMELESS: 'Channel {id}',
      FIX: {
        TURN_ON: 'Turn it on',
        GO_LIVE: 'Start answering customers',
        REQUEST_CONNECT: 'Ask us to connect it',
        REQUEST_LIMIT_RAISE: 'Ask us to raise it',
        // P-1.6: `channel_off`'s fix. Only an operator can switch a channel back on.
        REQUEST_SWITCH_ON: 'Ask us to switch it on',
        // P-1.6: a fix that needs an administrator, said to a team member with no button.
        ASK_OWNER: {
          REQUEST_CONNECT:
            'Ask your account owner to contact us about this number.',
          REQUEST_TOP_UP: 'Ask your account owner to top up credits.',
          REQUEST_LIMIT_RAISE: 'Ask your account owner to raise the limit.',
          TURN_ON: 'Ask your account owner to turn it on.',
          GO_LIVE: 'Ask your account owner to start answering customers.',
        },
        SENDING: 'Asking…',
        SENT: "We've got it. We'll let you know.",
        FAILED: "We couldn't send that request just now. Try again.",
        SAVE_FAILED:
          "We couldn't change that just now. Nothing was changed. Try again.",
      },
    },
    // P-1.8: one plain sentence under every reply and every handover, and at most one fix.
    // The BFF sends the code (`authoring-web/src/why.ts`); `helpers/why.js` lists them.
    WHY: {
      CODE: {
        TASK_STEP:
          'Your task reached its step that passes the chat to your team.',
        TASK_ASKED_FOR_PERSON:
          'The customer asked for a person during your task, so it passed the chat on.',
        TASK_STUCK:
          'The customer could not answer “{step}”, so it passed the chat on.',
        TASK_CALL_FAILED:
          'Your system did not answer during your task, so it passed the chat on.',
        TASK_BROKEN:
          'Your task “{task}” has a step that leads nowhere, so it passed the chat on.',
        TASK_SYSTEM_REFUSED:
          'Your system said no twice, so it passed the chat on.',
        TASK_CHANGED_AFTER_DONE:
          'The customer wanted to change something already done in your system, so it passed the chat on.',
        TASK_CANCEL_AFTER_DONE:
          'The customer asked to cancel something already done in your system, so it passed the chat on.',
        ASSISTANT_DECIDED:
          'It decided a person should answer this, so it passed the chat on.',
        GATE_WITHHELD:
          'It wrote a price, link, time or list it could not check, so it passed the chat on.',
        CLAIM_WITHHELD:
          'It said something was done when it was not, so it passed the chat on.',
        EMPTY_REPLY: 'It had nothing to say, so it passed the chat on.',
        NO_CREDITS: 'Your credits ran out, so it passed the chat on.',
        SPEND_CAP: 'A spending limit was reached, so it passed the chat on.',
        RUN_TOO_COSTLY:
          'This reply would have cost too much, so it passed the chat on.',
        PAUSED_BY_WASLO:
          'Waslo paused your assistant, so it passed the chat on.',
        TOO_MANY_MESSAGES:
          'Too many messages arrived at once, so it passed the chat on.',
        MESSAGE_TOO_OLD:
          'The message was too old to answer, so it passed the chat on.',
        BUSY_TOO_LONG: 'It was busy for too long, so it passed the chat on.',
        UNREADABLE_MESSAGE:
          'It could not read the message, so it passed the chat on.',
        SEND_UNCERTAIN:
          'Its reply may not have been delivered, so it passed the chat on.',
        PARTIAL_SEND:
          'Only part of its reply was delivered, so it passed the chat on.',
        CHANNEL_REFUSED:
          'The channel refused its reply, so it passed the chat on.',
        ERROR:
          'Something went wrong while answering, so it passed the chat on.',
        ASSISTANT_OFF: 'The assistant is off, so the chat went to your team.',
        PRACTICE:
          'You are still practising, so chats from customers go to your team.',
        ANSWER_WHEN_OPEN:
          'You are open now, so it left this chat for your team.',
        SKIP_LABEL:
          'This customer has the label “{label}”, so it left the chat for your team.',
        CONNECTION_FAILED: 'Your system “{name}” did not answer.',
        TASK_ASKED: 'It asked “{step}” for your task “{task}”.',
        TASK_FINISHED: 'It finished your task “{task}”.',
        ANSWERED_CLOSE: 'It answered from your saved answer “{q}”.',
        ANSWERED_LOOSE: 'It answered from a loosely related answer.',
        ANSWERED_NONE: 'It answered without a matching saved answer.',
        ANSWERED_PLAIN:
          'It answered from what you told it about your business.',
        FOLLOW_UP_SENT:
          'The customer went quiet at “{step}”, so it asked again.',
        FOLLOW_UP_PERSON:
          'It did not follow up: a person was looking after the chat.',
        FOLLOW_UP_WINDOW:
          'It did not follow up: the customer’s last message was almost a day old.',
        FOLLOW_UP_HOURS:
          'It did not follow up: your sending hours stayed closed until it was too late.',
        FOLLOW_UP_NO_WORDS:
          'It did not follow up: it had no words to send in the customer’s language.',
      },
      FIX: {
        TEACH: 'Teach it',
        FIX_TASK: 'Fix it',
        FIX_PERSONALITY: 'Fix it',
        REQUEST_TOP_UP: 'Request top-up',
        REQUEST_LIMIT_RAISE: 'Ask to raise the limit',
        CHECK_CONNECTION: 'Check connection',
        TURN_ON: 'Turn it on',
        SUGGEST: 'Suggest an answer',
      },
      // A fix that needs an administrator, said to a team member as a sentence (P-1.6).
      ASK_OWNER: {
        FIX_TASK: 'Ask your account owner to fix the task.',
        FIX_PERSONALITY:
          "Ask your account owner to check the assistant's settings.",
        REQUEST_TOP_UP: 'Ask your account owner to top up credits.',
        REQUEST_LIMIT_RAISE: 'Ask your account owner to raise the limit.',
        CHECK_CONNECTION: 'Ask your account owner to check the connection.',
        TURN_ON: 'Ask your account owner to turn it on.',
      },
      SENDING: 'Asking…',
      SENT: "We've got it. We'll let you know.",
      FAILED: "We couldn't do that just now. Try again.",
    },
    // PRACTICE (P-5.2): what a new account sees on Overview until it answers every customer.
    READINESS: {
      HEADING: 'Before it answers every customer',
      DESCRIPTION:
        'Your assistant is practising. It answers only the test numbers below and the Test it page, and passes every other chat to your team at no cost.',
      STEPS: {
        DESCRIBED: 'Your business is described',
        KNOWS: 'It knows something',
        TRIED: 'You tried it',
      },
      LINKS: {
        DESCRIBED: 'Open Personality',
        KNOWS: 'Open Answers',
        TRIED: 'Open Test it',
      },
      LEFT: {
        DESCRIBED: 'describe your business',
        KNOWS: 'give it something to answer from',
        TRIED: 'try it',
      },
      DONE: 'Done',
      NOT_DONE: 'Not yet',
      CONFIRM_LEFT:
        'Not done yet: {steps}. Start answering every customer anyway?',
      CONFIRM_READY: 'Start answering every customer now?',
      CONFIRM_YES: 'Yes, start now',
      CONFIRM_NO: 'Not yet',
      GO_LIVE_FAILED:
        "We couldn't start it just now. Nothing was changed. Try again.",
      LIVE_SINCE: 'Answering every customer since {date}.',
      BACK: 'Back to practice',
      BACK_CONFIRM:
        'Go back to practice? Only your test numbers will get replies, and every other chat goes to your team.',
      BACK_YES: 'Yes, back to practice',
      BACK_NO: 'Keep answering',
      BACK_FAILED:
        "We couldn't switch back just now. Nothing was changed. Try again.",
      TESTERS_FAILED: "We couldn't save that. Nothing was changed. Try again.",
      // The editor is `components/outbound/TestNumbersEditor.vue`, reused with `copy`
      // pointing here: the same eleven keys it reads under `OUTBOUND.SETUP.TEST`.
      TESTERS: {
        NUMBERS_HEADING: 'Test numbers',
        NUMBERS_DESCRIPTION:
          'While it practises, only these WhatsApp numbers get its replies. Put your own phone here first.',
        NUMBER_LABEL: 'Phone number',
        ADD: 'Add a number',
        PLACEHOLDER: '+96550001234',
        HINT: 'With the country code, like +96550001234.',
        REMOVE: 'Remove this number',
        EMPTY: 'There are none yet, so nobody gets replies while it practises.',
        FULL: 'That is as many as we can keep.',
        INVALID: "That doesn't look like a phone number with a country code.",
        DUPLICATE: 'That number is already on the list.',
      },
    },
    OVERVIEW: {
      TITLE: 'Overview',
      DESCRIPTION: 'How your assistant is doing right now.',
      REPLIES: {
        HEADING: 'Automatic replies',
        ON: 'On',
        OFF: 'Off',
        ON_BODY: 'Your assistant is answering customers on its own.',
        OFF_BODY:
          'Your assistant is not answering right now. Every message waits for a person.',
        SAVING: 'Saving…',
        SAVE_FAILED:
          "We couldn't change that just now. Nothing was changed — try again.",
      },
      CREDITS: {
        HEADING: 'Message credits',
        UNIT: 'credits left',
        NEXT_EXPIRY: 'Your next batch runs out on {date}.',
        NO_EXPIRY: 'No batch is running out soon.',
        // THIS CARD IS THE ONLY SURFACE ON THE PAGE THAT TALKS ABOUT THE BALANCE, so each
        // of these says only what is not already on screen beside it. "Your balance is
        // low" is gone from all three: the number directly above is amber (or ruby) and
        // reads "credits left", which is that sentence, in less space, unmissably. What
        // is left is the consequence and the one thing to do about it — and at zero even
        // the consequence is redundant, because the page's status line has already said
        // the assistant stopped.
        LOW: 'Your assistant goes quiet when this reaches zero.',
        CRITICAL: 'Your assistant is about to go quiet.',
        EMPTY:
          'Your assistant has stopped. Request a top-up to start it again.',
        INTERNAL: 'This workspace is ours. Nothing here is billed.',
        UNAVAILABLE: "We couldn't read your balance just now.",
        // P-5.3: the pace, counted in her own day. `{credits}` is `creditsPhrase` (one plural
        // phrase, `WASLO.AI_CONFIRM.CREDITS`), so these two carry no bare number. "Over the last
        // week", not the spec's "(last 7 days)": A1's rule is that a string printing `{credits}`
        // holds no digit of its own (`ai-confirm.spec.js`).
        USED_TODAY: 'Used today: {credits}',
        DAILY_AVERAGE: 'Daily average over the last week: {credits}',
        LASTS:
          'Lasts about {count} day at this pace. | Lasts about {count} days at this pace.',
        LASTS_TODAY: 'Runs out today at this pace.',
        NOT_ENOUGH_USE: 'Not enough use yet to tell how long it lasts.',
        // The one button. Billing is manual, so it asks us.
        REQUEST: 'Request top-up',
        SENDING: 'Sending…',
        REQUESTED: 'Request sent',
        SENT: "We've told the Waslo team. We'll be in touch soon.",
        TOLD: "We've already been told",
        REQUEST_FAILED: "We couldn't send that just now. Try again.",
      },
      // The top-of-page line. It is a SENTENCE about what the assistant is doing at this
      // moment, not a label — an owner opening this screen is asking one question and it
      // is answered before she reads anything else.
      STATUS: {
        ANSWERING: 'Your assistant is answering customers on its own.',
        PAUSED: 'Your assistant is paused.',
        PAUSED_BODY:
          'Nothing is answered automatically. Every new message waits for someone on your team.',
        STOPPED: 'Your assistant has stopped answering.',
        STOPPED_BODY:
          'You have run out of credits. It starts again the moment you top up.',
        // WHICH limit stopped it. One headline, four different reasons under it, because
        // what an owner should DO about each one is different: top up, wait for midnight,
        // ask us to raise a ceiling, or nothing at all. They shared the credits sentence
        // until W-02, so three of the four sent her to buy credits she already had.
        STOPPED_DAY_BODY:
          "Your assistant reached today's spending limit. It starts again tomorrow — contact us if you need the limit raised.",
        STOPPED_MONTH_BODY:
          "Your assistant reached this month's spending limit. Contact us to raise it.",
        // Ours, not hers. Nothing she can do changes it, so nothing here asks her to.
        STOPPED_PLATFORM_BODY:
          "Replies are paused by a Waslo-wide limit. We're on it — nothing on your side needs fixing.",
        LAST_REPLY: 'Last reply {when}.',
        NO_REPLY: 'It has not had to reply in the last {days} days.',
        JUST_NOW: 'just now',
        UNKNOWN_BODY: "We couldn't read how busy it has been just now.",
      },
      // How much of the month is gone, shown only once half of it is — below that it is
      // noise, and a number on screen for no reason teaches an owner to stop reading the
      // ones that matter. `{percent}` arrives already formatted for her locale, sign and
      // all: `ar` resolves to Latin digits on current ICU and reads "62%", while `ar-EG`
      // and `ar-KW` resolve to Arabic-Indic and read "٦٢٪". Which one she gets is the
      // locale's decision, which is exactly why the sign is not written into this string.
      //
      // A PERCENTAGE AND NOTHING ELSE. What the limit is worth in money is ours to know.
      LIMITS: {
        MONTH_USED: "{percent} of this month's AI limit used",
      },
      NUMBERS: {
        CONVERSATIONS: 'Conversations today',
        HANDOFFS: 'Handed to your team today',
        WINDOW_HINT: '{count} in the last {days} days',
        UNAVAILABLE: "We couldn't load today's numbers.",
        DAY_TITLE: 'Counted for {day}.',
      },
      // P-5.4: the last 7 whole days in her own timezone, and her own weekly email.
      WEEK: {
        HEADING: 'This week',
        DATES: '{from} to {to}',
        CONVERSATIONS: 'Conversations',
        HANDLED: 'Handled without your team',
        HANDLED_NONE: 'No conversations yet',
        HANDOVERS: 'Passed to your team',
        TASKS: 'Tasks finished',
        CREDITS: 'Credits used',
        UNAVAILABLE: "We couldn't load this week's numbers.",
        EMAIL_LABEL: 'Email me this every week',
        EMAIL_DAY: 'Send it on',
        EMAIL_TO: 'We send it to {address}, in the morning.',
        EMAIL_SAVING: 'Saving…',
        EMAIL_FAILED: "We couldn't save that just now. Nothing was changed.",
      },
      ATTENTION: {
        HEADING: 'Needs your attention',
        DESCRIPTION:
          'Everything listed here is one action away from being done.',
        CLEAR_TITLE: 'Nothing needs you right now',
        CLEAR_BODY:
          'Your assistant is handling its messages on its own. Anything that needs a decision from you will appear here.',
        UNCHECKED: "We couldn't check {sources} just now.",
        // No `CREDITS` entry. The balance is not one of this list's sources — the credit
        // card owns it end to end, including saying so when it could not be read.
        SOURCES: {
          INBOXES: 'your channels',
          NEEDS_YOU: 'your menus, forms and sources',
          ACTIVITY: 'the questions it could not answer',
          TEMPLATES: 'what WhatsApp decided about your messages',
          CONNECTIONS: 'your connected systems',
          TASKS: 'your tasks',
        },
        ASKED: 'Asked {count}×',
        DISCONNECTED_INBOX: {
          TITLE: 'No assistant on “{name}”',
          BODY: 'Messages arriving here wait for a person. Nothing is answered.',
          // NOT "Connect it". That verb promised something this screen cannot do — it
          // linked to a settings page that redirects straight back here, so the most
          // urgent row in the queue carried the one control that did nothing. Putting an
          // assistant on a number is ours to do, so the row asks us, and says so.
          VERB: 'Ask us to connect it',
        },
        // What became of that ask. No "you already asked today" anywhere: that is our
        // bookkeeping, and to an owner who has just pressed a button it reads as a
        // refusal. Both answers mean the same thing to her — we have it.
        REQUEST: {
          SENDING: 'Asking…',
          SENT: "Request sent — we'll connect it and let you know.",
          FAILED: "We couldn't send that request just now. Try again.",
        },
        FAILED_SOURCE: {
          TITLE: 'We could not read “{title}”',
          BODY: 'Nothing from it was saved, so your assistant cannot use it yet.',
          VERB: 'Try again',
        },
        UNANSWERED: {
          BODY: 'A customer asked this and your assistant had no answer.',
          VERB: 'Fix it',
        },
        // P-6.9: a "For now" note whose last day is tomorrow. Its verb opens Answers.
        NOTICE_ENDING: {
          TITLE: 'Your note ends tomorrow',
          VERB: 'Extend it',
        },
        // P-4.6: WhatsApp's verdict on a message she sent for approval, without her asking.
        TEMPLATE_REFUSED: {
          TITLE: 'WhatsApp refused “{name}” ({language}).',
          BODY: 'WhatsApp did not say why. Change the wording and send it again.',
          VERB: 'Fix it',
        },
        TEMPLATE_PAUSED: {
          TITLE: 'WhatsApp paused “{name}” ({language}).',
          BODY: 'WhatsApp stopped it for now, usually after customers blocked or reported it. Broadcasts cannot use it until it is back.',
          VERB: 'See why',
        },
        TEMPLATE_APPROVED: {
          TITLE: 'WhatsApp approved “{name}” ({language}).',
          BODY: 'You can use it in a WhatsApp broadcast now.',
          VERB: 'Use it in a broadcast',
        },
        /** Meta's own reason codes, in her words. A code not listed here is shown as Meta wrote it. */
        TEMPLATE_REASON: {
          ABUSIVE_CONTENT: 'WhatsApp found wording it does not allow.',
          INCORRECT_CATEGORY:
            'WhatsApp thinks it belongs in the other category. Pick it and send it again.',
          INVALID_FORMAT:
            'WhatsApp could not read how it is written, often a blank in the wrong place.',
          SCAM: 'WhatsApp thought it could mislead customers.',
          TAG_CONTENT_MISMATCH:
            'WhatsApp thinks the wording does not match the category you picked.',
        },
        LANGUAGE: {
          AR: 'Arabic',
          EN: 'English',
        },
        // P-3.5: the only failing-connection row. §5's list does not repeat it.
        FAILING_CONNECTION: {
          TITLE: '“{name}” is not answering',
          BODY: 'It failed {count} time this week. | It failed {count} times this week.',
          VERB: 'Open it',
        },
        TASK_CONNECTION_OFF: {
          TITLE: '“{task}” uses “{connection}”, which is switched off.',
          BODY: 'Customers who reach it are passed to your team.',
          VERB: 'Open your systems',
        },
        // P-1.3: nobody is chosen for passed-on chats, so nobody is alerted.
        HANDOVER_UNSET: {
          TITLE: 'Choose who gets chats the assistant passes on',
          BODY: 'Right now nobody is alerted when it passes a chat on.',
          VERB: 'Send them to me',
          FAILED: "We couldn't save that just now. Try again.",
        },
        // P-1.7: what the team suggested, waiting on the owner.
        TEAM_SUGGESTIONS: {
          TITLE:
            '{count} answer suggested by your team | {count} answers suggested by your team',
          BODY: 'Accept the ones that are right and your assistant learns them.',
          VERB: 'Review them',
        },
        // P-5.9: what breaks silently, each with one fix.
        MENU_TASK_MISSING: {
          TITLE:
            'The menu “{title}” has an option that opens a task you removed',
          BODY: 'Customers who tap it reach a dead end.',
          VERB: 'Edit the menu',
        },
        MENU_TASK_OFF: {
          TITLE: 'The menu “{title}” has an option whose task is switched off',
          BODY: 'Customers who tap it are passed to your team.',
          VERB: 'Edit the menu',
        },
        FORM_OUT_OF_DATE: {
          TITLE: 'The form for “{title}” is out of date',
          BODY: 'You changed the task after publishing its form. Customers who open the old form reach a dead end.',
          VERB: 'Publish it again',
        },
        FORM_PAUSED: {
          TITLE: 'WhatsApp paused the form for “{title}”',
          BODY: 'Customers had trouble using it. Your assistant asks these questions in the chat until WhatsApp lifts the pause, which it does by itself once the form works again.',
          VERB: 'See your forms',
        },
        SOURCE_TAUGHT_NOTHING: {
          TITLE: '“{title}” taught your assistant nothing',
          BODY: 'There was nothing in it your assistant could learn from.',
          VERB: 'Replace it',
        },
      },
      // THE CHANNEL LIST, folded here when Settings -> Channels was merged into this page.
      // The states below used to live under `SETTINGS.CHANNELS.STATE`, on a page whose
      // whole reason for existing was that a chip is not enough when something IS wrong.
      // That page is gone, so the consequence and the thing to do about it moved with the
      // rows rather than being dropped — see the header of `components/ChannelRow.vue`.
      CHANNELS: {
        HEADING: 'Your channels',
        DESCRIPTION:
          'Where your customers message you, and whether your assistant is on it.',
        NO_NUMBER: 'No number yet',
        NONE: 'No channel has been set up on this workspace yet.',
        UNAVAILABLE: "We couldn't list your channels just now.",
        STATE: {
          ANSWERING: 'Answering',
          ANSWERING_BODY:
            'Messages arriving here are answered by your assistant.',
          PAUSED: 'Replies off',
          PAUSED_BODY:
            'Your assistant is on this channel, but automatic replies are off, so every message waits for your team.',
          // The honest fourth state. `aiEnabled` read as a boolean makes a FAILED read
          // render as "Replies off" — a confident sentence about a setting nobody could
          // read. This row takes a tri-state so it can say what it actually knows.
          READY: 'Set up',
          READY_BODY: 'Your assistant is on this channel.',
          NOT_CONNECTED: 'Not connected',
          NOT_CONNECTED_BODY:
            'Nothing here is answered automatically. Contact us and we will put your assistant on this channel.',
        },
      },
    },
    PERSONALITY: {
      TITLE: 'Personality',
      SPOTLIGHT:
        'Your assistant reads this page before it answers anyone. Write it the way you would brief a new employee on their first day — short, plain sentences, no special words. Changes take effect on the very next message, and you can come back and change them whenever you like.',
      ABOUT: {
        HEADING: 'What your assistant is like',
        DESCRIPTION:
          'Four short answers are all it needs. Leave a box empty if it does not apply to you.',
      },
      FIELDS: {
        IDENTITY: {
          LABEL: 'Who it is',
          PLACEHOLDER:
            'You are Noura, the front desk of Bayt Al Oud. You are warm, short and polite, and you always reply in the language the customer wrote in.',
          HINT: 'A name, a job, and how it should sound.',
        },
        BUSINESS: {
          LABEL: 'What your business does',
          PLACEHOLDER:
            'Bayt Al Oud sells oud and perfume from one shop in Salmiya. We open 10am to 10pm, Saturday to Thursday. We deliver anywhere in Kuwait in two days for 2 KD, free over 30 KD.',
          HINT: 'What you sell, where you are, when you open, how you deliver.',
        },
        ALWAYS: {
          LABEL: 'What it must always do',
          PLACEHOLDER:
            'Ask which area the customer is in before promising a delivery day.\nSend the price list when someone asks about prices.\nOffer to bring in a person if the customer sounds upset.',
          HINT: 'One instruction on each line.',
        },
        NEVER: {
          LABEL: 'What it must never do',
          PLACEHOLDER:
            "Never invent a price or a delivery date.\nNever promise a discount that is not on the price list.\nNever tell one customer about another customer's order.",
          HINT: 'One instruction on each line. These are the lines it will not cross.',
        },
      },
      DOCUMENT: {
        BANNER:
          'Your instructions were written as one long note. You can keep editing it here, or let us try to split it into the four boxes.',
        BANNER_ACTION: 'Split it into boxes',
        LABEL: 'Your instructions',
        PLACEHOLDER:
          'Tell your assistant who it is, what your business does, and what it must and must not say.',
        HINT: 'This is exactly what you wrote. Nothing here has been changed.',
        PREVIEW_SPLITS:
          'Using this fills the four boxes above. Nothing is saved until you press Save, and you can throw it away instead.',
        PREVIEW_NO_SPLIT:
          'We could tidy the wording but not split it into boxes. Using it replaces the note above. Nothing is saved until you press Save.',
      },
      ASSIST: {
        ACTION: 'Suggest better wording',
        PREVIEW_TITLE: 'Suggested wording',
        PREVIEW_NOTE:
          'Nothing has changed yet. Read it, then keep it or throw it away.',
        ACCEPT: 'Use this',
        DISCARD: 'Throw it away',
        FAILED:
          'We could not write a suggestion just now. What you wrote has not changed.',
      },
      STYLE: {
        HEADING: 'How freely it writes',
        DESCRIPTION:
          'Choose how closely your assistant sticks to what you told it above.',
        OPTIONS: {
          STRICT: {
            LABEL: 'Stick to what it knows',
            DESCRIPTION:
              'It answers only from what you wrote here and from the answers you saved, in almost the same words every time. Safest for prices and policies.',
          },
          BALANCED: {
            LABEL: 'Balanced',
            DESCRIPTION:
              'It stays inside what you told it, but words each reply naturally. This suits most businesses.',
          },
          CREATIVE: {
            LABEL: 'More chatty',
            DESCRIPTION:
              'It talks more freely and fills small gaps in its own words. Livelier, and more likely to say something you did not plan for.',
          },
        },
        EXACT: 'Exact setting right now: {value}',
        ADVANCED: 'Advanced',
        ADVANCED_LABEL: 'Exact setting',
        ADVANCED_HINT:
          'A number from {min} to {max}. Lower is more predictable, higher is more varied. Most businesses never touch this.',
      },
      // P-1.3: who gets the chats the assistant passes on. Saved with the page's bar.
      HAND_TO: {
        HEADING: 'Hand chats to',
        DESCRIPTION:
          'The person who gets a chat your assistant passes on is alerted, and the chat is labelled so your team can find it.',
        ANYONE: 'Whoever is online',
        ANYONE_BODY: 'The first team member online gets it.',
        PERSON: 'A person',
        PERSON_BODY: 'Always the same person.',
        TEAM: 'A team',
        TEAM_BODY: 'Someone online in that team.',
        PERSON_LABEL: 'Who',
        TEAM_LABEL: 'Which team',
        FALLBACK_LABEL: 'If nobody is online, give it to',
        CHOOSE: 'Choose someone',
        CHOOSE_TEAM: 'Choose a team',
        UNSET:
          'Nobody is chosen yet, so passed-on chats wait for whoever picks them up.',
        NOT_MEMBER:
          '{name} cannot see {channels}, so they may miss chats from there.',
        PEOPLE_FAILED: "We couldn't load your team just now.",
      },
      HANDOVER: {
        HEADING: 'When a person takes over',
        DESCRIPTION:
          'Your assistant can answer on its own, and pass the chat to your team when it cannot help.',
        AUTO_LABEL: 'Reply to customers automatically',
        AUTO_ON:
          'New messages are answered by your assistant. Your team still sees everything.',
        AUTO_OFF:
          'Nothing is answered automatically. Every message waits for someone on your team.',
        MESSAGE_LABEL: 'What to say when a person takes over',
        // Was "Leave it empty to say nothing" — wrong: an empty box sends a built-in message.
        MESSAGE_HINT:
          'Customers who write in Arabic see the Arabic message; everyone else sees the English one. If one box is empty the other is used, and if both are empty a short built-in message is sent.',
        MESSAGE_EN_LABEL: 'Message in English',
        MESSAGE_EN_PLACEHOLDER:
          'One moment please — I am passing you to someone from our team.',
        MESSAGE_AR_LABEL: 'Message in Arabic',
        MESSAGE_AR_PLACEHOLDER: 'لحظة من فضلك، سأحوّلك إلى أحد موظفي فريقنا.',
        CLOSED_LABEL: 'When you’re closed',
        CLOSED_HINT:
          'Sent instead of the message above when a chat is passed to your team outside your opening hours. Leave it empty and we say when your team will reply.',
        CLOSED_EN_LABEL: 'When closed, in English',
        CLOSED_EN_PLACEHOLDER:
          'Thanks for your message. Our team is away now and will reply when we open.',
        CLOSED_AR_LABEL: 'When closed, in Arabic',
        CLOSED_AR_PLACEHOLDER:
          'شكرًا لرسالتك. فريقنا غير متاح الآن، وسيرد عليك عند فتحنا.',
      },
      // P-7.1 (decided O-2): bilinguality is optional. One setting, saved with the page's bar.
      LANGUAGES: {
        HEADING: 'Your customers write in',
        DESCRIPTION:
          'Your assistant answers in the language you choose here. With Both, it answers each customer in the language they write in, and you write your words in both.',
        OPTIONS: {
          AR: {
            LABEL: 'Arabic',
            DESCRIPTION:
              'Every reply is in Arabic, and you write only Arabic words.',
          },
          EN: {
            LABEL: 'English',
            DESCRIPTION:
              'Every reply is in English, and you write only English words.',
          },
          BOTH: {
            LABEL: 'Both',
            DESCRIPTION:
              'Each customer is answered in the language they write in. A word missing in one language is pointed out, never a reason a save is refused.',
          },
        },
      },
      // P-7.2: how the assistant speaks Arabic. Two choices that ride the Save bar.
      ARABIC_VOICE: {
        HEADING: 'How it speaks Arabic',
        DESCRIPTION:
          'Facts, names and prices stay exactly as you wrote them. This changes only how the Arabic sounds.',
        REGISTER_LABEL: 'How it writes',
        REGISTER: {
          MATCH: {
            LABEL: 'Like the customer',
            DESCRIPTION:
              'Dialect to dialect, formal to formal, and polite Kuwaiti Arabic when it cannot tell.',
          },
          FORMAL: {
            LABEL: 'Always formal Arabic',
            DESCRIPTION:
              'Clear, polite formal Arabic, even when the customer writes in dialect.',
          },
        },
        SPEAKS_AS_LABEL: 'Speaks as',
        SPEAKS_AS: {
          NONE: {
            LABEL: 'Not chosen',
            DESCRIPTION: 'It avoids words that say she or he where it can.',
          },
          SHE: {
            LABEL: 'She',
            DESCRIPTION: 'For example, موجودة and سعيدة بخدمتك.',
          },
          HE: {
            LABEL: 'He',
            DESCRIPTION: 'For example, موجود and سعيد بخدمتك.',
          },
        },
      },
      SAVE: 'Save changes',
      // The route-leave guard's dialog. It only ever appears when there is something to
      // lose, so it never says "are you sure" about nothing.
      LEAVE: {
        TITLE: 'Leave without saving?',
        BODY: 'You have changes that have not been saved yet. If you leave now they are lost.',
        CONFIRM: 'Leave without saving',
        CANCEL: 'Stay on this page',
      },
      PERMISSIONS: {
        HEADING: 'What it may do in a chat',
        DESCRIPTION:
          'Each one is off until you turn it on. Try it on the Test it page before you trust it with real customers.',
        ITEMS: {
          CLOSE: {
            LABEL: 'Mark a chat as done',
            BODY: 'When the customer has what they needed, it can close the chat so your team is not looking at something that is finished.',
          },
          ASSIGN: {
            LABEL: 'Pass a chat to someone',
            BODY: 'It can hand the chat to a team or a person you pick below — and to nobody else.',
          },
          LABELS: {
            LABEL: 'Put labels on a chat',
            BODY: 'It can tag a chat with the labels you pick below, so you can find them again later.',
          },
          NOTE: {
            LABEL: 'Leave a note for your team',
            BODY: 'It can write a note on the chat for your team. Your customer never sees it.',
          },
          CONTACT: {
            LABEL: 'Save customer details',
            BODY: 'When a customer tells it their name, email, phone or anything else you tick below, it saves it to their profile — and it can label them so you can find them again later.',
          },
        },
        ON: 'On',
        OFF: 'Off',
        SAVING: 'Saving…',
        SAVE: 'Save',
        SAVED: 'Saved. Your assistant follows this from the next message.',
        SAVE_FAILED:
          "We couldn't save that. Nothing was changed — please try again.",
        SWITCH_FAILED:
          "We couldn't change that just now. Nothing was changed — try again.",
        GUIDANCE: {
          LABEL: 'When should it do this?',
          PLACEHOLDER:
            'Only after the customer says their problem is solved, and never while they are waiting for a delivery.',
          HINT: 'Optional. Leave it empty and it decides for itself.',
        },
        // The five states of the "who may it pick" list. Four of them are the ones a
        // screenshot never shows, and every one of them used to be silently rendered as
        // an empty list of ticks — which is the same picture as "you have chosen nothing".
        CHOICES: {
          TEAMS_HEADING: 'Who it may pass a chat to',
          TEAMS_DESCRIPTION:
            'It can only pick from this list. Tick nobody and it can pass a chat to nobody.',
          LABELS_HEADING: 'Labels it may use',
          LABELS_DESCRIPTION:
            'It can only use these. Tick nothing and it can label nothing.',
          TEAM: 'Team',
          PERSON: 'Person',
          CHOSEN: 'none chosen | 1 chosen | {count} chosen',
          LOADING: 'Reading your teams, people and labels…',
          NONE_ASSIGN:
            'You have no teams or people set up yet, so there is nobody for it to pass a chat to.',
          NONE_LABELS:
            'You have no labels set up yet, so there is nothing for it to put on a chat.',
          UNREADABLE:
            "We couldn't read your teams, people and labels just now. What you chose before is kept exactly as it is — nothing here can change it until this loads.",
          UNREADABLE_KEPT: 'Kept: {names}',
          UNREADABLE_RETRY: 'Try again',
          INERT:
            'This is on, but nothing is picked — so your assistant still cannot do it. Pick at least one below.',
        },
        // Lead capture's chooser (`ContactFieldsEditor.vue`). Chatwoot calls these custom
        // attributes; the owner reads "details" on the customer's "profile".
        CONTACT_DETAILS: {
          FIELDS_HEADING: 'Details it may save',
          FIELDS_DESCRIPTION:
            'It only saves what the customer actually tells it, and only into the details you tick.',
          // What the sidecar's `contact-writer.ts` really does on a uniqueness 422: the
          // refused value is skipped, a private note goes on the chat, the rest is saved.
          FIELDS_CONFLICT:
            'If an email or phone number already belongs to another customer, it leaves that one as it is and writes a private note on the chat, so your team can check whether it is the same person. Everything else the customer gave is still saved.',
          LABELS_HEADING: 'Labels it may put on the customer',
          LABELS_DESCRIPTION:
            'These go on the customer’s profile, not on the chat, so you can reach them again later. Tick nothing and it labels nobody.',
          ADD_DETAIL:
            'Need a detail that is not here? Add it in your settings.',
          STANDARD: {
            NAME: 'Name',
            EMAIL: 'Email',
            PHONE_NUMBER: 'Phone number',
          },
          TYPES: {
            STANDARD: 'Built in',
            TEXT: 'Text',
            NUMBER: 'Number',
            CURRENCY: 'Amount',
            PERCENT: 'Percent',
            LINK: 'Link',
            DATE: 'Date',
            LIST: 'Choice',
            CHECKBOX: 'Yes or no',
          },
          LOADING: 'Reading the details and labels you keep…',
          UNREADABLE:
            "We couldn't read the details and labels you keep just now. What you chose before is kept exactly as it is — nothing here can change it until this loads.",
          UNREADABLE_KEPT: 'Kept: {names}',
          UNREADABLE_RETRY: 'Try again',
          NONE_LABELS:
            'You have no labels set up yet, so there is nothing for it to put on a customer.',
          CHOSEN: 'none chosen | 1 chosen | {count} chosen',
          NO_SERVICE_ACCOUNT:
            'This is on, but it needs one more thing connected on our side before it can save anything. Contact us and we will connect it.',
        },
      },
      SAVED:
        'Saved. Your assistant uses the new wording from the next message.',
      SAVE_FAILED:
        'We could not save that. Nothing was changed — please try again.',
      // The page holds two kinds of thing and one Save. When part of it lands and part of
      // it does not, saying "nothing was changed" is simply false, and it is the sentence
      // an owner would act on — she would write it all again. So the mixed result gets its
      // own words, in the toast and in the bar.
      SAVE_PARTIAL:
        'Saved — except for one of the things it may do. The message on that one says what happened.',
      // The docked bar. Five states, each of which has to be true on its own without the
      // owner remembering what she just did — which is why the two that follow an action
      // name the sections and the time rather than saying "changes".
      STATUS: {
        IDLE: 'Everything is saved',
        IDLE_DETAIL: 'Your assistant is using this now.',
        DIRTY: 'Not saved yet',
        DIRTY_DETAIL: 'Waiting to be saved: {sections}',
        SAVING: 'Saving…',
        SAVED: 'Saved at {time}',
        SAVED_DETAIL:
          '{sections} — your assistant uses this from the next message.',
        ERROR: 'We could not save that',
        ERROR_DETAIL:
          'Nothing was changed, and what you wrote is still on the page. Try again.',
        // The mixed result: part of the page is stored and part of it is not. It shares
        // the error state's ruby icon — something did go wrong — but it must not share
        // ERROR_DETAIL, which would sit on screen claiming nothing was changed.
        PARTIAL: 'Saved, but one thing was not',
        PARTIAL_DETAIL:
          'Saved: {sections}. One of the things it may do was not — the message on it says which.',
      },
      ERRORS: {
        EMPTY_PROMPT: 'Tell your assistant who it is before you save.',
        LONG_PROMPT: 'That is too long. Keep it under {limit}.',
        STYLE_RANGE: 'Enter a number between {min} and {max}.',
      },
    },
    ANSWERS: {
      TITLE: 'Answers',
      SPOTLIGHT:
        'Your assistant answers from what you put here, and from nothing else — your price list, your delivery rules, the pages of your website. Add a source, we read it and turn it into questions and answers, and it starts using them within a few minutes.',
      ADD: 'Add knowledge',
      // P-1.7: answers the team suggested from the chat panel (C3 adds mined ones).
      TEAM: {
        HEADING: 'Suggested by your team',
        DESCRIPTION:
          'Answers your team thinks the assistant should know. Accept one to teach it.',
        BY: 'Suggested by {name}',
        QUESTION_LABEL: 'When a customer asks',
        ANSWER_LABEL: 'Your assistant should say',
        ACCEPT: 'Accept',
        EDIT: 'Edit',
        DISMISS: 'Dismiss',
        ACCEPT_EDITED: 'Accept with my changes',
        CANCEL: 'Cancel',
        ACCEPTED: 'Taught. Your assistant uses it from its next message.',
        DISMISSED: 'Dismissed. Nothing was changed.',
        GONE: 'Someone already decided on this one.',
        FAILED: "We couldn't do that just now. Nothing was changed. Try again.",
        UNAVAILABLE: "We couldn't load your team's suggestions just now.",
      },
      EMPTY: {
        TITLE: 'Your assistant has nothing to read yet',
        BODY: 'Give it your price list, your delivery rules or your website, and it will start answering from them in a few minutes. You can add as much as you like, whenever you like.',
      },
      // The count is branched in code rather than through a plural rule: vue-i18n's
      // default rule has three forms and Arabic has six, so a `|` list would be wrong in
      // one of the two languages whatever we wrote. Four named cases are the honest
      // subset — none, one, the Arabic dual, and everything above it.
      COUNT: {
        NONE: 'No answers yet',
        ONE: '1 answer',
        TWO: '2 answers',
        MANY: '{count} answers',
      },
      LIBRARY: {
        HEADING: 'What your assistant has read',
        DESCRIPTION:
          'Open any of these to see the questions it learned, and the text it learned them from.',
        UPDATED: 'Updated {when}',
        ADDED: 'Added {when}',
        NOTHING_LEARNED: 'Nothing was learned from this one.',
        UNAVAILABLE: "We couldn't list what your assistant has read.",
      },
      WORKING: {
        HEADING: 'Just added',
        DESCRIPTION:
          'You can close this page — reading carries on without you.',
        FAILED_HEADING: 'Could not be read',
        FAILED_DESCRIPTION:
          'Nothing from these was saved. Add one again whenever you like, or dismiss it.',
        QUEUED: 'Waiting to start',
        QUEUED_BODY: 'It begins in a moment.',
        CRAWLING: 'Reading your website',
        CRAWLING_BODY: 'Pages read so far: {count}.',
        GENERATING: 'Writing the answers',
        GENERATING_BODY: 'Written so far: {count}.',
        RUNNING: 'Reading it',
        RUNNING_BODY: 'This usually takes a minute or two.',
        READY: 'Ready to use',
        READY_BODY: 'Your assistant is answering from this now.',
        READY_EMPTY:
          'There was nothing in it your assistant could learn from. Try a page with more writing on it.',
        READY_NOTE: 'One thing to know: {warning}',
        FAILED: 'We could not read this',
        FAILED_BODY:
          'Nothing from it was saved, so your assistant cannot use it yet.',
        STALLED: 'This is taking longer than usual',
        STALLED_BODY:
          'We stopped checking on it. Nothing is lost. Look again whenever you like.',
        CHECK: 'Check again',
        OPEN: 'Open it',
        AGAIN: 'Add it again',
        DISMISS: 'Dismiss',
        DISMISS_LABEL: 'Dismiss “{title}”',
        DISMISS_FAILED: "We couldn't dismiss that just now. Try again.",
        UNAVAILABLE: "We couldn't check what is being read right now.",
      },
      // P-5.6: why a source could not be read, one plain sentence per reason code. Never a
      // vendor name or a status number: those stay with us.
      REASON: {
        SITE_BUSY: 'That website was busy. Try again in an hour.',
        SITE_BLOCKED:
          'That website would not let us in. Try a page anyone can open without signing in.',
        SITE_UNREACHABLE:
          "We couldn't open that page. Check the address and try again.",
        TIMED_OUT:
          'It took too long to read. Try a smaller page or a shorter file.',
        FILE_UNREADABLE: 'That file had no text we could read.',
        TOO_LARGE:
          'That is too large to read in one go. Try a smaller file or one page.',
        NOTHING_FOUND:
          'This had nothing we could learn from. Try a page with more writing on it.',
        PAUSED_BY_LIMITS:
          'Reading was paused by a spending limit. Try again once it lifts.',
        NO_CREDITS:
          'There were not enough credits to read it. Try again after topping up.',
        OUR_SIDE:
          'Something went wrong on our side. Try again in a few minutes.',
        UNKNOWN: "We couldn't read it. Try again, or add it a different way.",
      },
      // P-5.6: a source that was read, with something worth knowing.
      NOTE: {
        NOTHING_FOUND:
          'There was nothing in it your assistant could learn from.',
        PAGE_LIMIT:
          'We read as many pages as we can in one go. Add the pages that matter most on their own.',
        PARTIAL_CRAWL:
          'Some pages could not be read, so a few answers may be missing.',
        UNKNOWN: 'Some of it could not be read.',
      },
      DIALOG: {
        TITLE: 'Add knowledge',
        DESCRIPTION:
          'Pick where this one comes from. You can add as many as you like, one at a time.',
        CHOICES: {
          TEXT: {
            LABEL: 'Type or paste it',
            DESCRIPTION:
              'Best for your prices, your opening hours, or a rule you can write out in a few lines.',
          },
          URL: {
            LABEL: 'Read my website',
            DESCRIPTION:
              'We follow the pages under one address and read what is written on them.',
          },
          PDF: {
            LABEL: 'Upload a PDF',
            DESCRIPTION:
              'A menu, a catalogue or a policy you already have as a file. A PDF that is only photographs of pages cannot be read.',
          },
        },
        NAME_LABEL: 'Name it',
        NAME_HINT: 'Only you see this. It is how you will find it again.',
        NAME_PLACEHOLDER: 'August price list',
        NAME_REQUIRED: 'Give this a name so you can find it later.',
        NAME_LONG: 'That name is too long. Keep it under {limit}.',
        TEXT_REQUIRED: 'Write or paste what your assistant should learn.',
        TEXT_LONG: 'That is too long to add in one go. Split it into two.',
        URL_INVALID: 'Enter the full address, starting with https://',
        FILE_REQUIRED: 'Choose a PDF to add.',
        FILE_LARGE: 'That file is bigger than {size} MB. Try a smaller one.',
        TEXT_LABEL: 'What should it know?',
        TEXT_HINT:
          'Write it the way you would tell a new employee. Short lines are easier for it than long paragraphs.',
        TEXT_PLACEHOLDER:
          'Dehn Al Oud Muattaq 12ml — 45 KD\nBakhoor Al Salam 50g — 12 KD\nDelivery is 2 KD anywhere in Kuwait, free over 30 KD.\nWe deliver within two days, Saturday to Thursday.',
        URL_LABEL: 'Your web address',
        URL_HINT:
          'Start with https://. We read the pages under this address, not the whole internet.',
        URL_PLACEHOLDER: 'https://baytaloud.com',
        FILE_LABEL: 'Your PDF',
        FILE_HINT: 'One file, up to {size} MB.',
        FILE_CHOOSE: 'Choose a file',
        FILE_CHANGE: 'Choose a different one',
        FILE_NONE: 'No file chosen yet',
        SUBMIT: 'Add it',
        CANCEL: 'Cancel',
        QUEUED: "Added. We're reading it now.",
        DEDUPED:
          'You already added that one — we are showing you the one already being read.',
        FAILED: "We couldn't add that just now. Nothing was saved — try again.",
      },
      SOURCE: {
        TITLE: 'Source',
        NOT_FOUND: 'That source is not here any more.',
        UNAVAILABLE: "We couldn't open this source just now.",
        SPOTLIGHT:
          'These are the questions your assistant learned from this source. It searches these, not the original text — so if it answered something wrongly, this is where you will see why.',
        ANSWERS_HEADING: 'What it learned',
        ANSWERS_DESCRIPTION:
          'Each question a customer might ask, and the answer your assistant would give.',
        ANSWERS_NONE:
          'This one produced no questions. Your assistant cannot answer from it yet — a source with more writing on it works better.',
        ANSWERS_UNAVAILABLE: "We couldn't read what it learned from this.",
        SHOWING: 'Showing the first {shown} of {total}.',
        ORIGINAL: 'The original text',
        ORIGINAL_HINT: 'Exactly what we read. Nothing here has been changed.',
        REMOVE: 'Remove this source',
        REMOVE_TITLE: 'Remove this source?',
        REMOVE_BODY:
          'Everything your assistant learned from it goes too, and it stops answering from it on the next message. You can put it back from Recently removed for {period}.',
        RESERVED_NOTE:
          'These are the answers you saved one by one. They are never removed all together.',
        REMOVE_CONFIRM: 'Remove it',
        REMOVED:
          'Removed. Your assistant stops using it from the next message.',
        REMOVE_FAILED:
          'We could not remove it. Nothing was changed — please try again.',
      },
    },
    // The Reports screen, renamed and rewritten. Credits are deliberately absent: the
    // balance, its expiry and the low-balance warning live on Overview, because billing
    // state is not a report (THE PLAN, §4 P9).
    ACTIVITY: {
      TITLE: 'Activity',
      SPOTLIGHT:
        'Everything on this page is what your assistant actually did, not what it is set up to do. Pick a period at the top and the numbers, the chart and the conversations all move with it.',
      RANGE: {
        LABEL: 'Period',
        DAYS: 'Last {days} days',
        UPDATING: 'Updating…',
      },
      NUMBERS: {
        CONVERSATIONS: 'Conversations',
        CONVERSATIONS_HINT: 'Customers your assistant talked to.',
        HANDOFFS: 'Handed to your team',
        // Short enough to hold ONE line at the tile's width: the three tiles sit in a row,
        // and a hint that wraps on one of them drops its comparison line below the others'
        // and makes a tidy row look ragged.
        HANDOFFS_HINT: '{handoffs} of {total} reached a person.',
        UNANSWERED: 'Questions with no answer',
        UNANSWERED_HINT: 'Different questions, each one listed below.',
        // Direction is carried by an arrow beside the number, so this sentence only has
        // to say what the number WAS. No "up 18%": whether more conversations or more
        // handovers is good news is the owner's judgement, not ours.
        WAS: 'Was {value} in the {days} days before.',
        NO_COMPARE: 'Nothing to compare this with yet.',
      },
      TREND: {
        HEADING: 'Day by day',
        DESCRIPTION:
          'How many customers wrote in each day, and how many of them ended up with someone on your team.',
        ANSWERED: 'Answered by your assistant',
        HANDED: 'Handed to your team',
        BAR: '{day} — {conversations} conversations, {handoffs} handed to your team',
        ARIA: 'Conversations each day over the last {days} days.',
        NONE: 'Nobody wrote in during this period.',
      },
      QUESTIONS: {
        HEADING: 'Questions it could not answer',
        DESCRIPTION:
          'Your customers asked these and your assistant had nothing to say. Every one you answer is one it never gets stuck on again.',
        ASKED: 'Asked {count}×',
        VERB: 'Fix it',
        CLEAR_TITLE: 'It answered everything it was asked',
        CLEAR_BODY:
          'Nobody asked your assistant something it had no answer for in this period.',
      },
      CONVERSATIONS: {
        HEADING: 'What it actually said',
        DESCRIPTION:
          'The most recent conversations your assistant handled. Open one to read it exactly as your customer saw it.',
        MESSAGES: '{count} messages',
        HANDED: 'Handed over',
        OPEN: 'Read it',
        CLOSE: 'Close',
        CUSTOMER: 'Your customer',
        ASSISTANT: 'Your assistant',
        NO_REPLY: 'It did not reply to this one.',
        USED_SAVED: 'Answered from what you saved',
        HANDED_HERE: 'Passed to your team here',
        // Task review item 11: where a passed-on chat went, and why.
        HANDED_TO: {
          SENT: 'Sent to {who}.',
          BY_STEP: 'Sent to {who}, as this task’s step says.',
          GONE_PERSON:
            'The person this task’s step names is no longer in your account, so “Hand chats to” was used.',
          GONE_TEAM:
            'The team this task’s step names no longer exists, so “Hand chats to” was used.',
        },
        WITHHELD: 'Held back — a team member was asked instead',
        RETRIED: 'Rewritten once before sending',
        LOADING: 'Opening…',
        FAILED: "We couldn't open this conversation just now. Try again.",
        TRUNCATED:
          'This chat is longer than fits here — the first {count} messages are shown.',
        NONE: 'Your assistant did not handle a conversation in this period.',
      },
    },
    TASKS: {
      TITLE: 'Tasks',
      ADD: 'Add a task',
      // ── THE TWO TABS ON THIS PAGE ──────────────────────────────────────────────────
      // The LABELS are `WASLO.NAV.TASKS` and `WASLO.NAV.MENUS`, reused rather than written
      // again, and that is the whole reason there are no new words here. Menus stopped
      // being a sidebar row in the navigation consolidation and became this page's second
      // tab; if the tab had been given a label of its own, the one word an owner clicked
      // in the rail and the word she lands on would be two strings that can drift — and,
      // worse, the new one would sit OUTSIDE `specs/vocabulary.spec.js`'s collision
      // ratchet, which is scoped to `WASLO.NAV.*`. Reusing the key keeps every tab label
      // inside the check that already refuses a word Chatwoot has spent.
      //
      // Only the accessible name of the tab GROUP is new, because a group needs one and
      // the rail never had a group.
      TABS: {
        LABEL: 'Tasks and menus',
      },
      // THE ONE PARAGRAPH THIS PIECE LIVES OR DIES BY. A task is the concept in this
      // product most likely to be misread, and it is misread in one specific way: as
      // another place to write answers. So the paragraph opens by naming the thing it is
      // NOT, and the whole sentence turns on say / do.
      SPOTLIGHT_TITLE: 'What a task is',
      SPOTLIGHT:
        'Answers tell your assistant what to say. A task tells it what to do: a short list of steps it walks a customer through, in order, until the job is finished — taking a delivery order, checking where an order has reached, booking a fitting. You write when it starts, what the steps are, and what it must write down along the way. Outside a task your assistant answers freely; inside one it follows your steps.',
      // The worked example is the other half of the explanation, and it is on the page
      // permanently rather than only in the empty state: an owner who has three tasks
      // already still has to be able to check what the shape is meant to be.
      EXAMPLE: {
        HEADING: 'For example',
        // Split so the quoted Arabic can sit in a real bidi isolate: as one string the
        // curly quotes are neutrals adjacent to an RTL run and the bidi algorithm moves
        // them, printing the customer's own sentence back with its punctuation reversed.
        TRIGGER: 'A customer writes {message}',
        // Guillemets, not curly quotes, even in the English table: the quoted sentence is
        // Arabic, `«»` are MIRRORED characters that the bidi algorithm turns the right way
        // round inside an RTL run, and `“”` are not — they render as a closing quote
        // opening the sentence.
        TRIGGER_MESSAGE: '«أبي أطلب عود وتوصيل للجهراء»',
        STEP_1: 'Ask which area in Kuwait they are in.',
        STEP_2: 'Ask what they would like, and how many.',
        STEP_3: 'Read the total and the delivery day back, and wait for a yes.',
        RESULT:
          'At the end you have the order written down: the area, what they wanted, and the day you agreed. Your assistant does not finish the task until it has all three.',
      },
      LIST: {
        // P-10.26: a task limited to some channels says which of the account's it is not on.
        NOT_ON: 'Not on {channel}',
        HEADING: 'Your tasks',
        DESCRIPTION:
          'On means your assistant will use it. Off keeps it here without using it.',
        STARTS_WHEN: 'Starts when: {trigger}',
        // Never a plural form. Arabic has six and the count is one small chip; a
        // label and a number is correct in every language and needs no rule.
        //
        // `Your systems`, matching the nav row. It said `Connections` — the word the nav
        // deliberately stopped using, because it names nothing an owner recognises and its
        // Arabic reads as TELECOM beside Chatwoot's own Contacts. The rename was done in one
        // place out of five; `specs/vocabulary.spec.js` now makes a sixth spelling impossible.
        CONNECTIONS: 'Your systems: {count}',
        // P-5.9. Rolling 7 days; the Test it page is never counted.
        USAGE_REACHED:
          'No chats reached it in the last 7 days. | 1 chat reached it in the last 7 days. | {count} chats reached it in the last 7 days.',
        USAGE_FINISHED: 'None finished. | 1 finished. | {count} finished.',
        UPDATED: 'Changed {when}',
        UPDATED_JUST_NOW: 'just now',
        EDIT: 'Edit',
        ON: 'On',
        OFF: 'Off',
        SAVE_FAILED:
          "We couldn't change that just now. Nothing was changed — try again.",
        UNAVAILABLE: "We couldn't load your tasks just now.",
        // ── WHERE A TASK GOES, from its own row ────────────────────────────────────
        // `grep frontendURL` across this tree used to return NOTHING outside each screen's
        // own links: five objects that only work together, and no way between any two of
        // them. These are the way. Each is a destination NAMED AS ONE — "Connect a system",
        // not "Connections" — because the row is offering a move, not a heading.
        //
        // The card-words link reuses `WASLO.TASKS.ARABIC.TITLE`, so what the link says and
        // what the screen it opens is called are one string.
        LINKS: {
          // Shown in place of the count when the count is zero. The count itself is
          // `CONNECTIONS` above, which becomes the link's words when there is one.
          CONNECT: 'Connect a system',
          FORM: 'Make it a form',
          MENU: 'Start it from a menu',
        },
      },
      EMPTY: {
        TITLE: 'No tasks yet',
        BODY: 'Your assistant still answers questions without them. Add a task for the jobs you want it to carry all the way through — an order, a booking, a complaint.',
        ACTION: 'Add your first task',
      },
      // F3.6. "Remove", as for menus, sources and connections, so "Recently removed" matches.
      REMOVE: {
        HEADING: 'Remove this task',
        DESCRIPTION: 'Your assistant stops using it straight away.',
        ACTION: 'Remove this task',
        ACTION_SHORT: 'Remove',
        CONFIRM: 'Remove this task?',
        CONFIRM_BODY:
          'You can put it back from Recently removed under Tasks for {period}. You can also switch it off instead.',
        CONFIRM_ACTION: 'Yes, remove it',
        CANCEL: 'Keep it',
        FAILED: 'We could not remove that. Nothing was changed. Try again.',
        REMOVED: 'Removed. You can put it back from Recently removed.',
      },
      EDITOR: {
        NEW_TITLE: 'New task',
        // ── THE STEP EDITOR (delivery 1b and 1c) ──────────────────────────────────
        // A task written as steps is driven by Waslo, not by the assistant's judgement,
        // and every word below is chosen so an owner never has to know that sentence.
        // She reads "step", "then", "if it worked", "if it failed" — never the words the
        // engine uses for the same things.
        FLOW: {
          LEAVE_WARNING:
            'You have changes that are not saved yet. Leave this page and they are lost.',
          STARTS_AT: 'The task starts at',
          STARTS_AT_REQUIRED: 'Choose the step this task starts at.',
          ADD_HERE: 'Add a step here',
          ADD: 'Add a step',
          COUNT: '{count} of {limit} steps',
          FULL: 'A task can have up to {limit} steps.',
          // The jump-back control. Spec §4 never mentioned `clears`, which was an omission
          // rather than a decision: without it "that day is full — pick another" is a dead
          // end, because the walk skips a step whose answer is already saved.
          //
          // THE SEMANTICS ARE STATED EXACTLY AND NOWHERE ELSE. It fires on ARRIVAL, and an
          // answer the customer gave in the same message is never forgotten. Both halves were
          // recently settled in the engine, and a confidently wrong sentence here would be
          // worse than no sentence — so this is the only copy in the section that describes it.
          CLEARS: {
            HEADING: 'When the chat comes back to this step, forget',
            DESCRIPTION:
              'Tick the answers that stop being true when a customer reaches this step again. Waslo forgets them on the way in, so the steps that collected them get asked afresh. Anything the customer said in the same message is kept.',
            FROM_STEP: 'Collected at step {number} · {name}',
            FROM_CONTACT: 'Saved on the customer’s record',
            OWN: 'This is this step’s own answer — tick it to ask the same question again.',
            ORPHANS:
              'Ticked, but nothing in this task collects them any more: {keys}. Until you untick them they forget nothing.',
            NOTHING_TO_FORGET:
              'This task does not collect any answers yet, so there is nothing to forget. Add an Ask, a Choose, or a step that remembers something from your own system.',
            KEPT: 'This step forgets {keys} when it is reached. The task stops here, so it makes no difference — it is shown because it is saved.',
          },
          TYPES: {
            ASK_LABEL: 'Ask',
            ASK_ABOUT: 'Ask the customer for one thing and wait for it.',
            CHOOSE_LABEL: 'Choose',
            CHOOSE_ABOUT: 'Offer a few options you have written yourself.',
            CHOOSE_LIVE_LABEL: 'Choose from your system',
            CHOOSE_LIVE_ABOUT:
              'Offer options taken from your own system, fresh each time — free times, branches, sizes in stock.',
            CALL_LABEL: 'Do it in your system',
            CALL_ABOUT:
              'Send what has been collected to your own system. Your assistant says nothing on this step.',
            TELL_LABEL: 'Tell',
            TELL_ABOUT: 'Say something and carry straight on.',
            HANDOFF_LABEL: 'Hand over to a person',
            HANDOFF_ABOUT:
              'Give the chat to one of your team, with a note about what happened.',
            END_LABEL: 'Finish',
            END_ABOUT:
              'The job is done. Your assistant goes back to answering freely.',
          },
          PICKER: {
            HEADING: 'What should this step do?',
            CANCEL: 'Never mind',
          },
          LINK: {
            OPTION: '{number} · {name}',
            UNNAMED: 'Step with no name',
            END: 'Finish the task here',
            MISSING: 'A step that is no longer here ({id})',
          },
          CARD: {
            UNNAMED: 'Step with no name',
            UP: 'Move up',
            DOWN: 'Move down',
            REMOVE: 'Remove this step',
            NAME: 'What do you call this step?',
            NAME_HINT: 'Only your team sees this. Your customers never do.',
            NAME_PLACEHOLDER: 'Which branch',
            THEN: 'Then go to',
            UNKNOWN_TYPE: 'Newer kind of step ({type})',
            UNKNOWN_BODY:
              'This step was made with a newer version of Waslo than this page knows about, so it cannot be changed here. It is kept exactly as it is and still works. You can move it or remove it.',
            UNKNOWN_SUMMARY: 'Kept as it is',
            SUMMARY_CHOOSE: '{count} options · {instruction}',
            SUMMARY_LIVE: 'Options from {connection}',
            SUMMARY_CALL: 'Sends to {connection}',
            SUMMARY_BLANK: 'Nothing written yet',
          },
          KINDS: {
            TEXT: 'Any words',
            NUMBER: 'A number',
            DATE: 'A date',
            TIME: 'A time',
            EMAIL: 'An email address',
            PHONE: 'A phone number',
          },
          ASK: {
            INSTRUCTION: 'What should it find out?',
            INSTRUCTION_HINT:
              'Write what you need, not the exact question. Your assistant asks it in the customer’s own language, and asks again if the answer does not fit.',
            INSTRUCTION_PLACEHOLDER:
              'Which area in Kuwait the delivery is going to.',
            KIND: 'What kind of answer',
            RULE_LABEL: 'Only accept',
            RULE_MODE_NONE: 'Anything of that kind',
            RULE_MODE_RANGE: 'A number between two numbers',
            RULE_MODE_ALLOWED: 'One of a list you write',
            RULE_MODE_PATTERN: 'Something that matches a form you write',
            RULE_MIN: 'Not less than',
            RULE_MAX: 'Not more than',
            RULE_ALLOWED_HINT:
              'Your assistant explains the list if the customer says something else. Unlike Choose, nothing is shown as a button.',
            RULE_ALLOWED_ONE: 'Accepted answer {number}',
            RULE_ALLOWED_ADD: 'Add another',
            RULE_ALLOWED_REMOVE: 'Remove this one',
            RULE_PATTERN: 'The form it must match',
            RULE_PATTERN_HINT:
              'For a code or a reference with a fixed shape. Leave this alone unless you already know what it does.',
          },
          SAVE: {
            HEADING: 'Where the answer is kept',
            DESCRIPTION:
              'Fill in one or both. The name on the left is what your own systems receive it as, later in this task. The field on the right writes it onto the customer’s record for good.',
            CHAT_LABEL: 'Name for it in this task',
            CHAT_HINT:
              'Small letters, numbers and underscores. This is the name you use when you send it to one of your systems.',
            CONTACT_LABEL: 'Field on the customer',
            CONTACT_HINT:
              'Leave empty unless this belongs on their record — their name, email or phone number.',
            NEITHER:
              'Fill in at least one of these, or the answer is forgotten as soon as the task moves on.',
          },
          CHOOSE: {
            INSTRUCTION: 'What should it say?',
            INSTRUCTION_HINT:
              'Your assistant writes the message itself; the options below go underneath it as buttons.',
            INSTRUCTION_PLACEHOLDER:
              'Ask which of our branches they would like.',
            OPTION: 'Option {number}',
            LABEL_EN: 'What it says in English',
            LABEL_AR: 'What it says in Arabic',
            COUNT: '{count} of {limit}',
            THEN: 'Then go to',
            THEN_DEFAULT: 'Wherever this step goes next',
            UP: 'Move up',
            DOWN: 'Move down',
            REMOVE: 'Remove this option',
            ADD: 'Add an option',
            FULL: 'WhatsApp shows at most {limit} options at a time.',
          },
          LIVE: {
            INSTRUCTION: 'What should it say?',
            INSTRUCTION_HINT:
              'Your assistant writes the message itself. The options come from your own system when the customer gets here.',
            INSTRUCTION_PLACEHOLDER: 'Ask which of these times suits them.',
            CONNECTION: 'Which of your systems',
            CONNECTION_PLACEHOLDER: 'Choose one',
            CONNECTION_OFF: '{name} (switched off)',
            CONNECTIONS_UNAVAILABLE:
              'We could not list your systems just now. Nothing on this step has been changed.',
            IF_NONE: 'If nothing comes back',
            IF_NONE_REQUIRED:
              'Choose where to go when there is nothing to offer. Without it the customer is left waiting.',
            HEADING: 'Which list, and which part of it the customer reads',
            DESCRIPTION:
              'Run it once and we will show you what came back, so you can point at the list and at the two parts we need: what the customer reads, and what we keep.',
            NEEDS_CONNECTION: 'Choose one of your systems first.',
            VALUES: 'Values to try it with',
            VALUES_HINT:
              'Just for this test. On a real chat these come from the answers collected earlier.',
            RUNNING: 'Trying…',
            ACTION: 'Try it',
            LIVE_WARNING: 'This really calls your system.',
            OK: 'It answered ({status})',
            FAILED: 'Your system answered with an error ({status})',
            NO_STATUS: 'Nothing came back at all',
            NO_LIST:
              'We could not find a list of rows in that answer. Write the path to it below if you know it.',
            LIST: 'Where the list is',
            LIST_PLACEHOLDER: 'Choose the list',
            WHOLE_LIST: 'The whole answer is the list',
            LIST_HINT:
              'The path to it in the answer, with a dot between each part.',
            FORMAT: 'How to write it for the customer',
            FORMAT_TIME: 'As a time',
            FORMAT_DATE: 'As a date',
            FORMAT_PRICE: 'As a price',
            FORMAT_TEXT: 'Exactly as it comes',
            LABEL_FIELD: 'What the customer reads',
            LABEL_FIELD_HINT: 'The part of each row a person can understand.',
            VALUE_FIELD: 'What we keep',
            VALUE_FIELD_HINT:
              'The part your own system needs back, usually a reference.',
            FIELD_PLACEHOLDER: 'Choose one',
            VALUE_NOTE:
              'What we keep is never shown to the customer. They only ever see the part above it.',
            SAMPLE: 'What the buttons will say',
            MORE: 'More…',
            MORE_NOTE:
              '{total} came back. WhatsApp shows the first nine and a “More…” option that brings the next ones.',
            MISSING_VALUE:
              'At least one row has nothing in the part we keep. A tap on it would save nothing.',
            NO_ID:
              'We could not match that to one of your systems, so it cannot be tried from here. Save the page and open it again.',
            UNAVAILABLE:
              'We could not run that just now. Nothing was changed — try again.',
          },
          CALL: {
            DESCRIPTION:
              'Your assistant says nothing on this step. Waslo sends what it has to your system and carries on.',
            CONNECTION: 'Which of your systems',
            CONNECTION_PLACEHOLDER: 'Choose one',
            ON_WORKED: 'If it worked, go to',
            ON_WORKED_DEFAULT: 'Wherever this step goes next',
            ON_FAILED: 'If it failed, go to',
            ON_FAILED_DEFAULT: 'Hand over to a person',
            ON_FAILED_NOTE:
              'With nothing chosen, a chat where this fails is handed to one of your team with a note. The customer never sees the error.',
          },
          INPUTS: {
            HEADING: 'What to send it',
            NONE: 'This one needs nothing sent to it.',
            UNKNOWN:
              'We could not read what this one expects. What is already set here is kept as it is.',
            STALE:
              'This one is no longer asked for by that system. It is kept until you remove it.',
            REMOVE: 'Remove this one',
            MODE: 'Where it comes from',
            MODE_ANSWER: 'An answer collected earlier',
            MODE_FIXED: 'The same value every time',
            MODE_LANG: 'The language of the chat',
            LANG_NOTE:
              'Sends "ar" when the chat is in Arabic and "en" when it is in English.',
            FROM: 'Which answer',
            FROM_PLACEHOLDER: 'Choose an answer',
            FIXED: 'The value',
            NO_KEYS:
              'No answers are collected before this step yet, so there is nothing to send on from earlier. Add an Ask or a Choose above it.',
            ADD: 'Add something to send',
            // F3.7.
            SUGGEST: 'Looks like a match: {answer}',
            SUGGEST_USE: 'Use it',
            SOME_PATHS:
              'Not offered here, because only some ways to this step collect it: {answers}',
            LIST_JOIN: ', ',
          },
          KEEP: {
            HEADING: 'What to remember from the answer',
            DESCRIPTION:
              'On the left, where it is in the answer. On the right, the name the rest of this task calls it by.',
            FIELD: 'Where it is in the answer',
            SAVE_AS: 'Name for it in this task',
            REMOVE: 'Remove this line',
            ADD: 'Remember something',
          },
          TELL: {
            EXACT_LABEL: 'Send these exact words',
            EXACT_ON:
              'Your assistant sends what you write, unchanged, in the customer’s language.',
            EXACT_OFF:
              'Your assistant says this in its own words, in the customer’s language.',
            INSTRUCTION: 'What should it say?',
            INSTRUCTION_HINT:
              'What you want the customer to know. Your assistant writes it.',
            INSTRUCTION_PLACEHOLDER:
              'Tell them the fitting takes about twenty minutes.',
            EXACT_EN: 'Word for word, in English',
            EXACT_AR: 'Word for word, in Arabic',
          },
          HANDOFF: {
            NOTE: 'What should the note say?',
            NOTE_HINT:
              'This is for whoever picks the chat up, not for the customer. Say what happened and what is still needed.',
            NOTE_PLACEHOLDER:
              'The customer wants a date we do not have online. Their area and phone number are already on the chat.',
            ENDS: 'The task ends here. Your team takes over from this point.',
          },
          END: {
            INSTRUCTION: 'A last word, if you want one',
            INSTRUCTION_HINT:
              'Leave it empty and your assistant simply stops and goes back to answering questions.',
            INSTRUCTION_PLACEHOLDER:
              'Confirm the booking back to them and say we will send a reminder the day before.',
            SILENT:
              'Nothing is said. The task is finished and your assistant answers freely again.',
          },
          PREVIEW: {
            HEADING: 'On the customer’s phone',
            NO_OPTIONS:
              'Add an option and it appears here as the customer sees it.',
            LIVE_EMPTY:
              'Press “Try it” above and the real options appear here, as buttons.',
            MORE: '{total} came back, so the last button is a “More…” that brings the next ones.',
            VALUES_HIDDEN:
              'Your customers only ever see these words. What is saved behind each one is never shown to them.',
            // Field fixes G2.3.
            MANY: 'They reply with the numbers of the ones they want.',
          },
          PROBLEMS: {
            ORPHAN:
              'Something is wrong with a step that is not on this page any more ({id}). Check where this task starts.',
            MISSING_FIRST:
              'This task starts at a step that is not here. Choose where it starts.',
            DANGLING: 'This step goes to a step that is not here any more.',
            DANGLING_DETAIL:
              'This step goes to “{detail}”, which is not here any more. Point it somewhere else.',
            UNREACHABLE:
              'Nothing leads to this step, so no customer will ever reach it. Point another step at it, or remove it.',
            INPUT_BEFORE_SAVE:
              'This step sends on an answer that has not been collected yet on every way here.',
            INPUT_BEFORE_SAVE_DETAIL:
              '“{detail}” is sent from here, but there is a way to this step where it was never collected. Your system would receive it empty.',
            DUPLICATE_LABEL: 'Two options here say the same thing.',
            DUPLICATE_LABEL_DETAIL:
              'Two options here both say “{detail}”. A customer tapping one of them cannot be told apart from the other.',
            UNKNOWN_CLEAR:
              'This step forgets an answer that nothing here collects.',
            UNKNOWN_CLEAR_DETAIL:
              'This step forgets “{detail}”, which no step here collects, so it would forget nothing.',
            LINK_UNLABELLED: 'Write the words on the button.',
            LINK_LABEL_LONG: 'Button words can be up to {limit}.',
            LINK_INVALID:
              'Give the button a link that starts with https://, or choose the answer that holds it.',
            NO_PLACE: 'Choose which place to send.',
            UNKNOWN_LINK_KEY:
              'This button’s link comes from an answer no step here keeps.',
            UNKNOWN_LINK_KEY_DETAIL:
              'This button’s link comes from “{detail}”, which no step here keeps.',
            UNKNOWN_PLACE:
              'This sends a place that is not one of your places any more. Choose another.',
            // Field fixes G2.3.
            MANY_BRANCHES:
              'A question they can pick more than one answer to cannot send each choice to its own step, or confirm a change. Take those steps off its choices.',
            MANY_RANGE:
              'How many they can pick does not fit this question’s choices. Check At least and At most, and give it two choices or more.',
            UNCONFIRMED_CHANGE:
              'Ask the customer to confirm before this step changes anything in your system.',
            LIVE_NEEDS_LOOKUP:
              'A list of choices can only come from a system that looks something up. Choose one that looks something up.',
            TYPED_NUMBER_LOOKUP:
              'This looks up your customers’ data using a number the customer types. Anyone could type someone else’s number. Use “This chat’s WhatsApp number” instead.',
            USERNAME_NEEDS_PHONE:
              'Some customers message from a WhatsApp username and have no phone number. Add a question that saves their phone number before this step.',
            READ_BACK_NOT_LINKED:
              'This reads back a step that is not a step that changes your system after this answer.',
            // Services task review E.
            UNKNOWN_KEPT_KEY:
              'This shows something your system sends back, but no step here keeps it.',
            UNKNOWN_KEPT_KEY_DETAIL:
              'This shows “{detail}”, but no step here keeps it from your system. Untick it, or keep it on the step that asks your system.',
            KEEP_WITHOUT_READ_BACK:
              'This shows what your system sent back, but it does not show the customer what will be sent. Switch that on, or untick what it shows.',
            SKIP_FEEDS_CONNECTION:
              'This answer is sent to your system, so it cannot be skipped.',
            SKIP_FEEDS_CONNECTION_DETAIL:
              'This answer is sent to “{detail}”, so it cannot be skipped.',
            // P-4.10: a form layout this task cannot be saved with. Fixed on Arrange form.
            FORM_PAGE_EMPTY:
              'A page of this task’s form has no question. Open Arrange form to fix it.',
            FORM_STEP_TWICE:
              'A question is on this task’s form twice. Open Arrange form to fix it.',
            FORM_TOO_MANY_PAGES:
              'This task’s form has too many pages. Open Arrange form to fix it.',
            FORM_TOO_MANY_QUESTIONS:
              'This task’s form has too many questions. Open Arrange form to fix it.',
            FORM_TEXT_TOO_LONG:
              'Some words on this task’s form are too long. Open Arrange form to fix them.',
            FORM_BRANCH_NOT_LAST:
              'This question now decides what comes next, so it must be the last question on the form. Open Arrange form and move it to the end.',
            // Services field fixes G2.4: photos and files on a form, and where they are kept.
            FORM_UPLOAD_NOT_LAST:
              'This question asks for photos or a file, so it must be on the last page of the form. Open Arrange form and move it there.',
            FORM_UPLOAD_TWICE:
              'This task’s form asks for photos or a file twice. Open Arrange form and keep one of them in the chat.',
            UPLOAD_SAVES_TO_CONTACT:
              'Photos and files are kept with the chat, not on the customer’s contact. Choose where this one is kept again.',
            // P-2.10 on a one-language account: the only box the owner sees is empty.
            OPTION_MISSING_LABEL_AR:
              'An option here has no Arabic words. Your customers read only Arabic, so write them.',
            OPTION_MISSING_LABEL_EN:
              'An option here has no English words. Your customers read only English, so write them.',
            TOO_MANY_STEPS:
              'This task has more steps than we can run. Remove some.',
            DUPLICATE_ID: 'Two steps here are the same step.',
            DUPLICATE_ID_DETAIL:
              'Another step is already using “{detail}”. Remove one of them.',
            BAD_STEP_ID:
              'This step is missing the name we run it by. Remove it and add it again.',
            BLANK_NAME:
              'Give this step a name, so you can find it in a list later.',
            NAME_LONG: 'That name is too long. Keep it under {limit}.',
            BLANK_INSTRUCTION: 'Say what this step should do.',
            INSTRUCTION_LONG: 'That is too long. Keep it under {limit}.',
            NO_SAVE_TARGET: 'Say where this step’s answer is kept.',
            BAD_SAVE_KEY:
              'The name for this answer can only use small letters, numbers and underscores.',
            BAD_SAVE_KEY_DETAIL:
              '“{detail}” cannot be a name for an answer. Use small letters, numbers and underscores.',
            NO_OPTIONS: 'Add at least one option.',
            TOO_MANY_OPTIONS:
              'WhatsApp shows at most ten options at a time, or twenty when they may pick more than one.',
            OPTION_UNLABELLED:
              'One option has nothing written in either language, so there is nothing to show.',
            LABEL_LONG:
              'One option is too long. WhatsApp cuts them off at {limit}.',
            LABEL_LONG_DETAIL:
              '“{detail}” is too long for a button. WhatsApp cuts them off at {limit}.',
            NO_CONNECTION: 'Choose which of your systems this step uses.',
            BAD_INPUT:
              'One of the things sent to your system names an answer that cannot exist.',
            BAD_INPUT_DETAIL:
              'What is sent as “{detail}” names an answer that cannot exist. Pick one from the list.',
            NO_LIST_PATH: 'Say where the list is in your system’s answer.',
            NO_LABEL_FIELD: 'Say which part of each row the customer reads.',
            NO_VALUE_FIELD: 'Say which part of each row we keep.',
            NO_IF_NONE: 'Say where to go when nothing comes back.',
            BAD_KEEP_KEY:
              'A name for something remembered can only use small letters, numbers and underscores.',
            BAD_KEEP_KEY_DETAIL:
              'What is kept from “{detail}” needs a name of small letters, numbers and underscores.',
            BLANK_EXACT: 'Write the exact words in at least one language.',
            EXACT_LONG: 'That is too long. Keep it under {limit}.',
            BLANK_NOTE: 'Write what the note for your team should say.',
            // Task review item 11.
            UNKNOWN_PERSON:
              'This step hands chats to someone who is no longer in your account. Choose someone else.',
            UNKNOWN_TEAM:
              'This step hands chats to a team that no longer exists. Choose another.',
            UNKNOWN_LABEL: 'This step adds a label your inbox does not have.',
            UNKNOWN_LABEL_DETAIL:
              'This step adds “{detail}”, which is not one of your inbox labels. Create it in your inbox settings, or take it off.',
            BAD_HAND_TO: 'Choose who gets the chats from this step.',
            BAD_HANDOFF_LABELS:
              'Labels here can be up to five, each once, in small letters.',
            // The two rules a step graph has nowhere to put — they are columns on the task
            // itself — written in the same shape every other problem arrives in, so the
            // composer has one list and one emptiness test. See `task-compose.js`.
            NO_TITLE: 'Give this task a name, so you can find it again later.',
            TITLE_LONG: 'That name is too long. Keep it under {limit}.',
            NO_TRIGGER: 'Say when your assistant should start this task.',
            TRIGGER_LONG: 'That is too long. Keep it under {limit}.',
            CTA_LONG:
              'The button that sends the form is too long. Keep it to {limit}.',
            // P-10.9, P-2.7 and P-10.26: how it starts, what it says first and where it runs.
            NO_START_WORDS: 'Add at least one word that starts this task.',
            START_WORDS_MANY: 'A task starts on ten words at most.',
            START_WORD_LONG: 'Each start word is at most {limit}.',
            TRIGGER_MISSING: 'Say when your assistant should start this task.',
            OPENING_LONG:
              'The first thing to say is too long. Keep it under {limit}.',
            NO_CHANNELS:
              'Pick at least one channel, or every channel. A task on no channel never starts.',
            OTHER:
              'There is something wrong with this step that we cannot put into words.',
            OTHER_DETAIL:
              'There is something wrong with this step that we cannot put into words ({detail}).',
          },
        },
        SAVE: 'Save changes',
        CREATE: 'Create this task',
        SAVED: 'Saved. Your assistant uses this from the next message.',
        CREATED: 'Created. Your assistant uses this from the next message.',
        SAVE_FAILED:
          'We could not save that. Nothing was changed — please try again.',
        NOT_FOUND: "We couldn't find that task. It may have been removed.",
        // Same five states as the Personality bar. ONLY `ERROR` HAS A SECOND LINE: the four
        // `*_DETAIL` keys that were here went with the free-text editor — the composer had
        // already stopped reading them, because a save bar must not make claims about
        // whether customers are being served (`TaskComposePage.vue`, `saveDetail`).
        STATUS: {
          IDLE: 'Everything is saved',
          NEW: 'Not created yet',
          DIRTY: 'Not saved yet',
          SAVING: 'Saving…',
          SAVED: 'Saved at {time}',
          ERROR: 'We could not save that',
          ERROR_DETAIL:
            'Nothing was changed, and what you wrote is still on the page. Try again.',
        },
      },
      // The read-only map of a task's steps. THE SPEC, §4.
      //
      // TWO WORDS THIS BLOCK REFUSES TO USE, and the vocabulary rule is the reason: a step
      // is a "step" and a line between two of them is what one step leads TO. An owner is
      // never shown the words a graph library would use for them. The branch words are the
      // same ones the step cards use — "if it worked", "if it failed" — so the map and the
      // cards read as one screen rather than two vocabularies.
      MAP: {
        DESCRIPTION:
          'Every step in this task, and where each one leads. Reading only — to change a step, open The steps.',
        // Never a plural form: Arabic has six and this is one short line.
        COUNT: '{count} steps in this task',
        EMPTY: 'There is nothing to draw yet',
        EMPTY_BODY:
          'Write the steps first. The map draws them as soon as they lead somewhere.',
        NARROW:
          'The map needs a wider screen. On a phone, read The steps instead.',
        ARIA: 'The task map, {count} steps',
        OPEN: 'Open the step {name}',
        NO_FIRST:
          'This task does not say which step comes first, so nothing below it can run yet.',
        CHECK: 'Worth a look',
        // What each step DOES, in the present tense, because that is how an owner reads a
        // map: "it asks, then it offers a choice, then it calls my system".
        TYPE: {
          ASK: 'Asks for something',
          CHOOSE: 'Offers a choice',
          CHOOSE_LIVE: 'Offers what your system returns',
          CALL: 'Calls your system',
          TELL: 'Says something',
          HANDOFF: 'Hands over to a person',
          END: 'Finishes',
          // A task saved by a newer Waslo than this dashboard. The step is still drawn.
          UNKNOWN: 'A newer kind of step',
        },
        LINK: {
          WORKED: 'if it worked',
          FAILED: 'if it failed',
          NONE: 'if there is nothing',
          MORE: '+{count} more',
        },
        BADGE: {
          FIRST: 'Starts here',
          ORPHAN: 'nothing leads here',
          DEAD_END: 'nothing follows',
          BROKEN: 'leads to a step that is gone',
        },
      },
      // The guided composer. One question per screen, in the order you would say it out
      // loud, and the links written for you.
      COMPOSE: {
        // "What comes back?" — the one control that collapses three step kinds and an
        // answer kind into a single question. `helpers/task-answer-kinds.js` builds these
        // key names from the row ids, so a row and its words cannot drift apart.
        ANSWER: {
          TEXT: {
            LABEL: 'Anything they type',
            HINT: 'A sentence, a name, an address — whatever they write.',
          },
          NUMBER: {
            LABEL: 'A number',
            HINT: 'How many, how much, how long.',
          },
          DATE: { LABEL: 'A date', HINT: 'A day they name.' },
          TIME: { LABEL: 'A time', HINT: 'A time of day.' },
          EMAIL: {
            LABEL: 'An email address',
            HINT: 'Checked before it is written down.',
          },
          PHONE: {
            LABEL: 'A phone number',
            HINT: 'Checked before it is written down.',
          },
          // Services field fixes G2.4: what the customer sends rather than types.
          PHOTO: {
            LABEL: 'Photos',
            HINT: 'The customer sends one or more photos.',
          },
          FILE: {
            LABEL: 'A file',
            HINT: 'The customer sends a document such as a PDF, or a photo.',
          },
          WRITTEN: {
            LABEL: 'One of these — you write them',
            HINT: 'You type the choices. Your customer taps one.',
          },
          LIVE: {
            LABEL: 'One of these — from your system',
            HINT: 'Your system sends the choices while the customer waits.',
          },
          // The seam at row 7, said out loud. The first six describe what the answer has
          // to look like; the last two describe where the choices come from.
          SOURCE_RULE: 'Or offer a list to tap:',
          HEADING: 'What comes back?',
        },
        // "See it in a chat" — a finished task read as the conversation it becomes,
        // before anything is created.
        STARTER: {
          SEE_IT: 'See it in a chat',
          PLAY_AGAIN: 'Play it again',
          USE_THIS: 'Use this',
          LANGUAGE: 'Read it in',
          ENGLISH: 'English',
          ARABIC: 'Arabic',
          CUSTOMER: 'Your customer',
          ASSISTANT: 'Your assistant',
          // THE HONEST LINE. Inside a chat your assistant phrases each step itself; only
          // a card sends your words untouched. Without this the preview reads as a
          // promise about wording that the engine never made.
          WORDING:
            'Your assistant writes its own words, in whatever language your customer used. This is the shape of the conversation, not the exact wording.',
          EMPTY: 'There is no conversation to play here.',
        },
        // Written as a fraction rather than a name so it is true on both lengths: a task
        // in which nothing can go wrong has four screens, not five.
        POSITION: 'Step {number} of {total}',
        NEXT: 'Next',
        PREVIOUS: 'Back',
        // WHAT STANDS WHERE THE NEXT BUTTON WOULD BE when a screen is not finished. There
        // is no disabled Next: two blind critics rejected exactly that control, in both
        // themes, at a measured ~2:1 — a solid fill and its label fade toward the
        // background together. One sentence carries more than a dead button.
        NEEDS: 'Finish what is marked above to carry on.',
        KEPT: 'You have unsaved changes to this task from {minutes} minutes ago.',
        KEPT_TAKE: 'Pick up where you left off',
        KEPT_DISCARD: 'Discard them',
        SAVE_BLOCKED:
          'Some steps still need finishing. They are marked on the step they belong to.',
        // NEVER A SENTENCE ABOUT CUSTOMERS. This read "Nothing has been sent to your
        // customers yet", which is true of a new task and false of one in its fourth month
        // of service — and the same line was shown on both. Whether anything is being sent
        // is `LIVE.*` below, reported from the stored row; this line is only about saving.
        SAVE_READY:
          'These changes are not saved yet, so your assistant is not using them.',
        // ── IS THIS THING ANSWERING CUSTOMERS? ──────────────────────────────────────
        // Its own fact, said at the top of every screen and again in the docked bar, in
        // every save state. A blind reader on screen 1 of 5 read "Everything is saved —
        // your assistant is using this now" and stopped: "the thing is already answering
        // my customers while I am still halfway through writing it. I would be scared to
        // touch this page at all during shop hours." The sentence was true and the default
        // was wrong — a task written here now starts OFF — but the deeper fault was that
        // one line was carrying two facts. These three report the second one on its own.
        LIVE: {
          NONE: 'This task does not exist yet. Nothing here reaches a customer until you turn it on, on the last screen.',
          OFF: 'This task is off. It is saved, and it is not answering anybody. You turn it on on the last screen.',
          ON: 'This task is on. Your assistant is running the saved version right now — your changes reach customers only once you save them.',
          CHIP_NONE: 'Not live yet',
          CHIP_OFF: 'Off — not answering',
          CHIP_ON: 'On — answering customers',
        },
        UNREADABLE:
          'This task was not written here, and the ordered list has nowhere to put its shape. Writing its steps below replaces what is stored.',
        RAIL: {
          LABEL: 'Writing this task',
          JOB: 'What it does',
          STEPS: 'The steps',
          WHATIF: 'What if',
          // THE SAME WORDS THE SCREEN ITSELF IS HEADED. It read "In Arabic" while the
          // screen it opens is "The words on the card" — two authors, neither wrong,
          // nobody choosing. The screen's name wins, and not by seniority: the assistant
          // already replies in Arabic on its own, so "In Arabic" quietly promises that
          // without this screen it would not, which is the one thing the screen's own
          // first paragraph exists to deny.
          ARABIC: 'The words on the Form',
          CHECK: 'Check it',
        },
        JOB: {
          HEADING: 'What should this task do?',
          DESCRIPTION:
            'A name only your team sees, and the moment your assistant should start it.',
          TITLE: 'What do you call it?',
          TITLE_HINT: 'Your customers never see this.',
          TITLE_PLACEHOLDER: 'Book an appointment',
          TITLE_REQUIRED: 'Give this task a name.',
          TITLE_LONG: 'That name is too long.',
          STARTERS: 'Start from one of these',
          STARTERS_HINT:
            'See what your customer would read before you pick. Nothing is created by looking.',
          TRIGGER: 'When should Waslo start it?',
          TRIGGER_HINT:
            'Describe the moment, not the exact words. Your assistant matches the meaning, in any language.',
          TRIGGER_PLACEHOLDER:
            'The customer wants to book, reserve or move an appointment.',
          TRIGGER_REQUIRED: 'Say when this task should start.',
          TRIGGER_LONG: 'That is too long.',
          TRIGGER_NOTE:
            'Your assistant reads what the customer wrote and decides. You do not list the words they might use.',
          // P-10.9: how a task starts. The trigger box is shown for the first card only.
          START: 'How does it start?',
          START_ASSISTANT:
            'When a customer asks for it (your assistant decides)',
          START_ASSISTANT_BODY:
            'Your assistant reads what the customer wrote and starts it when it fits.',
          START_WORDS: 'Only when the customer writes certain words',
          START_WORDS_BODY:
            'It starts when a message has one of your words in it, and never otherwise.',
          START_MENU: 'Only from a menu button',
          START_MENU_BODY:
            'It starts only when a customer taps a menu option that starts it.',
          WORDS: 'The words',
          WORDS_HINT:
            'Waslo ignores spelling marks and Arabic or English digits.',
          WORDS_PLACEHOLDER: 'كود١',
          WORDS_ADD: 'Add',
          WORDS_REMOVE: 'Remove {word}',
          WORDS_REQUIRED: 'Add at least one word.',
          WORDS_MANY: 'Ten words at most.',
          WORD_LONG: 'Each word is at most {limit}.',
          MENUS_STARTING: 'These menus start it: {menus}',
          NO_MENU: 'No menu starts this task yet.',
          // P-2.7: sent word for word when the task starts.
          OPENING: 'First thing to say (optional)',
          OPENING_HINT: 'Sent word for word when this task starts.',
          OPENING_LONG: 'That is too long.',
          // P-10.26: one task, one assistant, a channel filter.
          CHANNELS: 'This task can run on',
          CHANNELS_EVERY: 'Every channel',
          CHANNELS_NONE: 'Pick at least one channel.',
          // WHAT THE `59 / 500` COUNTER WAS PRETENDING TO BE. A blind reader read it as
          // "12% done, keep writing" on a box where one sentence is the whole answer. The
          // limit is still enforced; the scoreboard is replaced by the thing he actually
          // wanted, which is to know what a good one looks like and that his is enough.
          ENOUGH: 'One sentence is enough. There is nothing to fill up here.',
          EXAMPLES: 'Or press one of these:',
          EXAMPLE_1:
            'The customer wants to book a time, or move one they already have.',
          EXAMPLE_2:
            'The customer asks what something costs, or asks for a quote.',
          EXAMPLE_3: 'The customer wants to know where their order has got to.',
          // ── THE MAP OF THE JOB ────────────────────────────────────────────────────
          // "None of the five step names is where I connect my calendar. Checking whether
          // I'm free on Thursday is the whole point of booking." He is right that the rail
          // promised a complete path and hid the hard part inside one name — and wrong
          // that it needs a sixth name: a calendar is not a stage of WRITING a task, it is
          // a step inside one. So the outline says which screen holds it.
          MAP_HEADING: 'What you are about to write',
          MAP: {
            JOB: 'Its name, and the moment your assistant starts it. You are here.',
            STEPS:
              'Every question, message and action, in the order they happen. A step that checks your calendar or places a booking is where one of your own systems is attached — you set the system itself up under For developers, then Your systems.',
            WHATIF:
              'What your assistant does when one of your systems has nothing to offer, or does not answer.',
            ARABIC:
              'The exact words, in both languages, for anything that goes on a Form inside WhatsApp.',
            // NOT "it is off until you do". That is true of the task being written here
            // and false of the live one somebody opened to rename, and this block is
            // shown on both. Whether THIS task is on is reported above, from the stored
            // row; this line says only where the switch lives.
            CHECK:
              'Read the whole task back as a map. This is the one screen that turns a task on, and off again.',
          },
          // THE ONE SENTENCE THE REDESIGN DELETED, PUT BACK. It was on the old editor, and
          // the blind reader named it as the single thing he was most worried about with a
          // robot talking to his customers. It is on screen one rather than screen four,
          // because that is where the worry starts.
          PROMISE:
            'Waslo holds your place in this task and your assistant does the talking. It asks each step in its own words, in whatever language your customer is writing, saves the answer where you said, and moves on. It cannot skip a step or invent one.',
        },
        LIST: {
          HEADING: 'What happens, in order',
          DESCRIPTION:
            'Waslo works down this list. Anything that is not "carry on" is written under the step it belongs to.',
          EMPTY: 'Nothing here yet. Add the first thing that should happen.',
          ADD: 'Add a step',
          UNWRITTEN: 'Not written yet',
          THEN_GO: 'then go to: {name}',
          THEN_STOP: 'then the task stops',
          THEN_HANDOVER: 'then hand the chat to a person',
          UP: 'Move up',
          DOWN: 'Move down',
          UP_MEANS: 'Move up — Waslo will ask "{moved}" before "{other}"',
          DOWN_MEANS: 'Move down — Waslo will ask "{other}" before "{moved}"',
          MOVE_REFUSED:
            'That move is not possible: a later step needs "{detail}", and moving this one puts it after the step that answers it.',
          KIND_ASK: 'Asks for something',
          KIND_CHOOSE: 'Offers a choice',
          KIND_CHOOSE_LIVE: 'Offers a choice from your system',
          KIND_TELL: 'Says something',
          KIND_CALL: 'Does something in your system',
          KIND_HANDOFF: 'Hands the chat to a person',
          KIND_END: 'Finishes the task',
        },
        KIND: {
          HEADING: 'What happens here?',
          CANCEL: 'Not now',
          QUESTION_LABEL: 'Ask the customer something',
          QUESTION_ABOUT:
            'A question, and the answer is written down. You choose what kind of answer on the next screen.',
          TELL_LABEL: 'Say something',
          TELL_ABOUT:
            'Your assistant tells the customer something and carries on. Nothing is written down.',
          CALL_LABEL: 'Do something in your system',
          CALL_ABOUT:
            'Make the booking, place the order, look something up. Waslo calls one of your systems.',
          HANDOFF_LABEL: 'Hand the chat to a person',
          HANDOFF_ABOUT:
            'The task stops and somebody on your team takes over, with a note about where it got to.',
        },
        SCREEN: {
          BACK: 'Back to the steps',
          POSITION: 'Step {number} of {total}',
          UNWRITTEN: 'Not written yet',
          PREVIOUS: 'The step before',
          NEXT: 'The step after',
          MORE: 'More for this step',
          UP: 'Move up',
          DOWN: 'Move down',
          UP_MEANS: 'Move up — Waslo will ask "{moved}" before "{other}"',
          DOWN_MEANS: 'Move down — Waslo will ask "{other}" before "{moved}"',
          MOVE_REFUSED:
            'That move is not possible: a later step needs "{detail}", and moving this one puts it after the step that answers it.',
          REMOVE: 'Remove this step',
          REMOVE_CONFIRM:
            'Remove it? Anything pointing at it will say the step is no longer here.',
          REMOVE_YES: 'Yes, remove it',
          REMOVE_NO: 'Keep it',
          DONE: 'Done',
        },
        STEP: {
          NEED: 'What do you need from the customer?',
          NEED_LIVE: 'What do you need from the customer?',
          NEED_HINT: 'Write it as you would say it out loud.',
          NEED_PLACEHOLDER: 'Which day would you like to come in?',
          NEED_NOTE:
            'Waslo phrases this itself, in the language your customer wrote in. These words are used as they are only on a Form.',
          NEED_NOTE_LIVE:
            'Waslo phrases this itself, in the language your customer wrote in. These words are used as they are on a Form, when you show this list on it.',
          ON_FORM: 'Show on the form',
          ON_FORM_HINT:
            'The form loads these choices from your system while the customer fills it in, using what they picked earlier. If your system is slow, the chat carries on from this question. No credits are used.',
          OWN_WORDING: 'Your own wording is still being used.',
          OWN_WORDING_REPLACE: 'Use the new question instead',
          OPTIONS: 'The choices',
          OPTION_LABEL: 'Choice {number}',
          OPTION_LONG: 'Too long — up to {limit} characters.',
          OPTION_REMOVE: 'Remove this choice',
          OPTION_ADD: 'Add a choice',
          PHONE_LANDLINES: 'Accept landlines too',
          PHONE_LANDLINES_HINT:
            'Off: only mobile numbers, which can get WhatsApp and text messages.',
          PHONE_COUNTRIES: 'Which countries?',
          PHONE_COUNTRY_KW: 'Kuwait',
          PHONE_COUNTRY_SA: 'Saudi Arabia',
          PHONE_COUNTRY_AE: 'UAE',
          PHONE_COUNTRY_QA: 'Qatar',
          PHONE_COUNTRY_BH: 'Bahrain',
          PHONE_COUNTRY_OM: 'Oman',
          PHONE_COUNTRY_ANY: 'Any country (with its country code)',
          // Services field fixes G2.4: the owner's limits on a photo or file question.
          UPLOAD_COUNT: 'At most',
          UPLOAD_SIZE: 'Largest file',
          UPLOAD_SIZE_OPTION: '{size} MB',
          UPLOAD_NOTE:
            'On a WhatsApp form this question goes on the last page, and a form asks for photos or a file once.',
          SHAPE_BUTTONS:
            '{count} choices. WhatsApp shows these as buttons, up to {limit} characters each.',
          SHAPE_LIST:
            '{count} choices. WhatsApp shows these as a list, up to {limit} characters each.',
          // Field fixes G2.3.
          SHAPE_MANY:
            '{count} choices. WhatsApp shows these as a numbered list, up to {limit} characters each.',
          MANY: 'They may pick more than one',
          MANY_HINT:
            'The chat lists the choices with numbers, and they reply with the numbers of the ones they want. On a Form they tick boxes.',
          MANY_MIN: 'At least',
          MANY_MAX: 'At most',
          MANY_BLOCKED:
            'Not for a question whose choices lead to different steps.',
          MANY_LOCKED:
            'Remove choices down to {limit} first: a question with one answer shows at most {limit}.',
          SYSTEM: 'Which of your systems sends the choices?',
          SYSTEM_PLACEHOLDER: 'Choose one',
          SYSTEM_UNAVAILABLE: "We couldn't list your systems just now.",
          SYSTEM_NONE:
            'You have no systems connected yet. Connect one under For developers, or pick "Anything they type" and hand the chat to a person.',
          WIRING: 'What it sends and gets back',
          WIRING_OPEN: 'Set it up',
          SENDS: 'Sends {count} of its details.',
          SENDS_NONE: 'Sends nothing yet.',
          GETS: 'Your customer reads: {field}',
          GETS_NOTHING: 'Nothing picked out of the answer yet.',
          IFNONE: 'What if nothing comes back?',
          IFNONE_LABEL: 'Then',
          IFNONE_HANDOVER: 'Hand the chat to a person',
          SAVED_AS: 'Written down as "{name}"',
          SAVED_BOTH:
            'Written down as "{name}", and on the customer as {field}',
          SAVED_NOWHERE: 'Not written down anywhere yet',
          ADVANCED: 'How Waslo should ask',
          INSTRUCTION: 'What Waslo is told to do',
          INSTRUCTION_HINT:
            'Written for your assistant, never sent to a customer. Waslo writes this from your question; change it only if you need to.',
          ASKED: 'How it is asked',
          AS_WRITTEN: 'Send my words exactly',
          AS_WRITTEN_HINT:
            'Sent as you wrote it. The assistant may add one short line before it, for example to greet or to say why an answer did not fit.',
          AS_WRITTEN_OFF: 'Let the assistant phrase it',
          OPTION_VALUE: 'What your system receives',
          OPTION_VALUE_HINT: 'Leave empty to send the English words.',
          IFSTUCK: 'If they can’t answer',
          IFSTUCK_HANDOFF: 'Pass the chat to your team',
          IFSTUCK_SKIP: 'Skip this question',
          IFSTUCK_GOTO: 'Ask something else instead',
          IFSTUCK_GOTO_LABEL: 'Ask this instead',
          // Field report F1.3 and T-16.
          OPTIONAL: 'Optional',
          OPTIONAL_HINT:
            'The customer can skip it. If they say no or that they do not know, the next question is asked.',
          SKIP_BLOCKED:
            'This answer is sent to “{connection}”, so it cannot be skipped.',
          READ_BACK: 'Show the customer what will be sent',
          READ_BACK_HINT:
            'Before they answer, they see each detail your system will receive, written from what is sent.',
          SUMMARY_NAME: 'Name in the summary',
          SUMMARY_NAME_EN: 'The name, in English',
          SUMMARY_NAME_AR: 'The name, in Arabic',
          // Services task review E: what an earlier step got from your system, under the read-back.
          READ_BACK_KEEP: 'Also show what your system gave earlier',
          READ_BACK_KEEP_HINT:
            'Up to {limit}, each on its own line under the details. For example “Amount: 40 KWD”.',
          READ_BACK_KEEP_NAME_EN: 'Its name, in English',
          READ_BACK_KEEP_NAME_AR: 'Its name, in Arabic',
          READ_BACK_KEEP_SUFFIX_EN: 'After it, in English (optional)',
          READ_BACK_KEEP_SUFFIX_AR: 'After it, in Arabic (optional)',
        },
        MESSAGE: {
          SAY: 'What should it say?',
          SAY_HINT: 'Describe it. Your assistant writes the words.',
          SAY_PLACEHOLDER:
            "Tell them it's booked, with the branch and the time.",
          SAY_NOTE:
            'Your assistant says this in the language your customer wrote in.',
          EXACT: 'Say it in my exact words',
          EXACT_ABOUT:
            'For a price, a policy or a line that must not be re-phrased. You write both languages.',
          EXACT_LABEL: 'The exact words',
          EXACT_NOTE: 'Sent exactly as you type them.',
          SUMMARY: 'Add a summary of what was sent',
          SUMMARY_ABOUT:
            'Each detail your system received goes under the message, written from what was sent.',
          SUMMARY_KEEP: 'Also show what your system sent back',
          SEND_WITH: 'Send with',
          SEND_ABOUT:
            'On WhatsApp the button or the pin goes under your message. On other channels the link or the place is written out.',
          SEND_TEXT: 'Text',
          SEND_LINK: 'Link button',
          SEND_LOCATION: 'Location',
          LINK_LABEL_EN: 'Button words, in English',
          LINK_LABEL_AR: 'Button words, in Arabic',
          LINK_LABEL_EMPTY: 'Your button',
          LINK_FROM: 'Where the link comes from',
          LINK_FIXED: 'A link I type',
          LINK_SYSTEM: 'A link my system sends back',
          LINK_URL: 'The link',
          LINK_URL_INVALID:
            'The link has to start with https:// and have no spaces.',
          LINK_KEY: 'Which answer holds the link',
          LINK_KEY_PLACEHOLDER: 'Choose the answer',
          LINK_KEY_NONE: 'No step above keeps an answer from your system yet.',
          PREVIEW: 'What your customer sees',
        },
        PLACES: {
          PICK: 'Which place',
          PICK_PLACEHOLDER: 'Choose a place',
          ADD: 'Add a place',
          EDIT: 'Change this place',
          NAME_EN: 'Name, in English',
          NAME_AR: 'Name, in Arabic',
          ADDRESS_EN: 'Address, in English',
          ADDRESS_AR: 'Address, in Arabic',
          WHERE: 'Where it is on the map',
          WHERE_HINT:
            'Paste the full Google Maps link, or type the two numbers of the point.',
          POINT: 'The pin goes at {lat}, {lng}.',
          WHERE_SHORT_LINK:
            'That is a short link. Open it, then paste the full link from the address bar.',
          WHERE_NO_POINT:
            'No point found. Paste the full Google Maps link, or type the two numbers.',
          WHERE_OUT_OF_RANGE: 'That point is not on the map.',
          NAME_MISSING: 'Give this place a name.',
          SAVE: 'Save place',
          CANCEL: 'Cancel',
          REMOVE: 'Remove place',
          IN_USE:
            '{tasks} still sends this place. Take it out of that task first.',
          LIST_JOIN: ', ',
          LIMIT:
            'You can keep up to {count} places. Remove one you no longer use first.',
          SAVE_FAILED: 'We couldn’t save that. Try again.',
          LOAD_FAILED: 'We couldn’t load your places. Try again.',
        },
        ACTION: {
          NAME: 'What does this step do?',
          NAME_HINT: 'Your customers never see this. Your team does.',
          NAME_PLACEHOLDER: 'Check the calendar',
          NAME_LONG: 'Too long — up to {limit} characters.',
          SYSTEM: 'Which of your systems?',
          SYSTEM_PLACEHOLDER: 'Choose one',
          SYSTEM_UNAVAILABLE: "We couldn't list your systems just now.",
          SYSTEM_NONE:
            'You have no systems connected yet. Connect one under For developers.',
          WIRING: 'What it sends and gets back',
          WIRING_OPEN: 'Set it up',
          SENDS: 'Sends {count} of its details.',
          SENDS_NONE: 'Sends nothing yet.',
          KEEPS: 'Keeps {count} from the answer.',
          KEEPS_NOTHING: 'Keeps nothing from the answer.',
          FAILS_HANDOVER:
            'If it does not go through, Waslo hands the chat to a person.',
          FAILS_STEP:
            'If it does not go through, Waslo goes to the step you chose under What if.',
          CHANGE_NOTE:
            'This changes something in your system, so it runs only after the customer says yes to a question.',
        },
        HANDOFF: {
          NOTE: 'What should the person picking this up know?',
          NOTE_HINT:
            'A private note for your team. Your customer never sees it.',
          NOTE_PLACEHOLDER:
            'The customer wants to book and we could not offer a time.',
          LAST_WORD: 'Anything to say before it finishes?',
          LAST_WORD_HINT: 'Leave it empty to finish without a last message.',
          AFTER: 'Nothing runs after this. Your team has the chat.',
          ENDS: 'The task finishes here.',
          // Task review item 11: who gets the chats from this step.
          HAND_TO: 'Hand to',
          HAND_TO_ACCOUNT: 'Your usual choice (Hand chats to)',
          HAND_TO_PERSON: 'A person',
          HAND_TO_TEAM: 'A team',
          HAND_TO_ACCOUNT_HINT:
            'Chats from this step go wherever “Hand chats to” on the Personality page sends them.',
          WHICH_PERSON: 'Who',
          WHICH_TEAM: 'Which team',
          PERSON_HINT:
            'Every chat that reaches this step goes to this person, even if someone else has it.',
          TEAM_HINT:
            'Every chat that reaches this step goes to this team. If nobody in it is picked, it waits there for the whole team.',
          PERSON_GONE:
            'This person is no longer in your account. Choose someone else.',
          TEAM_GONE: 'This team no longer exists. Choose another.',
        },
        WIRING: {
          HEADING: 'What it sends and gets back',
          // F3.7: an earlier answer, named by how it was got and the step that got it.
          ANSWER_PICKED: 'The {answer} they picked ({step})',
          ANSWER_GAVE: 'The {answer} they gave ({step})',
          ANSWER_SYSTEM: 'The {answer} from your system ({step})',
          VALUES: 'Run it with these values',
          VALUES_HINT:
            'Only for this test. In a real chat, each one comes from what you chose above.',
          TRY: 'Try it now',
          RUNNING: 'Running…',
          TRY_NOTE: 'This really calls your system.',
          LOCKED_HEADING: 'Filled in by Waslo',
          LOCKED_PHONE: 'This chat’s WhatsApp number (locked)',
          LOCKED_PHONE_NOTE:
            'Taken from the chat. A number the customer types never replaces it.',
          LOCKED_NAME: 'The customer’s name, from their profile',
          LOCKED_EMAIL: 'The customer’s email, from their profile',
          LOCKED_CHAT: 'This chat’s reference number',
          PREVIEW_NOTE:
            'This one changes something in your system, so trying it here only shows what would be sent. To send it for real, open it under Your systems.',
          OK: 'It answered. ({status})',
          FAILED: 'Your system refused it. ({status})',
          NO_STATUS: 'Your system did not answer.',
          SHOWN_AS: 'Shown to the customer as',
          NAME_EN_FROM: 'English name comes from',
          NAME_AR_FROM: 'Arabic name comes from',
          NAME_FROM_SAME: 'The same field for both',
          SENDS_VALUES: '“{name}” sends: {values}',
          SHAPE_BUTTONS: '{count} choices. WhatsApp shows these as buttons.',
          SHAPE_LIST: '{count} choices. WhatsApp shows these as a list.',
          DONE: 'Done',
        },
        RESPONSE: {
          WAITING: 'Press Try it now to see what your system sends back.',
          HEADING: 'It answered. Click what you mean.',
          NO_LIST:
            'Nothing in this answer is a list of things a customer could choose from.',
          IS_LIST: 'the list',
          WHOLE_LIST: 'The whole answer is the list',
          FIELDS: 'The first one it sent back:',
          THEY_READ: 'they read this',
          WE_KEEP: 'we keep this',
          READS_AS: 'Your customer reads: {text}',
          KEEP_HEADING: 'It answered. Click what you want to keep.',
          NO_FIELDS: 'There is nothing in this answer to keep.',
          KEEP_AS: 'Keep it as',
        },
        WHATIF: {
          HEADING: 'What if something goes wrong?',
          DESCRIPTION:
            'Each of these already has an answer. Change any you disagree with.',
          NOTHING: 'Nothing in this task can go wrong in a way you decide.',
          // The referent. Every question below names a step, and a name is only a name if
          // you can see where it sits — "on screen four I cannot look back at step two
          // without losing my place".
          STEPS_HEADING: 'The steps these questions are about',
          AT_STEP: 'Step {number}',
          LIVE_EMPTY: 'Your system has nothing to offer for "{name}"',
          CALL_FAILED: '"{name}" did not go through',
          ANSWER: 'Then',
          HANDOVER: 'Hand the chat to a person',
          FORGETS:
            'When the chat comes back to "{name}", Waslo forgets: {keys}',
          PINNED: 'your list',
          UNPIN: 'Use what Waslo works out',
          ENGINE: 'You do not set these — Waslo already does them:',
          ENGINE_REFUSED:
            'If a customer will not answer the same question three times, Waslo hands the chat to a person.',
          ENGINE_UNREAD:
            'If Waslo cannot understand two turns running, it hands the chat to a person.',
          // P-2.2: where a Call's success goes, said on its "if it didn't go through" arm.
          WORKS_GOES_ON: 'When it works, the chat goes on to “{step}”.',
          WORKS_ENDS: 'When it works, the task ends here.',
          // P-10.6 and P-10.21: what the owner's own system answers.
          SYSTEM: {
            // P-2.11: the fields in the sample your system sent, to pick from instead of typing.
            SAMPLE_PICK: 'From your system’s last answer:',
            NO_SAMPLE:
              'Test this connection once and its fields show up here to pick from.',
            TOGGLE: 'What your system answers',
            REASON: 'Your system’s reason',
            REASON_HINT:
              'Where your system writes why it said no. We send it to the customer word for word.',
            SAY: 'Send your system’s own message to the customer',
            SAY_PATH: 'Where the message is',
            SAY_PATH_AR: 'Where the Arabic message is, if it has one',
            SAY_ON: 'Send it when',
            SAY_WORKED: 'it works',
            SAY_FAILED: 'your system says no',
            SAY_BOTH: 'either way',
            ANSWERS: 'If your system answers',
            ANSWERS_FIELD: 'Which part of the answer',
            FIELD_NAME: 'it',
            CASE_IS: '{field} is',
            CASE_HINT: 'Separate values with commas, up to {limit}.',
            CASE_GO: 'go to',
            ADD_CASE: 'Add an answer',
            REMOVE_CASE: 'Remove',
            ELSE: 'Any other answer goes on as when it works.',
          },
        },
        // There was a `COMPOSE.ARABIC` block here. It was written for a screen that was
        // replaced during the integration by `ArabicScreen.vue`, which speaks
        // `WASLO.TASKS.ARABIC.*` — `grep` for `COMPOSE.ARABIC` across the tree returned
        // nothing but these ten keys and their ten Arabic twins. Twenty strings that no
        // screen can reach are twenty strings a future reader has to prove are dead before
        // touching either block, so they are gone rather than kept "in case".
        CHECK: {
          // P-2.11: "Try it before customers do" and whether this version was tried.
          TRY: 'Try it before customers do',
          TRY_HEADING: 'Try this task',
          TRY_NOTE:
            'This is the task as it is on this screen, saved or not. Nothing reaches a customer.',
          TRY_START_NOW: 'Start the task with my first message',
          TRY_NEEDS_NAME: 'Give the task a name first.',
          TRIED: 'Tried {when}',
          NOT_TRIED: 'Not tried since your last change',
          // P-10.9: the start line for a task that does not start by the assistant's choice.
          STARTS_WORDS: 'Starts when a message has: {words}',
          STARTS_MENU: 'Starts only from a menu button',
          NO_MENU:
            'No menu starts this task yet, so customers cannot reach it. Add it to a menu first.',
          HEADING: 'This is what you wrote',
          DESCRIPTION: 'Click any step on the map to go back to it.',
          UNTITLED: 'This task has no name yet',
          STARTS: 'Starts when: {trigger}',
          STEPS: 'Steps: {count}',
          JUMPS:
            'This list does not read straight down: {count} steps send the chat somewhere else. The map is where that is easiest to read.',
          JUMPS_ONE:
            'This list does not read straight down — one step sends the chat somewhere else. The map is where that is easiest to read.',
          MAP_NOTE:
            'The map is drawn from your list. You never draw it yourself.',
          // WHERE IT STANDS, then the control that changes it. The label used to be read
          // off the DRAFT and with the arms crossed — "Save it, switched off" on a button
          // that switched it on. Now the state is reported from the stored row and each
          // button does only the thing it is named after.
          LIVE_OFF: 'This task is not live yet.',
          LIVE_OFF_BODY:
            'Nothing you have written reaches a customer until you turn it on here. You can turn it off again at any time, from this screen or from the Tasks list.',
          LIVE_ON: 'This task is on.',
          LIVE_ON_BODY:
            'Your assistant is running the saved version with customers right now. Anything you changed above reaches them only once you save it.',
          TURN_ON: 'Turn this task on',
          TURN_OFF: 'Turn this task off',
          SAVE_OFF: 'Save it, switched off',
          SAVE_CHANGES: 'Save these changes',
          BLOCKED: 'Finish the marked steps first.',
          CHANGES_AFTER:
            'Changes your system only after the customer answers: {question}',
          NEEDS_CONFIRM: 'Before this can go live',
          WARNINGS: 'Worth a look before you turn it on',
          // P-7.1: the form's submit button, written once like every other bilingual field.
          CTA: 'The button that sends the form',
          CTA_HINT:
            'Used only when this task is sent as a form inside WhatsApp. Leave it empty and the button says Send.',
          // P-2.2 and the index hand-offs: "worth a look", never a block. The step and the
          // connection are the owner's own names, looked up in the graph.
          WARN: {
            FAILURE_PATH:
              'When “{call}” works, the chat still goes on to “{step}”, which you set up for when it fails.',
            ENDS_SILENTLY:
              'The task ends right after “{step}” without telling the customer anything.',
            KEPT_FOR_CHAT:
              '«{step}» is kept for this chat only, so returning customers are asked again.',
            CONNECTION_OFF:
              '«{connection}» is switched off. Customers who reach this step are passed to your team.',
            // Field report F1.5 (D-02).
            SAVES_OVER:
              '«{step}» saves its answer where an earlier question already does, so customers are never asked it.',
          },
          SHOW_ME: 'Show me',
          FIX: {
            SKIP_WHEN_WORKS: 'Skip it when it works',
            SAVE_TO_PROFILE: 'Also save it to their profile',
            OPEN_CONNECTION: 'Open it',
          },
        },
      },
      // ── THE WORDS ON THE CARD ──────────────────────────────────────────────────────
      // The one sentence this whole section exists to say: the assistant already replies
      // in the customer's language on its own. These words are different — they go on a
      // CARD inside WhatsApp, and a card has nobody in it to phrase anything, so it is
      // sent exactly as it was typed.
      BILINGUAL: {
        // The quiet line under the box: the same question in the other language, so the
        // owner is never in the state of not knowing whether the other side exists.
        WRITTEN_ARIA: 'Written in {language}',
        // P-7.1: the ghost line names the language that is missing, never the one she wrote.
        MISSING: '{language}: not written',
        EDIT: 'Edit',
        SUGGEST: 'Suggest',
        SUGGEST_FAILED:
          'That did not work. Nothing was changed. Write it yourself, or try again.',
        SAVED_AS: 'Saved as the {language} version.',
        LOOKS_LIKE:
          'These words look {language}, but they are saved as the {other} version.',
        CHANGE: 'Change',
        WRITE: 'Write it',
        BACK: 'Back to {language}',
        TOO_LONG: '{count} of {max} characters',
        // Said once, on the step screen, under the box it is about.
        NOTE: 'Your assistant already replies in the customer’s own language. These words are different: they go on a Form inside WhatsApp, and a Form sends them exactly as you typed them.',
      },
      ARABIC: {
        TAB: 'The words on the Form',
        TITLE: 'The words on the Form',
        DESCRIPTION:
          'Your assistant phrases every question itself in a chat. On a Form inside WhatsApp there is nobody to phrase anything, so a Form can only be built in a language you wrote the words in.',
        PROGRESS: '{written} of {total} written',
        // Never a count NOUN after the number: Arabic would need six plural forms for
        // one short line, which is the rule the map's COUNT already follows.
        LEFT: '{count} left',
        ALL_DONE: 'Every word on this Form is written.',
        EMPTY: 'There is nothing to write yet',
        EMPTY_BODY:
          'Only a question you write out can go on a Form. Add a question step and it appears here.',
        // Why the numbers below have gaps in them. Never a warning: not one of these
        // steps could be a card field however carefully somebody wrote it.
        // The count is written AFTER the noun, deliberately: a plural form would need
        // six of them in Arabic for one short line, which is the same rule the map's
        // COUNT follows.
        OFF_SCREEN:
          'Steps that are not on this screen: {count}. A live list, an action, a message, a hand-over, an ending and a time-of-day question can never sit on a Form, so your assistant asks those in the chat, in the customer’s own language.',
        STEP: 'Step {position}',
        OPTION_LABEL: 'Option',
        // The three things a row can be, in the words of what it costs the customer.
        STATE: {
          MISSING:
            'Not written. {language} customers cannot get this task as a Form at all.',
          FALLS_BACK:
            'Not written. {language} customers read this one row in the other language.',
          NO_LABEL:
            'Not written in either language, so this option has nothing to show.',
          FROM_NAME: 'Using this step’s own name: “{name}”',
        },
        // Proposed, never applied. The model writes a suggestion beside the box and
        // nothing moves until the owner presses the button — the same discipline the
        // wording assistant on Personality already follows.
        TRANSLATE: 'Translate the missing words ({count})',
        TRANSLATE_WORKING: 'Writing them…',
        TRANSLATE_FAILED:
          'That did not work. Nothing was changed — write them yourself, or try again.',
        TRANSLATE_NONE: 'There was nothing to suggest.',
        SEE_WARNING: 'See what customers get',
        PROPOSED: 'Suggested',
        PROPOSED_NOTE:
          'Nothing is written until you press Use this. Read each one first — these words go to a customer exactly as they are.',
        ACCEPT: 'Use this',
        DISCARD: 'Discard',
        DISCARD_ALL: 'Discard all suggestions',
      },
    },
    MENUS: {
      TITLE: 'Menus',
      ADD: 'Add a menu',
      SPOTLIGHT_TITLE: 'What a menu is',
      SPOTLIGHT:
        'A menu is a short list of options your customer can tap instead of typing — Book an appointment, Prices, Opening hours. Your assistant puts one under its reply when the conversation fits. On WhatsApp it appears as buttons or as a list; on Instagram it arrives as a numbered list, and the customer answers with the number. An option can also start one of your tasks.',
      // The example IS the real preview, fed from these strings — the English table is the
      // English menu and the Arabic table the Arabic one (MenuExample reads both).
      //
      // IT FITS EVERY BUSINESS ON PURPOSE. It read Programs / Admissions / Fees / Campus
      // visits until W-13 — a prospectus, shown to a photo studio and a clinic alike, and
      // fixed copy no owner can edit her way out of. Booking, prices, hours and reaching
      // a person are what every one of them is asked about.
      EXAMPLE: {
        HEADING: 'For example',
        BODY: 'Welcome! How can we help?',
        BUTTON: 'See the options',
        OPTION_1: 'Book an appointment',
        OPTION_2: 'Prices',
        OPTION_3: 'Opening hours',
        OPTION_4: 'Talk to us',
        NOTE: 'Three options or fewer appear as buttons; four or more appear as a list behind one button. Switch the language to see what a customer writing in Arabic gets.',
      },
      LIST: {
        HEADING: 'Your menus',
        DESCRIPTION:
          'On means your assistant may show it. Off keeps it here without using it.',
        WHEN: 'Shown when: {when}',
        // Never a plural form — the same reason as the Tasks list's CONNECTIONS.
        OPTIONS: 'Options: {count}',
        USAGE_SHOWN:
          'Not shown in the last 7 days. | Shown to 1 customer in the last 7 days. | Shown to {count} customers in the last 7 days.',
        UPDATED: 'Changed {when}',
        UPDATED_JUST_NOW: 'just now',
        EDIT: 'Edit',
        ON: 'On',
        OFF: 'Off',
        SAVE_FAILED:
          "We couldn't change that just now. Nothing was changed — try again.",
      },
      EMPTY: {
        TITLE: 'No menus yet',
        BODY: 'Your assistant answers without them. Add a menu for the questions customers ask most, so they can tap an answer instead of typing one.',
        ACTION: 'Add your first menu',
      },
      EDITOR: {
        NEW_TITLE: 'New menu',
        SPOTLIGHT:
          'Your assistant writes its reply as usual and puts this menu under it. It never lists the options in its own words.',
        ABOUT: {
          HEADING: 'What this menu is',
          DESCRIPTION: 'A name, and when your assistant should show it.',
        },
        NAME: {
          LABEL: 'What do you call it?',
          PLACEHOLDER: 'Main menu',
          HINT: 'Your assistant picks menus by this name. Customers never see it.',
        },
        WHEN: {
          LABEL: 'When should it be shown?',
          PLACEHOLDER: 'The customer says hello, or asks what we offer.',
          HINT: 'Describe the moment, not the exact words. Your assistant matches the meaning, in any language.',
        },
        OPTIONS: {
          HEADING: 'The options',
          DESCRIPTION:
            'Up to {limit}, in the order they appear. Write each one in English and in Arabic. Your assistant uses the language the customer writes in.',
          LABEL: 'Option {number}',
          TITLE: 'What the option says',
          COUNT: '{count} of {limit}',
          TASK: 'When tapped',
          TASK_HINT:
            'If this task is already running, the chat simply carries on. If a different task is running, this one takes its place, and what the customer already said stays in the chat.',
          NO_TASK: 'Just answer the question',
          // THE OTHER HALF OF THE CROSS-LINK. `TaskCard` reaches this tab; this reaches the
          // task. Shown only on a row that actually names one — a link that goes nowhere on
          // nine rows out of ten is worse chrome than no link at all.
          OPEN_TASK: 'Open this task',
          TASK_OFF: '{title} (switched off)',
          TASKS_UNAVAILABLE:
            "We couldn't list your tasks just now, so what an option starts can't be changed here. Nothing else is affected.",
          STOP_WORD:
            'This is a word customers send to stop or restart messages. Choose another title.',
          ADD: 'Add an option',
          REMOVE: 'Remove this option',
          UP: 'Move up',
          DOWN: 'Move down',
          FULL: 'A menu can have up to {limit}.',
          BUTTONS_NOTE:
            'With three options or fewer, WhatsApp shows them as buttons, so each can be up to {limit}.',
          LIST_NOTE:
            'With four or more, WhatsApp shows them as a list, so each can be up to {limit}.',
        },
        MESSAGE: {
          HEADING: 'The words around it',
          DESCRIPTION:
            'Usually your assistant’s own reply sits above the menu. These are used only when its reply is too long to carry one.',
          BODY: 'Text above the menu',
          BODY_HINT: 'Leave them empty to use “Please choose an option:”.',
          BUTTON: 'List button',
          BUTTON_HINT:
            'The button that opens a list of four or more options. Up to {limit}. Leave it empty to use “Choose an option”.',
        },
        PREVIEW: {
          HEADING: 'What your customer sees',
          DESCRIPTION:
            'A WhatsApp preview that follows what you type. Anything too long for WhatsApp is marked in red.',
          LANGUAGE: 'Preview language',
          ENGLISH: 'English',
          ARABIC: 'Arabic',
          // CUSTOMER-facing, read in the PREVIEW's language (MenuPreview uses wasloT).
          SAMPLE_BODY: 'Your assistant’s reply appears here.',
          OVER: '{count} of {limit}, too long',
          TOO_LONG:
            'Too long for WhatsApp: {count}. Shorten what is marked in red.',
          BORROWED:
            'Some options have no title in this language yet, so the other one is shown — exactly what your customer would get.',
        },
        STATE: {
          HEADING: 'Use this menu',
          DESCRIPTION:
            'You can keep a menu here without letting your assistant show it yet.',
          LABEL: 'Your assistant may show this menu',
          ON: 'Your assistant will show this menu when it fits.',
          OFF: 'Your assistant will never show this menu.',
        },
        REMOVE: {
          HEADING: 'Remove this menu',
          DESCRIPTION:
            'Your assistant stops showing it straight away. Taps on menus already sent are answered as ordinary messages.',
          ACTION: 'Remove this menu',
          CONFIRM: 'Remove this menu?',
          CONFIRM_BODY:
            'You can put it back from Recently removed under Menus for {period}. You can also switch it off instead.',
          CONFIRM_ACTION: 'Yes, remove it',
          CANCEL: 'Keep it',
          FAILED: 'We could not remove that. Nothing was changed — try again.',
          REMOVED: 'Removed.',
        },
        SAVE: 'Save changes',
        CREATE: 'Create this menu',
        // "Within a minute", not "from the next message": the assistant's copy of the menus
        // is cached for 60 s in the process that answers customers.
        SAVED: 'Saved. Your assistant uses this within a minute.',
        CREATED: 'Created. Your assistant uses this within a minute.',
        SAVE_FAILED:
          'We could not save that. Nothing was changed — please try again.',
        NOT_FOUND: "We couldn't find that menu. It may have been removed.",
        STATUS: {
          IDLE: 'Everything is saved',
          IDLE_DETAIL: 'Your assistant is using this now.',
          NEW: 'Not created yet',
          NEW_DETAIL:
            'Fill in the name, when to show it, and the first option.',
          DIRTY: 'Not saved yet',
          DIRTY_DETAIL: 'Waiting to be saved: {sections}',
          SAVING: 'Saving…',
          SAVED: 'Saved at {time}',
          SAVED_DETAIL:
            '{sections} — your assistant uses this within a minute.',
          ERROR: 'We could not save that',
          ERROR_DETAIL:
            'Nothing was changed, and what you wrote is still on the page. Try again.',
        },
        ERRORS: {
          TITLE: 'Give this menu a name.',
          TITLE_LONG: 'That name is too long. Keep it under {limit}.',
          WHEN: 'Say when this menu should be shown.',
          WHEN_LONG: 'That is too long. Keep it under {limit}.',
          OPTIONS_EMPTY: 'Add at least one option.',
          OPTIONS_MANY: 'A menu can have up to {limit}.',
          OPTION_BLANK:
            'Option {number} has no title. Write it in English, Arabic, or both.',
          OPTION_LONG:
            'Option {number} is too long for WhatsApp. Keep it to {limit} characters.',
          OPTION_DUPLICATE:
            'Option {number} has the same title as an earlier option.',
          // P-2.10 on a one-language account.
          OPTION_MISSING_AR:
            'Option {number} needs its Arabic words: your customers read only Arabic.',
          OPTION_MISSING_EN:
            'Option {number} needs its English words: your customers read only English.',
          BODY_LONG: 'That is too long for WhatsApp. Keep it under {limit}.',
          BUTTON_LONG: 'That is too long for WhatsApp. Keep it to {limit}.',
        },
      },
    },
    /**
     * Approved messages — WhatsApp template wording, authored here. Roadmap project 2.
     *
     * NOT "templates". Chatwoot spends that word on its own read-only Templates page in the
     * same sidebar, and this product has called them "messages you can send" since the
     * Outbound API screens. One word per thing.
     */
    TEMPLATES: {
      TITLE: 'Approved messages',
      ADD: 'Write a new one',
      // The second tab is Forms, which stopped being a sidebar row in the navigation
      // consolidation. Both tab labels are `WASLO.NAV.TEMPLATES` and `WASLO.NAV.FORMS`,
      // reused — see the note on `WASLO.TASKS.TABS` for why a tab label is never written
      // twice. Only the group's accessible name is new.
      TABS: {
        LABEL: 'Approved messages and forms',
      },
      SPOTLIGHT_TITLE: 'WhatsApp approves your wording first',
      SPOTLIGHT:
        'WhatsApp only lets a business start a chat with wording it has approved in advance. Write it here and we send it to WhatsApp for you — approval usually takes minutes, sometimes a day. Replies inside an open chat need none of this.',
      SENDER_LABEL: 'Which number',
      REFRESH: 'Ask WhatsApp for the latest',
      REFRESHING: 'Asking WhatsApp…',
      REQUESTED:
        'We have asked WhatsApp for the latest, and read it back below.',
      REFRESH_FAILED:
        "We couldn't ask WhatsApp just now. Nothing was changed — try again.",
      UNAVAILABLE: "We couldn't read this number's messages just now.",
      LAST_CHECKED: 'Last asked WhatsApp: {when}',
      /** Meta's own count and ceiling, read from the account — never a number we printed. */
      SUMMARY: '{used} of {limit} messages used on this number.',
      // WhatsApp could not be reached, so these rows are the copy we last saved. Saying so is
      // the whole point: a stale list shown as live is how an owner sends refused wording.
      STORED:
        'WhatsApp could not be reached just now, so this is the copy we saved last time. It may be out of date, and the quality ratings are missing from it.',
      BASIC_FIELDS:
        'WhatsApp answered but did not send the extra details this time, so the quality ratings and delivery windows below are blank. Nothing is wrong with your messages.',
      TRUNCATED:
        'You have more approved messages than this page shows. Search by name or by what the message says to find the rest.',
      NO_SENDER: {
        TITLE: 'No WhatsApp number on this workspace yet',
        BODY: 'Approved wording belongs to a WhatsApp number. Once one is connected, this page is where you write it.',
      },
      LIST: {
        HEADING: 'What WhatsApp has agreed to',
        DESCRIPTION:
          'Everything this number may use to start a chat, with WhatsApp’s own verdict beside it.',
      },
      EMPTY: {
        TITLE: 'Nothing approved for this number yet',
        BODY: 'Write your first one and we will send it to WhatsApp. It appears here with WhatsApp’s answer as soon as there is one.',
      },
      /**
       * WHATSAPP'S OWN WORD, AND NOTHING WE MADE UP. `UNTOLD` is the state that matters: a
       * template we have no status for is NOT pending, and saying so is the difference
       * between an owner who waits and an owner who sends wording WhatsApp refused. A status
       * WhatsApp has newly invented is shown as WhatsApp wrote it rather than folded in here.
       */
      STATUS: {
        APPROVED: 'Approved',
        PENDING: 'Waiting on WhatsApp',
        IN_APPEAL: 'Being appealed',
        PAUSED: 'Paused by WhatsApp',
        PENDING_DELETION: 'Being removed',
        REJECTED: 'Refused',
        DISABLED: 'Switched off by WhatsApp',
        ARCHIVED: 'Put away by WhatsApp',
        DELETED: 'Removed',
        LIMIT_EXCEEDED: 'Over your limit',
        UNTOLD: 'WhatsApp hasn’t said',
      },
      /**
       * WhatsApp's four quality ratings, in the words its own manager uses. `UNKNOWN` is
       * WhatsApp's own answer for "no customer feedback yet", which is a real answer and not a
       * missing one; `NONE` is for a row it sent no rating for at all.
       */
      QUALITY: {
        HIGH: 'high quality',
        MEDIUM: 'medium quality',
        LOW: 'low quality',
        UNKNOWN: 'too early to rate',
        NONE: 'no rating yet',
      },
      /**
       * A legend is a manual, and a manual is what you write when the thing itself cannot be
       * read. These are what the two ratings that matter DO — because "medium" is not something
       * an owner can act on and "WhatsApp pauses it next" is.
       */
      QUALITY_NOTE: {
        MEDIUM:
          'Enough customers have blocked or reported this that WhatsApp has noticed. It is the only warning that comes before a pause.',
        LOW: 'WhatsApp pauses a message at this rating for {first}, then {second}, then switches it off for good. Change the wording or stop sending it.',
      },
      /** WhatsApp's documented reasons for refusing wording. */
      REASON: {
        INVALID_FORMAT:
          'the wording or the blanks are not laid out the way WhatsApp needs',
        TAG_CONTENT_MISMATCH:
          'the wording does not match what it is charged as',
        SCAM: 'WhatsApp read it as a scam',
        ABUSIVE_CONTENT: 'WhatsApp read it as abusive',
        INCORRECT_CATEGORY: 'it is charged as the wrong kind of message',
        PROMOTIONAL: 'WhatsApp read it as an advert, not a service message',
        CATEGORY_NOT_AVAILABLE:
          'that kind of message is not available on this number',
      },
      /** WhatsApp's three order-related shapes, which only it can assign. */
      SUB_CATEGORY: {
        ORDER_DETAILS: 'order details',
        ORDER_STATUS: 'order status',
        RICH_ORDER_STATUS: 'detailed order status',
      },
      /**
       * Every kind of button WhatsApp has a name for — far more than the four this page can
       * write. A business that built a message in WhatsApp’s own manager must still be able to
       * read it here, so all of them get a word an owner recognises.
       */
      BUTTON_TYPE: {
        QUICK_REPLY: 'A reply button',
        URL: 'A link',
        PHONE_NUMBER: 'A call button',
        COPY_CODE: 'A copy-the-code button',
        OTP: 'A sign-in code button',
        FLOW: 'A form button',
        MPM: 'A several-products button',
        SPM: 'A single-product button',
        CATALOG: 'A catalogue button',
        VOICE_CALL: 'A WhatsApp call button',
        ORDER_DETAILS: 'A pay-for-this-order button',
        APP: 'An open-the-app button',
        REMINDER: 'A remind-me button',
        POSTBACK: 'A reply button',
        EXTENSION: 'An extra button',
        SEND_LOCATION: 'A send-my-location button',
      },
      /**
       * Why the Edit door is shut on a template WhatsApp would let her edit.
       *
       * WhatsApp replaces EVERY part of a message when it is edited, and this form can only hold
       * a line of text above, the message, a line under and four kinds of button. Anything else
       * would be dropped — so the save is refused and the part is NAMED, because "this cannot be
       * edited" sends an owner looking for a fault of her own.
       */
      EDIT_BLOCKED: {
        /** Between two named parts. Punctuation is a translation, not a character to type. */
        JOIN: ', ',
        MEDIA_HEADER:
          'This one has {part} at the top. WhatsApp replaces every part of a message when it is changed, and we cannot carry that across yet — so changing it here would remove it. Change this one in WhatsApp Manager.',
        CAROUSEL:
          'This one has a swipeable set of cards. WhatsApp replaces every part of a message when it is changed, and we cannot carry the cards across yet — so changing it here would remove them. Change this one in WhatsApp Manager.',
        OFFER:
          'This one has a limited-time offer in it. WhatsApp replaces every part of a message when it is changed, and we cannot carry the offer across yet — so changing it here would remove it. Change this one in WhatsApp Manager.',
        BUTTON:
          'This one has {part}, which we cannot carry across yet. WhatsApp replaces every part of a message when it is changed, so changing it here would remove that button. Change this one in WhatsApp Manager.',
        PART: 'This one has a part we cannot carry across yet ({part}). WhatsApp replaces every part of a message when it is changed, so changing it here would remove it. Change this one in WhatsApp Manager.',
        // A different refusal from the others, and it undoes itself: she chose the new category a
        // second ago and can choose the old one back.
        CHANGING:
          'Changing what this is charged as would remove {part} from it. WhatsApp replaces every part of a message when it is changed, and the two kinds are not built the same way. Leave it as it is, or write a new message in the other kind.',
        WORDING:
          'WhatsApp writes the wording of a sign-in code message itself, so your own wording cannot be carried across. Change this one in WhatsApp Manager.',
        /** Names for the parts that have no word of their own, so every sentence above can say one. */
        PARTS: {
          CAROUSEL: 'the cards',
          OFFER: 'the offer',
          WORDING: 'your own wording',
          PART: 'that part',
        },
      },
      ROW: {
        NAME: 'Its name at WhatsApp',
        LANGUAGE: 'Language',
        CATEGORY: 'Charged as',
        CATEGORY_UNTOLD: 'WhatsApp hasn’t said',
        BLANKS: 'Blanks to fill in: {count}',
        NO_BLANKS: 'Nothing to fill in.',
        REASON: 'WhatsApp refused it because {reason}.',
        EDIT: 'Change wording',
        SHOW_FACTS: 'What WhatsApp knows',
        HIDE_FACTS: 'Hide the details',
      },
      CATEGORY: {
        UTILITY: 'Something they asked for',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'A sign-in code',
        FREE_SERVICE: 'A free service message',
      },
      CATEGORY_NOTE: {
        UTILITY:
          'An order, a booking, a delivery — anything the customer set in motion. The cheapest kind to send.',
        MARKETING:
          'An offer, an invitation, anything you are the one starting. Costs more per person, and WhatsApp reads the wording closely.',
        AUTHENTICATION:
          'A one-time code. WhatsApp writes the message itself for this kind — you choose only how long the code lasts.',
      },
      /**
       * Every detail WhatsApp sends about one message, and three different ways a line can be
       * empty: WhatsApp did not send the detail at all, WhatsApp sent it empty, or WhatsApp
       * sent a word we have no sentence for and it is shown as WhatsApp wrote it.
       */
      FACTS: {
        UNTOLD: 'WhatsApp hasn’t said',
        NOT_SENT: 'WhatsApp did not send this',
        ID: 'Its number at WhatsApp',
        QUALITY: 'How customers are reacting',
        QUALITY_AS_OF: '{quality} — as of {when}',
        CATEGORY: 'Charged as',
        SUB_CATEGORY: 'Kind of message',
        MOVED_FROM:
          'WhatsApp moved this from {from}, which changed what each send costs.',
        SHOULD_BE: 'WhatsApp thinks this should be charged as {should}.',
        BLANKS_FORMAT: 'How the blanks are written',
        FORMAT_NUMBERED: 'Numbered',
        FORMAT_NAMED: 'Named',
        WINDOW_LABEL: 'If the phone is off',
        WINDOW_DEFAULT: 'WhatsApp’s own default',
        WINDOW: {
          MINUTES: 'Keeps trying for {count} minutes',
          HOURS: 'Keeps trying for {count} hours',
          DAYS: 'Keeps trying for {count} days',
          MAX: 'Keeps trying as long as it can ({count} days)',
        },
        TRACKING: 'Counting link taps',
        TRACKING_ON: 'WhatsApp counts taps on the link',
        TRACKING_OFF: 'WhatsApp does not count taps on the link',
        FROM_LIBRARY: 'Copied from WhatsApp’s own ready-made message',
        PARTS: 'What the message is made of',
        HEADER_LABEL: 'Line above: ',
        FOOTER_LABEL: 'Line under: ',
        NO_HEADER: 'no line above',
        NO_BUTTONS: 'No buttons.',
        SECURITY_LINE: 'WhatsApp adds its own “do not share this code” line.',
        CODE_EXPIRY:
          'WhatsApp tells the customer the code lasts {count} minutes.',
        CARDS: 'A swipeable set of {count} cards.',
        OFFER: 'A limited-time offer: {text}',
        HEADER: {
          IMAGE: 'a picture',
          VIDEO: 'a video',
          GIF: 'a moving picture',
          DOCUMENT: 'a file',
          LOCATION: 'a map pin',
          PRODUCT: 'a product',
          TEXT: 'a line of text',
        },
      },
      /**
       * The four things WhatsApp does on its own, each of which surprises an owner exactly
       * once. The numbers are WhatsApp's own published ones.
       */
      LIFECYCLE: {
        TITLE: 'What WhatsApp does on its own',
        REVIEW:
          'Review: every new or changed message is read by WhatsApp. Usually minutes, up to a day.',
        PACING:
          'Watching a new one: WhatsApp may hold back the first sends of a brand-new message while it sees how customers react, then release them.',
        PAUSING:
          'Pausing: if enough customers block or report a message, WhatsApp pauses it for {first}, then {second} if it happens again, and switches it off for good the third time.',
        ARCHIVAL:
          'Putting one away: a message nobody has used for a year is put away by WhatsApp, and deleted {period} later unless you bring it back from WhatsApp’s own manager.',
        PAUSED:
          'A pause of {first} or {second} lifts by itself. If WhatsApp paused it while watching a new message, you have to lift it yourself.',
        // WhatsApp's own pages disagree here, and the rule everywhere else in this feature is
        // that a disagreement is REPORTED, not resolved. Resolving it cost the owner the
        // template's name and its history.
        DISABLED:
          'Switched off after repeated complaints. It cannot be sent, and WhatsApp will not let us remove it either. WhatsApp’s own guidance disagrees with itself about what comes next: one page says a switched-off message can be changed and sent for review again, another says only approved, refused and paused ones can be changed. Try it in WhatsApp Manager before you give up the name.',
        ARCHIVED:
          'WhatsApp puts away a message nobody has used for a year and DELETES it {period} later, for good. Bring it back from WhatsApp’s own manager before then.',
        PENDING_DELETION:
          'On its way out. WhatsApp keeps trying to deliver anything already sent for up to {period}, then it is gone.',
      },
      ACTIONS: {
        UNPAUSE: 'Lift the pause',
        UNPAUSING: 'Asking WhatsApp…',
        UNPAUSED: 'We asked WhatsApp to lift the pause and read the list back.',
        UNPAUSE_FAILED:
          'WhatsApp did not lift the pause. Nothing was changed — try again in a few minutes.',
        // An appeal happens in WhatsApp's own manager. There is no way to do it from here, so
        // this is a door rather than a button that would have to fail.
        APPEAL: 'Ask WhatsApp to look again',
        UNARCHIVE: 'Bring it back at WhatsApp',
        REMOVE: 'Remove from WhatsApp',
      },
      /**
       * Removing wording is irreversible, happens at WhatsApp, and by default takes EVERY
       * language of the name with it. All four consequences are spelled out where she decides,
       * and the name has to be typed before the button works.
       */
      REMOVE: {
        TITLE: 'Remove this message from WhatsApp?',
        BODY: 'Read this before you do. It happens at WhatsApp and we cannot undo it.',
        FOREVER:
          'It cannot be undone — not by us, not by WhatsApp, not by support.',
        NAME_LOCKED:
          'You will not be able to use this name again for {period}.',
        IN_FLIGHT:
          'Anything already sent keeps trying to arrive for up to {period}.',
        BREAKS_SENDS:
          'Any send or campaign built on it stops working, one customer at a time.',
        SCOPE_LABEL: 'How much to remove',
        SCOPE_LANGUAGE: 'Only the {language} version',
        SCOPE_NAME: 'Every language of this name ({count})',
        SCOPE_NAME_WARNING:
          'This also removes: {languages}. WhatsApp removes by name, so they go together.',
        TYPE_LABEL: 'Type {name} to confirm',
        TYPE_HINT: 'Exactly as it is spelled above.',
        CONFIRM: 'Remove it from WhatsApp',
        REMOVING: 'Removing…',
        CANCEL: 'Keep it',
        DONE: 'Removed {name} from WhatsApp.',
        FAILED:
          'WhatsApp did not remove it. Nothing was changed — try again in a few minutes.',
      },
      /**
       * HOW IT PERFORMED — the only part of this screen that is about results rather than
       * permission, and the reason a marketer opens it at all.
       *
       * The seven-day sentence is not a footnote. WhatsApp keeps read and tap counts for seven
       * days from the send and then resets them to zero, so a longer window would report zero
       * reads for messages that were read.
       */
      ANALYTICS: {
        HEADING: 'How it performed',
        LOAD: 'Show how it performed',
        SENT: 'Sent',
        DELIVERED: 'Arrived',
        READ: 'Read',
        WINDOW:
          'The last {count} days. WhatsApp keeps read and tap counts for seven days from each send and then resets them to zero, so a longer view would show zeros for messages that were read.',
        EMPTY:
          'This one has not been sent in the last {count} days, so there is nothing to show yet.',
        CLICK: {
          URL_BUTTON: 'Link taps',
          UNIQUE_URL_BUTTON: 'People who tapped the link',
          QUICK_REPLY_BUTTON: 'Reply button taps',
        },
        NO_CLICKS_AUTH:
          'WhatsApp does not count button taps on a sign-in code message.',
        TRACKING_OFF:
          'Link taps are not counted on this one, because link tracking is switched off for it.',
        OFF: 'WhatsApp has not started counting for this business yet. It only counts from the day you ask it to, and it cannot be switched off again afterwards.',
        // WhatsApp's own wording for what she is agreeing to. Ours would soften it, and this is
        // consent given on her business's behalf.
        CONSENT:
          'WhatsApp’s words: turning this on directs Meta to add insights to your WhatsApp Business account, including link tracking to report website clicks, and to collect and anonymise data from your chats with customers to improve its services. You can switch link tracking off on each message.',
        ENABLE: 'Start counting',
        ENABLING: 'Asking WhatsApp…',
        ENABLE_FAILED:
          'WhatsApp did not start counting. Nothing was changed — try again in a few minutes.',
        UNAVAILABLE:
          'WhatsApp does not offer these numbers for this business account. It leaves out accounts in the European Union and Japan.',
        FAILED: 'We could not read these numbers just now.',
      },
      /** Finding one message in a library that can hold hundreds. */
      FILTER: {
        SEARCH_LABEL: 'Search',
        SEARCH_HINT:
          'Searches the name and what the message says, in any language.',
        SEARCH_PLACEHOLDER: 'order ready',
        STATUS: 'WhatsApp’s verdict',
        CATEGORY: 'Charged as',
        LANGUAGE: 'Language',
        QUALITY: 'How customers react',
        ANY_STATUS: 'Any verdict',
        ANY_CATEGORY: 'Any kind',
        ANY_LANGUAGE: 'Any language',
        ANY_QUALITY: 'Any reaction',
        ATTENTION: 'Needs you ({count})',
        SHOWING: 'Showing {showing} of {total}.',
        CLEAR: 'Show everything',
        MORE: 'Show {count} more',
        NONE_TITLE: 'Nothing matches',
        NONE_BODY:
          'Try fewer words, or clear the filters to see everything on this number.',
      },
      EDITOR: {
        TITLE: 'Write a message for WhatsApp to approve',
        TITLE_EDIT: 'Change an approved message',
        NO_SENDER:
          'Open this from the approved-messages page so we know which number it is for.',
        SUBMIT: 'Send it to WhatsApp',
        SAVE: 'Send the change to WhatsApp',
        SUBMITTING: 'Sending…',
        SUBMIT_NOTE:
          'WhatsApp decides. We pass your wording on exactly as written and show you its answer.',
        // Meta's own published limits. An owner who does not know them edits twice in a day and
        // is refused for a reason that has nothing to do with her wording.
        EDIT_NOTE:
          'Saving sends this back to WhatsApp to be read again. An approved message can be changed {times} in {period}, and once a day.',
        REFUSED:
          'WhatsApp did not accept this wording, and did not say why. Change the wording and try again — nothing was sent to anyone.',
        CURRENT: 'WhatsApp says right now:',
        NOT_EDITABLE:
          'WhatsApp only lets you change a message that is approved, refused or paused. This one cannot be changed while it is in its current state.',
        GONE: 'That message is no longer at WhatsApp.',
        LOAD_FAILED: "We couldn't read that message from WhatsApp just now.",
        FORMAT_LABEL: 'How to write the blanks',
        FORMAT_LOCKED:
          'This cannot change after WhatsApp has approved the message.',
        FORMAT: {
          POSITIONAL: 'Numbered',
          NAMED: 'Named',
        },
        /**
         * READ OFF THE BLANKS, not chosen. `parameter_format` is the one field on this form an
         * owner cannot answer from anything in her own shop — both options do the same thing and
         * the difference is visible only to whoever fills the blanks in later. So the composer
         * reads it from what she wrote and says what it read, which is the same move the menu
         * editor makes with three-options-are-buttons. See `inferFormat`.
         */
        FORMAT_READ: {
          POSITIONAL:
            'Your blanks are numbered, like {blank}. Your own systems fill them in by position.',
          NAMED:
            'Your blanks are named, like {named}. Your own systems fill them in by name.',
        },
        /** What choosing a different kind will do to this form, before it does it. */
        SWITCH: {
          TITLE: 'Charging it as {category} changes this form',
          HIDES: 'These come off the screen: {parts}.',
          SHOWS: 'These appear instead: {parts}.',
          KEPT: 'Nothing you wrote is lost — it stays here, and choosing {from} again brings it straight back.',
          CLEARS:
            'This one really is cleared, because WhatsApp allows a different range for it: {parts}.',
          CONFIRM: 'Charge it as {category}',
          CANCEL: 'Leave it as {from}',
        },
        /** The parts of a message, by the name an owner sees on this screen. */
        PART: {
          HEADER: 'the line above',
          BODY: 'the message',
          FOOTER: 'the line under',
          BUTTONS: 'the buttons',
          BUTTON: 'a button',
          SECURITY: 'WhatsApp’s safety line',
          EXPIRY: 'how long the code lasts',
          WINDOW: 'how long WhatsApp keeps trying',
        },
        ABOUT: {
          HEADING: 'What to call it',
          DESCRIPTION:
            'Your team and your own systems ask for a message by this name, so pick one you will recognise in a year.',
          NAME_LABEL: 'Name',
          NAME_HINT:
            'Small letters, numbers and underscores only — like order_ready_v1. No spaces.',
          NAME_LOCKED:
            'The name cannot change. WhatsApp treats it as the message’s identity — a new name means a new message.',
          // One press, and it only ever rearranges what she typed. Offered when the name is
          // refused and there is something honest left to offer.
          NAME_FIX: 'Use {name}',
          LANGUAGE_LABEL: 'Language',
          LANGUAGE_HINT:
            'Exactly as WhatsApp writes it: ar for Arabic, en for English, en_US for American English. The case matters.',
          LANGUAGE_LOCKED:
            'The language cannot change. Write a separate message for another language.',
          CATEGORY_LABEL: 'Charged as',
          CATEGORY_LOCKED:
            'WhatsApp does not allow this to change once a message is approved.',
          CATEGORY_CHANGING:
            'You are changing what this is charged as, which changes the price of every send. WhatsApp reads the wording again against the new kind.',
        },
        MESSAGE: {
          HEADING: 'What the customer reads',
          DESCRIPTION:
            'Write it the way you would say it, and put a blank where a name, a number or a date goes.',
          HEADER_LABEL: 'A line above it (optional)',
          HEADER_HINT:
            'Up to {limit}. One blank allowed here, and WhatsApp wants an example for it.',
          HEADER_SAMPLE: 'Example for the blank in the line above',
          BODY_LABEL: 'The message',
          BODY_HINT_NUMBERED:
            'Up to {limit}. Write a blank as {blank}, then {next}, and skip none.',
          BODY_HINT_NAMED:
            'Up to {limit}. Write a blank as {named}, in small letters and underscores.',
          FOOTER_LABEL: 'A line under it (optional)',
          FOOTER_HINT: 'Up to {limit}, usually your shop name.',
          // The second one-press fix. A gap in the numbering is one of WhatsApp's own published
          // refusal reasons and nothing about closing it needs a decision.
          RENUMBER: 'Number the blanks 1, 2, 3',
        },
        BLANKS: {
          HEADING: 'An example for each blank',
          DESCRIPTION:
            'WhatsApp will not approve wording with blanks until it can see what goes in them. These examples are only for WhatsApp and for the preview — no customer ever sees them.',
          LABEL: 'Example for blank {number}',
        },
        BUTTONS: {
          HEADING: 'Buttons (optional)',
          DESCRIPTION:
            'Up to ten in total: ten reply buttons, two links, one call button and one copy-the-code button. A reply button puts the customer straight back in your inbox.',
          TYPE_LABEL: 'What it does',
          TEXT_LABEL: 'What it says',
          // Every other limit on this screen is disclosed in a hint — the message 1,024, the
          // two lines 60, the code 20, the expiry 1 to 90. This one was not, and its refusal
          // named no number either, so the only way to find the ceiling was to hit it.
          TEXT_HINT: 'Up to {limit}. Short reads best on a phone.',
          TEXT_PLACEHOLDER: 'Track my order',
          URL_LABEL: 'Where it goes',
          URL_HINT:
            'A full web address starting with https://. You may put one blank at the very end, so the link can change per customer.',
          URL_SAMPLE_LABEL: 'Example for the blank at the end of the link',
          URL_SAMPLE_HINT:
            'Just the part that changes — we put it into the address for WhatsApp.',
          PHONE_LABEL: 'Which number it calls',
          PHONE_HINT: 'With the country code, like +96522251188.',
          CODE_LABEL: 'The code to copy',
          CODE_HINT:
            'Up to {limit}. WhatsApp writes the button’s own label, and you cannot change it.',
          COPY_CODE_LABEL: 'Copy code',
          ADD: 'Add {type}',
          REMOVE: 'Remove',
          FULL: 'That is as many buttons as WhatsApp allows.',
          GROUPING:
            'WhatsApp needs the reply buttons together, not split by a link or a call button.',
          REGROUP: 'Put the reply buttons together',
          // THE SAME STATEMENT THE MENU EDITOR MAKES about three-options-are-buttons: not a
          // choice and not a warning, just what WhatsApp will do with the number she has.
          SHAPE: 'WhatsApp shows all {count} of these under the message.',
          CROWDED:
            '{count} buttons: WhatsApp shows {shown} and hides the other {hidden} behind a “See all options” tap, and the message cannot be opened on WhatsApp for computers.',
          TYPES: {
            QUICK_REPLY: 'a reply button',
            URL: 'a link',
            PHONE_NUMBER: 'a call button',
            COPY_CODE: 'a copy-the-code button',
          },
        },
        /** WhatsApp writes this kind of message itself. The owner chooses only two things. */
        AUTH: {
          HEADING: 'WhatsApp writes this one',
          DESCRIPTION:
            'For a sign-in code, WhatsApp writes the wording itself, in every language, and adds a button that copies the code. You choose two things.',
          SECURITY_LABEL: 'Add WhatsApp’s safety line',
          SECURITY_NOTE:
            'WhatsApp adds “For your security, do not share this code.” at the end.',
          EXPIRY_LABEL: 'How long the code lasts (minutes)',
          EXPIRY_HINT:
            'Between {min} and {max}. WhatsApp tells the customer, in her own language. Leave it blank to say nothing.',
          PREVIEW_BODY: '123456 is your verification code.',
          PREVIEW_SECURITY: 'For your security, do not share this code.',
          PREVIEW_EXPIRY: 'This code expires in {count} minutes.',
          PREVIEW_NOTE:
            'WhatsApp translates this wording itself, so a customer reading Arabic sees Arabic.',
        },
        WINDOW: {
          HEADING: 'If the phone is off',
          DESCRIPTION:
            'How long WhatsApp keeps trying to deliver before it gives up. A short window suits a code that expires; a long one suits an order update.',
          LABEL: 'Keep trying for',
          DEFAULT: 'WhatsApp’s own default',
          MARKETING:
            'WhatsApp does not let this be set for marketing messages through the connection we use. It uses its own window of {period}.',
        },
        // "Start from a goal" (spec P-4.5). A first draft she checks and sends herself. Nothing
        // here may suggest that anything sends the message on its own: nothing does.
        GOAL: {
          HEADING: 'Start from a goal',
          DESCRIPTION:
            'Pick what the message is for and add one sentence. We write a first draft in English and Arabic for you to check and send to WhatsApp. Nothing sends an approved message on its own: you send it in a WhatsApp broadcast, or your developer sends it from For developers.',
          CHIPS: {
            APPOINTMENT_REMINDER: 'Appointment reminder',
            ORDER_READY: 'Order ready',
            BACK_IN_STOCK: 'Back in stock',
            OFFER: 'Offer',
            CUSTOM: 'Describe it',
          },
          SENTENCE_LABEL: 'In one sentence, what should it say?',
          SENTENCE_HINT:
            'For example: remind them of their visit, with the time and the branch.',
          SENTENCE_NEEDED: 'Say in one sentence what the message is for.',
          WRITE: 'Write it for me',
          WRITING: 'Writing…',
          WRITTEN:
            'Written for you. Check every word before you send it to WhatsApp.',
          UNUSABLE:
            'The draft came back with something we cannot use, such as a price or a link you did not give us. Nothing was charged. Try again, or write it yourself.',
          FAILED:
            'We could not write a draft just now. Nothing was charged. Try again, or write it yourself.',
          OTHER: {
            AR: 'Make the Arabic one too',
            EN: 'Make the English one too',
          },
          OTHER_NOTE: 'It uses the same answer, so it costs nothing more.',
          SUGGESTED: 'Suggested: {category}. {reason}',
        },
        PREVIEW: {
          HEADING: 'What your customer will see',
          DESCRIPTION:
            'The real WhatsApp bubble, with your examples filled in. A blank you have not given an example for shows as its number.',
          EMPTY:
            'Write the message and it appears here as your customer will read it.',
          // The one thing Chatwoot's shared renderer cannot say. It cannot be typed — every box
          // carries WhatsApp's ceiling — so it only ever fires for wording written in WhatsApp
          // Manager and read back here.
          OVER: '{part} is {length} characters. WhatsApp allows {limit}.',
          // P-4.7: the preview shows every part, and a picture it cannot draw is named, not dropped.
          MEDIA_HEADER: 'A picture, video or file goes here',
          SEE_ALL: 'See all options',
        },
        SENT: {
          TITLE: 'Sent to WhatsApp',
          BODY: 'That is WhatsApp’s answer so far. It appears on the approved-messages page, and the verdict there updates as WhatsApp reviews it.',
          BODY_UNTOLD:
            'WhatsApp took it but has not given a verdict yet. Ask it for the latest on the approved-messages page in a few minutes.',
          RECATEGORISED:
            'WhatsApp moved this from {asked} to {given}, which changes what each send costs. Check the price before you use it.',
          BACK: 'Back to approved messages',
        },
        /**
         * An edit says NOTHING about the new verdict, because WhatsApp said nothing about it —
         * its answer is a bare "done". Claiming "waiting for review" here would be the same
         * invention this whole feature exists to avoid.
         */
        SAVED: {
          TITLE: 'Change sent to WhatsApp',
          BODY: 'WhatsApp took the change and did not say what it decided. Ask it for the latest on the approved-messages page in a few minutes.',
        },
        ERRORS: {
          NAME_BLANK: 'Give it a name so you can ask for it later.',
          NAME_INVALID:
            'WhatsApp only allows small letters, numbers and underscores here.',
          NAME_LONG: 'That name is too long for WhatsApp.',
          LANGUAGE_BLANK: 'Say which language this wording is in.',
          LANGUAGE_INVALID:
            'Write it the way WhatsApp does — ar, en, or en_US. A hyphen will not do.',
          CATEGORY_INVALID: 'Choose what this message is charged as.',
          BODY_BLANK: 'Write what the customer will read.',
          BODY_LONG: 'That is too long for WhatsApp. Keep it under {limit}.',
          BODY_NAMED_BLANK:
            'You are using numbered blanks, so write {blank}, then {next}. Switch to named blanks above if you would rather use words.',
          BLANK_SYMBOL:
            'A blank here holds a number and nothing else — write {blank}, then {next}. A sign like # or $ or % cannot go inside the brackets, so keep it in the wording around the blank.',
          BODY_NOT_A_NAME:
            'You are using named blanks, so write {named} — small letters and underscores, no spaces.',
          /**
           * A WARNING, NEVER A REFUSAL — see `nameWithDigit`. WhatsApp describes the allowed
           * letters but does not list a number among the things it turns wording down for, so
           * this says what it describes and what it may cost her, and the Save button ignores it.
           */
          NAME_DIGIT:
            'WhatsApp describes a named blank as small letters and underscores, and {name} has a number in it. It may come back refused once WhatsApp reads this wording — you can save it as it is, or rename it in letters to be safe.',
          BODY_SEQUENCE:
            'Start at {blank} and skip none. WhatsApp refuses a gap.',
          BLANK_BRACES:
            'A blank is written with two brackets on each side, like {blank}. One here is missing its pair, so WhatsApp would send the brackets as part of the message.',
          BODY_DANGLING:
            'The message cannot start or end with a blank. Write a word before the first one and after the last — WhatsApp refuses wording that begins or finishes with a blank.',
          DUPLICATE:
            'This says word for word what {name} already says, in the message and in the line under it. WhatsApp refuses a repeat — change a word, or use that one instead.',
          SAMPLES_MISSING:
            'Give an example for every blank. WhatsApp will not approve the wording without them.',
          SAMPLES_LONG: 'Keep each example short — a few words is plenty.',
          HEADER_LONG: 'The line above must be under {limit}.',
          HEADER_ONE_BLANK:
            'The line above can have one blank at most. Move the rest into the message.',
          HEADER_SAMPLE_MISSING:
            'Give an example for the blank in the line above.',
          FOOTER_LONG: 'The line under must be under {limit}.',
          FOOTER_NO_BLANKS:
            'The line under cannot have a blank in it. Move it into the message.',
          TTL_NOT_FOR_CATEGORY:
            'WhatsApp does not let this be set for this kind of message.',
          TTL_RANGE: 'Choose one of the windows WhatsApp allows.',
          EXPIRY_RANGE: 'Between {min} and {max}.',
          BUTTON_AT: 'Button {number}: {reason}',
          BUTTON_TEXT_BLANK: 'say what it should read.',
          BUTTON_TEXT_LONG:
            'that wording is too long for a button: {limit} at most.',
          BUTTON_URL_BLANK: 'say where the link goes.',
          BUTTON_URL_LONG: 'that address is too long.',
          BUTTON_URL_INVALID: 'that is not a full web address.',
          BUTTON_URL_ONE_BLANK:
            'a link can have one blank at most, at the very end.',
          BUTTON_URL_AT_END:
            'the blank has to be at the very end of the address.',
          BUTTON_URL_SAMPLE:
            'give an example of what goes in the blank at the end.',
          BUTTON_PHONE_BLANK: 'say which number it calls.',
          BUTTON_PHONE_INVALID:
            "that doesn't look like a phone number with a country code.",
          BUTTON_CODE_BLANK: 'say which code the customer copies.',
          BUTTON_CODE_LONG: 'that code is longer than {limit}.',
          BUTTONS_MANY: 'WhatsApp allows ten buttons in total.',
          BUTTONS_MANY_QUICK_REPLY:
            'WhatsApp allows ten reply buttons at most.',
          BUTTONS_MANY_URL: 'WhatsApp allows two links at most.',
          BUTTONS_MANY_PHONE_NUMBER: 'WhatsApp allows one call button at most.',
          BUTTONS_MANY_COPY_CODE:
            'WhatsApp allows one copy-the-code button at most.',
          BUTTONS_GROUPING:
            'Keep the reply buttons together — WhatsApp refuses them split by a link or a call button.',
          /**
           * The same refusals, arriving from the server instead of from this form.
           *
           * They exist because the browser is a convenience and the server is the boundary —
           * a value pasted past a disabled control, or a rule only the server has, still has
           * to reach the owner as a sentence. The server sends CODES, never sentences, so an
           * Arabic account can never be shown an English one; these are the sentences.
           */
          WIRE_BLANK: 'WhatsApp needs this filled in.',
          WIRE_INVALID: 'WhatsApp will not accept this.',
          WIRE_TOO_LONG: 'That is longer than WhatsApp allows here.',
          WIRE_VARIABLE_NOT_A_NUMBER:
            'You are using numbered blanks, so write {blank}, then {next}.',
          WIRE_VARIABLE_HAS_SYMBOL:
            'A blank here holds a number and nothing else. A sign like # or $ or % cannot go inside the brackets.',
          WIRE_VARIABLE_NOT_A_NAME:
            'You are using named blanks, so write {named} — small letters and underscores.',
          WIRE_VARIABLE_SEQUENCE: 'Start at {blank} and skip none.',
          WIRE_BRACES:
            'A blank here is missing one of its brackets. Write it as {blank}.',
          WIRE_DANGLING_VARIABLE:
            'The message cannot start or end with a blank. Write a word before the first one and after the last.',
          WIRE_MISSING: 'Give an example for every blank.',
          WIRE_NO_VARIABLES: 'This line cannot have a blank in it.',
          WIRE_ONE_VARIABLE: 'This line can have one blank at most.',
          WIRE_OUT_OF_RANGE: 'That number is outside what WhatsApp allows.',
          WIRE_NOT_FOR_THIS_CATEGORY:
            'WhatsApp does not let this be set for this kind of message.',
          WIRE_TOO_MANY: 'That is more buttons than WhatsApp allows.',
          WIRE_GROUPING:
            'Keep the reply buttons together, not split by another button.',
          WIRE_UNKNOWN_TYPE: 'we cannot send that kind of button yet.',
          WIRE_TEXT_BLANK: 'say what it should read.',
          WIRE_TEXT_TOO_LONG:
            'that wording is too long for a button: {limit} at most.',
          WIRE_URL_BLANK: 'say where the link goes.',
          WIRE_URL_TOO_LONG: 'that address is too long.',
          WIRE_URL_INVALID: 'that is not a full web address.',
          WIRE_URL_ONE_VARIABLE: 'a link can have one blank at most.',
          WIRE_URL_VARIABLE_AT_END:
            'the blank has to be at the very end of the address.',
          WIRE_URL_SAMPLE_MISSING:
            'give an example of what goes in the blank at the end.',
          WIRE_PHONE_BLANK: 'say which number it calls.',
          WIRE_PHONE_INVALID:
            "that doesn't look like a phone number with a country code.",
          WIRE_PHONE_TOO_LONG: 'that number is too long.',
          WIRE_CODE_BLANK: 'say which code the customer copies.',
          WIRE_CODE_TOO_LONG: 'that code is too long.',
          WIRE_TOO_MANY_QUICK_REPLY:
            'WhatsApp allows ten reply buttons at most.',
          WIRE_TOO_MANY_URL: 'WhatsApp allows two links at most.',
          WIRE_TOO_MANY_PHONE_NUMBER:
            'WhatsApp allows one call button at most.',
          WIRE_TOO_MANY_COPY_CODE:
            'WhatsApp allows one copy-the-code button at most.',
          WIRE_NOT_ROUND_TRIPPABLE:
            'This message has a part we cannot carry across yet — a picture, a set of cards, an offer or a button we do not build. Changing it here would remove that part, so change it in WhatsApp Manager instead.',
          WIRE_REJECTED: 'WhatsApp refused this wording.',
          WIRE_NOT_FOUND: 'That message is no longer at WhatsApp.',
          WIRE_SUBMISSION_FAILED:
            "We couldn't reach WhatsApp just now. Nothing was sent — try again.",
          WIRE_NOT_A_WHATSAPP_CLOUD_INBOX:
            'This number cannot have wording approved through us. Contact us and we will look at it.',
          WIRE_UNKNOWN:
            'WhatsApp will not accept this as it is. Change it and try again.',
        },
      },
    },
    TEST: {
      TITLE: 'Test it',
      RESTART: 'Start a new chat',
      SPOTLIGHT:
        'Talk to your assistant the way one of your customers would, before a real customer does. Nothing you write here reaches anyone, your team never sees it, and you can try wording you have not saved yet. Each message costs the same credits a real reply does.',
      SETUP: {
        WORDING: 'Try wording you have not saved',
        CUSTOMER: 'Pretend to be a particular customer',
      },
      // Testing has a budget of its own, per day, and it is NOT the account's spending.
      // Shown from halfway so a long session ends with a warning rather than a wall.
      LIMITS: {
        TEST_BUDGET: "Today's testing budget: {percent} used",
      },
      /**
       * WHY THE SERVER'S OWN SENTENCE IS NOT USED FOR THESE FIVE. Everywhere else on this
       * page a refusal is rendered as `error.message`, which is already shippable copy.
       * A spending refusal is the exception, twice over: the BFF said the same thing
       * about all five ("You have run out of AI credits") whatever had really stopped the
       * message, and it said it in English to an owner reading Arabic. The server emits
       * the CONDITION, this file emits the SENTENCE.
       */
      REFUSED: {
        TEST_BUDGET_USED:
          "Today's testing budget is used up. Your customers are not affected — testing works again tomorrow.",
        SPEND_LIMIT_DAY:
          "Your assistant reached today's spending limit. Contact us to raise it.",
        SPEND_LIMIT_MONTH:
          "Your assistant reached this month's spending limit. Contact us to raise it.",
        SPEND_LIMIT_PLATFORM:
          "Waslo is limiting AI replies right now. We're on it — try again shortly.",
        OUT_OF_CREDITS:
          'You have run out of credits. It starts again the moment you top up.',
      },
      WORDING: {
        NOTE: 'This is what your assistant is using today. Change it here and this chat answers with your change — nothing is saved, and no customer sees it.',
        LABEL: 'Wording to try',
        HINT: 'Only this chat uses it. What you saved stays exactly as it is.',
        DEFAULT:
          'Your assistant is still using the wording it came with. Try your own here before you save it on the Personality page.',
        CHANGED:
          'This chat is answering with the wording below, not with what you saved.',
        RESET: 'Put back what is saved',
        UNAVAILABLE:
          "We couldn't read your saved wording just now, so this chat uses it exactly as it is.",
        EMPTY:
          'This box is empty. Write something to try, or put back what is saved.',
      },
      CUSTOMER: {
        NOTE: 'Optional. Fill in whatever you like, and your assistant answers as if the message came from that person.',
        NAME: 'Name',
        NAME_PLACEHOLDER: 'نورة العجمي',
        PHONE: 'Phone',
        PHONE_PLACEHOLDER: '+965 9988 7744',
        EMAIL: 'Email',
        // `@` starts vue-i18n's linked-message syntax, so a bare address fails to
        // compile and the whole placeholder renders as an error. `{'@'}` is the literal.
        EMAIL_PLACEHOLDER: "noura{'@'}example.com",
        ATTRS: 'Anything else you keep about them',
        ATTRS_HINT:
          'Whatever you would have on file — their area, their last order, whether they buy wholesale.',
        ATTR_KEY: 'What it is',
        ATTR_KEY_PLACEHOLDER: 'area',
        ATTR_VALUE: 'What it says',
        ATTR_VALUE_PLACEHOLDER: 'Salmiya',
        TOO_LONG: 'One of those details is too long. Shorten it and try again.',
      },
      // P-2.12: the live customer card beside the chat.
      CARD: {
        HEADING: 'The customer',
        RESET: 'Reset customer',
        NOTE: 'Nothing here reaches a real customer.',
        JUST_CHANGED: 'Just changed',
        REFUSED_EMAIL:
          'Would not be saved: the profile already has a different email',
        REFUSED_PHONE:
          'Would not be saved: the profile already has a different phone number',
        REFUSED_OTHER: 'Would not be saved',
        REMOVE: 'Remove',
        ADD_DETAIL: 'Add a detail',
        LABELS: 'Labels',
        REMOVE_LABEL: 'Remove {label}',
        LABEL_PLACEHOLDER: 'Type a label and press Enter',
        ANSWERS: 'Answers so far',
        SHOW: 'Show the customer',
        HIDE: 'Hide the customer',
      },
      // P-2.10: "Show it as".
      CHANNEL: {
        LABEL: 'Show it as',
        WEBSITE_NOTE:
          'Website chat shows choices as a numbered list. The chat box on your website cannot send a tap back, so customers type the number.',
      },
      THREAD: {
        EMPTY_TITLE: 'Try it before your customers do',
        EMPTY_BODY:
          'Send your assistant a message the way one of your customers would write it. You get its answer, and everything it used to write that answer.',
        STARTERS: 'Or start with one of these',
        // B5 hand-off: an option shown in the other language, because its own is blank.
        FALLBACK:
          'Shown in the other language: this option has no words in this one yet.',
        // The THREE NEUTRAL ONES, and they are a fallback, not the offer: the screen
        // asks for this account's own opening messages first and only tops the list up
        // from here. They belong to no trade on purpose — a shop, a clinic and a photo
        // studio all ask about hours, booking and prices, and the copy they replaced
        // (a 12ml Dehn Al Oud gift box, a delivery to Jahra) belonged to exactly one
        // business and was shown to every one of them.
        STARTER_ONE: 'What are your opening hours?',
        STARTER_TWO: 'Can I book for tomorrow?',
        // Arabic in the English table BY DESIGN — a sample CUSTOMER message, and a
        // Kuwaiti customer writes Arabic whether or not the dashboard is in English.
        // It is also the page's Arabic guarantee (`TestPage.vue`), so it must stay
        // Arabic in both tables.
        STARTER_THREE: 'شنو الأسعار عندكم؟',
        WRITING: 'Your assistant is writing…',
        NO_REPLY: 'It did not answer this one itself.',
        HANDOFF: 'It stopped here and asked for someone on your team.',
        FAILED: 'Nothing was sent to anyone, and your live setup is unchanged.',
        MENU_TAP: 'Tap an option to answer the way a customer would.',
      },
      COMPOSER: {
        LABEL: 'Message from a customer',
        PLACEHOLDER: 'Type what a customer would say…',
        SEND: 'Send',
        HINT: 'Enter sends. Shift and Enter start a new line.',
        EMPTY: 'Type a message first.',
      },
      // The named regression this screen exists to kill lives here: the old control was a
      // bright white filled square on a near-black page, and it decided whether the
      // assistant really called the owner's own systems. It is a themed switch now, and
      // the sentence beside it changes with it, because "off" and "on" are not equally
      // safe and must not read the same.
      LIVE: {
        LABEL: 'Let it really use the systems you connected',
        ON: 'It will really call them. An order it places, a booking it makes or a message it sends is real.',
        OFF: 'Rehearsal only. It is told what your systems would have said, and nothing outside this chat happens.',
      },
      TRACE: {
        // P-2.11: where a connection's answer came from when real actions are off.
        SOURCE_SAMPLE: 'Using the sample your system sent on {date}',
        SOURCE_SAMPLE_UNDATED: 'Using the sample your system sent',
        SOURCE_NO_SAMPLE:
          'No sample from "{name}" yet. Test it once under For developers, or turn on real actions.',
        FETCH_SAMPLE: 'Fetch a sample now',
        FETCHED: 'Got a sample. Send your message again to use it.',
        FETCH_FAILED:
          'Your system did not send a sample. Test it once under For developers.',
        // P-10.9: how the task started.
        STARTED_BY: {
          ASSISTANT: 'Your assistant saw that the message asks for this task.',
          WORDS: 'It started because the message has one of its words.',
          MENU: 'It started from a menu button.',
          SWITCH: 'The customer moved to it from another task.',
          REHEARSAL: 'It started because you are trying it.',
        },
        SHOW: 'How it answered',
        HIDE: 'Hide how it answered',
        NOTHING: 'from your wording alone',
        NOTHING_BODY:
          'It answered from what you wrote on the Personality page. It did not look anything up and it did not use any of the systems you connected.',
        CHIP_ACTIONS: 'used 1 connection | used {count} connections',
        CHIP_LOOKUPS: 'read 1 saved answer | read {count} saved answers',
        CHIP_TASK: 'followed a task',
        CHIP_MENU: 'showed a menu',
        ACTIONS_HEADING: 'Systems it reached for',
        ACTION_REHEARSED: 'Rehearsed',
        ACTION_LIVE: 'Really ran',
        // P-5.7: each thing it did, in her words. Only a CONNECTION leaves the chat, so only
        // a connection carries Rehearsed or Really ran.
        KIND: {
          LOOKUP: 'Looked in your answers',
          HANDOVER: 'Passed the chat to your team',
          MENU: 'Showed the menu “{label}”',
          MENU_UNNAMED: 'Showed a menu',
          CLOSE: 'Closed the chat',
          ASSIGN: 'Gave the chat to someone on your team',
          ADD_LABELS: 'Added a label to the chat',
          REMOVE_LABELS: 'Took a label off the chat',
          NOTE: 'Left a private note in the chat',
          SAVE_CONTACT: 'Saved {label} on the customer',
          SAVE_CONTACT_UNNAMED: 'Saved a detail on the customer',
          CONNECTION: 'Asked “{label}”',
          OTHER: 'Did something in this chat',
        },
        INSIDE_HEADING: 'What it did in this chat',
        TECHNICAL: 'Technical details',
        ACTION_SENT: 'What it sent',
        ACTION_GOT: 'What came back',
        TRUNCATED: '… the rest is too long to show here.',
        LOOKUPS_HEADING: 'Saved answers it read',
        LOOKUP_QUERY: 'It looked for “{query}”',
        LOOKUP_NONE: 'Nothing you have saved was close enough to use.',
        TASK_HEADING: 'Task it was following',
        TASK_STARTED: 'It started “{name}”.',
        TASK_COMPLETED: 'It finished the task it was on.',
        TASK_ACTIVE: 'It is now working through “{name}”.',
        MENU_HEADING: 'Menu it showed',
        MENU_SHOWN: 'It put “{title}” under its reply.',
        // ── the step-driven engine, and the reply gate's verdict ────────────────────
        // Project 1, 1a. Everything below answers one of two questions an owner asks of
        // a task that misbehaved — "where had it got to?" and "why did it say nothing?"
        // — and neither is answerable from the four blocks above, which between them
        // know that A task was followed and nothing else.
        //
        // NOT ONE OF THESE SENTENCES NAMES A FIELD. The wire says `ruleFailed`,
        // `onWorked`, `next: 'silent'`; an owner reading this screen at 11pm because a
        // booking went wrong needs "it asked that one again", "did not answer", "sent
        // nothing on this message". The mapping lives in the template, which is the only
        // place that has both.
        CHIP_STEP: 'worked through a task, step by step',
        CHIP_WITHHELD: 'held back what it wrote',
        STEP_HEADING: 'The task it is working through',
        STEP_TASK: 'It is working through “{name}”.',
        STEP_ON: 'It is on the step “{step}”.',
        STEP_RULE_FAILED:
          'What they sent did not fit “{step}”, so it asked that one again.',
        // THE SAME TWO LINES FOR A STEP WITH NO LABEL, and "marked" is the whole
        // difference. The trace carries the owner's own wording for a step ("Which day")
        // beside its id (`day`), but the wording is genuinely absent for a step she left
        // unlabelled and for a task she edited under a chat already in progress. Printing
        // the bare id through the sentence above would send her hunting the builder for a
        // step CALLED `day`, which is not what anything there is called.
        STEP_ON_ID: 'It is on the step marked “{step}”.',
        STEP_RULE_FAILED_ID:
          'What they sent did not fit the step marked “{step}”, so it asked that one again.',
        STEP_NEXT_ASK: 'Then it asked the next question.',
        STEP_NEXT_SAY: 'Then it replied and stayed on the same step.',
        STEP_NEXT_SILENT: 'It sent nothing on this message.',
        STEP_NEXT_DONE: 'It reached the end of the task.',
        STEP_NEXT_HANDOFF:
          'It stopped the task and asked for someone on your team.',
        STEP_NEXT_SYSTEM_REFUSED:
          'Your system said no twice, so the chat went to your team.',
        STEP_SENT_AS_WRITTEN: 'Sent as written.',
        STEP_NEXT_NONE: 'This message did not move the task along.',
        STEP_ANSWERS: 'What it has written down so far',
        STEP_ANSWERS_NONE: 'Nothing yet.',
        // Drawn only when the stored form differs from what the customer saw — a tap on
        // "Tomorrow, 4pm" is sent on to a connected system as something like
        // `2026-09-26T16:00`, and a mapping that has gone wrong is invisible otherwise.
        STEP_ANSWER_STORED: 'sent on as {value}',
        STEP_CONNECTIONS: 'Systems it checked for this task',
        // The honest bound on this block, said rather than left to be noticed: a
        // connection's answer can carry another customer's details and a sign-in your
        // system chose, so it is never sent to this screen at all.
        STEP_CONNECTIONS_NOTE:
          'Whether each one answered — not what it said back.',
        STEP_CONNECTION_OK: 'Answered',
        STEP_CONNECTION_FAILED: 'Did not answer',
        STEP_DROPPED: 'Not saved: the customer did not say it.',
        STEP_NOT_SENT_AGAIN:
          'Already done earlier in this chat, not sent again.',
        GATE_HEADING: 'Before it sent',
        GATE_WITHHELD:
          'It had written a reply, did not send it, and asked for someone on your team instead.',
        GATE_RETRIED: 'It rewrote its reply once before sending it.',
        GATE_PRICE:
          'It had put the price {value} in, and nothing you gave it says that.',
        GATE_PRICE_PLAIN:
          'It had put in a price that nothing you gave it says.',
        GATE_URL:
          'It had put the link {value} in, and nothing you gave it says that.',
        GATE_URL_PLAIN: 'It had put in a link that nothing you gave it says.',
        GATE_LENGTH: 'It had written more than this channel carries.',
        GATE_LANGUAGE:
          'It had written in the wrong language for this customer.',
        GATE_CLAIM:
          'It said something was done when it was not, so that line was removed.',
        GATE_PROMPT_ECHO:
          'It repeated its own instructions, so that line was removed.',
        GATE_TIME:
          'It named the time {value}, which nothing you gave it says, so that line was removed.',
        GATE_TIME_PLAIN:
          'It named a time that nothing you gave it says, so that line was removed.',
        GATE_LIST:
          'It listed {value}, which nothing you gave it says, so that line was removed.',
        GATE_LIST_PLAIN:
          'It listed something that nothing you gave it says, so that line was removed.',
        // A reason this screen shipped before. It renders, rather than being narrowed
        // away: "we cannot name it" is still a better answer than an unexplained silence.
        GATE_OTHER: 'Something in it did not pass a check.',
      },
    },
    // The conversation pane. THE PLAN, §4 P11 — the two-click path from "it answered that
    // wrongly" to "it will not answer that wrongly again", without leaving the inbox.
    //
    // EVERY SENTENCE HERE IS READ BY AN AGENT MID-CONVERSATION, with a customer waiting.
    // That is why none of them is a label: "Nobody is answering here" tells her not to
    // wait for the assistant; "Your assistant has not replied here" tells her there is
    // nothing to correct. A pane of nouns would make her work out both.
    PANEL: {
      TITLE: 'Waslo AI',
      STATUS: {
        QUIET: 'Your assistant has not replied here.',
        QUIET_BODY:
          'Nothing in this conversation was answered automatically. You can still teach it an answer for next time.',
        WHEN: 'Last reply {when}.',
        JUST_NOW: 'just now',
      },
      // P-1.6: who owns THIS chat, from Chatwoot's own status, and the one tap that changes it.
      CHAT: {
        ANSWERING: 'Assistant is answering',
        TAKE: "I'll take this one",
        YOU: "You're handling this",
        SOMEONE: '{name} is handling this',
        TEAM: 'Your team is handling this',
        GIVE_BACK: 'Give it back',
        BLOCKED: 'Nobody is answering here',
        CLOSED:
          'Closed. If the customer writes again, the assistant answers first.',
        PRACTICE_NOTE:
          'It is practising, so it answers only your test numbers here.',
        WORKING: 'One moment…',
        FAILED:
          "We couldn't change that just now. Nothing was changed. Try again.",
      },
      // P-1.6: the account switch, moved to the foot of the pane. Administrators only.
      FOOTER: {
        ALL_CHATS: 'For all chats',
      },
      // P-1.7: what a team member does instead of teaching. The owner decides on Answers.
      SUGGEST: {
        OPEN: 'Suggest this answer to the owner',
        TITLE: 'Suggest this answer to the owner',
        BODY: 'Your account owner sees it on Answers and decides whether the assistant learns it.',
        SEND: 'Send suggestion',
        SENDING: 'Sending…',
        SENT: 'Sent. Your account owner will see it on Answers.',
        FULL: 'Your owner has {count} suggestion waiting. | Your owner has {count} suggestions waiting.',
        TOO_MANY:
          'You have sent a lot of suggestions this hour. Try again later.',
        FAILED: "We couldn't send that just now. Try again.",
      },
      LAST: {
        HEADING: 'Its last reply',
        CUSTOMER: 'They asked',
        ASSISTANT: 'It answered',
        SILENT: 'It said nothing and waited for a person.',
      },
      TRACE: {
        SHOW: 'How it answered',
        HIDE: 'Hide how it answered',
        NOTHING: 'It answered from your wording alone.',
        LOOKUP_QUERY: 'It looked for “{query}”',
        LOOKUP_NONE: 'Nothing you have saved was close enough to use.',
        // P-1.8: words instead of the similarity percent.
        CLOSE: 'A close match',
        LOOSE: 'A loose match',
        NONE: 'Nothing close',
        ACTIONS_HEADING: 'Systems it reached for',
      },
      TEACH: {
        OPEN: 'Teach the right answer',
        TITLE: 'Teach the right answer',
        BODY: 'Write it the way you would say it to a customer. Your assistant uses it from its very next message — here and in every other conversation.',
        QUESTION_LABEL: 'When a customer asks',
        QUESTION_PLACEHOLDER: 'What did they actually ask?',
        QUESTION_HINT:
          'Their own words work best. Shorten it if they wrote a paragraph.',
        ANSWER_LABEL: 'Your assistant should say',
        ANSWER_PLACEHOLDER: 'Type the reply you would send…',
        SAVE: 'Save this answer',
        CANCEL: 'Cancel',
        SAVING: 'Saving…',
        // Two different things happened and the agent acted differently in each case:
        // she either added an answer or overwrote one she had already saved. Reporting
        // both as "Saved" tells somebody who thinks she added a second answer the
        // opposite of what she did.
        SAVED: 'Saved. Your assistant uses it from its next message.',
        REPLACED:
          'Replaced. This question had an answer saved already, and now it has this one.',
        TOTAL:
          'Nothing saved from conversations yet. | 1 answer saved from conversations. | {count} answers saved from conversations.',
        FAILED:
          "We couldn't save that just now. Nothing was changed — try again.",
        AGAIN: 'Teach another',
      },
      // The way out of the pane and into the section — the everything this panel is a
      // 320px window onto. Rendered in EVERY state, including the two where the read
      // failed, because "we could not read this" is exactly when somebody wants to go and
      // look properly.
      OPEN: 'Open Waslo AI',
      ERROR: "We couldn't read what your assistant did here.",
      // The last-resort line. See `ConversationWasloPanel.vue`: the point of it is the
      // second sentence, because the person reading it is mid-conversation and needs to
      // know whether to trust the rest of the screen.
      BROKEN:
        'This part could not be shown. Everything else in your inbox is working normally.',
    },
    // ── Settings: Channels · Permissions · Connections. THE PLAN, §4 P8. ────────────
    // The sub-group, and the only corner of this section an owner can go a year without
    // opening. Every sentence here is written for that: it says what the page is for
    // BEFORE it says what the controls do, and the Connections copy says outright that
    // most businesses never need one — because the failure mode of an advanced screen is
    // not that it is hard, it is that its existence makes someone think they are missing
    // a setup step they are not.
    SETTINGS: {
      CONNECTIONS: {
        TITLE: 'Your systems',
        // The advanced corner, said out loud, in the first sentence.
        SPOTLIGHT:
          'This is the advanced corner. A connection lets your assistant look something up in another system while it is talking — whether a bottle is in stock, where an order has reached, which delivery days are open. Most businesses never add one, and everything else your assistant does works perfectly without it.',
        HEADING: 'Systems your assistant can reach',
        DESCRIPTION:
          'Each one is somewhere it can look something up in the middle of a chat.',
        ADD: 'Add a connection',
        LIST: {
          ON: 'On',
          OFF: 'Off',
          SIGN_IN_SAVED: 'Sign-in saved',
          NAME: 'Your tasks call this {name}',
          UPDATED: 'Changed {when}',
          UPDATED_JUST_NOW: 'just now',
          OPEN: 'Open',
          PURPOSE_LOOKUP: 'Looks something up',
          PURPOSE_CHANGE: 'Changes something',
          TRY_FIRST: 'Try it first',
          NOT_TRIED: 'Not tried since your last change',
          LEGACY: 'Used in open chat for now. Add it to a task.',
          LEGACY_STOP: 'Stop using it in open chat',
          // Field report F1.6.
          TASK_ONLY: 'Only inside tasks',
          TASK_ONLY_HINT: 'The assistant never uses it in open chat.',
          SIGN_IN_NEEDED: 'Sign-in needed',
          SAVE_FAILED:
            "We couldn't change that just now. Nothing was changed — try again.",
          OFF_CONFIRM:
            '{count} task that is on uses this. Customers who reach it will be passed to your team. | {count} tasks that are on use this. Customers who reach it will be passed to your team.',
          OFF_YES: 'Switch it off',
          OFF_NO: 'Keep it on',
        },
        // P-3.5: how a connection did in live use this week, under its row. `{count}` is a
        // plural choice; the Arabic has six forms (`helpers/plural.js`).
        HEALTH: {
          LAST_USED: 'Last used {when}',
          JUST_NOW: 'just now',
          WEEK: '{worked}, {failed} this week',
          WORKED: '{count} worked | {count} worked',
          FAILED: '{count} failed | {count} failed',
          IDLE: 'Not used this week',
          REASON: {
            SIGN_IN: 'Your system refused the sign-in.',
            NOT_FOUND: 'Your system says this address does not exist.',
            SERVER: 'Your system had a problem on its side.',
            HTTP: 'Your system answered with an error ({status}).',
            TIMEOUT: 'Your system took too long to answer.',
            NETWORK: 'We could not reach your system.',
            BLOCKED: 'We cannot call this address.',
            INVALID_INPUT: 'A value sent to it was not one it could use.',
            MISSING_CONTACT_DETAIL:
              'A customer had no phone number or email for it to send.',
            NOT_GIVEN: 'The customer had not given a detail it needs.',
            SWITCHED_OFF: 'It is switched off, and a task tried to use it.',
          },
        },
        EMPTY: {
          TITLE: 'You have none, and that is the normal answer',
          BODY: 'Your assistant already answers from what you wrote on the Personality page and from the answers you saved. Add a connection only when it needs to read something out of another system — usually with a hand from whoever built that system.',
          ACTION: 'Add one anyway',
        },
        EDITOR: {
          NEW_TITLE: 'New connection',
          // The way back, when she was sent here from a step of a task that needed a
          // system it did not have. Never shown otherwise.
          RETURN: 'Back to the step',
          // Asked in the docked bar, where the button she has not pressed lives.
          LEAVE: {
            TITLE: 'You have changes that are not saved',
            BODY: 'Leaving now loses them. Nothing has been sent to your system.',
            STAY: 'Stay here',
            CONFIRM: 'Leave without saving',
          },
          SPOTLIGHT:
            'Whoever built the system you are connecting can fill this in in a few minutes. Nothing here is ever shown to a customer: your assistant reads the answer and then says it in its own words.',
          ABOUT: {
            HEADING: 'What it is',
            DESCRIPTION:
              'A short name for you, and one sentence your assistant reads to decide when to use it.',
          },
          NAME: {
            LABEL: 'Short name',
            PLACEHOLDER: 'Delivery slots',
            HINT: 'Your tasks refer to it by this, so it cannot be changed once it is saved.',
            FIXED: 'Your tasks call this {name}. That cannot be changed.',
          },
          WHAT: {
            LABEL: 'What it does',
            PLACEHOLDER:
              'Which delivery days are still open in a particular area.',
            HINT: 'Your assistant reads this and nothing else to decide when to use it. One plain sentence.',
          },
          CALL: {
            HEADING: 'What it calls',
            DESCRIPTION:
              'The address of the system, and how to call it. Whoever built it will know both.',
          },
          METHOD: { LABEL: 'How to call it' },
          PURPOSE: {
            LABEL: 'What does this do?',
            LOOKUP: 'Look something up',
            LOOKUP_HINT:
              'Checks your system and reads back an answer. Nothing changes.',
            CHANGE: 'Change something',
            CHANGE_HINT:
              'Books, orders, cancels or saves something in your system.',
          },
          ADVANCED: 'Advanced',
          IDENTITY_NOTE:
            'This looks up your customers’ data using a number the customer types. Anyone could type someone else’s number. Use “This chat’s WhatsApp number” instead.',
          URL: {
            LABEL: 'Web address',
            PLACEHOLDER: 'https://api.yourshop.com/slots?area=',
            HINT: 'It has to start with https://. Put {token} where a value goes — after the website part of the address, never inside it.',
          },
          DETAILS: {
            HEADING: 'Details it fills in',
            DESCRIPTION:
              'Every {token} you used above needs a line here, so your assistant knows what to put in its place.',
            NONE: 'None yet. Add one for each value your assistant has to fill in.',
            CONTACT_HEADING: 'We fill these in from your customer',
            CONTACT_DESCRIPTION:
              'Use any of these above and we put in what we already know about the person messaging. Your assistant never picks them, so they cannot come out wrong — and they need no line here.',
            ADD: 'Add a detail',
            FULL: 'That is as many as we can save.',
            NAME: 'Name',
            NAME_PLACEHOLDER: 'area',
            TYPE: 'Kind',
            NOTE: 'What it is',
            NOTE_PLACEHOLDER: 'The governorate the customer is in.',
            REQUIRED: 'Must have it',
            REMOVE: 'Remove this detail',
            TYPES: {
              STRING: 'Text',
              NUMBER: 'Number',
              INTEGER: 'Whole number',
              BOOLEAN: 'Yes or no',
            },
          },
          BODY: {
            HEADING: 'What it sends',
            DESCRIPTION:
              'Only for calls that send something along with the address.',
            LABEL: 'What to send',
            // No placeholder key: a JSON example is all braces, and a brace is vue-i18n's
            // interpolation syntax. `ConnectionForm.vue` builds it from `TOKEN_EXAMPLE`.
            HINT: 'Written as JSON. Use {token} anywhere a value goes.',
          },
          SIGN_IN: {
            HEADING: 'Sign-in',
            DESCRIPTION:
              'How the other system knows the call really came from you.',
            LABEL: 'Kind of sign-in',
            TYPES: {
              NONE: 'None',
              BEARER: 'A token',
              HEADER: 'A key sent in a header',
              BASIC: 'A user name and password',
            },
            HEADER_NAME: 'Header name',
            HEADER_NAME_PLACEHOLDER: 'X-Api-Key',
            SECRET: 'The value',
            SECRET_HINT_NEW:
              'We lock this away and never show it again — not to you, and not to your assistant.',
            SECRET_HINT_SAVED:
              'A sign-in is already saved. Leave this empty to keep it.',
            // THE WORDING CHANGED WITH THE WIRING. It used to say the saved sign-in "is
            // not sent there", which is not what happens: nothing is sent anywhere, the
            // save is refused. Both the BFF and the sidecar refuse an edit that moves a
            // connection to a different website while a secret is stored, because keeping
            // it would forward a live credential to a website nobody agreed to.
            SECRET_HINT_HOST:
              'That address is a different website from the saved one. A saved sign-in is never carried somewhere new, so enter it again before you save.',
          },
          KEEP: {
            HEADING: 'What to keep from the answer',
            DESCRIPTION:
              'Some systems answer with far more than your assistant needs. This keeps it to the useful part.',
            LABEL: 'Keep only these',
            PLACEHOLDER: 'slots\nfee',
            HINT: 'One name on each line. Leave it empty to keep the whole answer.',
          },
          STATE: {
            HEADING: 'Whether it is in use',
            DESCRIPTION:
              'Switch it off to stop your assistant using it, without losing anything you set up here.',
            LABEL: 'Your assistant may use this',
            ON: 'Your assistant can use this while it is talking to a customer.',
            OFF: 'Your assistant will not use this. Your tasks keep pointing at it.',
            UNTESTED:
              'Try it first. It can be switched on once a test has worked.',
            NOT_TRIED: 'Not tried since your last change.',
            CHANGED:
              'Save this change, then try it again. It can be switched on once that test works.',
            ON_CHANGE:
              'Your tasks can use this after the customer says yes. Your assistant never uses it on its own in a chat.',
          },
          TRY: {
            // P-2.11: the answer from the last test, kept for Test it's rehearsals.
            KEPT_SAMPLE: {
              SAVED:
                'Waslo keeps the answer from your last test on {date}, so Test it can use it.',
              FORGET: 'Forget the sample',
              FORGOTTEN: 'The sample is gone. Test it again to keep a new one.',
              FORGET_FAILED: 'We could not forget the sample. Try again.',
            },
            HEADING: 'Try it',
            DESCRIPTION:
              'Call it once, right now, with values you choose. Your assistant is not involved and no customer sees anything.',
            NEEDS_SAVE: 'Save it first, then you can try it here.',
            NEEDS_SIGN_IN:
              'Add the sign-in and save it, then you can try it here.',
            SAMPLE_HEADING: 'Sample customer',
            SAMPLE_HINT:
              'On a real chat we fill these in from the customer. Type test values here.',
            SAMPLE: {
              CONTACT_NAME: 'Name',
              CONTACT_EMAIL: 'Email',
              CONTACT_PHONE_NUMBER: 'Phone number',
              CONVERSATION_ID: 'Chat number',
            },
            VALUES: 'Values to try',
            ACTION: 'Try it',
            RUNNING: 'Calling…',
            OK: 'It answered.',
            OK_STATUS: 'It answered with {status}.',
            FAILED: 'It did not answer.',
            FAILED_STATUS: 'It answered with {status}, which is an error.',
            NO_STATUS: 'Nothing came back at all.',
            PREVIEW_ACTION: 'See what will be sent',
            PREVIEW_TITLE:
              'This is what would be sent. Nothing has been sent yet.',
            PREVIEW_ADDRESS: 'Address',
            PREVIEW_HEADERS: 'Also sent with it (values hidden)',
            PREVIEW_BODY: 'What it sends',
            SEND_CONFIRM: 'This really sends to your system. Use test details.',
            SEND_ACTION: 'Send it for real',
            RESULT: 'What came back',
            ERROR_TITLE: 'What it said',
            DETAIL_LABEL: 'What the system replied',
            UNAVAILABLE:
              "We couldn't run that just now. Nothing was called and nothing was changed.",
          },
          REMOVE: {
            HEADING: 'Remove this connection',
            DESCRIPTION:
              'Any task pointing at it stops being able to look anything up. Switching it off does the same thing and keeps your settings.',
            ACTION: 'Remove this connection',
            CONFIRM: 'Remove this connection?',
            CONFIRM_BODY:
              'You can put it back from Recently removed in Your systems for {period}. It comes back switched off, and you add its sign-in again. You can also switch it off instead.',
            CONFIRM_ACTION: 'Yes, remove it',
            CANCEL: 'Keep it',
            REMOVED: 'Removed.',
            FAILED:
              'We could not remove that. Nothing was changed — try again.',
          },
          SAVE: 'Save changes',
          CREATE: 'Create this connection',
          SAVED: 'Saved. Your assistant uses this from the next message.',
          CREATED: 'Created. Your assistant uses this from the next message.',
          SAVE_FAILED:
            'We could not save that. Nothing was changed — please try again.',
          NOT_FOUND:
            "We couldn't find that connection. It may have been removed.",
          // The same five states as every other editor in this section.
          STATUS: {
            IDLE: 'Everything is saved',
            IDLE_DETAIL: 'Your assistant is using this now.',
            NEW: 'Not created yet',
            NEW_DETAIL: 'Fill in the name, what it does, and the web address.',
            DIRTY: 'Not saved yet',
            DIRTY_DETAIL: 'Waiting to be saved: {sections}',
            SAVING: 'Saving…',
            SAVED: 'Saved at {time}',
            SAVED_DETAIL:
              '{sections} — your assistant uses this from the next message.',
            ERROR: 'We could not save that',
            ERROR_DETAIL:
              'Nothing was changed, and what you wrote is still on the page. Try again.',
          },
          ERRORS: {
            PURPOSE: 'Choose what this does.',
            NAME: 'Give this connection a short name.',
            NAME_SHAPE:
              'Use at least three English letters or numbers — we turn what you type into the short name your tasks use.',
            DESCRIPTION:
              'Say what this does, so your assistant knows when to use it.',
            DESCRIPTION_LONG: 'That is too long. Keep it under {limit}.',
            URL: 'Give the web address this should call.',
            URL_LONG: 'That address is too long.',
            URL_HTTPS: 'The address has to start with https://.',
            URL_HOST:
              'The website part of the address has to be fixed — a value can only go after it.',
            BODY_JSON:
              'This is not valid JSON yet, so we cannot save it. Check the brackets and the quotes.',
            PARAMS_FULL: 'That is more details than we can save.',
            PARAM_BLANK: 'Detail {number} has no name.',
            PARAM_SHAPE:
              'Detail {number} can only use English letters, numbers and underscores, and has to start with a letter.',
            PARAM_DUPLICATE: 'Two details have the same name.',
            PARAM_UNDECLARED:
              'Nothing tells us what “{name}” is. Add it as a detail below.',
            PARAM_UNKNOWN_CONTACT:
              '“{name}” is not one of the details we fill in from your customer. Those are: {known}.',
            HEADER_NAME: 'Say which header the key goes in.',
            SECRET: 'This sign-in needs a value.',
            KEEP_FULL: 'That is more lines than we can save.',
          },
        },
      },
    },
    // THE ONE SCREEN IN THIS PRODUCT THAT SPENDS THE OWNER'S MONEY, and every string here
    // is written against that. Three rules the copy follows without exception:
    //   • the number of people is always in the sentence, never implied;
    //   • nothing says "sent" until it has been;
    //   • the two sentences that gate the send are NOT here — they are the BFF's, carried
    //     verbatim from the implementation this replaces (`routes/api/broadcasts.ts`), and
    //     the screen prints what the server said rather than a translation of it. That
    //     copy is hard-won and it must not drift into a second wording.
    BROADCASTS: {
      TITLE: 'WhatsApp broadcasts',
      SPOTLIGHT:
        'A broadcast sends one approved WhatsApp message to many of your customers at once. WhatsApp charges for every message it delivers, so nothing goes out until you have read it back and seen how many people it reaches. Once it starts you cannot take it back.',
      NEW: 'New broadcast',
      LIST: {
        HEADING: 'What you have sent',
        DESCRIPTION:
          'Open any of these to watch it go out, or to see where it stopped.',
        UNAVAILABLE: "We couldn't list your broadcasts just now.",
      },
      EMPTY: {
        TITLE: 'You have not sent one yet',
        BODY: 'Pick one of your approved WhatsApp messages, choose who it goes to, and read it back before it sends. You see exactly how many people it reaches before anything is charged.',
        ACTION: 'Send your first broadcast',
      },
      NOT_SET_UP: {
        TITLE: 'Not set up yet',
        BODY: 'Sending broadcasts has to be set up for this workspace first. We do that for you.',
        ASK: 'Ask us to finish setup',
        ASKING: 'Asking…',
        ASKED: "We've got it. We'll set it up and let you know.",
        FAILED: "We couldn't send that request just now. Try again.",
      },
      STATUS: {
        DRAFT: 'Not sent',
        RUNNING: 'Sending now',
        PAUSED: 'Paused',
        COMPLETED: 'Finished',
        CANCELLED: 'Stopped',
        FAILED: 'Did not send',
        UNKNOWN: 'Not recognised',
      },
      UNKNOWN_NOTE:
        'This send is in a state this page does not know about. Nothing here will change it — contact us and we will look.',
      ROW: {
        SENT: '{sent} of {total} sent',
        // Dispatched, and WhatsApp has not reported back yet. Three words, and the SAME
        // three used for the tile and the chip on the detail page: one bucket, one name.
        // Never a failure word — these were accepted, only the receipt is missing.
        PENDING: '{count} on the way',
        SKIPPED: '{count} skipped',
        FAILED: '{count} did not arrive',
        STARTED: 'Started {when}',
        NOT_STARTED: 'Not started',
        JUST_NOW: 'just now',
        OPEN: 'Open',
      },
      COMPOSE: {
        TITLE: 'New broadcast',
        BACK: 'WhatsApp broadcasts',
        // Written as a fraction rather than a name so it is true on both lengths: a
        // message with no blanks in it has five steps, not six.
        POSITION: 'Step {number} of {total}',
        NEXT: 'Next',
        PREVIOUS: 'Back',
        LEAVE: 'Leave it',
        // WHAT STANDS WHERE THE NEXT BUTTON WOULD BE when the step is not finished. There
        // is no disabled Next: two blind critics rejected exactly that control on the
        // Personality bar, in both themes, at a measured ~2:1 — `Button`'s
        // `disabled:opacity-50` composites the whole element over the page, so a solid
        // accent fill and its white label fade toward the background together. The answer
        // there was to stop asking a primary to represent "nothing to do", and it is the
        // answer here: one sentence naming what is missing carries more than a dead button.
        NEEDS: {
          NAME: 'Give this broadcast a name to carry on.',
          CHANNEL: 'Choose the number it comes from to carry on.',
          MESSAGE: 'Choose one of your approved messages to carry on.',
          FILL: 'Fill in every blank to carry on.',
          AUDIENCE: 'Add at least one condition to carry on.',
        },
        STEPS: {
          NAME: 'Name it',
          CHANNEL: 'The number',
          MESSAGE: 'The message',
          FILL: 'Fill in the blanks',
          AUDIENCE: 'Who gets it',
          CHECK: 'Check and send',
        },
        NAME: {
          HEADING: 'What do you want to call this?',
          DESCRIPTION:
            'Only your team sees this. Your customers never do — it is how you will find this send again next month.',
          LABEL: 'Name it',
          PLACEHOLDER: 'Eid offer — Salmiya regulars',
          REQUIRED: 'Give this broadcast a name.',
        },
        CHANNEL: {
          HEADING: 'Which number does it come from?',
          DESCRIPTION:
            'Your customers see this number as the sender, and they can reply to it.',
          PLACEHOLDER: 'Choose a number',
          NO_NUMBER: 'No number yet',
          UNUSABLE: 'Not set up for sending',
          NONE: 'No WhatsApp number on this workspace can send a broadcast yet.',
          UNAVAILABLE: "We couldn't list your numbers just now.",
        },
        MESSAGE: {
          HEADING: 'Which approved message goes out?',
          DESCRIPTION:
            'WhatsApp only lets a business start a chat with wording it approved in advance. These are yours, exactly as they were approved.',
          BLANKS: 'Blanks to fill in: {count}',
          NO_BLANKS: 'Nothing to fill in.',
          MARKETING: 'Charged as marketing',
          NONE: 'This number has no approved messages yet. Add one in WhatsApp, and it appears here once it is approved.',
          UNAVAILABLE:
            "We couldn't read the approved messages for this number.",
          CHOOSE: 'Use this one',
          CHOSEN: 'Chosen',
        },
        FILL: {
          HEADING: 'What goes in the blanks?',
          DESCRIPTION:
            'Each blank is filled in per person, so every customer gets their own version of the message.',
          BLANK: 'Blank {name}',
          TITLE_BLANK: 'Blank {name} in the title',
          BUTTON_BLANK: 'Button {n}: the end of its link, or its code',
          SOURCE: 'Fill it from',
          SOURCES: {
            FIELD: 'Something you keep about them',
            ATTR: 'One of your own details',
            LITERAL: 'The same words for everyone',
          },
          FIELDS: {
            NAME: 'Their name',
            PHONENUMBER: 'Their phone number',
            EMAIL: 'Their email',
          },
          ATTR_LABEL: 'Which detail',
          ATTR_NONE:
            'This workspace keeps no extra details about contacts yet.',
          LITERAL_LABEL: 'What it should say',
          LITERAL_PLACEHOLDER: '20%',
          LITERAL_REQUIRED: 'Write what should go in this blank.',
          EMPTY_WARNING:
            'A customer with nothing in this detail is skipped rather than sent a half-written message.',
        },
        AUDIENCE: {
          HEADING: 'Who should receive this?',
          DESCRIPTION:
            'Add conditions until the list is the one you meant. A customer has to match every condition to get the message.',
          FIELD: 'Detail',
          FIELD_PLACEHOLDER: 'Choose a detail',
          CONDITION: 'Condition',
          CONDITION_PLACEHOLDER: 'Choose a condition',
          VALUE: 'Value',
          ADD: 'Add a condition',
          REMOVE: 'Remove this condition',
          YES: 'Yes',
          NO: 'No',
          DAYS_HINT: 'A whole number of days.',
          DATE_HINT: 'As year-month-day, like 2026-08-01.',
          // No "send to everyone" button anywhere. An accidentally unfiltered broadcast is
          // the most expensive mistake this screen could permit, and "everyone" is always
          // expressible as a deliberate condition.
          REQUIRED: 'Add at least one condition.',
          UNAVAILABLE: "We couldn't read what you keep about your contacts.",
        },
        CHECK: {
          HEADING: 'Read it back before it goes',
          DESCRIPTION:
            'This is the last screen before your customers get this message.',
          MESSAGE_HEADING: 'What each customer receives',
          MESSAGE_NOTE:
            'The words between « » are filled in per person from what you chose.',
          COUNT_ACTION: 'Count who gets it',
          COUNTING: 'Counting…',
          COUNT_NOTE:
            'We look through your contacts and tell you exactly how many will receive this. Nothing is sent and nothing is charged.',
          WILL_RECEIVE: '{count} customers will receive this.',
          WILL_RECEIVE_ONE: '1 customer will receive this.',
          SKIPPED: '{count} will be skipped.',
          SKIPPED_ONE: '1 will be skipped.',
          SKIP_REASON:
            '{count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason}',
          SAMPLE_NOTE:
            'The total above is exact. These reasons come from the first {count} skipped.',
          NO_REASONS: 'No reason was reported for the skipped contacts.',
          // P-3.7: why a contact is skipped, from the sidecar's code, in the owner's words.
          SKIP_CODE: {
            NO_PHONE:
              'No phone number (for example, customers who message from a WhatsApp username)',
            UNUSABLE_PHONE: 'The phone number cannot be used',
            SUPPRESSED: 'They asked not to receive messages',
            RENDER_FAILED: 'A detail this message needs is empty for them',
          },
          NOBODY_MATCHED:
            'Nobody matches these conditions, so there is nobody to send to. Widen them, then count again.',
          NOBODY_LEFT:
            'Everyone matching these conditions was skipped, so there is nobody to send to. Read the reasons above, then count again.',
          ACK: 'I understand {count} customers will not receive this message.',
          ACK_ONE: 'I understand 1 customer will not receive this message.',
          STALE: 'Something changed since you counted. Count again, then send.',
          RESOLVE_NOTE:
            'Your contacts are looked at again when you send, so the final number may differ slightly.',
          SEND: 'Send it now',
          SENDING: 'Sending…',
          CONFIRM_TITLE: 'Send this to {count} customers now?',
          CONFIRM_TITLE_ONE: 'Send this to 1 customer now?',
          CONFIRM_BODY:
            'This cannot be undone and WhatsApp charges for every message it delivers.',
          CONFIRM_SEND: 'Yes, send it',
          CONFIRM_CANCEL: 'Not yet',
          FAILED:
            "We couldn't reach the service. Nothing was sent — try again.",
        },
      },
      DETAIL: {
        BACK: 'WhatsApp broadcasts',
        NOT_FOUND: "We couldn't find that broadcast. It may have been removed.",
        UNAVAILABLE: "We couldn't open this broadcast just now.",
        SENT_MESSAGE: 'What went out',
        NUMBERS: {
          RECIPIENTS: 'Going to',
          SKIPPED: 'Skipped',
          SENT: 'Sent',
          // The bucket `SENT` used to swallow. Same words as the row fragment and as the
          // `accepted` chip below — three names for one number is how an owner ends up
          // counting the same messages twice.
          PENDING: 'On the way',
          DELIVERED: 'Arrived',
          FAILED: 'Did not arrive',
        },
        PROGRESS: {
          HEADING: 'Where each message has got to',
          DESCRIPTION:
            'This moves on its own while the send is running. Nothing here needs you.',
          UNAVAILABLE:
            "We couldn't read where each message has got to. The send itself is unaffected.",
          NOTHING: 'Nothing has left yet.',
          QUEUED: 'Waiting',
          SENDING: 'Going out',
          // Was 'Accepted by WhatsApp'. Renamed to match `NUMBERS.PENDING` and the list
          // row, which now count this exact bucket: the tile and this chip sit on one
          // screen, and two names for one thing reads as two things. The state is
          // unchanged — accepted, receipt outstanding.
          ACCEPTED: 'On the way',
          SENT: 'Sent',
          DELIVERED: 'Arrived',
          READ: 'Read',
          FAILED: 'Did not arrive',
          SKIPPED: 'Skipped',
          // Never "failed". Twilio may have accepted and charged for these, and the retry
          // deliberately excludes them — a label that called them failures would invite
          // the owner to pay twice.
          UNCONFIRMED: 'Not confirmed',
          UNCONFIRMED_NOTE:
            'These may have gone out and been charged for — we never heard back. They are left out of "send again" on purpose.',
        },
        REFRESH: 'Refresh',
        STOPPED_BECAUSE: 'It stopped because: {reason}',
        // WHAT THE SERVER SENDS IS A CODE, and these are the sentences for it. The
        // sidecar used to put a raw `err.message` in this field — an owner mid-send was
        // shown `chatwoot POST /api/v1/accounts/3/… -> 401` inside an Arabic sentence.
        // The server emits conditions; this file emits prose. See
        // `services/ai-sidecar/src/broadcast/stop-reasons.ts`.
        //
        // UNKNOWN is not a fallback for a missing translation so much as a promise: a
        // code we do not recognise — including the free text older rows still carry —
        // renders as a true sentence rather than leaking an identifier at her.
        STOPPED: {
          SEND_AUTH_FAILED:
            'We lost permission to send from your WhatsApp number. Contact us and we will reconnect it — nothing was charged for the messages that did not go out.',
          LAUNCH_FAILED:
            'Something went wrong before this send started, so nobody was messaged. You can try again, and contact us if it keeps happening.',
          // Field report G2.1: not a fault. It carries on by itself when the hours open.
          OUTSIDE_SENDING_HOURS:
            'Your sending hours are closed, so it is waiting. It carries on by itself when they open, or you can start it again now.',
          UNKNOWN:
            'Something went wrong and we stopped the send. Contact us and we will look at what happened.',
        },
        ACTIONS: {
          HEADING: 'What you can do about it',
          CANCEL: 'Stop it',
          CANCEL_BODY:
            'Everyone still waiting is skipped. Messages already sent cannot be taken back.',
          CANCEL_CONFIRM: 'Stop this broadcast?',
          RESUME: 'Start it again',
          RESUME_BODY:
            'Everyone still waiting starts receiving it again from where it stopped.',
          RESUME_CONFIRM: 'Start sending again?',
          RETRY: 'Send again to the ones it missed',
          // "up to", and the second sentence, because the count is an upper bound and
          // always was: anyone WhatsApp may already have been paid for is left out of the
          // re-send on purpose, and nothing on this page can tell which of them those
          // are. Naming that here is what stops NOTHING_TO_RETRY reading as a fault.
          RETRY_BODY:
            'Each one is a new WhatsApp message and is charged again. Anyone who may already have been sent one is left out, so it can reach fewer than that.',
          RETRY_CONFIRM: 'Send again to up to {count} customers?',
          // The sidecar's expected refusal, in her language and about money rather than
          // about recipients. Nothing here is broken and nothing was charged.
          NOTHING_TO_RETRY:
            'None of these can be sent again — they may already have gone out and been charged for. Nothing was sent.',
          CONFIRM_YES: 'Yes, do it',
          CONFIRM_NO: 'Leave it',
          NONE: 'There is nothing to do about this one.',
          FAILED:
            "We couldn't do that just now. Nothing was changed — try again.",
        },
      },
    },
    // THE DEVELOPER-FACING CORNER OF AN OWNER-FACING PRODUCT, and the copy resolves that
    // tension in one direction: the reader is the business owner. She is the one who
    // switches it on, who turns a key off when she changes suppliers, and who opens the
    // log when a customer swears nothing arrived. Whoever wires the code up reads the
    // names on the page and needs no prose.
    //
    // Four rules the strings below follow without exception:
    //   • nothing is named after its mechanism — there are numbers, keys and messages;
    //   • every refusal says what WOULD undo it, or says plainly that nothing here can
    //     (the two customer opt-outs are the whole reason that rule exists);
    //   • "not sent" and "failed" are never the same word, and neither is ever used for
    //     `unconfirmed` — WhatsApp may have delivered and charged for those;
    //   • a key is described as unrecoverable everywhere it appears, not once.
    /**
     * Forms on WhatsApp — a fill-in card that opens inside the chat instead of the assistant
     * asking one question at a time.
     *
     * THE VOCABULARY DECISION. Meta calls these "Flows" and so does its own Business Manager.
     * This screen calls them FORMS, in both languages, because that is the word an owner already
     * has for "a thing my customer fills in" — and because `Flows` in Arabic transliterates into
     * something that names a mechanism rather than a thing. The screen never says "Flow", so an
     * owner who goes looking in Meta's own tools is the only person who meets that word, and she
     * meets it there with a form already published in front of her.
     *
     * EVERY REFUSAL HERE NAMES WHAT WOULD UNDO IT, or says plainly that nothing on this page
     * can. A rating that has slipped is not something she fixes by pressing anything, and the
     * wording says so instead of implying a retry.
     */
    FORMS: {
      TITLE: 'Forms',
      SPOTLIGHT_TITLE:
        'Let a customer fill a Form in, instead of answering question by question',
      SPOTLIGHT:
        'A form opens inside WhatsApp: a few boxes, one button, done in one go. It suits the parts of a task where you always ask the same things — a date, a number of people, a phone number. Anything that depends on what the customer just said stays in the chat, and the assistant handles it there as it does today.',
      SENDER_LABEL: 'Which number',
      UNAVAILABLE: "We couldn't read this number's forms just now.",
      NO_SENDER: {
        TITLE: 'No WhatsApp number on this workspace yet',
        BODY: 'A form belongs to a WhatsApp number. Once one is connected, this page is where you publish one.',
      },
      /** The verdict. `WHY` carries one sentence per code the form builder can answer with. */
      READY: {
        TITLE: 'This number can use forms',
        // NOT "WhatsApp has approved the name customers see" — it never said that, and a number
        // whose name WhatsApp has not approved publishes forms perfectly well. Claiming an
        // approval we never checked is how the old rule came to block a working number.
        BODY: 'WhatsApp reports nothing about this number that stops it sending a form.',
      },
      NOT_READY: {
        TITLE: 'This number cannot use forms yet',
      },
      /**
       * ONE SENTENCE PER VERDICT THE SERVER CAN GIVE, and one more for the verdicts it has not
       * been taught yet. The rules themselves live in exactly one place and it is not here —
       * these are the words for their answers.
       *
       * `UNTOLD` is not a placeholder. The rules still move — a number WhatsApp has suspended
       * answers `number_not_connected`, which is deliberately not transcribed here — so a verdict
       * this build has no wording for is EXPECTED rather than a bug, and the honest thing to show
       * for one is that the number cannot use forms plus the server's own explanation, quoted.
       * Inventing a cause we happen to have wording for is how an owner is sent to fix the wrong
       * thing.
       *
       * THE RULES ALSO SHRINK, which this table learnt the hard way. Two sentences lived here for
       * rules that were refusing numbers WhatsApp accepts — one about the name not being approved,
       * one about the number not being rated yet — and both are gone with the rules. Wording is
       * the easiest thing to leave behind in a deletion like that, and a leftover sentence is
       * worse than a missing one: it reads as evidence the rule was right.
       */
      WHY: {
        NO_VERIFIED_NAME:
          'We have not been able to read the name customers see on this number, so we cannot tell you a form will reach them. Give it an hour and look again.',
        QUALITY_RISKY:
          'WhatsApp has marked this number down, usually because customers blocked or reported it. WhatsApp asks for good message quality before a form is sent, so forms come back on their own once the rating recovers — there is nothing to press here.',
        // Meta's one hard requirement for forms, read from the business's WhatsApp account (prod,
        // 2026-10-04: Meta refused a publish for it while this screen said the number was ready).
        // Used for the verdict AND for the publish refusal, so both say the same thing.
        BUSINESS_NOT_VERIFIED:
          'Forms need your business to be verified by Meta. Your tasks keep running in the chat until then. To verify: open Meta Business Settings, then Security Center, then Start verification, and follow the steps with your company documents.',
        UNKNOWN_SIGNALS:
          'We have not heard back from WhatsApp about this number yet, so we cannot tell whether it can use forms. Give it an hour and look again — nothing is wrong.',
        UNTOLD:
          'This number cannot use forms yet. The reason we were given: {reason}',
        UNTOLD_SILENT:
          'This number cannot use forms yet, and we were not told why. Nothing you can do here will change that — please tell us if you see this.',
      },
      /** The verdict itself could not be obtained. Not the same as a refusal, and not a pass. */
      VERDICT_UNAVAILABLE: {
        TITLE: 'We could not check this number just now',
        BODY: 'Publishing is off until we can. Nothing is wrong with the number as far as we know, and the forms below are unaffected.',
      },
      SIGNALS: {
        NAME: 'Name customers see',
        NAME_UNTOLD: 'WhatsApp hasn’t said',
        RATING: 'WhatsApp’s rating',
        RATING_UNTOLD: 'Not rated yet',
        STANDING: 'Standing with WhatsApp',
        STANDING_UNTOLD: 'WhatsApp hasn’t said',
        TIER: 'People you may start a chat with each day',
        TIER_UNTOLD: 'WhatsApp hasn’t said',
        CHECKED: 'Last heard from WhatsApp: {when}',
      },
      /** Meta's own list. A status is WhatsApp's word, and an absent one says so. */
      PUBLISHED: {
        HEADING: 'Forms this number already has',
        DESCRIPTION:
          'Everything WhatsApp holds for this number, with its own verdict beside it.',
        EMPTY_TITLE: 'No forms on this number yet',
        EMPTY_BODY:
          'Pick a task below and publish its form. It appears here with WhatsApp’s answer as soon as there is one.',
        INVALID: 'WhatsApp reported a problem with this one: {detail}',
        // F3.2. A label and a number: correct in every language with no plural rule.
        COUNT: 'Forms on this number: {count}',
        MORE: 'WhatsApp holds more forms than this list can show.',
        VERSION: 'Built from version {version} of the task',
        // Published before a form's name said which language it was. It still works and WhatsApp
        // will not let anyone delete a published form — so the only useful thing to say is what
        // to do, which is publish the task again and get a properly named pair.
        LEGACY:
          'Published before forms came in both languages. It still works, and WhatsApp does not allow a published form to be deleted. Publish the task again to get an English and an Arabic one the assistant can send.',
      },
      /** The two languages a form is published in, named for the sentences above. */
      LANG: {
        EN: 'English',
        AR: 'Arabic',
      },
      STATUS: {
        DRAFT: 'Not live yet',
        PUBLISHED: 'Live',
        DEPRECATED: 'Retired',
        BLOCKED: 'Blocked by WhatsApp',
        THROTTLED: 'Slowed down by WhatsApp',
        UNTOLD: 'WhatsApp hasn’t said',
      },
      /** The tasks half: which of her tasks could become a form, and what it would ask. */
      TASKS: {
        HEADING: 'Tasks that could become a form',
        DESCRIPTION:
          'A task written as steps can have its straightforward questions moved into a form. Open one to see exactly what the form would ask and what would stay in the chat.',
        EMPTY_TITLE: 'No task is written as steps yet',
        EMPTY_BODY:
          'A form is built from a task’s steps. Write a task as steps first, then come back here.',
        PREVIEW: 'See what it would ask',
        PREVIEWING: 'Reading the task…',
        HIDE: 'Close',
        PUBLISH: 'Publish this form',
        PUBLISHING: 'Sending it to WhatsApp…',
        // Beside a Publish button that is off because Meta has not verified the business. The
        // card above carries the steps; this line says why the button does nothing.
        PUBLISH_BLOCKED_BUSINESS:
          'Publishing is off until Meta verifies your business. How to verify is shown above.',
        PUBLISHED_NOTE:
          'Published as {name}. WhatsApp decides when it goes live — reload this page to see its answer.',
        // A task is published in BOTH languages, one call each, so a task whose questions are
        // written in only one of them gets a form and a sentence rather than half a silence.
        NO_FORM_IN_LANG:
          'No {language} form: nothing in this task can become a box on one yet. Write its questions in {language} and publish again.',
        // Published at WhatsApp and unreachable by a customer, which is a different thing from
        // "not live yet" and needs its own sentence.
        NOT_SENDABLE:
          'WhatsApp has {name}, but the assistant could not record it and will not send it. Publish again.',
        FIELDS_HEADING: 'What the form would ask',
        FIELD_COUNT: 'Boxes on the form: {count}',
        // The cap is WhatsApp's limit as Waslo currently understands it, and it is the server's
        // number rather than one typed here — so an owner reading "8 of 10" is reading the same
        // figure the builder enforced.
        FIELD_COUNT_CAPPED: 'Boxes on the form: {count} of {max}',
        NO_FIELDS: 'Nothing in this task can become a box on a form.',
        // WhatsApp caps a box's label well below what an owner can type, so the rest of her
        // question is shown above the box instead. She is told, rather than reading the short
        // version here and assuming it is what she wrote.
        FIELD_CUT: 'label shortened to fit',
        STAYS_HEADING: 'What would stay in the chat',
        STAYS_NOTE:
          'These steps depend on the moment, so the assistant keeps asking them the way it does today.',
        STEP_SHAPE: '{name} · {shape}',
        // P-4.8: what is live, per language, read from the form's two fingerprints (P-10.27).
        STATE: {
          LINE: '{language}: {state}',
          LIVE: 'Live',
          TRY: 'Try it',
          TRYING: 'Opening…',
          TRY_FAILED:
            'WhatsApp did not give us a preview just now. Try again in a moment.',
          WORDS_CHANGED: 'Live. Your latest wording is not on the form yet.',
          QUESTIONS_CHANGED:
            "You changed the form's questions. Customers get them in the chat until you update the form.",
          NOT_PUBLISHED: 'Not published',
          UPDATE: 'Update',
          PUBLISH: 'Publish',
          UNKNOWN:
            "We couldn't check which of this task's forms are live. Try again in a moment.",
        },
      },
      SHAPE: {
        TEXT: 'typing',
        NUMBER: 'a number',
        DATE: 'a date',
        EMAIL: 'an email address',
        PHONE: 'a phone number',
        OPTION: 'a choice from a list',
        LIVE: 'a choice from your system',
        PHOTO: 'photos',
        FILE: 'a file',
        UNTOLD: 'a box',
      },
      /** One sentence per reason the form builder can give for leaving a step in the chat. */
      STAY: {
        RUNTIME_ROWS: 'its choices are not known until the moment it is asked',
        RUNS_A_CONNECTION: 'it calls one of your own systems',
        HANDS_OFF: 'it passes the chat to one of your team',
        SAID_IN_CHAT: 'it says something rather than asking',
        ENDS_THE_TASK: 'it is where the task finishes',
        TIME_OF_DAY: 'it asks for a time of day, which a form cannot collect',
        NO_LABEL: 'one of its choices has no words written on it yet',
        // The most likely reason a form comes out with nothing in it, and it had no sentence at
        // all: it rendered as "the form builder did not say why". It must say what to DO.
        NO_QUESTION_IN_LANGUAGE:
          'its question is not written in the language this form is in — write it on that step',
        AFTER_A_BRANCH:
          'it comes after a step that sends the chat down different routes',
        AFTER_THE_FORM: 'it comes after the form is handed back',
        FORM_FULL: 'the form is already as long as WhatsApp allows',
        UNREACHABLE: 'nothing in the task leads to it',
        // P-4.10: she keeps it in the chat instead of on the form.
        IN_CHAT: 'you chose to ask it in the chat',
        // Services field fixes G2.4.
        UPLOAD_PLACE: 'a form takes photos or a file once, on its last page',
        UNTOLD: 'the form builder did not say why',
      },
      // P-4.10: the Arrange form page. "Form" and "page" are the owner's own words for both.
      ARRANGE: {
        OPEN: 'Arrange form',
        HEADER: 'Arrange the form for “{task}”',
        INTRO_NOTE:
          'Every box on the form is one of this task’s own questions, so every answer is kept where the chat would have kept it.',
        NEW_NOTE:
          'This task’s form is still one page. Save to keep the pages you see.',
        FORM_TITLE: 'Form title',
        INTRO: 'Opening words',
        PAGE: 'Page {n}',
        PAGE_OF: 'Page {n} of {total}',
        PAGE_TITLE: 'Page title',
        BUTTON: 'Button words',
        NEXT_DEFAULT: 'Next',
        ADD_PAGE: 'Add a page',
        REMOVE_PAGE: 'Remove this page',
        MOVE_UP: 'Move up',
        MOVE_DOWN: 'Move down',
        MOVE_TO: 'Move to page',
        SKIPPABLE: 'Customer can skip this',
        SKIP_NOTE:
          'If they skip it, the assistant asks it in the chat after the form.',
        TO_CHAT: 'Ask in the chat instead',
        TO_FORM: 'Put in the form',
        IN_CHAT: 'Asked in the chat',
        AUTO: 'Split into pages for me',
        SUMMARY: '{questions} on {pages}',
        QUESTIONS: '{count} question | {count} questions',
        PAGES: '{count} page | {count} pages',
        CUT_SHORT:
          'The phone shows this cut short. The full question appears above the box.',
        // Field fixes G2.3.
        MANY: 'They can tick more than one.',
        LIVE_CHOICES:
          'The choices come from your system when the customer opens this page.',
        ERR_EMPTY: 'A page needs at least one question.',
        ERR_PAGES: 'A form can have up to {count} pages.',
        ERR_QUESTIONS: 'A form can have up to {count} questions.',
        ERR_LONG: 'Button words can be up to {count} letters.',
        ERR_LONG_TITLE: 'A title can be up to {count} letters.',
        ERR_LONG_INTRO: 'Opening words can be up to {count} letters.',
        ERR_BRANCH:
          'The question that decides what comes next must be the last one.',
        ERR_LIVE:
          'This question’s choices come from your system and need an answer from an earlier page. Move it to a later page.',
        ERR_TWICE: 'This question is on the form twice.',
        // Services field fixes G2.4: Meta takes photos and files only on the last page, once.
        ERR_UPLOAD_LAST:
          'A question for photos or a file must be on the last page.',
        ERR_UPLOAD_TWICE:
          'A form can ask for photos or a file only once. Keep the other one in the chat.',
        WARN_GONE:
          '“{question}” was removed from the task, so it is no longer in the form.',
        WARN_GONE_UNNAMED:
          'A question was removed from the task, so it is no longer in the form.',
        WARN_NOT_ELIGIBLE:
          '“{question}” can no longer be on the form, so the assistant asks it in the chat.',
        WARN_PAGE_EMPTIED: 'Page {n} had no questions left, so it was removed.',
        WARN_WORDS:
          'Some words on the form are not written in {language} yet, so the form shows Waslo’s own words there.',
        WARN_QUESTION:
          '“{question}” is not written in {language}, so the assistant asks it in the chat in that language.',
        PREVIEW: 'What your customer sees',
        PREVIEW_LOADING: 'Drawing the form…',
        PREVIEW_FAILED:
          'The preview could not be drawn. Your changes are still here.',
        PREVIEW_EMPTY: 'Nothing is on the form in this language yet.',
        PREVIEW_PHOTOS: 'Add photos',
        PREVIEW_FILE: 'Add a file',
        SAVE: 'Save the form',
        SAVED:
          'Form saved. The Forms tab says whether WhatsApp has your latest words.',
        SAVE_FAILED: 'The form was not saved. Try again.',
        STATUS_DIRTY: 'Not saved yet',
        STATUS_SAVED: 'Saved',
        CHECK_LINE:
          'On WhatsApp, this task’s questions can be one form with pages.',
      },
      ERROR: {
        BUILDER_UNREACHABLE:
          'We could not reach the part of Waslo that builds the form. Nothing was sent to WhatsApp — try again in a minute.',
        BUILDER_REFUSED:
          'Building forms out of tasks is not switched on for this workspace yet. Nothing was sent to WhatsApp.',
        BUILDER_MALFORMED:
          'The form came back in a shape we do not recognise, so nothing was sent to WhatsApp. This one is on us.',
        NOT_ELIGIBLE:
          'This number cannot use forms yet, so nothing was sent to WhatsApp.',
        NO_FORM:
          'Every step of this task depends on the moment, so there is nothing a form could ask.',
        // Almost certainly ours and not hers: this task exists on the page because we listed it,
        // so if the form builder cannot find it the two disagree about how a task is named.
        NO_SUCH_TASK:
          'We could not find this task on the side that builds forms. Nothing was sent to WhatsApp, and this one is on us rather than on anything you did.',
        REJECTED: 'WhatsApp did not accept this form.',
        STAGE: {
          CREATE: 'WhatsApp refused to create it, so nothing was left behind.',
          UPLOAD:
            'WhatsApp created it but would not accept its contents. It is sitting on your WhatsApp account unfinished, under the name {name}.',
          PUBLISH:
            'WhatsApp accepted the form but would not make it live. It is on your WhatsApp account as {name}.',
        },
        META_SAID: 'WhatsApp said: {detail}',
        GENERIC: 'Something went wrong and nothing was published. Try again.',
        ENDPOINT_UNAVAILABLE:
          'This form loads choices from your system, and this number could not be made ready for that yet. Nothing was published. Try again in a minute.',
      },
    },
    OUTBOUND: {
      TITLE: 'Outbound API',
      COMMON: {
        SECTION: 'Outbound API',
        BACK: 'Outbound API',
        ON: 'On',
        OFF: 'Off',
        SAVING: 'Saving…',
        SAVED: 'Saved.',
        SAVE_FAILED:
          "We couldn't change that just now. Nothing was changed — try again.",
        UNAVAILABLE: "We couldn't load this just now.",
        // The 403 the sidecar answers when this workspace has no number wired for
        // sending. Not a failure and not something the reader can fix from this page.
        NOT_SET_UP_TITLE:
          'Sending from your own systems is not switched on here',
        NOT_SET_UP_BODY:
          "This workspace has no WhatsApp number connected for it yet — contact us and we'll connect one. Everything on these pages appears once it is running.",
        COPY: 'Copy',
        COPIED: 'Copied',
        COPY_FAILED: "We couldn't copy that. Select it and copy it by hand.",
        CLOSE: 'Close',
        CANCEL: 'Cancel',
        NONE: 'None',
        NOT_YET: 'Not yet',
        UNKNOWN: 'Not known',
        JUST_NOW: 'just now',
        SENDER: 'Number it sends from',
        SENDER_ANY: 'Any of your numbers',
        SENDER_UNKNOWN: 'A number that is no longer here',
        NO_NUMBER: 'No number yet',
        REFRESH: 'Refresh',
        PAGER: {
          SHOWING: 'Showing {from}–{to} of {total}',
          PREVIOUS: 'Previous',
          NEXT: 'Next',
          NONE: 'Nothing to show',
        },
      },
      // The nine states of one message, in owner words. `api/client.js`'s
      // `WASLO_OUTBOUND_STATUSES` is the same nine, in the same order.
      STATUS: {
        ALL: 'All',
        QUEUED: 'Waiting',
        SENDING: 'Sending',
        ACCEPTED: 'Handed to WhatsApp',
        SENT: 'Sent',
        DELIVERED: 'Delivered',
        READ: 'Read',
        FAILED: 'Failed',
        // Never "failed". WhatsApp may have delivered these and charged for them; we
        // simply never heard back. A label calling them failures invites paying twice.
        UNCONFIRMED: 'Not confirmed',
        // Never "failed" either, and for the opposite reason: nothing went wrong. We
        // decided not to send, and the message says which rule decided it.
        SUPPRESSED: 'Not sent',
        EXPLAIN: {
          QUEUED: 'We have it, and it has not gone out yet.',
          SENDING: 'It is going out right now.',
          ACCEPTED:
            'WhatsApp has it. It has not told us it went to your customer yet.',
          SENT: 'WhatsApp sent it to your customer.',
          DELIVERED: 'It reached your customer’s phone.',
          READ: 'Your customer opened it.',
          FAILED: 'It did not go out.',
          UNCONFIRMED:
            'It may have gone out and been charged for — we never heard back either way.',
          SUPPRESSED: 'We did not send it, on purpose.',
        },
      },
      // TWO VOCABULARIES THAT LOOK ALIKE AND ARE NOT. LIST.* says who put a number on the
      // Do not contact list. MESSAGE.* says which rule stopped one particular send. They
      // share no values and no meaning; `api/client.js` keeps them in two frozen lists
      // for the same reason.
      REASON: {
        LIST: {
          MANUAL: 'You added it',
          MANUAL_BODY:
            'Someone on your team put this number on the list from this page.',
          API: 'Your own system added it',
          API_BODY:
            'One of your own systems asked us to stop messaging this number.',
          KEYWORD: 'The customer stopped it',
          KEYWORD_BODY:
            'Your customer replied STOP. Only they can undo it, by sending START to your number.',
          PROVIDER: 'WhatsApp stopped it',
          PROVIDER_BODY:
            'WhatsApp itself stopped messages to this number. Only your customer can undo it, by sending START to your number.',
          UNKNOWN: 'Not known',
        },
        MESSAGE: {
          OPT_OUT: 'This number is on your Do not contact list.',
          TEST_MODE:
            'Test mode was on, and this number is not one of your test numbers.',
          RECIPIENT_CAP:
            'This number had already had as many messages as one number may get in a day.',
        },
      },
      SETUP: {
        TITLE: 'Setup',
        SPOTLIGHT:
          'This is how your own systems — your website, your booking form, your shop — send a WhatsApp message through your number. It is off for every workspace until someone turns it on here, and nothing can send until you create a key.',
        ENABLE: {
          HEADING: 'Sending from your own systems',
          LABEL: 'Let my systems send messages',
          ON_BODY:
            'Your systems can send WhatsApp messages through your number.',
          // The default state of every workspace, said plainly. THE PLAN's Setup §1.
          OFF_BODY:
            "Your own systems can't send messages yet. Turn this on when you are ready for them to.",
          SAVING: 'Saving…',
          FAILED:
            "We couldn't change that just now. Nothing was changed — try again.",
        },
        PAUSE: {
          HEADING: 'Pause everything',
          LABEL: 'Pause sending',
          ON_BODY:
            'Nothing is going out. Your systems are told to try again later, so nothing is lost.',
          OFF_BODY: 'Messages your systems ask for go out as usual.',
          // A workspace whose sends are being refused must be able to see who stopped
          // them, and when. Both are rendered whenever we have them.
          PAUSED_BY: 'Paused by {who}, {when}.',
          PAUSED_BY_UNKNOWN: 'Paused {when}. We have no record of who did it.',
          PAUSED_WHEN_UNKNOWN: 'Paused by {who}.',
          PAUSED_BARE: 'Sending is paused.',
          RESUME: 'Start sending again',
          FAILED:
            "We couldn't change that just now. Nothing was changed — try again.",
        },
        TEST: {
          HEADING: 'Test mode',
          LABEL: 'Only send to my test numbers',
          ON_BODY:
            'Only the numbers below receive anything. Everything else is written down and not sent, so real code can be tried safely.',
          OFF_BODY: 'Messages go to whichever number your system asks for.',
          // "Test numbers", never the internal word for this list. The vocabulary lint
          // in `deploy/branding/checks/waslo-lint.mjs` fails the image build on it.
          NUMBERS_HEADING: 'Test numbers',
          NUMBERS_DESCRIPTION:
            'While test mode is on, these are the only numbers that receive anything. Put your own phone here first.',
          // The FIELD and the BUTTON, and they are two strings on purpose: the box says
          // what to type, the button says what pressing it does. `ADD` used to be the
          // box's label while the button borrowed 'Add it' from the Do not contact tree —
          // two screens sharing one sentence, so tuning either one moved the other.
          NUMBER_LABEL: 'Phone number',
          ADD: 'Add a number',
          PLACEHOLDER: '+96550001234',
          HINT: 'With the country code, like +96550001234.',
          REMOVE: 'Remove this number',
          EMPTY: 'There are none, so test mode sends to nobody at all.',
          FULL: 'That is as many as we can keep.',
          INVALID: "That doesn't look like a phone number with a country code.",
          DUPLICATE: 'That number is already on the list.',
          SAVING: 'Saving…',
          FAILED: "We couldn't save that. Nothing was changed — try again.",
        },
        TEMPLATES: {
          HEADING: 'Messages you can send',
          DESCRIPTION:
            'WhatsApp only lets a business start a chat with wording it approved in advance. Your systems ask for one of these by name.',
          SENDER_LABEL: 'Which number',
          REFRESH: 'Refresh from WhatsApp',
          REFRESHING: 'Asking WhatsApp…',
          // The refresh really is background work upstream, and it can be refused. The
          // page prints what the server said about it rather than claiming the list
          // below is now current.
          REQUESTED: 'We have asked WhatsApp for a fresh list.',
          REFRESH_FAILED:
            "We couldn't ask for a refresh just now. Nothing was changed — try again.",
          NAME: 'Ask for it by this name',
          LANGUAGE: 'Language',
          LANGUAGE_HINT: 'Send the language exactly as it is written here.',
          CATEGORY: 'Charged as',
          STATUS: 'WhatsApp says',
          BLANKS: 'Blanks to fill in: {count}',
          NO_BLANKS: 'Nothing to fill in.',
          BUTTONS: 'Buttons: {list}',
          BUTTON_NEEDS_VALUE: '{text} (send a value)',
          LIST_JOIN: ', ',
          EMPTY:
            'This number has no approved messages yet. Add one in WhatsApp, and it appears here once it is approved.',
          UNAVAILABLE:
            "We couldn't read the approved messages for this number.",
          NOT_SET_UP:
            'Approved messages are not set up for this workspace yet. Contact us and we will set them up.',
        },
        QUICKSTART: {
          HEADING: 'Your first message',
          DESCRIPTION:
            'Give this to whoever looks after your systems. Run as it is, it sends one real WhatsApp message.',
          // NO EXAMPLE STRING HERE ON PURPOSE. The command is all braces and quotes, and
          // a brace is vue-i18n's interpolation syntax — the same reason the broadcast
          // composer has no JSON placeholder key. `OutboundSetupPage.vue` assembles it
          // from the address, a real message name and the placeholder below.
          ADDRESS: 'Where it is sent',
          ADDRESS_NOTE:
            "If your first call can't reach this address, contact us — we switch it on per workspace.",
          KEY_PLACEHOLDER: 'PUT-YOUR-KEY-HERE',
          KEY_NOTE:
            'Put the key you created above where it says so. We cannot fill it in for you — we keep no copy of it.',
          NEEDS_KEY: 'Create a key above, then this is ready to run.',
          NEEDS_TEMPLATE:
            'Once one of your messages is approved, its name goes here.',
          COPY: 'Copy the command',
        },
        LIMITS: {
          HEADING: 'What your workspace is allowed',
          DESCRIPTION:
            'These stop one runaway system spending your whole month in an afternoon. Nothing here is changed from this page.',
          // Shown against every field an operator pinned. A control that writes nowhere
          // is worse than a label, so those fields are labels.
          SET_BY_WASLO: 'Set by Waslo',
          SET_BY_WASLO_HINT:
            'We set this one for your workspace. Contact us to change it.',
          RATE_PER_MIN: 'Messages a minute',
          RATE_PER_DAY: 'Messages a day',
          READ_RATE_PER_DAY: 'Checks a day',
          READ_RATE_PER_DAY_HINT:
            'How often your systems may ask us what happened to a message.',
          RECIPIENT_CAP_PER_DAY: 'Messages to one person a day',
          MONTHLY_MESSAGES: 'Messages a month',
          MONTHLY_MESSAGES_NONE: 'No monthly limit of ours',
          // Zero is an operator deliberately stopping this workspace, and it must not
          // read as "unlimited".
          MONTHLY_MESSAGES_ZERO: 'None — sending is stopped for this month.',
          COUNTRIES: 'Countries you may message',
          COUNTRIES_ANY: 'Anywhere',
          COUNTRIES_NONE:
            'None, so nothing can be sent anywhere. Contact us if that is not what you expected.',
          STOP_WORDS: 'Words that stop messages',
          START_WORDS: 'Words that start them again',
          RETENTION: 'How long the message log is kept',
          DAYS: '{count} days',
          PER_MINUTE: '{count} a minute',
          PER_DAY: '{count} a day',
          PER_MONTH: '{count} a month',
        },
      },
      KEYS: {
        HEADING: 'Keys',
        DESCRIPTION:
          'A key is what lets one of your systems send through your number. Give each system its own, so you can turn one off without touching the rest.',
        CREATE: 'Create a key',
        EMPTY: {
          TITLE: 'No keys yet',
          BODY: 'Nothing can send until you create one. You see it once, at the moment you create it, and never again.',
          ACTION: 'Create your first key',
        },
        ROW: {
          NO_LABEL: 'No name',
          PREFIX: 'Starts with',
          CREATED: 'Created {when}',
          LAST_USED: 'Last used {when}',
          NEVER_USED: 'Never used',
          REVOKED: 'Turned off {when}',
          REVOKED_BADGE: 'Turned off',
          SENDER: 'Sends from {number}',
          SENDER_ANY: 'Sends from any of your numbers',
          // WHY THIS LINE EXISTS. "Last used" is stamped before a send is judged, so a
          // key that is being rejected on every single call still read "Last used just
          // now" — the broken integration looked healthier than one nobody uses. Two
          // forms rather than one, because "Refused 1 times" is how a screen tells an
          // owner nobody read it.
          //
          // "IN TOTAL" IS LOAD-BEARING. The two halves of this sentence are measured over
          // different spans: the count is the key's LIFETIME total (the sidecar increments
          // it and never resets it) while {when} is only the most recent one. Without the
          // marker, "Refused 401 times · last yesterday" reads as 401 refusals yesterday —
          // which would be a five-alarm number for a key that was noisy in January and has
          // misfired once since.
          REFUSED_ONE: 'Refused once · last {when}: {reason}',
          REFUSED_MANY:
            'Refused {count} times in total · last {when}: {reason}',
        },
        /**
         * The server sends a CODE; these are the sentences. Same rule as every other
         * refusal in this product (`vocabulary.spec.js`): the server emits conditions and
         * the UI emits words, or an Arabic owner reads English mid-failure. Each one says
         * what happened in her terms, so the next thing to do is obvious from the line.
         */
        REFUSAL: {
          SENDER_REMOVED: 'the number this key sends from was removed',
          NOT_SET_UP: 'none of your numbers is ready to send yet',
          AMBIGUOUS_SENDER: 'it did not say which of your numbers to send from',
          RATE_LIMITED: 'it was sending faster than your limit allows',
          MONTHLY_ALLOWANCE: "this month's message allowance is used up",
          DISABLED: 'sending from your own systems is turned off',
          PAUSED: 'sending from your own systems is paused',
          PLAN_REQUIRED:
            'sending from your own systems is not part of your plan',
          INVALID_REQUEST: 'the request was missing something we need',
          COUNTRY_NOT_ALLOWED:
            'it tried to message a country you may not message',
          CIRCUIT_OPEN: 'we held sending back after several failures in a row',
          BUSY: 'we were too busy to take it — it can be sent again',
          IDEMPOTENCY_CONFLICT:
            'it reused a reference we had already used for a different message',
          TEMPLATE: 'the message it asked to send is not one you may send',
          // Anything this build has never heard of. It still says how many and how long
          // ago, and it never prints the code itself — that is our vocabulary, not hers.
          UNKNOWN: 'contact us and we will tell you why',
        },
        UNAVAILABLE: "We couldn't list your keys just now.",
        NEW: {
          TITLE: 'Create a key',
          BODY: 'You see the key once, on the next screen, and never again. Have somewhere to paste it before you press Create.',
          LABEL: 'What is it for?',
          LABEL_PLACEHOLDER: 'Booking form on the website',
          LABEL_HINT:
            'Only your team sees this. It is how you will know which system to turn off later.',
          SENDER: 'Which number should it send from?',
          SENDER_PLACEHOLDER: 'Choose a number',
          SENDER_HINT: 'This key can only send from the number you pick.',
          // The BFF's own wording for `inbox_required`, kept identical here so the
          // form-level message and the server's refusal cannot drift into two sentences.
          SENDER_REQUIRED:
            'This workspace has more than one WhatsApp number — choose the one this key should send from.',
          SENDER_NONE: 'No WhatsApp number on this workspace can send yet.',
          CREATE: 'Create it',
          CREATING: 'Creating…',
          CANCEL: 'Not now',
          FAILED:
            "We couldn't create that key. Nothing was created — try again.",
        },
        SECRET: {
          TITLE: 'Your new key',
          ONCE: "This is the only time you'll see it. Copy it now and paste it into your system — we keep no copy, and nobody can show it to you again.",
          LABEL: 'The key',
          REVEAL: 'Show the key',
          HIDE: 'Hide it',
          COPY: 'Copy the key',
          COPIED: 'Copied',
          DONE: 'I have saved it',
          LOST: 'If you lose it, turn this key off and create another. Nothing else is affected.',
        },
        REVOKE: {
          ACTION: 'Turn this key off',
          CONFIRM_TITLE: 'Turn this key off?',
          CONFIRM_BODY:
            'Any system still using it stops being able to send, straight away. This cannot be undone — you would create a new key instead.',
          CONFIRM_YES: 'Yes, turn it off',
          CANCEL: 'Keep it',
          DONE: 'Turned off. Anything using it can no longer send.',
          FAILED:
            "We couldn't turn that key off. Nothing was changed — try again.",
        },
      },
      LOG: {
        TITLE: 'Message log',
        SPOTLIGHT:
          'Every message your own systems asked us to send, newest first — including the ones we deliberately did not send, and why.',
        HEADING: 'What your systems have sent',
        FILTER: {
          LABEL: 'Show',
          ALL: 'All',
        },
        ROW: {
          TO: 'To',
          TEMPLATE: 'Message',
          WHEN: 'When',
          // WHY THE ROW MOVED. The list is ordered by last change and dated by when the
          // owner's system asked for it, and for a late delivery receipt those are days
          // apart — the row jumps to the top while its date stays put, and the list reads
          // as unsorted. Shown only when the two actually say different things.
          UPDATED: 'Last change {when}',
          OPEN: 'Open',
          JUST_NOW: 'just now',
        },
        // Said out loud so an owner who cannot find last quarter's send concludes the
        // right thing. The number comes from the workspace's own settings, never a
        // constant typed twice.
        RETENTION: 'Messages are kept here for {days} days, then removed.',
        RETENTION_UNKNOWN:
          "We couldn't read how long messages are kept. Older ones are removed after a while.",
        EMPTY: {
          TITLE: 'Nothing has been sent yet',
          BODY: 'The first message one of your systems sends appears here, whether it arrived or not.',
          // A DIFFERENT STATE, and the distinction is the point: "you have sent nothing"
          // and "nothing is in this state" are opposite facts about the same screen.
          FILTERED_TITLE: 'Nothing matches this filter',
          FILTERED_BODY: 'There are messages here, just none in this state.',
          CLEAR_FILTER: 'Show all',
        },
        UNAVAILABLE: "We couldn't read the message log just now.",
        DETAIL: {
          BACK: 'Message log',
          TITLE: 'One message',
          NOT_FOUND:
            "We couldn't find that message. It may be older than we keep.",
          UNAVAILABLE: "We couldn't open this message just now.",
          WHAT_HAPPENED: 'What happened',
          WHY_NOT_SENT: 'Why it was not sent',
          WHY_FAILED: 'What went wrong',
          FIELDS: {
            TO: 'Sent to',
            TEMPLATE: 'Message used',
            LANGUAGE: 'Language',
            SENDER: 'Sent from',
            STATUS: 'Where it got to',
            PROVIDER_STATUS: 'What WhatsApp called it',
            ERROR_CODE: 'Error code',
            ERROR: 'What went wrong',
            ATTEMPTS: 'Tries',
            ATTEMPTS_VALUE: 'not tried yet | 1 try | {count} tries',
            REFERENCE: 'Your reference',
            REFERENCE_HINT:
              'The reference your own system sent with it, so it can match this message to its own record.',
            METADATA: 'What your system attached',
            NO_METADATA: 'Your system attached nothing.',
            CONVERSATION: 'The chat it went into',
            CONVERSATION_REUSED:
              'It went into a chat you already had with this customer.',
            CONVERSATION_NEW: 'It started a new chat with this customer.',
            CONVERSATION_NONE: 'It never reached a chat.',
            OPEN_CONVERSATION: 'Open the chat',
          },
          TIMES: {
            HEADING: 'When each thing happened',
            CREATED: 'Your system asked for it',
            ENQUEUED: 'We took it',
            SENT: 'It went out',
            TERMINAL: 'It finished',
            UPDATED: 'Last change',
            NOT_YET: 'Not yet',
          },
        },
      },
      NUMBERS: {
        TITLE: 'Do not contact',
        SPOTLIGHT:
          'Numbers your systems must never message. A customer who replies STOP lands here on their own, and anything on this list is refused before it costs you anything.',
        HEADING: 'Numbers we will not message',
        DESCRIPTION:
          'Every message your systems ask for is checked against this list first.',
        ADD: {
          HEADING: 'Add a number',
          LABEL: 'Phone number',
          PLACEHOLDER: '+96550001234',
          HINT: 'With the country code, like +96550001234.',
          ACTION: 'Add it',
          ADDING: 'Adding…',
          ADDED: 'Added. Nothing will be sent to this number.',
          INVALID: "That doesn't look like a phone number with a country code.",
          DUPLICATE: 'That number is already on the list.',
          FAILED:
            "We couldn't add that number. Nothing was changed — try again.",
        },
        ROW: {
          WHY: 'Why',
          ADDED_WHEN: 'Added {when}',
          REMOVED_WHEN: 'Taken off {when}',
          REMOVED_BY: 'Taken off by {who}',
          REMOVED_BADGE: 'No longer on the list',
        },
        // WHAT STANDS WHERE THE REMOVE BUTTON WOULD BE on the two reasons the API
        // refuses. A button that 403s teaches an owner the page is broken; a sentence
        // naming the one thing that WOULD undo it teaches her what to tell her customer.
        LOCKED: {
          HEADING: 'This one is not yours to lift',
          KEYWORD:
            'Only your customer can undo this — they send START to your number.',
          PROVIDER:
            'Only your customer can undo this — they send START to your number.',
        },
        REMOVE: {
          ACTION: 'Take it off the list',
          CONFIRM_TITLE: 'Message this number again?',
          CONFIRM_BODY:
            'Your systems will be able to send to it from the next message. Only do this if the customer asked you to.',
          CONFIRM_YES: 'Yes, take it off',
          CANCEL: 'Leave it',
          DONE: 'Taken off. Your systems can message this number again.',
          FAILED:
            "We couldn't take that number off. Nothing was changed — try again.",
          REFUSED:
            'This number opted out directly, so only the customer can undo it — they can send START to your number.',
        },
        SHOW_REMOVED: 'Include numbers taken off the list',
        EMPTY: {
          TITLE: 'Nobody is on the list',
          BODY: 'A customer who replies STOP is added here on their own. You can also add a number yourself.',
          FILTERED_TITLE: 'Nothing to show',
          FILTERED_BODY: 'No number has been taken off this list yet.',
        },
        UNAVAILABLE: "We couldn't read the list just now.",
      },
    },
    // ── SET UP WITH AI (spec §8 P-8.1 to P-8.3) ─────────────────────────────────────
    SETUP_AI: {
      UNDO: 'Undo',
      SOURCE: {
        PROFILE: 'From your WhatsApp profile',
        WEBSITE: 'From your website',
        ACCOUNT_NAME: 'From your account name',
        WASLO: 'Waslo suggestion',
      },
      VOICE: {
        RECORD: 'Record a voice note',
        STOP: 'Stop {time}',
        TURN: 'Turn into text',
        TURNING: 'Listening…',
        DISCARD: 'Discard',
        DENIED:
          'The browser was not allowed to use the microphone. You can type instead.',
        NOTHING_HEARD:
          'We could not hear any words in that recording. Nothing was charged.',
        FAILED: 'We could not turn that into text. Try again.',
      },
      REVIEW: {
        KEEP: 'Keep',
        EDIT: 'Edit',
        REMOVE: 'Remove',
        ANSWERS: 'Answers for your customers',
        QUESTION: 'Question',
        ANSWER: 'Answer',
        QUESTIONS: 'Only you can answer these',
        QUESTIONS_HINT:
          'Waslo did not find these anywhere, so it did not guess. Answer the ones you know, and leave the rest empty.',
        SKIP_PLACEHOLDER: 'Leave empty to skip',
      },
      FIRST_DRAFT: {
        TITLE: 'Write it for me',
        OFFER:
          'Your assistant still uses Waslo’s starting words. Waslo can write a first draft from what your WhatsApp profile already says.',
        OPEN: 'Write it for me',
        ALREADY_WRITTEN:
          'Your Personality is already written in your own words, so there is nothing to draft. You can change it on Personality.',
        FOUND_INTRO:
          'This is what Waslo found about your business. The draft uses only this, and anything it cannot find becomes a question for you.',
        ACCOUNT_NAME: 'Your account name: {name}',
        NOTHING_FOUND:
          'Waslo found nothing about your business yet. Add your website below, or write your Personality yourself.',
        WEBSITE: 'Your website',
        WEBSITE_HINT:
          'Waslo can read it first, as a source of answers you can see and switch off.',
        READ_WEBSITE: 'Read the website first',
        SKIP_WEBSITE: 'Skip the website',
        READING: 'Reading your website. The draft starts when it is done.',
        WEBSITE_READ: 'Your website was read. You can draft now.',
        WEBSITE_FAILED:
          'We could not read that website. You can skip it and draft from the rest.',
        DRAFT: 'Draft',
        DRAFTING: 'Writing your draft…',
        UNUSABLE:
          'The draft came back with nothing we could use. Nothing was charged. Try again.',
        FAILED: 'Something went wrong. Nothing was changed. Try again.',
        REVIEW_INTRO:
          'Keep what is right, edit what is close, and remove the rest. Nothing changes until you press Save these.',
        TEACH_NOTE:
          'Saving answers teaches them to your assistant, and costs credits once.',
        SAVE: 'Save these',
        START_AGAIN: 'Start again',
        SAVED:
          'Saved. Your assistant uses these from the next message. You can undo all of it at once.',
        UNDONE: 'Undone. Everything is as it was before you saved.',
        OPEN_PERSONALITY: 'Open Personality',
      },
      WHAT_CHANGED: {
        HEADING: 'Something changed? Tell Waslo.',
        DESCRIPTION:
          'A new price, a closed branch, holiday hours. Say it once, and Waslo shows every place it changes before anything is changed.',
        PLACEHOLDER:
          'From next week the small car wash is 4 KD, and the Salmiya branch is closed until 1 November.',
        PROPOSE: 'Propose changes',
        PROPOSING: 'Reading what changed…',
        UNUSABLE:
          'We could not turn that into changes. Nothing was charged. Try saying it another way.',
        FAILED: 'Something went wrong. Nothing was changed. Try again.',
        NOTHING:
          'Waslo found nothing in your setup that this changes. Nothing was charged.',
      },
      CHANGES: {
        INTRO: 'Waslo suggests these changes for:',
        APPLY_ALL: 'Apply all',
        APPLY_SELECTED: 'Apply the ones I ticked',
        NOT_NOW: 'Not now',
        DONE: 'Applied. You can undo all of it at once.',
        UNDONE: 'Undone. Everything is as it was before.',
        FAILED: 'Nothing was changed. Try again.',
        OPEN_PAGE: 'Open where it lives',
        UNTIL: 'Until {day}',
        ACTIONS: {
          ANSWER_ADD: 'Add an answer',
          ANSWER_EDIT: 'Change an answer',
          ANSWER_OFF: 'Stop using an answer',
          LINE_EDIT: 'Change a line of your Personality',
          OPTION_LABEL: 'Change the words of a task option',
          OPTION_REMOVE: 'Remove a task option',
          TASK_OFF: 'Switch a task off',
          ROW_LABEL: 'Change the words of a menu option',
          ROW_REMOVE: 'Remove a menu option',
          HOURS_SET: 'Change your opening hours',
          NOTICE_ADD: 'Add a note for now',
        },
        PAGES: {
          ANSWERS: 'Answers',
          PERSONALITY: 'Personality',
          TASKS: 'Tasks',
          MENUS: 'Menus',
          HOURS: 'Opening hours',
          NOTICES: 'Notes for now',
        },
      },
      DAYS: {
        SUN: 'Sunday',
        MON: 'Monday',
        TUE: 'Tuesday',
        WED: 'Wednesday',
        THU: 'Thursday',
        FRI: 'Friday',
        SAT: 'Saturday',
        CLOSED: 'closed',
      },
      DESCRIBE: {
        TITLE: 'Start from a description',
        HINT: 'Say what customers do in this task, in your own words. Waslo drafts the steps, and you check them before anything is saved.',
        PLACEHOLDER:
          'Customers book a car wash: they pick the branch, the car size, a day and a time from our booking system, and we confirm.',
        DRAFT: 'Draft task',
        DRAFTING: 'Drafting the task…',
        UNUSABLE:
          'The draft came back with nothing we could use. Nothing was charged. Try describing it another way.',
        FAILED: 'Something went wrong. Nothing was charged. Try again.',
        NEEDS_SYSTEM:
          'Needs a connected system: {what}. Until you connect it, the chat goes to your team at this step.',
        LEFT_OUT:
          'Waslo stopped the draft at this step. Add the steps after it once the system is connected.',
        PROBLEMS_HEADING:
          'Waslo could not finish these. Fix them before you turn the task on:',
        MENU_ADD: 'When I turn this task on, also add “{option}” to my menu',
        MENU_WHICH: 'Which menu',
        MENU_FAILED:
          'The task is on, but we could not add it to the menu. Add it on Menus.',
        MESSAGE: 'Also write an approved message for this task',
        PROBLEMS: {
          NO_STEPS: 'The draft has no steps yet. Add the first question.',
          STEPS_MANY:
            'The draft had more steps than a task can hold, so the last ones were left out.',
          OPTIONS_MANY:
            'A question had more than ten options. Keep the ten that matter.',
          NO_OPTIONS:
            'A choice came without options, so it was made a question. Add the options if you need them.',
          OPTION_LABEL_LONG:
            'An option’s words were too long for a WhatsApp button, so they were left empty. Write them shorter.',
          QUESTION_LONG:
            'A question was too long, so it was left empty. Write it shorter.',
          QUESTION_MISSING:
            'A question is missing in one of your customers’ languages. Write it in both.',
          MESSAGE_LONG:
            'A message was too long, so it was left empty. Write it shorter.',
          SHAPE: 'Something in the draft could not be read. Check each step.',
        },
      },
    },
    CLAUDE: {
      CHIP: 'Suggested by Claude',
      CONNECT: {
        TITLE: 'Connect Claude',
        ASK: 'Allow {app} to read your setup and draft changes for {account}?',
        RETURNS_TO: 'After you answer, you go back to {host}.',
        ACCOUNT: 'Workspace',
        CAN: 'It can',
        CAN_READ:
          'Read your Personality, answers, opening hours, credit balance and how your assistant is doing.',
        CAN_CHATS:
          'Read your recent customer chats and why they went to a person. They go to your own Claude.',
        CAN_DRAFT:
          'Draft answers, tasks, menus, connections, notes for now, approved messages and test numbers. Each one waits for you, switched off.',
        CANNOT: 'It cannot',
        CANNOT_SWITCH: 'Switch anything on or start answering customers.',
        CANNOT_SEND:
          'Send messages to customers or send anything to WhatsApp for approval.',
        CANNOT_SECRETS: 'See or add passwords and keys.',
        CANNOT_REMOVE: 'Remove anything for good.',
        CREDITS:
          'Claude works with your own Claude account and never spends your Waslo credits. Keeping what it suggests can cost credits, and Waslo shows you the price first.',
        ALLOW: 'Allow',
        CANCEL: 'Cancel',
        EXPIRED: 'This request has expired. Start again from Claude.',
        FAILED: 'We could not connect Claude. Try again.',
        LEAVING: 'Taking you back to Claude.',
      },
      APPS: {
        HEADING: 'Connected AI apps',
        DESCRIPTION:
          'AI apps you allowed to read your setup and draft changes for you.',
        NONE: 'No AI app is connected yet.',
        UNAVAILABLE: 'We could not check your connected apps just now.',
        CONNECTED_BY: 'Connected by {who}, {when}',
        LAST_USED: 'Last used {when}',
        NEVER_USED: 'Not used yet',
        JUST_NOW: 'just now',
        DISCONNECT: 'Disconnect',
        DISCONNECTED:
          'Disconnected. It can no longer read or draft anything here.',
        FAILED: 'We could not do that. Try again.',
        HOW_HEADING: 'Use Waslo from your Claude',
        HOW_ADD:
          'In Claude, open Settings, then Connectors, and add a custom connector with this address:',
        HOW_ALLOW:
          'Claude brings you back here to allow it. You never copy a key.',
        COPY: 'Copy',
        COPIED: 'Copied',
      },
      BATCHES: {
        LINE: 'Claude suggested {count} change | Claude suggested {count} changes',
        REVIEW: 'Review',
        HIDE: 'Hide',
        UNDO_ALL: 'Undo all',
        UNDONE:
          'Undone. Everything from that Claude session is back as it was.',
        FAILED: 'We could not undo that. Try again.',
        DRAFTED:
          'Some of it was saved switched off, marked Suggested by Claude:',
        OPEN_TASKS: 'Tasks and menus',
        OPEN_CONNECTIONS: 'Your systems',
      },
      REVIEW: {
        CHANGES_INTRO: 'Claude suggests these changes.',
        PROMPT: 'A new Personality',
        PROMPT_USE: 'Use this Personality',
        STALE:
          'Your Personality changed after Claude read it. Ask Claude again.',
        MESSAGE: 'An approved message',
        MESSAGE_OPEN: 'Open in the editor',
        MESSAGE_SENDER: 'For the number',
        MESSAGE_NO_NUMBER: 'Connect a WhatsApp number first.',
        NUMBERS: 'Test numbers for your website',
        NUMBERS_USE: 'Use these numbers',
        WHY: 'Why: {why}',
        NOT_NOW: 'Not now',
        DONE: 'Done.',
        FAILED: 'Nothing was changed. Try again.',
      },
      ANSWERS: {
        HEADING: 'Suggested by Claude',
        DESCRIPTION:
          'Answers Claude drafted. Your assistant does not use them until you keep them.',
        KEEP: 'Keep',
        KEEP_ALL: 'Keep all',
        REMOVE: 'Remove',
        KEPT: 'Kept. Your assistant uses them from now on.',
        CONFLICTS:
          'Some of them disagree with answers you already have. Check them in All answers.',
        FAILED: 'Nothing was kept. Try again.',
      },
    },
    NOT_READY: {
      TITLE: 'This part is still being built',
      BODY: 'It arrives in the next update. Everything else here keeps working.',
    },
  },
};

const ar = {
  WASLO: {
    CHANNEL_NAMES: {
      WHATSAPP: 'واتساب',
      INSTAGRAM: 'إنستغرام',
      MESSENGER: 'ماسنجر',
      TELEGRAM: 'تيليجرام',
      WEBSITE: 'دردشة الموقع',
      EMAIL: 'البريد الإلكتروني',
      SMS: 'الرسائل النصية',
    },
    AI_CONFIRM: {
      CREDITS:
        'لا شيء | وحدة واحدة | وحدتان | {count} وحدات | {count} وحدة | {count} وحدة',
      TITLE: 'هذا يستخدم الذكاء الاصطناعي',
      BODY: 'هذا يستخدم الذكاء الاصطناعي ويخصم {credits} من رصيدك. متبقٍ لك {balance}.',
      TICK: 'أفهم أن هذا يُخصم من رصيدي',
      CONFIRM: 'تأكيد',
      CANCEL: 'إلغاء',
      PRICED: '{action} · {credits}',
      PRICE_CHANGED: 'تغيّر السعر إلى {credits}. أكّد مرة أخرى.',
      PRICE_KEEPS_CHANGING:
        'تغيّر السعر أكثر من مرة، فلم يُنفَّذ شيء ولم يُخصم شيء. حاول مرة أخرى.',
      RECEIPT: 'خُصم من رصيدك {credits}.',
      NOTHING_CHARGED: 'لم يُخصم شيء.',
      SESSION: {
        TEST_MESSAGE_BODY: 'كل رسالة تجربة تخصم {credits}. متبقٍ لك {balance}.',
        TEST_MESSAGE_TICK: 'أفهم أن كل رسالة تجربة تخصم {credits}',
        SEARCH_BY_MEANING_BODY:
          'كل بحث بالمعنى يخصم {credits}. متبقٍ لك {balance}.',
        SEARCH_BY_MEANING_TICK: 'أفهم أن كل بحث بالمعنى يخصم {credits}',
      },
    },
    BUY_CREDITS: {
      OPEN: 'شراء رصيد',
      TITLE: 'شراء رصيد',
      INTRO: 'اختر باقة. تدفع في صفحة دفع آمنة ثم تعود إلى هنا مباشرة.',
      PRICE: '{amount} د.ك',
      PAY_KNET: 'ادفع عبر كي نت',
      PAY_CARD: 'ادفع بالبطاقة',
      TEST_MODE: 'دفعات تجريبية: لا تنتقل أي أموال حقيقية في هذه المساحة.',
      UNAVAILABLE:
        'الدفع الإلكتروني غير متاح لهذه المساحة بعد. اطلب منا وسنضيف الرصيد لك.',
      LOAD_FAILED: 'تعذّر تحميل الباقات الآن. حاول مرة أخرى.',
      START_FAILED:
        'تعذّر فتح صفحة الدفع الآن. لم يُخصم أي مبلغ. حاول مرة أخرى.',
      RETURN: {
        TITLE: 'عملية الدفع',
        WAITING: 'بانتظار تأكيد البنك لعملية الدفع…',
        STALLED:
          'يستغرق هذا وقتًا أطول من المعتاد. سيُضاف رصيدك تلقائيًا فور تأكيد البنك.',
        CHECK_AGAIN: 'تحقّق مرة أخرى',
        PAID: 'أُضيف {credits} إلى رصيدك.',
        NOT_PAID: 'لم تكتمل عملية الدفع.',
        CHECK: 'وصلتنا هذه الدفعة ونحتاج إلى مراجعتها يدويًا. سنتواصل معك.',
        MISSING: 'لم نعثر على هذه الدفعة.',
        LOAD_FAILED: 'تعذّر التحقق من الدفعة الآن.',
        BACK: 'العودة إلى النظرة العامة',
        TRY_AGAIN: 'حاول مرة أخرى',
      },
    },
    LANGUAGES: {
      OPTIONS_MISSING_AR:
        'لا خيارات بدون كلمات عربية. | خيار واحد بدون كلمات عربية بعد. سيرى عملاؤك العرب الكلمات الإنجليزية. | خياران بدون كلمات عربية بعد. سيرى عملاؤك العرب الكلمات الإنجليزية. | {count} خيارات بدون كلمات عربية بعد. سيرى عملاؤك العرب الكلمات الإنجليزية. | {count} خيارًا بدون كلمات عربية بعد. سيرى عملاؤك العرب الكلمات الإنجليزية. | {count} خيار بدون كلمات عربية بعد. سيرى عملاؤك العرب الكلمات الإنجليزية.',
      OPTIONS_MISSING_EN:
        'لا خيارات بدون كلمات إنجليزية. | خيار واحد بدون كلمات إنجليزية بعد. سيرى عملاؤك الإنجليز الكلمات العربية. | خياران بدون كلمات إنجليزية بعد. سيرى عملاؤك الإنجليز الكلمات العربية. | {count} خيارات بدون كلمات إنجليزية بعد. سيرى عملاؤك الإنجليز الكلمات العربية. | {count} خيارًا بدون كلمات إنجليزية بعد. سيرى عملاؤك الإنجليز الكلمات العربية. | {count} خيار بدون كلمات إنجليزية بعد. سيرى عملاؤك الإنجليز الكلمات العربية.',
      OTHERS_MISSING_AR:
        'لا مواضع أخرى. | موضع آخر بدون كلمات عربية بعد. | موضعان آخران بدون كلمات عربية بعد. | {count} مواضع أخرى بدون كلمات عربية بعد. | {count} موضعًا آخر بدون كلمات عربية بعد. | {count} موضع آخر بدون كلمات عربية بعد.',
      OTHERS_MISSING_EN:
        'لا مواضع أخرى. | موضع آخر بدون كلمات إنجليزية بعد. | موضعان آخران بدون كلمات إنجليزية بعد. | {count} مواضع أخرى بدون كلمات إنجليزية بعد. | {count} موضعًا آخر بدون كلمات إنجليزية بعد. | {count} موضع آخر بدون كلمات إنجليزية بعد.',
      TRANSLATE: 'ترجمها لي',
      USE_ALL: 'استخدمها كلها',
      TOO_LONG:
        'أطول مما يتسع له المكان ({length} من {limit} حرفًا). اكتبها بنفسك.',
      CUT_OFF: 'لم تصل هذه. اكتبها بنفسك أو حاول مرة أخرى.',
      FAILED: 'لم ينجح هذا. لم يتغيّر أي شيء. حاول مرة أخرى.',
      WHAT: {
        QUESTION: 'سؤال: {name}',
        OPTION: 'خيار: {name}',
        MESSAGE: 'رسالة: {name}',
        FORM_BUTTON: 'زر إرسال النموذج',
        MENU_BODY: 'رسالة القائمة',
        MENU_BUTTON: 'زر فتح القائمة',
        HANDOVER: 'الرسالة عندما يتولى شخص المحادثة',
        HANDOVER_CLOSED: 'الرسالة عندما تكون مغلقًا',
      },
    },
    UNDO: {
      WHO: {
        YOU: 'أنت',
        TEAMMATE: 'أحد أعضاء فريقك',
        // In Arabic letters: `vocabulary.spec.js` fails an Arabic value that is still the English one.
        CLAUDE: 'كلود',
        WASLO: 'واصلو',
      },
      HISTORY: {
        HEADING: 'أرجِعه كما كان',
        DESCRIPTION:
          'نحتفظ بكل حفظ لمدة {period}. الإرجاع يعيده كما كان قبل ذلك الحفظ مباشرةً، ويمكنك الإرجاع مرة أخرى بعدها.',
        ROW: 'قبل تعديل {when} · {who}',
        RESTORE: 'أرجِعه',
        RESTORED: 'أُرجع. يستخدمه مساعدك من الرسالة التالية.',
        RESTORED_OFF: 'أُرجع وهو موقوف. راجعه ثم فعّله.',
        CONFLICT:
          'تغيّر هذا منذ ذلك الحين، فتعذّر إرجاعه بهذه الطريقة. أعد تحميل الصفحة وحاول مرة أخرى.',
        FAILED: 'تعذّر الإرجاع. لم يتغيّر شيء. حاول مرة أخرى.',
      },
      REMOVED: {
        HEADING: 'حُذف مؤخرًا',
        DESCRIPTION:
          'ما حذفته خلال آخر {period}. إرجاعه مجاني، ويعمل من جديد فورًا.',
        ROW: 'حُذف {when} · {who}',
        JUST_NOW: 'الآن',
        PUT_BACK: 'أرجِعه',
        PUT_BACK_DONE: 'أُرجع. يستخدمه مساعدك من الرسالة التالية.',
        PUT_BACK_OFF: 'أُرجع وهو موقوف. راجعه ثم فعّله.',
        PUT_BACK_RENAMED:
          'أُرجع وهو موقوف، وأُضيفت «(restored)» إلى اسمه لأن اسمه صار مستخدمًا.',
        CONFLICT: 'أُرجع هذا من قبل، أو حُذف ما ينتمي إليه. أعد تحميل الصفحة.',
        FAILED: 'تعذّر الإرجاع. لم يتغيّر شيء. حاول مرة أخرى.',
        KIND: {
          SOURCE: 'مصدر',
          ANSWER: 'إجابة',
          TASK: 'مهمة',
          MENU: 'قائمة',
          CONNECTION: 'اتصال',
          NOTICE: 'ملاحظة',
        },
      },
    },
    // ── Ease C3 (spec §6). ───────────────────────────────────────────────────────────
    GAPS: {
      WAYS: 'بلا صيغة | بصيغة واحدة | بصيغتين | بـ{count} صيغ مختلفة | بـ{count} صيغة مختلفة | بـ{count} صيغة مختلفة',
    },
    FIX: {
      TITLE_FIX: 'صحّح هذه الإجابة',
      TITLE_EDIT: 'عدّل هذه الإجابة',
      BODY_FIX:
        'اكتب الإجابة التي يجب أن يقولها مساعدك. سيستخدمها من رسالته التالية.',
      BODY_EDIT:
        'غيّر الكلمات التي يستخدمها مساعدك. سيستخدم الكلمات الجديدة من رسالته التالية.',
      IT_SAID: 'قال',
      NOTHING_SAID: 'لا شيء. لم يكن يعرف.',
      SAVED: 'تم الحفظ. سيستخدمها مساعدك من رسالته التالية.',
      ALSO_SAYS: 'يقول «{source}» أيضًا: {answer}',
      REPLACES: 'هذه تحل محل الإجابة من «{source}».',
      USE_MINE: 'استخدم إجابتي بدلًا منها',
      KEEP_BOTH: 'أبقِ الاثنتين',
      USED_MINE: 'تم. إجابتك وحدها تُستخدم الآن.',
      CHECK: 'جرّبها الآن',
      BEFORE: 'قبل',
      NOW: 'الآن',
      NOW_HANDOFF: 'سيحوّل هذه المحادثة إلى فريقك.',
      CHECK_FAILED: 'تعذّرت التجربة الآن. إجابتك محفوظة.',
      DONE: 'تم',
      VERB: 'صحّحه',
      WRONG: 'خطأ؟ صحّحه',
      FAILED: 'تعذّر الحفظ الآن. لم يتغيّر شيء. حاول مرة أخرى.',
    },
    ALL_ANSWERS: {
      HEADING: 'كل الإجابات',
      DESCRIPTION:
        'كل ما يعرفه مساعدك، من كل مصدر ومن كل إجابة كتبتها. صحّح أي إجابة وستُستخدم من الرسالة التالية.',
      SEARCH_LABEL: 'ابحث في الإجابات',
      SEARCH_PLACEHOLDER: 'اكتب كلمة من السؤال أو الإجابة',
      SEARCH_WORDS: 'بحث',
      SEARCH_MEANING: 'ابحث بالمعنى',
      CLEAR: 'مسح',
      FILTER: {
        ALL: 'الكل',
        YOURS: 'كتبتها أنت',
        SOURCES: 'من مصادرك',
        OFF: 'موقوفة',
      },
      YOURS: 'إجابتك',
      OFF: 'موقوفة',
      REPLACED: 'حلّت محلها إجابتك',
      FROM: 'من «{source}»',
      EDIT: 'تعديل',
      SWITCH_OFF: 'أوقفها',
      SWITCH_ON: 'شغّلها',
      REMOVE: 'حذف',
      REMOVED: 'حُذفت. يمكنك إرجاعها من «المحذوفات مؤخرًا» خلال {period}.',
      HISTORY: 'النسخ السابقة',
      NONE: 'لا توجد إجابات مطابقة.',
      EMPTY: 'لا توجد لدى مساعدك إجابات بعد. أضف مصدرًا أو اكتب إجابة.',
      UNAVAILABLE: 'تعذّرت قراءة الإجابات الآن.',
      SHOWING:
        'عرض {from} إلى {to} من {total}. | عرض {from} إلى {to} من {total}. | عرض {from} إلى {to} من {total}. | عرض {from} إلى {to} من {total}. | عرض {from} إلى {to} من {total}. | عرض {from} إلى {to} من {total}.',
      PREVIOUS: 'السابق',
      NEXT: 'التالي',
      FAILED: 'تعذّر التغيير الآن. لم يتغيّر شيء. حاول مرة أخرى.',
    },
    NOTICES: {
      HEADING: 'مؤقتًا',
      DESCRIPTION:
        'شيء صحيح لفترة، مثل عطلة أو فرع مغلق. يستخدمه مساعدك حتى اليوم الذي تختاره، ثم يُحذف من تلقاء نفسه.',
      TEXT_LABEL: 'ما الصحيح الآن',
      TEXT_PLACEHOLDER: 'نحن مغلقون للعيد من الثلاثاء إلى الخميس.',
      ENDS_LABEL: 'آخر يوم',
      ADD: 'إضافة',
      ENDS: 'يُحذف من تلقاء نفسه بعد {date}',
      EXTEND: 'تمديد',
      SAVE: 'حفظ',
      CANCEL: 'إلغاء',
      REMOVE: 'حذف',
      FULL: 'يمكنك الاحتفاظ بعشر ملاحظات كحد أقصى. احذف واحدة أولًا.',
      FAILED: 'تعذّر الحفظ الآن. لم يتغيّر شيء. حاول مرة أخرى.',
      UNAVAILABLE: 'تعذّرت قراءة ملاحظاتك الآن.',
    },
    SCAN: {
      START: 'ابحث في ردود فريقي',
      RUNNING: 'نبحث في ردود فريقك. قد يستغرق ذلك بضع دقائق.',
      DONE: 'لم نجد إجابات | وجدنا إجابة واحدة يقدّمها فريقك. | وجدنا إجابتين يقدّمهما فريقك. | وجدنا {count} إجابات يقدّمها فريقك. | وجدنا {count} إجابة يقدّمها فريقك. | وجدنا {count} إجابة يقدّمها فريقك.',
      NOTHING: 'لا جديد هذه المرة. ردود فريقك مغطّاة.',
      FAILED: 'لم يكتمل البحث. لم يُخصم شيء. يمكنك المحاولة مرة أخرى.',
      TOMORROW: 'يمكنك البحث مرة أخرى غدًا.',
      CAME_UP:
        'لم يظهر في أي محادثة | ظهر في محادثة واحدة | ظهر في محادثتين | ظهر في {count} محادثات | ظهر في {count} محادثة | ظهر في {count} محادثة',
      FROM_REPLIES: 'من ردود فريقك',
    },
    PHOTOS: {
      CHOICE: 'صور',
      CHOICE_HINT: 'حتى عشر صور لقائمة طعام أو قائمة أسعار أو صفحة.',
      LABEL: 'الصور',
      HINT: 'JPG أو PNG أو WebP، حتى {size} ميغابايت لكل صورة.',
      TOO_MANY: 'اختر عشر صور كحد أقصى.',
      TOO_BIG: 'حجم كل صورة حتى {size} ميغابايت.',
      NOT_PHOTO: 'اختر صورًا بصيغة JPG أو PNG أو WebP.',
    },
    RECEIPT: {
      PAGES:
        'لم تُقرأ أي صفحة. | قُرئت صفحة واحدة. | قُرئت صفحتان. | قُرئت {count} صفحات. | قُرئت {count} صفحة. | قُرئت {count} صفحة.',
      LEARNED:
        'لم يتعلّم أي إجابة. | تعلّم إجابة واحدة. | تعلّم إجابتين. | تعلّم {count} إجابات. | تعلّم {count} إجابة. | تعلّم {count} إجابة.',
      LINES:
        'لم يُحتفظ بأي سطر. | احتُفظ بسطر واحد كما هو. | احتُفظ بسطرين كما هما. | احتُفظ بـ{count} أسطر كما هي. | احتُفظ بـ{count} سطرًا كما هي. | احتُفظ بـ{count} سطر كما هي.',
      PAGES_UNREADABLE: 'تعذّرت قراءة الصفحة {pages}.',
      LINES_UNREADABLE: 'تعذّرت قراءة هذه الأسطر:',
    },
    HOURS: {
      HEADING: 'ساعات العمل',
      DESCRIPTION:
        'يخبر مساعدك العملاء متى تكون مفتوحًا. أضف وقتًا ثانيًا إن كنت تغلق في منتصف اليوم.',
      TIMEZONE: 'المنطقة الزمنية',
      CLOSED: 'مغلق',
      ADD_TIME: 'أضف وقتًا',
      REMOVE_TIME: 'حذف',
      FROM: 'من',
      TO: 'إلى',
      NEXT_DAY: '(اليوم التالي)',
      DAYS: {
        MON: 'الاثنين',
        TUE: 'الثلاثاء',
        WED: 'الأربعاء',
        THU: 'الخميس',
        FRI: 'الجمعة',
        SAT: 'السبت',
        SUN: 'الأحد',
      },
      NEEDS_HOURS: 'أضف ساعات العمل أولًا.',
    },
    ANSWER_WHEN: {
      HEADING: 'متى يجيب',
      ALWAYS: 'طوال الوقت',
      ALWAYS_HINT: 'يجيب عن كل رسالة، ليلًا ونهارًا.',
      CLOSED_ONLY: 'فقط عندما تكون مغلقًا',
      CLOSED_ONLY_HINT:
        'أثناء ساعات العمل تذهب المحادثات إلى فريقك. ويجيب هو بعد الإغلاق.',
      TEST_NOTE:
        'التجربة تجيب دائمًا، حتى أثناء ساعات العمل، لتتمكن من التجربة في أي وقت.',
    },
    TYPING: {
      HEADING: 'إظهار أنه يكتب',
      ON: 'أظهره',
      ON_HINT:
        'على واتساب يرى عميلك «يكتب» أثناء كتابة الرد، وتظهر رسالته مقروءة.',
      OFF: 'لا تُظهره',
      OFF_HINT: 'لا يرى عميلك شيئًا حتى يصل الرد.',
    },
    SENDING_HOURS: {
      HEADING: 'متى يبدأ رسالة',
      DESCRIPTION:
        'المتابعات والرسائل الجماعية تُرسل فقط ضمن هذه الأوقات. أما الرد على عميل كتب للتو فيُرسل فورًا دائمًا.',
      WEEK: {
        OPENING: 'ساعات العمل لديك',
        OPENING_HINT: 'يبدأ الرسائل فقط وأنت مفتوح.',
        OPENING_NONE_HINT:
          'لم تحدد ساعات العمل، لذلك يمكنه أن يبدأ الرسائل في أي وقت.',
        OWN: 'أوقات أخرى',
        OWN_HINT: 'اختر الأوقات التي يرسل فيها.',
        HEADING: 'الأوقات التي يرسل فيها',
      },
      PRAYER: {
        LABEL: 'انتظر وقت صلاة الجمعة',
        FROM: 'من',
        TO: 'إلى',
      },
      SEASONS: {
        HEADING: 'مواسم بأوقات مختلفة',
        DESCRIPTION: 'مثل رمضان: أدخل تواريخه كل سنة، مع أوقات تلك الأيام.',
        ADD: 'أضف موسمًا',
        NAME: 'الاسم',
        NAME_PLACEHOLDER: 'رمضان',
        FROM: 'أول يوم',
        TO: 'آخر يوم',
        WEEK: 'الأوقات في هذا الموسم',
        WEEK_HINT: 'الأوقات التي يرسل فيها في هذه الأيام.',
        REMOVE: 'حذف',
      },
      PAUSES: {
        HEADING: 'أيام لا يرسل فيها شيئًا',
        DESCRIPTION: 'مثل العيد: لا يُرسل شيء يبدأه في هذه الأيام.',
        ADD: 'أضف أيام عطلة',
        NAME: 'الاسم',
        NAME_PLACEHOLDER: 'العيد',
        FROM: 'أول يوم',
        TO: 'آخر يوم',
        REMOVE: 'حذف',
      },
      BROADCASTS: 'الرسائل الجماعية تنتظر هذه الأوقات أيضًا',
      BROADCASTS_HINT:
        'الرسالة الجماعية التي تصل إلى نهاية هذه الأوقات تتوقف، وتكمل وحدها عندما تبدأ من جديد.',
      WEBSITE_NOTE:
        'الرسائل التي يرسلها موقعك تُرسل فورًا، إلا إذا طلب الانتظار.',
      ERRORS: {
        GAP_ORDER: 'يجب أن ينتهي وقت الصلاة بعد أن يبدأ.',
        DATES: 'أعطِ كل موسم وكل أيام عطلة اسمًا وأول يوم وآخر يوم.',
        CHECK: 'راجع التواريخ والأوقات في هذا القسم.',
      },
    },
    FOLLOW_UPS: {
      HEADING: 'المتابعة عندما يسكت العميل',
      DESCRIPTION:
        'إذا توقف العميل عن الرد في منتصف مهمة، يمكنه أن يسأله مرة ثانية. كل متابعة رسالة واتساب، وقد يحاسبك واتساب عليها.',
      OFF: 'لا تتابع',
      OFF_HINT: 'ينتظر حتى يكتب العميل مرة ثانية.',
      ON: 'تابع',
      ON_HINT:
        'يسأل مرة ثانية، مرتين على الأكثر، ولا يتابع أبدًا بعد أن يمسك شخص المحادثة.',
      TIMES: 'كم مرة',
      ONCE: 'مرة واحدة',
      TWICE: 'مرتين',
      FIRST: 'المتابعة الأولى',
      SECOND: 'المتابعة الثانية',
      FIRST_AFTER: {
        M30: 'بعد نصف ساعة',
        M60: 'بعد ساعة',
        M180: 'بعد ثلاث ساعات',
      },
      SECOND_AFTER: {
        M180: 'بعد ثلاث ساعات من الأولى',
        M360: 'بعد ست ساعات من الأولى',
        M720: 'بعد اثنتي عشرة ساعة من الأولى',
      },
      WORDING: {
        QUESTION: 'اسأل نفس السؤال مرة ثانية',
        QUESTION_HINT: 'يرسل السؤال كما كتبته في المهمة تمامًا.',
        OWN: 'أرسل كلامي أنا',
        OWN_HINT: 'يرسل الكلام الذي تكتبه بالأسفل.',
      },
      WORDS_EN: 'كلامك بالإنجليزية',
      WORDS_AR: 'كلامك بالعربية',
      WORDS_HINT: 'يُرسل أيضًا عندما لا يمكن إرسال السؤال كما كُتب.',
      WORDS_PLACEHOLDER_EN:
        'Are you still there? Just reply and we will carry on.',
      WORDS_PLACEHOLDER_AR: 'حاب تكمل؟ رد علينا ونكمل.',
      RULES:
        'لا يتابع أبدًا بعد أن يرد شخص، أو بعد أن تذهب المحادثة لفريقك، أو عندما تقترب آخر رسالة من العميل من يوم كامل. ويرسل فقط ضمن الأوقات التي اخترتها في الأعلى.',
      ERRORS: {
        NEEDS_WORDS: 'اكتب الكلام الذي يرسله، بلغة واحدة على الأقل.',
        CHECK: 'راجع إعدادات المتابعة.',
      },
    },
    SKIP_LABELS: {
      HEADING: 'محادثات يتركها لفريقك',
      DESCRIPTION:
        'ضع تصنيفًا على محادثة أو جهة اتصال في صندوق الوارد، وسيترك مساعدك تلك المحادثة لفريقك.',
      LABEL: 'التصنيفات',
      PLACEHOLDER: 'اختر التصنيفات',
      NONE: 'لا توجد تصنيفات بعد. أنشئها من إعدادات صندوق الوارد.',
    },
    // Task review item 11: the labels a handover adds, on a task's step and on Personality.
    HAND_LABELS: {
      HEADING: 'أضف تصنيفات',
      DESCRIPTION:
        'توضع هذه التصنيفات على المحادثة حين تُحوَّل إلى فريقك، بجانب needs-a-person. يمكن لقواعد صندوق الوارد وعوامل التصفية استخدامها.',
      NONE: 'لا توجد تصنيفات بعد. أنشئها من إعدادات صندوق الوارد.',
      GONE: 'التصنيف المعلَّم هنا لم يعد في إعدادات صندوق الوارد. اضغط عليه لإزالته.',
      FULL: 'يمكنك إضافة {max} تصنيفات كحد أقصى.',
    },
    NAV: {
      SECTION: 'واصلو AI',
      OVERVIEW: 'نظرة عامة',
      ANSWERS: 'الإجابات',
      PERSONALITY: 'الشخصية',
      TASKS: 'المهام',
      MENUS: 'القوائم',
      TEMPLATES: 'الرسائل المعتمدة',
      FORMS: 'النماذج',
      TEST: 'جرّبه',
      ACTIVITY: 'النشاط',
      CONNECTIONS: 'أنظمتك',
      // `واجهة الإرسال البرمجية` did not fit the 200px rail. What survived the ellipsis
      // was `واجهة الإرسال` — "sending interface" — which INVITES a non-technical owner
      // into the developer corner instead of warning her off it. Nine characters cannot
      // truncate.
      OUTBOUND: 'للمطوّرين',
      // Was `الإعداد`, one character from `الإعدادات` (Settings) directly above it.
      OUTBOUND_SETUP: 'مفاتيح وحدود',
      OUTBOUND_LOG: 'ما أُرسل',
      OUTBOUND_NUMBERS: 'أرقام لا تُراسَل',
      BROADCASTS: 'رسائل واتساب الجماعية',
    },
    COMMON: {
      RETRY: 'حاول مرة أخرى',
      ERROR_TITLE: 'تعذّر تحميل هذا الآن',
      ERROR_BODY:
        'لم يتغيّر أي شيء. تحقّق من اتصالك وحاول مرة أخرى — وإن تكرّر الأمر تواصل معنا.',
      ERROR_SESSION:
        'انتهت جلسة دخولك. أعد تحميل الصفحة لتسجيل الدخول من جديد — لم يتغيّر أي شيء.',
      NOT_PROVISIONED_TITLE: 'لا يوجد مساعد في مساحة العمل هذه بعد',
      NOT_PROVISIONED_BODY:
        'لم يتم إعداد مساعد لمساحة العمل هذه. تواصل معنا وسنربط مساعدًا برقم واتساب الخاص بك — وسيظهر كل ما في هذه الصفحات بمجرد تشغيله.',
      LOADING: 'جارٍ التحميل',
      LIST_SEPARATOR: '، ',
      LANG_TAG: { EN: 'English', AR: 'العربية' },
      LANG_NAME: { EN: 'الإنجليزية', AR: 'العربية' },
      COUNT: {
        DAYS: 'لا أيام | يوم واحد | يومين | {count} أيام | {count} يومًا | {count} يوم',
        HOURS:
          'لا ساعات | ساعة واحدة | ساعتين | {count} ساعات | {count} ساعة | {count} ساعة',
        MINUTES:
          'لا دقائق | دقيقة واحدة | دقيقتين | {count} دقائق | {count} دقيقة | {count} دقيقة',
        CHARACTERS:
          'لا حروف | حرف واحد | حرفين | {count} أحرف | {count} حرفًا | {count} حرف',
        OPTIONS:
          'لا خيارات | خيار واحد | خيارين | {count} خيارات | {count} خيارًا | {count} خيار',
        VALUES:
          'لا قيم | قيمة واحدة | قيمتين | {count} قيم | {count} قيمة | {count} قيمة',
        TIMES:
          'ولا مرة | مرة واحدة | مرتين | {count} مرات | {count} مرة | {count} مرة',
      },
    },
    STATUS: {
      NOT_SET_UP: {
        TITLE: 'مساعدك غير مُجهَّز بعد.',
        BODY: 'ما زلنا نربطه، وحتى ذلك الحين تنتظر كل رسالة فريقك.',
        CHIP: 'غير مُجهَّز',
      },
      NOT_CONNECTED: {
        TITLE: 'مساعدك غير موجود على هذه القناة.',
        BODY: 'الرسائل الواصلة هنا تنتظر فريقك، ويمكننا وضع مساعدك عليها.',
        CHIP: 'غير مرتبطة',
      },
      CHANNEL_OFF: {
        TITLE: 'الردود متوقفة على هذه القناة.',
        BODY: 'مساعدك موجود على هذه القناة لكنه لا يردّ هنا، فكل رسالة تنتظر فريقك.',
        CHIP: 'متوقفة هنا',
      },
      PAUSED_BY_US: {
        TITLE: 'أوقفنا الردود مؤقتًا.',
        BODY: 'أوقفنا مساعدك مؤقتًا وسنتواصل معك، وحتى ذلك الحين تنتظر كل رسالة فريقك.',
        CHIP: 'أوقفناه مؤقتًا',
      },
      PLATFORM_LIMIT: {
        TITLE: 'توقّف مساعدك عن الرد.',
        BODY: 'الردود متوقفة بسبب حدّ عام في واصلو. نحن نعمل عليه، ولا يلزمك إصلاح شيء.',
        CHIP: 'متوقف',
      },
      OUT_OF_CREDITS: {
        TITLE: 'توقّف مساعدك عن الرد.',
        BODY: 'نفد رصيدك. يعود للعمل فور تعبئة الرصيد.',
        CHIP: 'نفد الرصيد',
      },
      MONTH_LIMIT: {
        TITLE: 'توقّف مساعدك عن الرد.',
        BODY: 'بلغ حدّ الإنفاق لهذا الشهر، ويمكننا رفعه لك.',
        CHIP: 'بلغ حدّ الشهر',
      },
      DAY_LIMIT: {
        TITLE: 'توقّف مساعدك عن الرد.',
        BODY: 'بلغ حدّ الإنفاق لليوم ويعود للعمل غدًا، ويمكننا رفع الحدّ لك.',
        CHIP: 'بلغ حدّ اليوم',
      },
      SWITCHED_OFF: {
        TITLE: 'مساعدك مطفأ.',
        BODY: 'لا شيء يُجاب تلقائيًا، وكل محادثة جديدة تذهب إلى فريقك.',
        CHIP: 'الردود متوقفة',
      },
      PRACTICE: {
        TITLE: 'مساعدك يتدرّب.',
        BODY: 'لا يردّ إلا على أرقام التجربة وصفحة «جرّبه»، وكل محادثة أخرى تذهب إلى فريقك.',
        CHIP: 'يتدرّب',
      },
      ANSWERING: {
        TITLE: 'مساعدك يردّ على العملاء بنفسه.',
        BODY: 'الرسائل التي تصل هنا يردّ عليها مساعدك.',
        CHIP: 'يردّ',
      },
      UNKNOWN: 'تعذّر التحقق من مساعدك الآن.',
      RESUMES: 'يعود للعمل {when}.',
      CUSTOMERS: 'العملاء الآن: {state}',
      CHANNEL_NAMELESS: 'القناة {id}',
      FIX: {
        TURN_ON: 'شغّله',
        GO_LIVE: 'ابدأ الرد على العملاء',
        REQUEST_CONNECT: 'اطلب منّا ربطه',
        REQUEST_LIMIT_RAISE: 'اطلب منّا رفعه',
        REQUEST_SWITCH_ON: 'اطلب منا تشغيله',
        ASK_OWNER: {
          REQUEST_CONNECT: 'اطلب من صاحب الحساب يتواصل معنا بخصوص هذا الرقم.',
          REQUEST_TOP_UP: 'اطلب من صاحب الحساب يشحن الرصيد.',
          REQUEST_LIMIT_RAISE: 'اطلب من صاحب الحساب يرفع الحد.',
          TURN_ON: 'اطلب من صاحب الحساب يشغّله.',
          GO_LIVE: 'اطلب من صاحب الحساب يبدأ الرد على العملاء.',
        },
        SENDING: 'جارٍ الإرسال…',
        SENT: 'وصلنا طلبك وسنخبرك.',
        FAILED: 'تعذّر إرسال الطلب الآن. حاول مرة أخرى.',
        SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء، حاول مرة أخرى.',
      },
    },
    WHY: {
      CODE: {
        TASK_STEP: 'مهمتك وصلت للخطوة اللي تحوّل المحادثة لفريقك.',
        TASK_ASKED_FOR_PERSON:
          'العميل طلب يكلم شخص أثناء مهمتك، فحوّل المحادثة.',
        TASK_STUCK: 'العميل ما قدر يجاوب «{step}»، فحوّل المحادثة.',
        TASK_CALL_FAILED: 'نظامك ما رد أثناء مهمتك، فحوّل المحادثة.',
        TASK_BROKEN:
          'مهمتك «{task}» فيها خطوة ما تودي لأي مكان، فحوّل المحادثة.',
        TASK_SYSTEM_REFUSED: 'نظامك رفض مرتين، فحوّل المحادثة.',
        TASK_CHANGED_AFTER_DONE:
          'العميل يبي يغيّر شي تم في نظامك، فحوّل المحادثة.',
        TASK_CANCEL_AFTER_DONE:
          'العميل طلب إلغاء شي تم في نظامك، فحوّل المحادثة.',
        ASSISTANT_DECIDED: 'قرر إن شخص لازم يرد على هذا، فحوّل المحادثة.',
        GATE_WITHHELD:
          'كتب سعر أو رابط أو وقت أو قائمة ما قدر يتأكد منها، فحوّل المحادثة.',
        CLAIM_WITHHELD: 'قال إن شي تم وهو ما تم، فحوّل المحادثة.',
        EMPTY_REPLY: 'ما كان عنده شي يقوله، فحوّل المحادثة.',
        NO_CREDITS: 'خلص رصيدك، فحوّل المحادثة.',
        SPEND_CAP: 'وصلت حد الصرف، فحوّل المحادثة.',
        RUN_TOO_COSTLY: 'هذا الرد كان بيكلّف واجد، فحوّل المحادثة.',
        PAUSED_BY_WASLO: 'واصلو وقّف المساعد مؤقتاً، فحوّل المحادثة.',
        TOO_MANY_MESSAGES: 'وصلت رسايل واجد مرة وحدة، فحوّل المحادثة.',
        MESSAGE_TOO_OLD: 'الرسالة كانت قديمة واجد، فحوّل المحادثة.',
        BUSY_TOO_LONG: 'كان مشغول فترة طويلة، فحوّل المحادثة.',
        UNREADABLE_MESSAGE: 'ما قدر يقرأ الرسالة، فحوّل المحادثة.',
        SEND_UNCERTAIN: 'رده يمكن ما وصل، فحوّل المحادثة.',
        PARTIAL_SEND: 'جزء بس من رده وصل، فحوّل المحادثة.',
        CHANNEL_REFUSED: 'القناة رفضت رده، فحوّل المحادثة.',
        ERROR: 'صار خطأ أثناء الرد، فحوّل المحادثة.',
        ASSISTANT_OFF: 'المساعد مطفأ، فراحت المحادثة لفريقك.',
        PRACTICE: 'أنت بعدك تتدرّب، فمحادثات العملاء تروح لفريقك.',
        ANSWER_WHEN_OPEN: 'أنت فاتح الحين، فترك المحادثة لفريقك.',
        SKIP_LABEL: 'هذا العميل عليه التصنيف «{label}»، فترك المحادثة لفريقك.',
        CONNECTION_FAILED: 'نظامك «{name}» ما رد.',
        TASK_ASKED: 'سأل «{step}» لمهمتك «{task}».',
        TASK_FINISHED: 'خلّص مهمتك «{task}».',
        ANSWERED_CLOSE: 'رد من جوابك المحفوظ «{q}».',
        ANSWERED_LOOSE: 'رد من جواب قريب شوي.',
        ANSWERED_NONE: 'رد بدون جواب محفوظ يطابق.',
        ANSWERED_PLAIN: 'رد من اللي كتبته عن نشاطك.',
        FOLLOW_UP_SENT: 'العميل سكت عند «{step}»، فسأله مرة ثانية.',
        FOLLOW_UP_PERSON: 'ما تابع: كان فيه شخص ماسك المحادثة.',
        FOLLOW_UP_WINDOW: 'ما تابع: آخر رسالة من العميل صار لها تقريباً يوم.',
        FOLLOW_UP_HOURS: 'ما تابع: أوقات الإرسال ظلت مسكّرة لين فات الوقت.',
        FOLLOW_UP_NO_WORDS: 'ما تابع: ما كان عنده كلام يرسله بلغة العميل.',
      },
      FIX: {
        TEACH: 'علّمه',
        FIX_TASK: 'صلّحه',
        FIX_PERSONALITY: 'صلّحه',
        REQUEST_TOP_UP: 'اطلب شحن الرصيد',
        REQUEST_LIMIT_RAISE: 'اطلب رفع الحد',
        CHECK_CONNECTION: 'افحص الربط',
        TURN_ON: 'شغّله',
        SUGGEST: 'اقترح رد',
      },
      ASK_OWNER: {
        FIX_TASK: 'اطلب من صاحب الحساب يصلّح المهمة.',
        FIX_PERSONALITY: 'اطلب من صاحب الحساب يراجع إعدادات المساعد.',
        REQUEST_TOP_UP: 'اطلب من صاحب الحساب يشحن الرصيد.',
        REQUEST_LIMIT_RAISE: 'اطلب من صاحب الحساب يرفع الحد.',
        CHECK_CONNECTION: 'اطلب من صاحب الحساب يفحص الربط.',
        TURN_ON: 'اطلب من صاحب الحساب يشغّله.',
      },
      SENDING: 'جارٍ الإرسال…',
      SENT: 'وصلنا طلبك وبنخبرك.',
      FAILED: 'ما قدرنا نسوي هذا الحين. حاول مرة ثانية.',
    },
    READINESS: {
      HEADING: 'قبل أن يردّ على كل العملاء',
      DESCRIPTION:
        'مساعدك يتدرّب. لا يردّ إلا على أرقام التجربة أدناه وصفحة «جرّبه»، ويحوّل كل محادثة أخرى إلى فريقك دون أي تكلفة.',
      STEPS: {
        DESCRIBED: 'وصفتَ نشاطك التجاري',
        KNOWS: 'لديه ما يجيب منه',
        TRIED: 'جرّبته',
      },
      LINKS: {
        DESCRIBED: 'افتح الشخصية',
        KNOWS: 'افتح الإجابات',
        TRIED: 'افتح جرّبه',
      },
      LEFT: {
        DESCRIBED: 'صف نشاطك التجاري',
        KNOWS: 'أعطه ما يجيب منه',
        TRIED: 'جرّبه',
      },
      DONE: 'تمّ',
      NOT_DONE: 'ليس بعد',
      CONFIRM_LEFT:
        'لم يكتمل بعد: {steps}. هل تبدأ الرد على كل العملاء على أي حال؟',
      CONFIRM_READY: 'هل تبدأ الرد على كل العملاء الآن؟',
      CONFIRM_YES: 'نعم، ابدأ الآن',
      CONFIRM_NO: 'ليس بعد',
      GO_LIVE_FAILED: 'تعذّر البدء الآن. لم يتغيّر شيء، حاول مرة أخرى.',
      LIVE_SINCE: 'يردّ على كل العملاء منذ {date}.',
      BACK: 'العودة إلى التدريب',
      BACK_CONFIRM:
        'هل تعود إلى التدريب؟ لن يردّ إلا على أرقام التجربة، وتذهب كل محادثة أخرى إلى فريقك.',
      BACK_YES: 'نعم، عُد إلى التدريب',
      BACK_NO: 'استمر في الرد',
      BACK_FAILED: 'تعذّرت العودة الآن. لم يتغيّر شيء، حاول مرة أخرى.',
      TESTERS_FAILED: 'تعذّر الحفظ. لم يتغيّر شيء، حاول مرة أخرى.',
      TESTERS: {
        NUMBERS_HEADING: 'أرقام التجربة',
        NUMBERS_DESCRIPTION:
          'أثناء التدريب، لا تصل ردوده إلا إلى أرقام واتساب هذه. ضع رقم هاتفك أولًا.',
        NUMBER_LABEL: 'رقم الهاتف',
        ADD: 'أضف رقمًا',
        PLACEHOLDER: '+96550001234',
        HINT: 'مع رمز الدولة، مثل +96550001234.',
        REMOVE: 'احذف هذا الرقم',
        EMPTY: 'لا توجد أرقام بعد، فلن يصل أي رد أثناء التدريب.',
        FULL: 'هذا أقصى عدد يمكننا حفظه.',
        INVALID: 'لا يبدو هذا رقم هاتف مع رمز الدولة.',
        DUPLICATE: 'هذا الرقم موجود في القائمة.',
      },
    },
    OVERVIEW: {
      TITLE: 'نظرة عامة',
      DESCRIPTION: 'كيف يعمل مساعدك الآن.',
      REPLIES: {
        HEADING: 'الردود التلقائية',
        ON: 'مشغّلة',
        OFF: 'مطفأة',
        ON_BODY: 'مساعدك يردّ على العملاء بنفسه.',
        OFF_BODY: 'مساعدك لا يردّ الآن. كل رسالة تنتظر ردّ موظف.',
        SAVING: 'جارٍ الحفظ…',
        SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
      },
      CREDITS: {
        HEADING: 'رصيد الرسائل',
        UNIT: 'رصيد متبقٍ',
        NEXT_EXPIRY: 'تنتهي دفعتك التالية في {date}.',
        NO_EXPIRY: 'لا توجد دفعة تنتهي قريبًا.',
        LOW: 'يتوقّف مساعدك عن الرد عند وصول هذا الرقم إلى صفر.',
        CRITICAL: 'مساعدك على وشك التوقّف عن الرد.',
        EMPTY: 'توقّف مساعدك. اطلب إضافة رصيد ليعود للعمل.',
        INTERNAL: 'هذه المساحة تخصّنا، ولا يُحتسب عليها أي مبلغ.',
        UNAVAILABLE: 'تعذّرت قراءة رصيدك الآن.',
        USED_TODAY: 'المستخدم اليوم: {credits}',
        DAILY_AVERAGE: 'المعدل اليومي في الأسبوع الأخير: {credits}',
        LASTS:
          'ينفد اليوم بهذا المعدل. | يكفي يومًا واحدًا تقريبًا بهذا المعدل. | يكفي يومين تقريبًا بهذا المعدل. | يكفي {count} أيام تقريبًا بهذا المعدل. | يكفي {count} يومًا تقريبًا بهذا المعدل. | يكفي {count} يوم تقريبًا بهذا المعدل.',
        LASTS_TODAY: 'ينفد اليوم بهذا المعدل.',
        NOT_ENOUGH_USE: 'لا يوجد استخدام كافٍ بعد لمعرفة كم يكفي.',
        REQUEST: 'اطلب إضافة رصيد',
        SENDING: 'جارٍ الإرسال…',
        REQUESTED: 'أُرسل الطلب',
        SENT: 'أبلغنا فريق واصلو، وسنتواصل معك قريبًا.',
        TOLD: 'وصلنا طلبك من قبل',
        REQUEST_FAILED: 'تعذّر إرسال الطلب الآن. حاول مرة أخرى.',
      },
      STATUS: {
        ANSWERING: 'مساعدك يردّ على العملاء بنفسه.',
        PAUSED: 'مساعدك متوقّف مؤقتًا.',
        PAUSED_BODY:
          'لا شيء يُجاب عليه تلقائيًا. كل رسالة جديدة تنتظر ردّ موظف من فريقك.',
        STOPPED: 'توقّف مساعدك عن الرد.',
        STOPPED_BODY: 'نفد رصيدك. وسيعود للعمل فور إضافة رصيد جديد.',
        STOPPED_DAY_BODY:
          'بلغ مساعدك حدّ الإنفاق لهذا اليوم. وسيعود للعمل غدًا — وتواصل معنا إن أردت رفع الحد.',
        STOPPED_MONTH_BODY:
          'بلغ مساعدك حدّ الإنفاق لهذا الشهر. تواصل معنا لرفع الحد.',
        STOPPED_PLATFORM_BODY:
          'الردود متوقّفة مؤقتًا بسبب حدّ عام على مستوى واصلو. نحن نعالج الأمر، ولا شيء لديك يحتاج إصلاحًا.',
        LAST_REPLY: 'آخر ردّ {when}.',
        NO_REPLY:
          'لم يحتج إلى الردّ اليوم. | لم يحتج إلى الردّ خلال آخر يوم. | لم يحتج إلى الردّ خلال آخر يومين. | لم يحتج إلى الردّ خلال آخر {days} أيام. | لم يحتج إلى الردّ خلال آخر {days} يومًا. | لم يحتج إلى الردّ خلال آخر {days} يوم.',
        JUST_NOW: 'الآن',
        UNKNOWN_BODY: 'تعذّرت معرفة حجم العمل الآن.',
      },
      LIMITS: {
        MONTH_USED: 'استُهلك {percent} من حدّ الذكاء الاصطناعي لهذا الشهر',
      },
      NUMBERS: {
        CONVERSATIONS: 'محادثات اليوم',
        HANDOFFS: 'حُوّلت إلى فريقك اليوم',
        WINDOW_HINT:
          '{count} اليوم | {count} خلال آخر يوم | {count} خلال آخر يومين | {count} خلال آخر {days} أيام | {count} خلال آخر {days} يومًا | {count} خلال آخر {days} يوم',
        UNAVAILABLE: 'تعذّر تحميل أرقام اليوم.',
        DAY_TITLE: 'محسوبة ليوم {day}.',
      },
      WEEK: {
        HEADING: 'هذا الأسبوع',
        DATES: 'من {from} إلى {to}',
        CONVERSATIONS: 'المحادثات',
        HANDLED: 'أُنجزت دون تدخّل فريقك',
        HANDLED_NONE: 'لا محادثات بعد',
        HANDOVERS: 'حُوّلت إلى فريقك',
        TASKS: 'المهام المكتملة',
        CREDITS: 'الرصيد المستخدم',
        UNAVAILABLE: 'تعذّر تحميل أرقام هذا الأسبوع.',
        EMAIL_LABEL: 'أرسل لي هذا بالبريد كل أسبوع',
        EMAIL_DAY: 'يوم الإرسال',
        EMAIL_TO: 'نرسله صباحًا إلى {address}.',
        EMAIL_SAVING: 'جارٍ الحفظ…',
        EMAIL_FAILED: 'تعذّر الحفظ الآن. لم يتغيّر شيء.',
      },
      ATTENTION: {
        HEADING: 'يحتاج انتباهك',
        DESCRIPTION: 'كل ما هنا يفصله إجراء واحد عن الإنجاز.',
        CLEAR_TITLE: 'لا شيء يحتاجك الآن',
        CLEAR_BODY:
          'مساعدك يتولّى رسائله بنفسه. وسيظهر هنا كل ما يحتاج قرارًا منك.',
        UNCHECKED: 'تعذّر التحقق من {sources} الآن.',
        SOURCES: {
          INBOXES: 'قنواتك',
          NEEDS_YOU: 'قوائمك ونماذجك ومصادرك',
          ACTIVITY: 'الأسئلة التي لم يستطع الإجابة عنها',
          TEMPLATES: 'ما قرّره واتساب بشأن رسائلك',
          CONNECTIONS: 'أنظمتك المتصلة',
          TASKS: 'مهامك',
        },
        ASKED:
          'لم يتكرّر | تكرّر مرة واحدة | تكرّر مرتين | تكرّر {count} مرات | تكرّر {count} مرة | تكرّر {count} مرة',
        DISCONNECTED_INBOX: {
          TITLE: 'لا يوجد مساعد على «{name}»',
          BODY: 'الرسائل الواصلة هنا تنتظر موظفًا، ولا يُجاب عليها تلقائيًا.',
          VERB: 'اطلب منّا ربطه',
        },
        REQUEST: {
          SENDING: 'جارٍ الإرسال…',
          SENT: 'وصلنا طلبك — سنربطه ونخبرك.',
          FAILED: 'تعذّر إرسال الطلب الآن. حاول مرة أخرى.',
        },
        FAILED_SOURCE: {
          TITLE: 'تعذّرت قراءة «{title}»',
          BODY: 'لم يُحفظ منه شيء، لذلك لا يستطيع مساعدك استخدامه بعد.',
          VERB: 'حاول مرة أخرى',
        },
        UNANSWERED: {
          BODY: 'سأل عميل عن هذا ولم تكن لدى مساعدك إجابة.',
          VERB: 'صحّحه',
        },
        NOTICE_ENDING: {
          TITLE: 'ملاحظتك تنتهي غدًا',
          VERB: 'مدّدها',
        },
        TEMPLATE_REFUSED: {
          TITLE: 'رفض واتساب «{name}» ({language}).',
          BODY: 'لم يذكر واتساب السبب. عدّل الصياغة وأرسلها مرة أخرى.',
          VERB: 'عدّلها',
        },
        TEMPLATE_PAUSED: {
          TITLE: 'أوقف واتساب «{name}» مؤقتًا ({language}).',
          BODY: 'أوقفها واتساب مؤقتًا، غالبًا بعد أن حظرها العملاء أو أبلغوا عنها. لا يمكن استخدامها في الرسائل الجماعية حتى تعود.',
          VERB: 'اعرف السبب',
        },
        TEMPLATE_APPROVED: {
          TITLE: 'اعتمد واتساب «{name}» ({language}).',
          BODY: 'يمكنك استخدامها الآن في رسالة واتساب جماعية.',
          VERB: 'استخدمها في رسالة جماعية',
        },
        TEMPLATE_REASON: {
          ABUSIVE_CONTENT: 'وجد واتساب صياغة لا يسمح بها.',
          INCORRECT_CATEGORY:
            'يرى واتساب أنها تنتمي إلى التصنيف الآخر. اختره وأرسلها مرة أخرى.',
          INVALID_FORMAT:
            'لم يستطع واتساب قراءة طريقة كتابتها، غالبًا بسبب فراغ في مكان خاطئ.',
          SCAM: 'رأى واتساب أنها قد تضلّل العملاء.',
          TAG_CONTENT_MISMATCH:
            'يرى واتساب أن الصياغة لا تناسب التصنيف الذي اخترته.',
        },
        LANGUAGE: {
          AR: 'العربية',
          EN: 'الإنجليزية',
        },
        FAILING_CONNECTION: {
          TITLE: '«{name}» ما يرد',
          BODY: 'لم يفشل هذا الأسبوع. | فشل مرة واحدة هذا الأسبوع. | فشل مرتين هذا الأسبوع. | فشل {count} مرات هذا الأسبوع. | فشل {count} مرة هذا الأسبوع. | فشل {count} مرة هذا الأسبوع.',
          VERB: 'افتحه',
        },
        TASK_CONNECTION_OFF: {
          TITLE: '«{task}» تستخدم «{connection}» وهو مطفأ.',
          BODY: 'العملاء الذين يصلون إليها يُحوَّلون إلى فريقك.',
          VERB: 'افتح أنظمتك',
        },
        HANDOVER_UNSET: {
          TITLE: 'اختار منو يستلم المحادثات اللي يحوّلها المساعد',
          BODY: 'الحين ما أحد يوصله تنبيه لما يحوّل محادثة.',
          VERB: 'حوّلها لي',
          FAILED: 'ما قدرنا نحفظ هذا الحين. حاول مرة ثانية.',
        },
        TEAM_SUGGESTIONS: {
          TITLE:
            'ما في ردود اقترحها فريقك | ‏رد واحد اقترحه فريقك | ‏ردّين اقترحهم فريقك | ‏{count} ردود اقترحها فريقك | ‏{count} رد اقترحه فريقك | ‏{count} رد اقترحه فريقك',
          BODY: 'اقبل الصحيحة والمساعد يتعلمها.',
          VERB: 'راجعها',
        },
        MENU_TASK_MISSING: {
          TITLE: 'في القائمة «{title}» خيار يفتح مهمة حذفتها',
          BODY: 'العملاء الذين يختارونه يصلون إلى طريق مسدود.',
          VERB: 'عدّل القائمة',
        },
        MENU_TASK_OFF: {
          TITLE: 'في القائمة «{title}» خيار مهمته مطفأة',
          BODY: 'العملاء الذين يختارونه يُحوَّلون إلى فريقك.',
          VERB: 'عدّل القائمة',
        },
        FORM_OUT_OF_DATE: {
          TITLE: 'النموذج الخاص بـ«{title}» قديم',
          BODY: 'غيّرت المهمة بعد نشر نموذجها، فمن يفتح النموذج القديم يصل إلى طريق مسدود.',
          VERB: 'انشره من جديد',
        },
        FORM_PAUSED: {
          TITLE: 'أوقف واتساب نموذج «{title}» مؤقتًا',
          BODY: 'واجه العملاء مشكلة في استخدامه. يسأل مساعدك هذه الأسئلة في المحادثة حتى يرفع واتساب الإيقاف، وهو يرفعه بنفسه حين يعمل النموذج من جديد.',
          VERB: 'اعرض نماذجك',
        },
        SOURCE_TAUGHT_NOTHING: {
          TITLE: '«{title}» لم يعلّم مساعدك شيئًا',
          BODY: 'لم يكن فيه ما يستطيع مساعدك التعلّم منه.',
          VERB: 'استبدله',
        },
      },
      CHANNELS: {
        HEADING: 'قنواتك',
        DESCRIPTION: 'من أين يراسلك عملاؤك، وهل مساعدك يعمل عليها.',
        NO_NUMBER: 'لا يوجد رقم بعد',
        NONE: 'لم تُضبط أي قناة في هذه المساحة بعد.',
        UNAVAILABLE: 'تعذّر عرض قنواتك الآن.',
        STATE: {
          ANSWERING: 'يردّ',
          ANSWERING_BODY: 'الرسائل التي تصل هنا يردّ عليها مساعدك.',
          PAUSED: 'الردود متوقفة',
          PAUSED_BODY:
            'مساعدك موجود على هذه القناة، لكن الردود التلقائية متوقفة، فكل رسالة تنتظر فريقك.',
          READY: 'جاهز',
          READY_BODY: 'مساعدك موجود على هذه القناة.',
          NOT_CONNECTED: 'غير مرتبطة',
          NOT_CONNECTED_BODY:
            'لا شيء هنا يُجاب تلقائيًا. تواصل معنا ونضع مساعدك على هذه القناة.',
        },
      },
    },
    PERSONALITY: {
      TITLE: 'الشخصية',
      SPOTLIGHT:
        'يقرأ مساعدك هذه الصفحة قبل أن يردّ على أي عميل. اكتبها كما لو كنت تشرح العمل لموظف جديد في يومه الأول — جمل قصيرة وواضحة بلا مصطلحات. يسري التغيير من الرسالة التالية مباشرة، ويمكنك تعديله متى شئت.',
      ABOUT: {
        HEADING: 'شخصية مساعدك',
        DESCRIPTION:
          'أربع إجابات قصيرة تكفي. اترك أي خانة فارغة إن كانت لا تنطبق عليك.',
      },
      FIELDS: {
        IDENTITY: {
          LABEL: 'من هو',
          PLACEHOLDER:
            'أنت نورة، موظفة الاستقبال في بيت العود. أسلوبك ودود ومختصر ومهذّب، وتردّين دائمًا بلغة العميل.',
          HINT: 'اسم، ووظيفة، وأسلوب في الكلام.',
        },
        BUSINESS: {
          LABEL: 'ماذا يقدّم عملك',
          PLACEHOLDER:
            'بيت العود يبيع العود والعطور من محل واحد في السالمية. نفتح من ١٠ صباحًا إلى ١٠ مساءً، من السبت إلى الخميس. نوصّل داخل الكويت خلال يومين مقابل ٢ د.ك، والتوصيل مجاني فوق ٣٠ د.ك.',
          HINT: 'ماذا تبيع، وأين أنت، ومتى تفتح، وكيف توصّل.',
        },
        ALWAYS: {
          LABEL: 'ما يجب أن يفعله دائمًا',
          PLACEHOLDER:
            'اسأل عن منطقة العميل قبل تحديد يوم التوصيل.\nأرسل قائمة الأسعار عند السؤال عن الأسعار.\nاعرض تحويل المحادثة إلى موظف إذا بدا العميل منزعجًا.',
          HINT: 'تعليمة واحدة في كل سطر.',
        },
        NEVER: {
          LABEL: 'ما يجب ألا يفعله أبدًا',
          PLACEHOLDER:
            'لا تخترع سعرًا أو موعد توصيل.\nلا تَعِد بخصم غير موجود في قائمة الأسعار.\nلا تُطلع عميلًا على طلب عميل آخر.',
          HINT: 'تعليمة واحدة في كل سطر. هذه حدود لا يتجاوزها.',
        },
      },
      DOCUMENT: {
        BANNER:
          'تعليماتك مكتوبة كنصّ واحد طويل. يمكنك متابعة تعديلها هنا، أو أن نحاول تقسيمها إلى الخانات الأربع.',
        BANNER_ACTION: 'قسّمها إلى خانات',
        LABEL: 'تعليماتك',
        PLACEHOLDER:
          'اشرح لمساعدك من هو، وماذا يقدّم عملك، وما الذي يقوله وما الذي لا يقوله.',
        HINT: 'هذا نصّك كما كتبته تمامًا، لم يتغيّر منه شيء.',
        PREVIEW_SPLITS:
          'اعتماد هذا النص يملأ الخانات الأربع بالأعلى. لا يُحفظ شيء حتى تضغط حفظ، ويمكنك رفضه بدلًا من ذلك.',
        PREVIEW_NO_SPLIT:
          'استطعنا تحسين الصياغة فقط دون تقسيمها إلى خانات. اعتماده يستبدل النص بالأعلى، ولا يُحفظ شيء حتى تضغط حفظ.',
      },
      ASSIST: {
        ACTION: 'اقترح صياغة أفضل',
        PREVIEW_TITLE: 'صياغة مقترحة',
        PREVIEW_NOTE: 'لم يتغيّر شيء بعد. اقرأها ثم اعتمدها أو ارفضها.',
        ACCEPT: 'اعتمد هذه',
        DISCARD: 'ارفضها',
        FAILED: 'تعذّر إعداد اقتراح الآن. وما كتبته لم يتغيّر.',
      },
      STYLE: {
        HEADING: 'إلى أي حدّ يتصرّف بحرية',
        DESCRIPTION: 'اختر مدى التزام مساعدك بما كتبته له بالأعلى.',
        OPTIONS: {
          STRICT: {
            LABEL: 'التزام تام بما يعرفه',
            DESCRIPTION:
              'يجيب فقط مما كتبته هنا ومن الإجابات التي حفظتها، وبالكلمات نفسها تقريبًا في كل مرة. الأنسب للأسعار والسياسات.',
          },
          BALANCED: {
            LABEL: 'متوازن',
            DESCRIPTION:
              'يبقى ضمن ما أخبرته به، لكنه يصوغ كل ردّ بأسلوب طبيعي. يناسب معظم الأعمال.',
          },
          CREATIVE: {
            LABEL: 'أكثر انفتاحًا',
            DESCRIPTION:
              'يتحدّث بحرية أكبر ويسدّ الفجوات الصغيرة بكلماته. أكثر حيوية، وأكثر احتمالًا لقول شيء لم تخطّط له.',
          },
        },
        EXACT: 'القيمة الحالية بالضبط: {value}',
        ADVANCED: 'إعدادات متقدمة',
        ADVANCED_LABEL: 'القيمة بالضبط',
        ADVANCED_HINT:
          'رقم بين {min} و{max}. الأقل أكثر ثباتًا، والأعلى أكثر تنوّعًا. ومعظم الأعمال لا تحتاج إلى تغييره.',
      },
      HAND_TO: {
        HEADING: 'حوّل المحادثات إلى',
        DESCRIPTION:
          'الشخص اللي يستلم المحادثة المحوّلة يوصله تنبيه، والمحادثة تنعلّم عشان يلقاها فريقك.',
        ANYONE: 'أي أحد متصل',
        ANYONE_BODY: 'أول واحد متصل من الفريق يستلمها.',
        PERSON: 'شخص محدد',
        PERSON_BODY: 'دايماً نفس الشخص.',
        TEAM: 'فريق',
        TEAM_BODY: 'أحد متصل من هذا الفريق.',
        PERSON_LABEL: 'منو',
        TEAM_LABEL: 'أي فريق',
        FALLBACK_LABEL: 'إذا ما أحد متصل، عطها لـ',
        CHOOSE: 'اختار شخص',
        CHOOSE_TEAM: 'اختار فريق',
        UNSET: 'ما اخترت أحد للحين، فالمحادثات المحوّلة تنتظر أي أحد يستلمها.',
        NOT_MEMBER: '{name} ما يشوف {channels}، فيمكن تفوته محادثات من هناك.',
        PEOPLE_FAILED: 'ما قدرنا نحمّل فريقك الحين.',
      },
      HANDOVER: {
        HEADING: 'حين يتولّى موظف المحادثة',
        DESCRIPTION:
          'يستطيع مساعدك الردّ بنفسه، وتحويل المحادثة إلى فريقك حين لا يستطيع المساعدة.',
        AUTO_LABEL: 'الردّ على العملاء تلقائيًا',
        AUTO_ON: 'مساعدك يردّ على الرسائل الجديدة، وفريقك يرى كل شيء كما هو.',
        AUTO_OFF:
          'لا شيء يُجاب عليه تلقائيًا. كل رسالة تنتظر ردّ موظف من فريقك.',
        MESSAGE_LABEL: 'ماذا يقول حين يتولّى موظف المحادثة',
        MESSAGE_HINT:
          'يرى العملاء الذين يكتبون بالعربية الرسالة العربية، ويرى الباقون الرسالة الإنجليزية. إذا تركت خانة فارغة تُستخدم الأخرى، وإذا تركتهما فارغتين تُرسل رسالة قصيرة جاهزة.',
        MESSAGE_EN_LABEL: 'الرسالة بالإنجليزية',
        MESSAGE_EN_PLACEHOLDER:
          'One moment please — I am passing you to someone from our team.',
        MESSAGE_AR_LABEL: 'الرسالة بالعربية',
        MESSAGE_AR_PLACEHOLDER: 'لحظة من فضلك، سأحوّلك إلى أحد موظفي فريقنا.',
        CLOSED_LABEL: 'عندما تكون مغلقًا',
        CLOSED_HINT:
          'تُرسل بدل الرسالة أعلاه حين تُحوَّل محادثة إلى فريقك خارج ساعات العمل. اتركها فارغة ونقول متى سيرد فريقك.',
        CLOSED_EN_LABEL: 'عند الإغلاق، بالإنجليزية',
        CLOSED_EN_PLACEHOLDER:
          'Thanks for your message. Our team is away now and will reply when we open.',
        CLOSED_AR_LABEL: 'عند الإغلاق، بالعربية',
        CLOSED_AR_PLACEHOLDER:
          'شكرًا لرسالتك. فريقنا غير متاح الآن، وسيرد عليك عند فتحنا.',
      },
      LANGUAGES: {
        HEADING: 'عملاؤك يكتبون بـ',
        DESCRIPTION:
          'يردّ مساعدك باللغة التي تختارها هنا. ومع «الاثنتين» يردّ على كل عميل باللغة التي يكتب بها، وتكتب كلماتك باللغتين.',
        OPTIONS: {
          AR: {
            LABEL: 'العربية',
            DESCRIPTION: 'كل الردود بالعربية، وتكتب الكلمات بالعربية فقط.',
          },
          EN: {
            LABEL: 'الإنجليزية',
            DESCRIPTION:
              'كل الردود بالإنجليزية، وتكتب الكلمات بالإنجليزية فقط.',
          },
          BOTH: {
            LABEL: 'الاثنتين',
            DESCRIPTION:
              'يُردّ على كل عميل باللغة التي يكتب بها. والكلمة الناقصة بإحدى اللغتين يُنبَّه إليها، ولا تمنع الحفظ أبدًا.',
          },
        },
      },
      ARABIC_VOICE: {
        HEADING: 'كيف يتكلم بالعربية',
        DESCRIPTION:
          'المعلومات والأسماء والأسعار تبقى كما كتبتها تمامًا. هذا يغيّر طريقة الكلام بالعربية فقط.',
        REGISTER_LABEL: 'طريقة الكتابة',
        REGISTER: {
          MATCH: {
            LABEL: 'مثل العميل',
            DESCRIPTION:
              'باللهجة لمن يكتب باللهجة، وبالفصحى لمن يكتب بالفصحى، وبلهجة كويتية مهذبة إذا لم يتبيّن ذلك.',
          },
          FORMAL: {
            LABEL: 'الفصحى دائمًا',
            DESCRIPTION: 'فصحى واضحة ومهذبة، حتى لو كتب العميل باللهجة.',
          },
        },
        SPEAKS_AS_LABEL: 'يتكلم بصيغة',
        SPEAKS_AS: {
          NONE: {
            LABEL: 'بدون تحديد',
            DESCRIPTION:
              'يتجنب الكلمات التي تحدد المؤنث أو المذكر قدر الإمكان.',
          },
          SHE: {
            LABEL: 'المؤنث',
            DESCRIPTION: 'مثل: موجودة، سعيدة بخدمتك.',
          },
          HE: {
            LABEL: 'المذكر',
            DESCRIPTION: 'مثل: موجود، سعيد بخدمتك.',
          },
        },
      },
      SAVE: 'حفظ التغييرات',
      LEAVE: {
        TITLE: 'تخرج من دون حفظ؟',
        BODY: 'عندك تعديلات لم تُحفظ بعد. إذا خرجت الآن ستُفقد.',
        CONFIRM: 'اخرج من دون حفظ',
        CANCEL: 'ابقَ في هذه الصفحة',
      },
      PERMISSIONS: {
        HEADING: 'ما يُسمح له بفعله داخل المحادثة',
        DESCRIPTION:
          'كل بند مطفأ حتى تشغّله. جرّبه في صفحة «جرّبه» قبل أن تأتمنه على عملاء حقيقيين.',
        ITEMS: {
          CLOSE: {
            LABEL: 'إنهاء المحادثة',
            BODY: 'حين يحصل العميل على ما يريد، يستطيع إغلاق المحادثة حتى لا يبقى فريقك ينظر إلى شيء انتهى.',
          },
          ASSIGN: {
            LABEL: 'تحويل المحادثة إلى أحد',
            BODY: 'يستطيع تحويل المحادثة إلى فريق أو شخص تختاره أدناه — ولا أحد غيرهم.',
          },
          LABELS: {
            LABEL: 'وضع وسوم على المحادثة',
            BODY: 'يستطيع وضع الوسوم التي تختارها أدناه، لتجدها لاحقًا بسهولة.',
          },
          NOTE: {
            LABEL: 'ترك ملاحظة لفريقك',
            BODY: 'يستطيع كتابة ملاحظة على المحادثة لفريقك. ولا يراها عميلك أبدًا.',
          },
          CONTACT: {
            LABEL: 'حفظ معلومات العميل',
            BODY: 'حين يخبره العميل باسمه أو بريده أو هاتفه أو بأي معلومة تختارها أدناه، يحفظها في ملف العميل — ويستطيع وضع وسوم عليه لتجده لاحقًا بسهولة.',
          },
        },
        ON: 'مشغّل',
        OFF: 'مطفأ',
        SAVING: 'يُحفظ…',
        SAVE: 'حفظ',
        SAVED: 'تم الحفظ. يتبع مساعدك هذا من الرسالة القادمة.',
        SAVE_FAILED: 'تعذّر الحفظ. لم يتغيّر شيء — حاول مرة أخرى.',
        SWITCH_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
        GUIDANCE: {
          LABEL: 'متى يفعل هذا؟',
          PLACEHOLDER:
            'فقط بعد أن يقول العميل إن مشكلته انحلّت، ولا يفعلها أبدًا وهو ينتظر توصيل طلبه.',
          HINT: 'اختياري. اتركه فارغًا فيقرّر بنفسه.',
        },
        CHOICES: {
          TEAMS_HEADING: 'إلى من يُسمح له بتحويل المحادثة',
          TEAMS_DESCRIPTION:
            'لا يختار إلا من هذه القائمة. لا تختر أحدًا فلا يستطيع التحويل إلى أحد.',
          LABELS_HEADING: 'الوسوم التي يُسمح له باستخدامها',
          LABELS_DESCRIPTION:
            'لا يستخدم غيرها. لا تختر شيئًا فلا يستطيع وسم شيء.',
          TEAM: 'فريق',
          PERSON: 'شخص',
          CHOSEN:
            'لم يُختر شيء | اختير واحد | اختير اثنان | اختير {count} | اختير {count} | اختير {count}',
          LOADING: 'نقرأ فرقك وأشخاصك ووسومك…',
          NONE_ASSIGN:
            'لا توجد لديك فرق ولا أشخاص بعد، فليس هناك من يحوّل إليه المحادثة.',
          NONE_LABELS: 'لا توجد لديك وسوم بعد، فليس هناك ما يضعه على المحادثة.',
          UNREADABLE:
            'تعذّر علينا قراءة فرقك وأشخاصك ووسومك الآن. وما اخترته سابقًا محفوظ كما هو — ولا شيء هنا يستطيع تغييره حتى تُقرأ القائمة.',
          UNREADABLE_KEPT: 'المحفوظ: {names}',
          UNREADABLE_RETRY: 'حاول مرة أخرى',
          INERT:
            'هذا مشغّل، لكن لم يُختر شيء — فلا يستطيع مساعدك فعله بعد. اختر واحدًا على الأقل أدناه.',
        },
        CONTACT_DETAILS: {
          FIELDS_HEADING: 'المعلومات التي يُسمح له بحفظها',
          FIELDS_DESCRIPTION:
            'لا يحفظ إلا ما يقوله العميل فعلًا، وفقط في المعلومات التي تختارها.',
          FIELDS_CONFLICT:
            'إذا كان البريد أو رقم الهاتف يخصّ عميلًا آخر بالفعل، فإنه يتركه كما هو ويكتب ملاحظة خاصة على المحادثة، ليتحقّق فريقك ممّا إذا كان الشخص نفسه. وكل ما عداه ممّا ذكره العميل يُحفظ.',
          LABELS_HEADING: 'الوسوم التي يُسمح له بوضعها على العميل',
          LABELS_DESCRIPTION:
            'توضع هذه الوسوم على ملف العميل لا على المحادثة، لتصل إليه لاحقًا. لا تختر شيئًا فلا يضع وسمًا على أحد.',
          ADD_DETAIL: 'تحتاج معلومة غير موجودة هنا؟ أضفها من الإعدادات.',
          STANDARD: {
            NAME: 'الاسم',
            EMAIL: 'البريد الإلكتروني',
            PHONE_NUMBER: 'رقم الهاتف',
          },
          TYPES: {
            STANDARD: 'أساسية',
            TEXT: 'نص',
            NUMBER: 'رقم',
            CURRENCY: 'مبلغ',
            PERCENT: 'نسبة',
            LINK: 'رابط',
            DATE: 'تاريخ',
            LIST: 'اختيار من قائمة',
            CHECKBOX: 'نعم أو لا',
          },
          LOADING: 'نقرأ المعلومات والوسوم التي تحتفظ بها…',
          UNREADABLE:
            'تعذّر علينا قراءة المعلومات والوسوم التي تحتفظ بها الآن. وما اخترته سابقًا محفوظ كما هو — ولا شيء هنا يستطيع تغييره حتى تُقرأ القائمة.',
          UNREADABLE_KEPT: 'المحفوظ: {names}',
          UNREADABLE_RETRY: 'حاول مرة أخرى',
          NONE_LABELS: 'لا توجد لديك وسوم بعد، فليس هناك ما يضعه على العميل.',
          CHOSEN:
            'لم يُختر شيء | اختير واحد | اختير اثنان | اختير {count} | اختير {count} | اختير {count}',
          NO_SERVICE_ACCOUNT:
            'هذا مشغّل، لكنه يحتاج إلى ربط شيء إضافي من جهتنا قبل أن يحفظ أي شيء. تواصل معنا وسنربطه.',
        },
      },
      SAVED: 'تم الحفظ. سيستخدم مساعدك الصياغة الجديدة من الرسالة التالية.',
      SAVE_FAILED: 'تعذّر الحفظ. لم يتغيّر شيء — حاول مرة أخرى.',
      SAVE_PARTIAL:
        'تم الحفظ — ما عدا واحدًا مما يُسمح له بفعله. الرسالة الظاهرة عليه تقول ما الذي حدث.',
      STATUS: {
        IDLE: 'كل شيء محفوظ',
        IDLE_DETAIL: 'مساعدك يستخدم هذا الآن.',
        DIRTY: 'لم يُحفظ بعد',
        DIRTY_DETAIL: 'بانتظار الحفظ: {sections}',
        SAVING: 'جارٍ الحفظ…',
        SAVED: 'تم الحفظ في {time}',
        SAVED_DETAIL: '{sections} — يستخدمها مساعدك من الرسالة التالية.',
        ERROR: 'تعذّر الحفظ',
        ERROR_DETAIL:
          'لم يتغيّر شيء، وما كتبته ما زال في الصفحة. حاول مرة أخرى.',
        PARTIAL: 'تم الحفظ، لكن شيئًا واحدًا لم يُحفظ',
        PARTIAL_DETAIL:
          'حُفظ: {sections}. وواحد مما يُسمح له بفعله لم يُحفظ — الرسالة الظاهرة عليه تقول أيّها.',
      },
      ERRORS: {
        EMPTY_PROMPT: 'أخبر مساعدك من هو قبل الحفظ.',
        LONG_PROMPT: 'النص طويل جدًا. أبقِه دون {limit}.',
        STYLE_RANGE: 'أدخل رقمًا بين {min} و{max}.',
      },
    },
    ANSWERS: {
      TITLE: 'الإجابات',
      SPOTLIGHT:
        'يجيب مساعدك مما تضعه هنا فقط — قائمة أسعارك، وشروط التوصيل لديك، وصفحات موقعك. أضف مصدرًا فنقرأه ونحوّله إلى أسئلة وإجابات، ويبدأ باستخدامها خلال دقائق.',
      ADD: 'أضف معرفة',
      TEAM: {
        HEADING: 'اقتراحات فريقك',
        DESCRIPTION:
          'ردود يشوف فريقك إن المساعد لازم يعرفها. اقبل أي واحد عشان تعلّمه.',
        BY: 'اقترحه {name}',
        QUESTION_LABEL: 'إذا سأل العميل',
        ANSWER_LABEL: 'المساعد يقول',
        ACCEPT: 'اقبل',
        EDIT: 'عدّل',
        DISMISS: 'تجاهل',
        ACCEPT_EDITED: 'اقبل بتعديلاتي',
        CANCEL: 'إلغاء',
        ACCEPTED: 'تعلّمه. المساعد يستخدمه من رسالته الجاية.',
        DISMISSED: 'تجاهلناه. ما تغيّر شي.',
        GONE: 'أحد قرر بخصوص هذا من قبل.',
        FAILED: 'ما قدرنا نسوي هذا الحين. ما تغيّر شي. حاول مرة ثانية.',
        UNAVAILABLE: 'ما قدرنا نحمّل اقتراحات فريقك الحين.',
      },
      EMPTY: {
        TITLE: 'لا يوجد ما يقرأه مساعدك بعد',
        BODY: 'أعطه قائمة أسعارك أو شروط التوصيل أو موقعك، وسيبدأ بالإجابة منها خلال دقائق. ويمكنك إضافة ما تشاء في أي وقت.',
      },
      COUNT: {
        NONE: 'لا توجد إجابات بعد',
        ONE: 'إجابة واحدة',
        TWO: 'إجابتان',
        MANY: 'لا إجابات | إجابة واحدة | إجابتان | {count} إجابات | {count} إجابة | {count} إجابة',
      },
      LIBRARY: {
        HEADING: 'ما قرأه مساعدك',
        DESCRIPTION:
          'افتح أيًا منها لترى الأسئلة التي تعلّمها، والنص الذي تعلّمها منه.',
        UPDATED: 'آخر تحديث {when}',
        ADDED: 'أُضيف {when}',
        NOTHING_LEARNED: 'لم يتعلّم شيئًا من هذا المصدر.',
        UNAVAILABLE: 'تعذّر عرض ما قرأه مساعدك.',
      },
      WORKING: {
        HEADING: 'أُضيف للتو',
        DESCRIPTION: 'يمكنك إغلاق الصفحة — تستمر القراءة من دونك.',
        FAILED_HEADING: 'تعذّرت قراءتها',
        FAILED_DESCRIPTION:
          'لم يُحفظ منها شيء. أضِف أيًا منها من جديد متى شئت، أو أزِله.',
        QUEUED: 'بانتظار البدء',
        QUEUED_BODY: 'يبدأ بعد لحظات.',
        CRAWLING: 'نقرأ موقعك',
        CRAWLING_BODY:
          'الصفحات المقروءة حتى الآن: {count}. | الصفحات المقروءة حتى الآن: {count}. | الصفحات المقروءة حتى الآن: {count}. | الصفحات المقروءة حتى الآن: {count}. | الصفحات المقروءة حتى الآن: {count}. | الصفحات المقروءة حتى الآن: {count}.',
        GENERATING: 'نكتب الإجابات',
        GENERATING_BODY:
          'المكتوب حتى الآن: {count}. | المكتوب حتى الآن: {count}. | المكتوب حتى الآن: {count}. | المكتوب حتى الآن: {count}. | المكتوب حتى الآن: {count}. | المكتوب حتى الآن: {count}.',
        RUNNING: 'نقرأه',
        RUNNING_BODY: 'يستغرق هذا عادةً دقيقة أو دقيقتين.',
        READY: 'جاهز للاستخدام',
        READY_BODY: 'مساعدك يجيب من هذا المصدر الآن.',
        READY_EMPTY:
          'لم يكن فيه ما يتعلّمه مساعدك. جرّب صفحة تحتوي على نص أكثر.',
        READY_NOTE: 'ملاحظة واحدة: {warning}',
        FAILED: 'تعذّرت قراءة هذا المصدر',
        FAILED_BODY: 'لم يُحفظ منه شيء، لذلك لا يستطيع مساعدك استخدامه بعد.',
        STALLED: 'يستغرق هذا وقتًا أطول من المعتاد',
        STALLED_BODY: 'أوقفنا المتابعة. لم يضع شيء. تحقّق مرة أخرى متى شئت.',
        CHECK: 'تحقّق مرة أخرى',
        OPEN: 'افتحه',
        AGAIN: 'أضفه من جديد',
        DISMISS: 'إزالة',
        DISMISS_LABEL: 'إزالة «{title}»',
        DISMISS_FAILED: 'تعذّرت الإزالة الآن. حاول مرة أخرى.',
        UNAVAILABLE: 'تعذّرت معرفة ما يُقرأ الآن.',
      },
      REASON: {
        SITE_BUSY: 'كان الموقع مشغولًا. حاول مرة أخرى بعد ساعة.',
        SITE_BLOCKED:
          'لم يسمح لنا الموقع بالدخول. جرّب صفحة يفتحها أي شخص دون تسجيل دخول.',
        SITE_UNREACHABLE:
          'تعذّر فتح تلك الصفحة. تأكّد من العنوان وحاول مرة أخرى.',
        TIMED_OUT:
          'استغرقت القراءة وقتًا طويلًا. جرّب صفحة أصغر أو ملفًا أقصر.',
        FILE_UNREADABLE: 'لم يكن في الملف نص نستطيع قراءته.',
        TOO_LARGE:
          'هذا أكبر من أن يُقرأ دفعة واحدة. جرّب ملفًا أصغر أو صفحة واحدة.',
        NOTHING_FOUND: 'لم يكن فيه ما نتعلّم منه. جرّب صفحة فيها كتابة أكثر.',
        PAUSED_BY_LIMITS:
          'توقّفت القراءة بسبب حدّ الإنفاق. حاول مرة أخرى بعد رفعه.',
        NO_CREDITS:
          'لم يكن الرصيد كافيًا لقراءته. حاول مرة أخرى بعد تعبئة الرصيد.',
        OUR_SIDE: 'حدث خطأ من جهتنا. حاول مرة أخرى بعد دقائق.',
        UNKNOWN: 'تعذّرت قراءته. حاول مرة أخرى أو أضفه بطريقة أخرى.',
      },
      NOTE: {
        NOTHING_FOUND: 'لم يكن فيه ما يستطيع مساعدك التعلّم منه.',
        PAGE_LIMIT:
          'قرأنا أقصى عدد من الصفحات في المرة الواحدة. أضف الصفحات الأهم وحدها.',
        PARTIAL_CRAWL: 'تعذّرت قراءة بعض الصفحات، فقد تنقص بعض الإجابات.',
        UNKNOWN: 'تعذّرت قراءة جزء منه.',
      },
      DIALOG: {
        TITLE: 'أضف معرفة',
        DESCRIPTION:
          'اختر مصدر هذه المعرفة. يمكنك إضافة ما تشاء، واحدًا تلو الآخر.',
        CHOICES: {
          TEXT: {
            LABEL: 'اكتبها أو الصقها',
            DESCRIPTION:
              'الأنسب لأسعارك أو أوقات دوامك أو قاعدة يمكنك كتابتها في بضعة أسطر.',
          },
          URL: {
            LABEL: 'اقرأ موقعي',
            DESCRIPTION:
              'نتتبّع الصفحات ضمن عنوان واحد ونقرأ ما هو مكتوب فيها.',
          },
          PDF: {
            LABEL: 'ارفع ملف PDF',
            DESCRIPTION:
              'قائمة طعام أو كتالوج أو سياسة لديك أصلًا كملف. أما ملف PDF المكوَّن من صور للصفحات فلا يمكن قراءته.',
          },
        },
        NAME_LABEL: 'سمِّه',
        NAME_HINT: 'أنت وحدك من يراه، وبه تجده لاحقًا.',
        NAME_PLACEHOLDER: 'قائمة أسعار أغسطس',
        NAME_REQUIRED: 'أعطه اسمًا لتجده لاحقًا.',
        NAME_LONG: 'الاسم طويل جدًا. أبقِه دون {limit}.',
        TEXT_REQUIRED: 'اكتب أو الصق ما تريد أن يتعلّمه مساعدك.',
        TEXT_LONG: 'النص أطول من أن يُضاف دفعة واحدة. قسّمه إلى اثنين.',
        URL_INVALID: 'أدخل العنوان كاملًا مبتدئًا بـ https://',
        FILE_REQUIRED: 'اختر ملف PDF لإضافته.',
        FILE_LARGE: 'حجم الملف أكبر من {size} ميغابايت. جرّب ملفًا أصغر.',
        TEXT_LABEL: 'ما الذي يجب أن يعرفه؟',
        TEXT_HINT:
          'اكتبها كما تشرحها لموظف جديد. الأسطر القصيرة أسهل عليه من الفقرات الطويلة.',
        TEXT_PLACEHOLDER:
          'دهن العود المعتّق ١٢ مل — ٤٥ د.ك\nبخور السلام ٥٠ غرام — ١٢ د.ك\nالتوصيل ٢ د.ك داخل الكويت، ومجاني فوق ٣٠ د.ك.\nنوصّل خلال يومين من السبت إلى الخميس.',
        URL_LABEL: 'عنوان موقعك',
        URL_HINT:
          'ابدأه بـ https://. نقرأ الصفحات ضمن هذا العنوان، لا الإنترنت كله.',
        URL_PLACEHOLDER: 'https://baytaloud.com',
        FILE_LABEL: 'ملف PDF لديك',
        FILE_HINT: 'ملف واحد، حتى {size} ميغابايت.',
        FILE_CHOOSE: 'اختر ملفًا',
        FILE_CHANGE: 'اختر ملفًا آخر',
        FILE_NONE: 'لم تختر ملفًا بعد',
        SUBMIT: 'أضفه',
        CANCEL: 'إلغاء',
        QUEUED: 'تمت الإضافة. نقرأه الآن.',
        DEDUPED: 'أضفت هذا من قبل — نعرض لك النسخة التي تُقرأ الآن.',
        FAILED: 'تعذّرت الإضافة الآن. لم يُحفظ شيء — حاول مرة أخرى.',
      },
      SOURCE: {
        TITLE: 'المصدر',
        NOT_FOUND: 'لم يعد هذا المصدر موجودًا.',
        UNAVAILABLE: 'تعذّر فتح هذا المصدر الآن.',
        SPOTLIGHT:
          'هذه هي الأسئلة التي تعلّمها مساعدك من هذا المصدر. وهو يبحث فيها لا في النص الأصلي — فإن أجاب إجابة خاطئة، فهنا ترى السبب.',
        ANSWERS_HEADING: 'ما الذي تعلّمه',
        ANSWERS_DESCRIPTION:
          'كل سؤال قد يسأله عميل، والإجابة التي سيقدّمها مساعدك.',
        ANSWERS_NONE:
          'لم يُنتج هذا المصدر أي أسئلة، فلا يستطيع مساعدك الإجابة منه بعد. المصادر التي تحتوي نصًا أكثر تعطي نتيجة أفضل.',
        ANSWERS_UNAVAILABLE: 'تعذّرت قراءة ما تعلّمه من هذا المصدر.',
        SHOWING:
          'نعرض أول {shown} من {total}. | نعرض أول {shown} من {total}. | نعرض أول {shown} من {total}. | نعرض أول {shown} من {total}. | نعرض أول {shown} من {total}. | نعرض أول {shown} من {total}.',
        ORIGINAL: 'النص الأصلي',
        ORIGINAL_HINT: 'هذا ما قرأناه تمامًا، ولم يتغيّر منه شيء.',
        REMOVE: 'احذف هذا المصدر',
        REMOVE_TITLE: 'هل تحذف هذا المصدر؟',
        REMOVE_BODY:
          'يُحذف معه كل ما تعلّمه مساعدك منه، ويتوقّف عن الإجابة منه من الرسالة التالية. يمكنك إرجاعه من «حُذف مؤخرًا» خلال {period}.',
        RESERVED_NOTE:
          'هذه هي الإجابات التي حفظتها واحدة واحدة، ولا تُحذف كلها معًا أبدًا.',
        REMOVE_CONFIRM: 'احذفه',
        REMOVED: 'تم الحذف. يتوقّف مساعدك عن استخدامه من الرسالة التالية.',
        REMOVE_FAILED: 'تعذّر الحذف. لم يتغيّر شيء — حاول مرة أخرى.',
      },
    },
    ACTIVITY: {
      TITLE: 'النشاط',
      SPOTLIGHT:
        'كل ما في هذه الصفحة هو ما فعله مساعدك فعلًا، لا ما هو مُعدّ لفعله. اختر الفترة من الأعلى وستتغيّر معها الأرقام والرسم والمحادثات جميعًا.',
      RANGE: {
        LABEL: 'الفترة',
        DAYS: 'اليوم | آخر يوم | آخر يومين | آخر {days} أيام | آخر {days} يومًا | آخر {days} يوم',
        UPDATING: 'جارٍ التحديث…',
      },
      NUMBERS: {
        CONVERSATIONS: 'المحادثات',
        CONVERSATIONS_HINT: 'عملاء تحدّث معهم مساعدك.',
        HANDOFFS: 'حُوّلت إلى فريقك',
        HANDOFFS_HINT:
          '{handoffs} من {total} وصلت إلى موظف. | {handoffs} من {total} وصلت إلى موظف. | {handoffs} من {total} وصلت إلى موظف. | {handoffs} من {total} وصلت إلى موظف. | {handoffs} من {total} وصلت إلى موظف. | {handoffs} من {total} وصلت إلى موظف.',
        UNANSWERED: 'أسئلة بلا إجابة',
        UNANSWERED_HINT: 'أسئلة مختلفة، وكل واحد منها مذكور بالأسفل.',
        WAS: 'كانت {value} في الفترة التي قبلها. | كانت {value} في اليوم الذي قبله. | كانت {value} في اليومين اللذين قبلهما. | كانت {value} في الأيام الـ{days} التي قبلها. | كانت {value} في الـ{days} يومًا التي قبلها. | كانت {value} في الـ{days} يوم التي قبلها.',
        NO_COMPARE: 'لا يوجد ما يُقارن به بعد.',
      },
      TREND: {
        HEADING: 'يومًا بيوم',
        DESCRIPTION:
          'كم عميلًا راسلك في كل يوم، وكم منهم انتهى به الأمر مع موظف من فريقك.',
        ANSWERED: 'أجاب عنها مساعدك',
        HANDED: 'حُوّلت إلى فريقك',
        BAR: '{day} — {conversations} محادثة، منها {handoffs} حُوّلت إلى فريقك',
        ARIA: 'المحادثات اليوم. | المحادثات خلال آخر يوم. | المحادثات في كل يوم خلال آخر يومين. | المحادثات في كل يوم خلال آخر {days} أيام. | المحادثات في كل يوم خلال آخر {days} يومًا. | المحادثات في كل يوم خلال آخر {days} يوم.',
        NONE: 'لم يراسلك أحد خلال هذه الفترة.',
      },
      QUESTIONS: {
        HEADING: 'أسئلة لم يستطع الإجابة عنها',
        DESCRIPTION:
          'سأل عملاؤك عن هذه ولم تكن لدى مساعدك إجابة. وكل سؤال تجيب عنه لن يتعثّر فيه مرة أخرى.',
        ASKED:
          'لم يتكرّر | تكرّر مرة واحدة | تكرّر مرتين | تكرّر {count} مرات | تكرّر {count} مرة | تكرّر {count} مرة',
        VERB: 'صحّحه',
        CLEAR_TITLE: 'أجاب عن كل ما سُئل عنه',
        CLEAR_BODY:
          'لم يسأل أحد مساعدك عن شيء لم تكن لديه إجابة عنه خلال هذه الفترة.',
      },
      CONVERSATIONS: {
        HEADING: 'ماذا قال فعلًا',
        DESCRIPTION:
          'أحدث المحادثات التي تولّاها مساعدك. افتح واحدة لتقرأها تمامًا كما رآها عميلك.',
        MESSAGES:
          'لا رسائل | رسالة واحدة | رسالتان | {count} رسائل | {count} رسالة | {count} رسالة',
        HANDED: 'حُوّلت',
        OPEN: 'اقرأها',
        CLOSE: 'إغلاق',
        CUSTOMER: 'عميلك',
        ASSISTANT: 'مساعدك',
        NO_REPLY: 'لم يردّ على هذه.',
        USED_SAVED: 'أجاب من إجاباتك المحفوظة',
        HANDED_HERE: 'حُوّلت إلى فريقك هنا',
        // Task review item 11: where a passed-on chat went, and why.
        HANDED_TO: {
          SENT: 'راحت إلى {who}.',
          BY_STEP: 'راحت إلى {who}، حسب خطوة المهمة.',
          GONE_PERSON:
            'الشخص اللي تحدده خطوة المهمة ما عاد موجود في حسابك، فانستخدم «حوّل المحادثات إلى».',
          GONE_TEAM:
            'الفريق اللي تحدده خطوة المهمة ما عاد موجود، فانستخدم «حوّل المحادثات إلى».',
        },
        WITHHELD: 'تم حجب الرد — تم تحويل العميل لأحد أفراد الفريق',
        RETRIED: 'أعيدت صياغته مرة قبل الإرسال',
        LOADING: 'جارٍ الفتح…',
        FAILED: 'تعذّر فتح هذه المحادثة الآن. حاول مرة أخرى.',
        TRUNCATED:
          'هذه المحادثة أطول مما يتّسع هنا. | هذه المحادثة أطول مما يتّسع هنا، فتظهر أول رسالة فقط. | هذه المحادثة أطول مما يتّسع هنا، فتظهر أول رسالتين. | هذه المحادثة أطول مما يتّسع هنا، فتظهر أول {count} رسائل. | هذه المحادثة أطول مما يتّسع هنا، فتظهر أول {count} رسالة. | هذه المحادثة أطول مما يتّسع هنا، فتظهر أول {count} رسالة.',
        NONE: 'لم يتولَّ مساعدك أي محادثة خلال هذه الفترة.',
      },
    },
    TASKS: {
      TITLE: 'المهام',
      ADD: 'أضف مهمة',
      TABS: {
        LABEL: 'المهام والقوائم',
      },
      SPOTLIGHT_TITLE: 'ما هي المهمة',
      SPOTLIGHT:
        'الإجابات تخبر مساعدك بما يقوله. أما المهمة فتخبره بما يفعله: خطوات قصيرة يمشي فيها مع العميل بالترتيب حتى ينتهي العمل — استلام طلب توصيل، أو معرفة أين وصل طلب، أو حجز موعد قياس. أنت تكتب متى تبدأ، وما هي الخطوات، وما الذي يجب تدوينه في أثنائها. خارج المهمة يجيب مساعدك بحرية، وداخلها يلتزم بخطواتك.',
      EXAMPLE: {
        HEADING: 'مثال',
        TRIGGER: 'يكتب العميل {message}',
        TRIGGER_MESSAGE: '«أبي أطلب عود وتوصيل للجهراء»',
        STEP_1: 'اسأل في أي منطقة في الكويت هو.',
        STEP_2: 'اسأل ماذا يريد وكم الكمية.',
        STEP_3: 'أعد عليه المبلغ ويوم التوصيل، وانتظر موافقته.',
        RESULT:
          'في النهاية يصبح الطلب مدوّنًا لديك: المنطقة، وما طلبه، واليوم الذي اتفقتم عليه. ولا ينهي مساعدك المهمة قبل أن تكتمل الثلاثة.',
      },
      LIST: {
        NOT_ON: 'غير متاحة على {channel}',
        HEADING: 'مهامك',
        DESCRIPTION:
          'مُفعّلة تعني أن مساعدك سيستخدمها. ومتوقفة تعني بقاءها هنا دون استخدام.',
        STARTS_WHEN: 'تبدأ حين: {trigger}',
        CONNECTIONS:
          'أنظمتك: {count} | أنظمتك: {count} | أنظمتك: {count} | أنظمتك: {count} | أنظمتك: {count} | أنظمتك: {count}',
        USAGE_REACHED:
          'لم تصل إليها أي محادثة خلال الأيام السبعة الأخيرة. | وصلت إليها محادثة واحدة خلال الأيام السبعة الأخيرة. | وصلت إليها محادثتان خلال الأيام السبعة الأخيرة. | وصلت إليها {count} محادثات خلال الأيام السبعة الأخيرة. | وصلت إليها {count} محادثة خلال الأيام السبعة الأخيرة. | وصلت إليها {count} محادثة خلال الأيام السبعة الأخيرة.',
        USAGE_FINISHED:
          'لم تكتمل أي منها. | اكتملت واحدة. | اكتملت اثنتان. | اكتملت {count}. | اكتملت {count}. | اكتملت {count}.',
        UPDATED: 'عُدّلت {when}',
        UPDATED_JUST_NOW: 'الآن',
        EDIT: 'تعديل',
        ON: 'مشغّلة',
        OFF: 'مطفأة',
        SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
        UNAVAILABLE: 'تعذّر تحميل مهامك الآن.',
        LINKS: {
          CONNECT: 'اربط نظامًا',
          FORM: 'اجعلها نموذجًا',
          MENU: 'ابدأها من قائمة',
        },
      },
      EMPTY: {
        TITLE: 'لا توجد مهام بعد',
        BODY: 'يظلّ مساعدك يجيب على الأسئلة من دونها. أضف مهمة لكل عمل تريده أن يُتمّه حتى النهاية — طلب، أو حجز، أو شكوى.',
        ACTION: 'أضف أول مهمة',
      },
      REMOVE: {
        HEADING: 'حذف هذه المهمة',
        DESCRIPTION: 'يتوقف مساعدك عن استخدامها فورًا.',
        ACTION: 'احذف هذه المهمة',
        ACTION_SHORT: 'حذف',
        CONFIRM: 'حذف هذه المهمة؟',
        CONFIRM_BODY:
          'يمكنك إرجاعها من «حُذف مؤخرًا» في المهام خلال {period}. ويمكنك إطفاؤها بدلًا من ذلك.',
        CONFIRM_ACTION: 'نعم، احذفها',
        CANCEL: 'أبقِها',
        FAILED: 'تعذّر حذفها. لم يتغيّر شيء. حاول مرة أخرى.',
        REMOVED: 'حُذفت. يمكنك إرجاعها من «حُذف مؤخرًا».',
      },
      EDITOR: {
        NEW_TITLE: 'مهمة جديدة',
        FLOW: {
          LEAVE_WARNING: 'لديك تغييرات لم تُحفظ بعد. إن خرجت من الصفحة ضاعت.',
          STARTS_AT: 'تبدأ المهمة من',
          STARTS_AT_REQUIRED: 'اختر الخطوة التي تبدأ منها هذه المهمة.',
          ADD_HERE: 'أضف خطوة هنا',
          ADD: 'أضف خطوة',
          COUNT:
            'الخطوات: {count} من {limit} | الخطوات: {count} من {limit} | الخطوات: {count} من {limit} | الخطوات: {count} من {limit} | الخطوات: {count} من {limit} | الخطوات: {count} من {limit}',
          FULL: 'يمكن أن تحتوي المهمة على {limit} خطوة كحد أقصى.',
          CLEARS: {
            HEADING: 'عند رجوع المحادثة إلى هذه الخطوة، انسَ',
            DESCRIPTION:
              'أشِر إلى الأجوبة التي تسقط حين يصل العميل إلى هذه الخطوة مرة أخرى. ينساها واصلو عند الوصول إليها، فتُسأل الخطوات التي جمعتها من جديد. أما ما قاله العميل في الرسالة نفسها فيبقى.',
            FROM_STEP: 'تُجمع في الخطوة {number} · {name}',
            FROM_CONTACT: 'محفوظ في سجلّ العميل',
            OWN: 'هذا جواب هذه الخطوة نفسها — أشِر إليه ليُسأل السؤال نفسه مرة أخرى.',
            ORPHANS:
              'مُشار إليها ولم يبقَ في هذه المهمة ما يجمعها: {keys}. ولن تُنسي شيئًا حتى تُزيل الإشارة عنها.',
            NOTHING_TO_FORGET:
              'لا تجمع هذه المهمة أي أجوبة بعد، فليس هناك ما يُنسى. أضف «اسأل» أو «اختيار» أو خطوة تحفظ شيئًا من نظامك.',
            KEPT: 'تنسى هذه الخطوة {keys} عند الوصول إليها. وتنتهي المهمة هنا فلا فرق في ذلك، وإنما يُعرض لأنه محفوظ.',
          },
          TYPES: {
            ASK_LABEL: 'اسأل',
            ASK_ABOUT: 'اسأل العميل عن شيء واحد وانتظر جوابه.',
            CHOOSE_LABEL: 'اختيار',
            CHOOSE_ABOUT: 'اعرض خيارات قليلة كتبتها أنت.',
            CHOOSE_LIVE_LABEL: 'اختيار من نظامك',
            CHOOSE_LIVE_ABOUT:
              'اعرض خيارات مأخوذة من نظامك، مُحدَّثة في كل مرة — الأوقات الفاضية، أو الفروع، أو المقاسات المتوفرة.',
            CALL_LABEL: 'نفّذها في نظامك',
            CALL_ABOUT:
              'أرسل ما جُمع إلى نظامك. ولا يقول مساعدك شيئًا في هذه الخطوة.',
            TELL_LABEL: 'أخبِر',
            TELL_ABOUT: 'قل شيئًا واستمرّ فورًا.',
            HANDOFF_LABEL: 'سلّمها إلى شخص',
            HANDOFF_ABOUT: 'أعطِ المحادثة لأحد فريقك، مع ملاحظة عمّا حدث.',
            END_LABEL: 'إنهاء',
            END_ABOUT: 'انتهى العمل، ويرجع مساعدك إلى الإجابة بحرية.',
          },
          PICKER: {
            HEADING: 'ما الذي تفعله هذه الخطوة؟',
            CANCEL: 'تراجَع',
          },
          LINK: {
            OPTION: '{number} · {name}',
            UNNAMED: 'خطوة بلا اسم',
            END: 'أنهِ المهمة هنا',
            MISSING: 'خطوة لم تبقَ موجودة ({id})',
          },
          CARD: {
            UNNAMED: 'خطوة بلا اسم',
            UP: 'حرّكها لأعلى',
            DOWN: 'حرّكها لأسفل',
            REMOVE: 'احذف هذه الخطوة',
            NAME: 'بماذا تسمّي هذه الخطوة؟',
            NAME_HINT: 'يراه فريقك فقط، ولا يراه عملاؤك أبدًا.',
            NAME_PLACEHOLDER: 'أي فرع',
            THEN: 'ثم انتقل إلى',
            UNKNOWN_TYPE: 'نوع خطوة أحدث ({type})',
            UNKNOWN_BODY:
              'أُنشئت هذه الخطوة بنسخة من واصلو أحدث ممّا تعرفه هذه الصفحة، فلا يمكن تعديلها هنا. وهي محفوظة كما هي وتعمل كما هي. ويمكنك تحريكها أو حذفها.',
            UNKNOWN_SUMMARY: 'محفوظة كما هي',
            SUMMARY_CHOOSE:
              'لا خيارات · {instruction} | خيار واحد · {instruction} | خياران · {instruction} | {count} خيارات · {instruction} | {count} خيارًا · {instruction} | {count} خيار · {instruction}',
            SUMMARY_LIVE: 'خيارات من {connection}',
            SUMMARY_CALL: 'تُرسل إلى {connection}',
            SUMMARY_BLANK: 'لم يُكتب شيء بعد',
          },
          KINDS: {
            TEXT: 'أي كلام',
            NUMBER: 'رقم',
            DATE: 'تاريخ',
            TIME: 'وقت',
            EMAIL: 'بريد إلكتروني',
            PHONE: 'رقم هاتف',
          },
          ASK: {
            INSTRUCTION: 'ما الذي يعرفه في هذه الخطوة؟',
            INSTRUCTION_HINT:
              'اكتب ما تحتاجه لا نصّ السؤال. يسأله مساعدك بلغة العميل نفسها، ويعيد السؤال إن كان الجواب لا يناسب.',
            INSTRUCTION_PLACEHOLDER: 'إلى أي منطقة في الكويت يُوصَّل الطلب.',
            KIND: 'نوع الجواب',
            RULE_LABEL: 'لا تقبل إلا',
            RULE_MODE_NONE: 'أي شيء من هذا النوع',
            RULE_MODE_RANGE: 'رقمًا بين رقمين',
            RULE_MODE_ALLOWED: 'واحدًا من قائمة تكتبها',
            RULE_MODE_PATTERN: 'ما يوافق صيغة تكتبها',
            RULE_MIN: 'لا يقلّ عن',
            RULE_MAX: 'لا يزيد على',
            RULE_ALLOWED_HINT:
              'يشرح مساعدك القائمة إن قال العميل شيئًا آخر. وبخلاف الاختيار، لا يُعرض شيء منها كزرّ.',
            RULE_ALLOWED_ONE: 'الجواب المقبول {number}',
            RULE_ALLOWED_ADD: 'أضف جوابًا آخر',
            RULE_ALLOWED_REMOVE: 'احذف هذا الجواب',
            RULE_PATTERN: 'الصيغة التي يجب أن يوافقها',
            RULE_PATTERN_HINT:
              'للرموز والمراجع ذات الشكل الثابت. اتركها كما هي إن لم تكن تعرف عملها.',
          },
          SAVE: {
            HEADING: 'أين يُحفظ الجواب',
            DESCRIPTION:
              'اكتب واحدًا منهما أو كليهما. الاسم على اليمين هو ما تستقبل به أنظمتك هذا الجواب لاحقًا في المهمة. والحقل على اليسار يكتبه في سجلّ العميل نهائيًا.',
            CHAT_LABEL: 'اسمه في هذه المهمة',
            CHAT_HINT:
              'حروف لاتينية صغيرة وأرقام وشرطات سفلية. وهو الاسم الذي تستخدمه حين ترسله إلى أحد أنظمتك.',
            CONTACT_LABEL: 'حقل في سجلّ العميل',
            CONTACT_HINT:
              'اتركه فارغًا إلا إن كان يخصّ سجلّه — اسمه أو بريده أو رقمه.',
            NEITHER:
              'اكتب واحدًا منهما على الأقل، وإلا نُسي الجواب بمجرّد انتقال المهمة.',
          },
          CHOOSE: {
            INSTRUCTION: 'ما الذي يقوله في هذه الخطوة؟',
            INSTRUCTION_HINT:
              'يكتب مساعدك الرسالة بنفسه، وتظهر الخيارات أدناه تحتها كأزرار.',
            INSTRUCTION_PLACEHOLDER: 'اسأله أي فروعنا يريد.',
            OPTION: 'الخيار {number}',
            LABEL_EN: 'نصّه بالإنجليزية',
            LABEL_AR: 'نصّه بالعربية',
            COUNT:
              '{count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit}',
            THEN: 'ثم انتقل إلى',
            THEN_DEFAULT: 'إلى حيث تنتقل هذه الخطوة',
            UP: 'حرّكه لأعلى',
            DOWN: 'حرّكه لأسفل',
            REMOVE: 'احذف هذا الخيار',
            ADD: 'أضف خيارًا',
            FULL: 'يعرض واتساب {limit} خيارات في المرة كحد أقصى.',
          },
          LIVE: {
            INSTRUCTION: 'ما الذي يقوله في هذه الخطوة؟',
            INSTRUCTION_HINT:
              'يكتب مساعدك الرسالة بنفسه. وتأتي الخيارات من نظامك حين يصل العميل إلى هنا.',
            INSTRUCTION_PLACEHOLDER: 'اسأله أي هذه الأوقات يناسبه.',
            CONNECTION: 'أي أنظمتك',
            CONNECTION_PLACEHOLDER: 'اختر واحدًا',
            CONNECTION_OFF: '{name} (متوقف)',
            CONNECTIONS_UNAVAILABLE:
              'تعذّر عرض أنظمتك الآن، ولم يتغيّر شيء في هذه الخطوة.',
            IF_NONE: 'إن لم يرجع شيء',
            IF_NONE_REQUIRED:
              'اختر إلى أين ينتقل حين لا يوجد ما يُعرض. وبدونه يبقى العميل في انتظار.',
            HEADING: 'أي قائمة، وأي جزء منها يقرؤه العميل',
            DESCRIPTION:
              'شغّله مرة ونعرض لك ما رجع، فتشير إلى القائمة وإلى الجزأين اللذين نحتاجهما: ما يقرؤه العميل، وما نحفظه نحن.',
            NEEDS_CONNECTION: 'اختر أحد أنظمتك أولًا.',
            VALUES: 'قيم للتجربة',
            VALUES_HINT:
              'لهذه التجربة فقط. وفي المحادثة الحقيقية تأتي هذه من الأجوبة المجموعة قبلها.',
            RUNNING: 'جارٍ التجربة…',
            ACTION: 'جرّبه',
            LIVE_WARNING: 'هذا يستدعي نظامك فعلًا.',
            OK: 'أجاب ({status})',
            FAILED: 'أجاب نظامك بخطأ ({status})',
            NO_STATUS: 'لم يرجع شيء إطلاقًا',
            NO_LIST:
              'لم نجد قائمة صفوف في ذلك الجواب. اكتب مسارها أدناه إن كنت تعرفه.',
            LIST: 'موضع القائمة',
            LIST_HINT: 'مسارها في الجواب، بنقطة بين كل جزء وآخر.',
            LIST_PLACEHOLDER: 'اختر القائمة',
            WHOLE_LIST: 'الجواب كله هو القائمة',
            FORMAT: 'كيف يُكتب للعميل',
            FORMAT_TIME: 'كوقت',
            FORMAT_DATE: 'كتاريخ',
            FORMAT_PRICE: 'كسعر',
            FORMAT_TEXT: 'كما يأتي تمامًا',
            LABEL_FIELD: 'ما يقرؤه العميل',
            LABEL_FIELD_HINT: 'الجزء من كل صف الذي يفهمه الإنسان.',
            VALUE_FIELD: 'ما نحفظه نحن',
            VALUE_FIELD_HINT:
              'الجزء الذي يحتاجه نظامك مرجوعًا إليه، وهو عادةً مرجع.',
            FIELD_PLACEHOLDER: 'اختر واحدًا',
            VALUE_NOTE:
              'ما نحفظه لا يُعرض على العميل أبدًا، ولا يرى إلا الجزء الذي فوقه.',
            SAMPLE: 'نصّ الأزرار',
            MORE: 'المزيد…',
            MORE_NOTE:
              'رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها. | رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها. | رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها. | رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها. | رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها. | رجع {total}. ويعرض واتساب التسعة الأولى وخيار «المزيد…» الذي يجيب ما بعدها.',
            MISSING_VALUE:
              'أحد الصفوف على الأقل لا يحمل شيئًا في الجزء الذي نحفظه، والضغط عليه لا يحفظ شيئًا.',
            NO_ID:
              'لم نستطع مطابقة ذلك بأحد أنظمتك، فلا يمكن تجربته من هنا. احفظ الصفحة ثم افتحها مرة أخرى.',
            UNAVAILABLE: 'تعذّر تشغيل ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
          },
          CALL: {
            DESCRIPTION:
              'لا يقول مساعدك شيئًا في هذه الخطوة. يرسل واصلو ما لديه إلى نظامك ثم يستمرّ.',
            CONNECTION: 'أي أنظمتك',
            CONNECTION_PLACEHOLDER: 'اختر واحدًا',
            ON_WORKED: 'إن نجح، انتقل إلى',
            ON_WORKED_DEFAULT: 'إلى حيث تنتقل هذه الخطوة',
            ON_FAILED: 'إن فشل، انتقل إلى',
            ON_FAILED_DEFAULT: 'سلّمها إلى شخص',
            ON_FAILED_NOTE:
              'إن لم تختر شيئًا، سُلّمت المحادثة التي يفشل فيها هذا إلى أحد فريقك مع ملاحظة. ولا يرى العميل الخطأ أبدًا.',
          },
          INPUTS: {
            HEADING: 'ما يُرسل إليه',
            NONE: 'هذا لا يحتاج إلى إرسال شيء إليه.',
            UNKNOWN:
              'تعذّر معرفة ما يتوقّعه هذا. وما هو مضبوط هنا يبقى كما هو.',
            STALE: 'لم يعد ذلك النظام يطلب هذا. ويبقى حتى تحذفه.',
            REMOVE: 'احذف هذا',
            MODE: 'من أين يأتي',
            MODE_ANSWER: 'جواب جُمع قبله',
            MODE_FIXED: 'القيمة نفسها في كل مرة',
            MODE_LANG: 'لغة المحادثة',
            LANG_NOTE:
              'يرسل «ar» إذا كانت المحادثة بالعربي و«en» إذا كانت بالإنجليزي.',
            FROM: 'أي جواب',
            FROM_PLACEHOLDER: 'اختر جوابًا',
            FIXED: 'القيمة',
            NO_KEYS:
              'لا تُجمع أي أجوبة قبل هذه الخطوة بعد، فليس هناك ما يُرسل من السابق. أضف «اسأل» أو «اختيار» فوقها.',
            ADD: 'أضف ما يُرسل',
            SUGGEST: 'يبدو مطابقًا: {answer}',
            SUGGEST_USE: 'استخدمه',
            SOME_PATHS:
              'غير معروض هنا لأن بعض الطرق إلى هذه الخطوة فقط تجمعه: {answers}',
            LIST_JOIN: '، ',
          },
          KEEP: {
            HEADING: 'ما يُحفظ من الجواب',
            DESCRIPTION:
              'على اليمين موضعه في الجواب، وعلى اليسار الاسم الذي تناديه به بقية المهمة.',
            FIELD: 'موضعه في الجواب',
            SAVE_AS: 'اسمه في هذه المهمة',
            REMOVE: 'احذف هذا السطر',
            ADD: 'احفظ شيئًا',
          },
          TELL: {
            EXACT_LABEL: 'أرسل هذه الكلمات حرفيًا',
            EXACT_ON: 'يرسل مساعدك ما تكتبه دون تغيير، بلغة العميل.',
            EXACT_OFF: 'يقول مساعدك هذا بكلماته، بلغة العميل.',
            INSTRUCTION: 'ما الذي يقوله في هذه الخطوة؟',
            INSTRUCTION_HINT: 'ما تريد أن يعرفه العميل، ويكتبه مساعدك بنفسه.',
            INSTRUCTION_PLACEHOLDER: 'أخبِره أن القياس يأخذ نحو عشرين دقيقة.',
            EXACT_EN: 'حرفيًا بالإنجليزية',
            EXACT_AR: 'حرفيًا بالعربية',
          },
          HANDOFF: {
            NOTE: 'ماذا تقول الملاحظة؟',
            NOTE_HINT:
              'هذه لمن يتابع المحادثة لا للعميل. اكتب ما حدث وما الذي ما زال مطلوبًا.',
            NOTE_PLACEHOLDER:
              'يريد العميل موعدًا غير متاح عندنا على الإنترنت. ومنطقته ورقمه موجودان في المحادثة.',
            ENDS: 'تنتهي المهمة هنا، ويتابع فريقك من هذه النقطة.',
          },
          END: {
            INSTRUCTION: 'كلمة أخيرة، إن أردت',
            INSTRUCTION_HINT:
              'اتركها فارغة فيتوقّف مساعدك ببساطة ويرجع إلى الإجابة على الأسئلة.',
            INSTRUCTION_PLACEHOLDER:
              'أكّد له الحجز وقل إننا سنرسل تذكيرًا في اليوم الذي قبله.',
            SILENT: 'لا يُقال شيء. انتهت المهمة ويجيب مساعدك بحرية مرة أخرى.',
          },
          PREVIEW: {
            HEADING: 'على هاتف العميل',
            NO_OPTIONS: 'أضف خيارًا فيظهر هنا كما يراه العميل.',
            LIVE_EMPTY:
              'اضغط «جرّبه» أعلاه فتظهر الخيارات الحقيقية هنا كأزرار.',
            MORE: 'رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها. | رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها. | رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها. | رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها. | رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها. | رجع {total}، فيكون الزرّ الأخير «المزيد…» الذي يجيب ما بعدها.',
            VALUES_HIDDEN:
              'لا يرى عملاؤك إلا هذه الكلمات، وما يُحفظ خلف كل واحدة منها لا يُعرض عليهم أبدًا.',
            MANY: 'يرد العميل بأرقام الخيارات التي يريدها.',
          },
          PROBLEMS: {
            ORPHAN:
              'هناك خلل في خطوة لم تبقَ في هذه الصفحة ({id}). راجِع من أين تبدأ هذه المهمة.',
            MISSING_FIRST:
              'تبدأ هذه المهمة من خطوة غير موجودة هنا. اختر من أين تبدأ.',
            DANGLING: 'تنتقل هذه الخطوة إلى خطوة لم تبقَ موجودة.',
            DANGLING_DETAIL:
              'تنتقل هذه الخطوة إلى «{detail}» ولم تبقَ موجودة. وجّهها إلى موضع آخر.',
            UNREACHABLE:
              'لا شيء يؤدّي إلى هذه الخطوة، فلن يصل إليها أي عميل. وجّه إليها خطوة أخرى أو احذفها.',
            INPUT_BEFORE_SAVE:
              'ترسل هذه الخطوة جوابًا لم يُجمع بعد في كل طريق يؤدّي إليها.',
            INPUT_BEFORE_SAVE_DETAIL:
              'يُرسل «{detail}» من هنا، ولكن هناك طريق إلى هذه الخطوة لم يُجمع فيه أصلًا، فيستقبله نظامك فارغًا.',
            DUPLICATE_LABEL: 'خيارَان هنا يقولان الشيء نفسه.',
            DUPLICATE_LABEL_DETAIL:
              'خيارَان هنا يقولان «{detail}» كلاهما، ولا يمكن تمييز ضغطة العميل على أحدهما عن الآخر.',
            UNKNOWN_CLEAR: 'تُنسي هذه الخطوة جوابًا لا تجمعه أي خطوة هنا.',
            UNKNOWN_CLEAR_DETAIL:
              'تُنسي هذه الخطوة «{detail}» ولا تجمعه أي خطوة هنا، فلن تُنسي شيئًا.',
            LINK_UNLABELLED: 'اكتب كلمات الزر.',
            LINK_LABEL_LONG: 'كلمات الزر حتى {limit}.',
            LINK_INVALID:
              'أعطِ الزر رابطًا يبدأ بـ https://، أو اختر الجواب اللي فيه الرابط.',
            NO_PLACE: 'اختر المكان اللي ترسله.',
            UNKNOWN_LINK_KEY:
              'رابط هذا الزر يأتي من جواب لا تحفظه أي خطوة هنا.',
            UNKNOWN_LINK_KEY_DETAIL:
              'رابط هذا الزر يأتي من «{detail}» ولا تحفظه أي خطوة هنا.',
            UNKNOWN_PLACE:
              'هذه الخطوة ترسل مكانًا لم يعد من أماكنك. اختر غيره.',
            MANY_BRANCHES:
              'السؤال الذي يمكن فيه اختيار أكثر من إجابة لا يرسل كل خيار إلى خطوة خاصة به، ولا يؤكد تغييرًا. أزل تلك الخطوات من خياراته.',
            MANY_RANGE:
              'عدد ما يمكن اختياره لا يناسب خيارات هذا السؤال. راجع «على الأقل» و«على الأكثر»، واجعل له خيارين أو أكثر.',
            UNCONFIRMED_CHANGE:
              'اطلب من العميل التأكيد قبل أن تغيّر هذه الخطوة أي شيء في نظامك.',
            LIVE_NEEDS_LOOKUP:
              'قائمة الخيارات تأتي فقط من نظام يبحث عن معلومة. اختر نظامًا يبحث عن معلومة.',
            TYPED_NUMBER_LOOKUP:
              'هذا يبحث في بيانات عملائك برقم يكتبه العميل. يستطيع أي شخص كتابة رقم غيره. استخدم «رقم واتساب لهذه المحادثة» بدلًا منه.',
            USERNAME_NEEDS_PHONE:
              'بعض العملاء يراسلونك باسم مستخدم واتساب بلا رقم هاتف. أضف سؤالًا يحفظ رقم هاتفهم قبل هذه الخطوة.',
            READ_BACK_NOT_LINKED:
              'هذه الخطوة تعرض تفاصيل خطوة لا تتصل بنظامك بعد هذه الإجابة.',
            UNKNOWN_KEPT_KEY:
              'هذه الخطوة تعرض شيئًا يعيده نظامك، لكن لا توجد خطوة هنا تحفظه.',
            UNKNOWN_KEPT_KEY_DETAIL:
              'هذه الخطوة تعرض «{detail}»، لكن لا توجد خطوة هنا تحفظه من نظامك. ألغِ اختياره، أو احفظه في الخطوة التي تسأل نظامك.',
            KEEP_WITHOUT_READ_BACK:
              'هذه الخطوة تعرض ما أعاده نظامك، لكنها لا تعرض على العميل ما سيُرسل. فعّل ذلك، أو ألغِ اختيار ما تعرضه.',
            SKIP_FEEDS_CONNECTION:
              'هذه الإجابة تُرسل إلى نظامك، لذلك لا يمكن تخطيها.',
            SKIP_FEEDS_CONNECTION_DETAIL:
              'هذه الإجابة تُرسل إلى «{detail}»، لذلك لا يمكن تخطيها.',
            FORM_PAGE_EMPTY:
              'إحدى صفحات نموذج هذه المهمة بلا سؤال. افتح «ترتيب النموذج» لإصلاحها.',
            FORM_STEP_TWICE:
              'أحد الأسئلة موجود مرتين في نموذج هذه المهمة. افتح «ترتيب النموذج» لإصلاحه.',
            FORM_TOO_MANY_PAGES:
              'نموذج هذه المهمة فيه صفحات أكثر من المسموح. افتح «ترتيب النموذج» لإصلاحه.',
            FORM_TOO_MANY_QUESTIONS:
              'نموذج هذه المهمة فيه أسئلة أكثر من المسموح. افتح «ترتيب النموذج» لإصلاحه.',
            FORM_TEXT_TOO_LONG:
              'بعض كلمات نموذج هذه المهمة أطول من المسموح. افتح «ترتيب النموذج» لإصلاحها.',
            FORM_BRANCH_NOT_LAST:
              'هذا السؤال يحدد الآن الخطوة التالية، لذلك يجب أن يكون آخر سؤال في النموذج. افتح «ترتيب النموذج» وانقله إلى النهاية.',
            FORM_UPLOAD_NOT_LAST:
              'هذا السؤال يطلب صورًا أو ملفًا، لذلك يجب أن يكون في الصفحة الأخيرة من النموذج. افتح «ترتيب النموذج» وانقله إليها.',
            FORM_UPLOAD_TWICE:
              'نموذج هذه المهمة يطلب صورًا أو ملفًا مرتين. افتح «ترتيب النموذج» واترك أحدهما في المحادثة.',
            UPLOAD_SAVES_TO_CONTACT:
              'الصور والملفات تُحفظ مع المحادثة، لا في بيانات العميل. اختر مكان حفظ هذا السؤال من جديد.',
            OPTION_MISSING_LABEL_AR:
              'أحد الخيارات هنا بلا كلمات عربية. عملاؤك يقرؤون العربية فقط، فاكتبها.',
            OPTION_MISSING_LABEL_EN:
              'أحد الخيارات هنا بلا كلمات إنجليزية. عملاؤك يقرؤون الإنجليزية فقط، فاكتبها.',
            TOO_MANY_STEPS:
              'خطوات هذه المهمة أكثر ممّا نستطيع تشغيله. احذف بعضها.',
            DUPLICATE_ID: 'خطوتان هنا هما الخطوة نفسها.',
            DUPLICATE_ID_DETAIL:
              'تستخدم خطوة أخرى «{detail}» بالفعل. احذف إحداهما.',
            BAD_STEP_ID:
              'تنقص هذه الخطوة الاسم الذي نشغّلها به. احذفها وأضفها مرة أخرى.',
            BLANK_NAME: 'أعطِ هذه الخطوة اسمًا لتجدها في قائمة لاحقًا.',
            NAME_LONG: 'الاسم طويل جدًا. أبقِه دون {limit}.',
            BLANK_INSTRUCTION: 'اكتب ما تفعله هذه الخطوة.',
            INSTRUCTION_LONG: 'النص طويل جدًا. أبقِه دون {limit}.',
            NO_SAVE_TARGET: 'حدّد أين يُحفظ جواب هذه الخطوة.',
            BAD_SAVE_KEY:
              'اسم هذا الجواب لا يقبل إلا الحروف اللاتينية الصغيرة والأرقام والشرطات السفلية.',
            BAD_SAVE_KEY_DETAIL:
              '«{detail}» لا يصلح اسمًا لجواب. استخدم حروفًا لاتينية صغيرة وأرقامًا وشرطات سفلية.',
            NO_OPTIONS: 'أضف خيارًا واحدًا على الأقل.',
            TOO_MANY_OPTIONS:
              'يعرض واتساب عشرة خيارات في المرة كحد أقصى، أو عشرين خيارًا إذا كان بإمكان العميل اختيار أكثر من خيار.',
            OPTION_UNLABELLED:
              'أحد الخيارات لا يحمل نصًّا بأي من اللغتين، فليس هناك ما يُعرض.',
            LABEL_LONG: 'أحد الخيارات طويل جدًا، ويقطعه واتساب عند {limit}.',
            LABEL_LONG_DETAIL:
              '«{detail}» أطول من أن يكون زرًّا، ويقطعه واتساب عند {limit}.',
            NO_CONNECTION: 'اختر أي أنظمتك تستخدمه هذه الخطوة.',
            BAD_INPUT: 'أحد ما يُرسل إلى نظامك يسمّي جوابًا لا يمكن أن يوجد.',
            BAD_INPUT_DETAIL:
              'ما يُرسل باسم «{detail}» يسمّي جوابًا لا يمكن أن يوجد. اختر واحدًا من القائمة.',
            NO_LIST_PATH: 'حدّد موضع القائمة في جواب نظامك.',
            NO_LABEL_FIELD: 'حدّد أي جزء من كل صف يقرؤه العميل.',
            NO_VALUE_FIELD: 'حدّد أي جزء من كل صف نحفظه نحن.',
            NO_IF_NONE: 'حدّد إلى أين ينتقل حين لا يرجع شيء.',
            BAD_KEEP_KEY:
              'اسم ما يُحفظ لا يقبل إلا الحروف اللاتينية الصغيرة والأرقام والشرطات السفلية.',
            BAD_KEEP_KEY_DETAIL:
              'ما يُحفظ من «{detail}» يحتاج اسمًا من حروف لاتينية صغيرة وأرقام وشرطات سفلية.',
            BLANK_EXACT: 'اكتب النصّ الحرفي بلغة واحدة على الأقل.',
            EXACT_LONG: 'النص طويل جدًا. أبقِه دون {limit}.',
            BLANK_NOTE: 'اكتب ما تقوله الملاحظة لفريقك.',
            // Task review item 11.
            UNKNOWN_PERSON:
              'هذه الخطوة تحوّل المحادثات لشخص لم يعد في حسابك. اختر غيره.',
            UNKNOWN_TEAM:
              'هذه الخطوة تحوّل المحادثات لفريق لم يعد موجودًا. اختر غيره.',
            UNKNOWN_LABEL: 'هذه الخطوة تضيف تصنيفًا غير موجود في صندوق الوارد.',
            UNKNOWN_LABEL_DETAIL:
              'هذه الخطوة تضيف «{detail}»، وهو ليس من تصنيفات صندوق الوارد. أنشئه من إعدادات صندوق الوارد، أو أزله.',
            BAD_HAND_TO: 'اختر من يستلم المحادثات من هذه الخطوة.',
            BAD_HANDOFF_LABELS:
              'التصنيفات هنا خمسة كحد أقصى، كل واحد مرة واحدة.',
            NO_TITLE: 'سمِّ هذه المهمة حتى تجدها لاحقًا.',
            TITLE_LONG: 'هذا الاسم طويل. اجعله أقل من {limit}.',
            NO_TRIGGER: 'حدّد متى يبدأ مساعدك هذه المهمة.',
            TRIGGER_LONG: 'هذا طويل. اجعله أقل من {limit}.',
            CTA_LONG: 'زر إرسال النموذج طويل. اجعله في حدود {limit}.',
            NO_START_WORDS: 'أضف كلمة واحدة على الأقل تبدأ بها هذه المهمة.',
            START_WORDS_MANY: 'تبدأ المهمة بعشر كلمات على الأكثر.',
            START_WORD_LONG: 'كل كلمة بدء {limit} على الأكثر.',
            TRIGGER_MISSING: 'حدّد متى يبدأ مساعدك هذه المهمة.',
            OPENING_LONG: 'أول ما يُقال طويل. اجعله أقل من {limit}.',
            NO_CHANNELS:
              'اختر قناة واحدة على الأقل، أو كل القنوات. المهمة بلا قناة لا تبدأ أبدًا.',
            OTHER: 'في هذه الخطوة خلل لا نستطيع صياغته بالكلمات.',
            OTHER_DETAIL:
              'في هذه الخطوة خلل لا نستطيع صياغته بالكلمات ({detail}).',
          },
        },
        SAVE: 'حفظ التغييرات',
        CREATE: 'أنشئ هذه المهمة',
        SAVED: 'تم الحفظ. يستخدمها مساعدك من الرسالة التالية.',
        CREATED: 'تم الإنشاء. يستخدمها مساعدك من الرسالة التالية.',
        SAVE_FAILED: 'تعذّر الحفظ. لم يتغيّر شيء — حاول مرة أخرى.',
        NOT_FOUND: 'تعذّر العثور على هذه المهمة. ربما حُذفت.',
        STATUS: {
          IDLE: 'كل شيء محفوظ',
          NEW: 'لم تُنشأ بعد',
          DIRTY: 'لم يُحفظ بعد',
          SAVING: 'جارٍ الحفظ…',
          SAVED: 'تم الحفظ في {time}',
          ERROR: 'تعذّر الحفظ',
          ERROR_DETAIL:
            'لم يتغيّر شيء، وما كتبته ما زال في الصفحة. حاول مرة أخرى.',
        },
      },
      MAP: {
        DESCRIPTION:
          'كل خطوة في هذه المهمة وإلى أين تقود. للقراءة فقط — ولتغيير خطوة افتح «الخطوات».',
        COUNT:
          'لا خطوات في هذه المهمة | خطوة واحدة في هذه المهمة | خطوتان في هذه المهمة | {count} خطوات في هذه المهمة | {count} خطوةً في هذه المهمة | {count} خطوة في هذه المهمة',
        EMPTY: 'لا شيء لرسمه بعد',
        EMPTY_BODY:
          'اكتب الخطوات أولًا، وترسمها الخريطة بمجرد أن تقود إلى مكان.',
        NARROW:
          'تحتاج الخريطة شاشة أوسع. على الجوال اقرأ «الخطوات» بدلًا منها.',
        ARIA: 'خريطة المهمة، بلا خطوات | خريطة المهمة، خطوة واحدة | خريطة المهمة، خطوتان | خريطة المهمة، {count} خطوات | خريطة المهمة، {count} خطوةً | خريطة المهمة، {count} خطوة',
        OPEN: 'افتح الخطوة {name}',
        NO_FIRST:
          'هذه المهمة لا تحدّد الخطوة الأولى، فلا شيء تحتها يستطيع أن يعمل بعد.',
        CHECK: 'يستحق نظرة',
        TYPE: {
          ASK: 'يسأل عن شيء',
          CHOOSE: 'يعرض خيارًا',
          CHOOSE_LIVE: 'يعرض ما يرجعه نظامك',
          CALL: 'ينادي نظامك',
          TELL: 'يقول شيئًا',
          HANDOFF: 'يحوّل إلى شخص',
          END: 'ينتهي',
          UNKNOWN: 'نوع خطوة أحدث',
        },
        LINK: {
          WORKED: 'إذا نجح',
          FAILED: 'إذا لم ينجح',
          NONE: 'إذا لم يوجد شيء',
          MORE: '+{count} غير ذلك | +{count} غير ذلك | +{count} غير ذلك | +{count} غير ذلك | +{count} غير ذلك | +{count} غير ذلك',
        },
        BADGE: {
          FIRST: 'تبدأ هنا',
          ORPHAN: 'لا شيء يقود إليها',
          DEAD_END: 'لا شيء بعدها',
          BROKEN: 'تقود إلى خطوة غير موجودة',
        },
      },
      COMPOSE: {
        ANSWER: {
          TEXT: {
            LABEL: 'أي شيء يكتبونه',
            HINT: 'جملة أو اسم أو عنوان — أي شيء يكتبه العميل.',
          },
          NUMBER: {
            LABEL: 'رقم',
            HINT: 'كم العدد، كم المبلغ، كم المدة.',
          },
          DATE: { LABEL: 'تاريخ', HINT: 'يوم يحدّده العميل.' },
          TIME: { LABEL: 'وقت', HINT: 'وقت من اليوم.' },
          EMAIL: {
            LABEL: 'بريد إلكتروني',
            HINT: 'نتحقق منه قبل تدوينه.',
          },
          PHONE: {
            LABEL: 'رقم هاتف',
            HINT: 'نتحقق منه قبل تدوينه.',
          },
          PHOTO: {
            LABEL: 'صور',
            HINT: 'يرسل العميل صورة أو أكثر.',
          },
          FILE: {
            LABEL: 'ملف',
            HINT: 'يرسل العميل مستندًا مثل PDF، أو صورة.',
          },
          WRITTEN: {
            LABEL: 'واحد من هذه — تكتبها أنت',
            HINT: 'تكتب الخيارات، ويضغط عميلك واحدًا منها.',
          },
          LIVE: {
            LABEL: 'واحد من هذه — من نظامك',
            HINT: 'يرسل نظامك الخيارات والعميل ينتظر.',
          },
          SOURCE_RULE: 'أو اعرض قائمة يضغط منها:',
          HEADING: 'ماذا يرجع منه؟',
        },
        STARTER: {
          SEE_IT: 'شوفها في محادثة',
          PLAY_AGAIN: 'شغّلها مرة ثانية',
          USE_THIS: 'استخدم هذه',
          LANGUAGE: 'اقرأها بـ',
          ENGLISH: 'الإنجليزية',
          ARABIC: 'العربية',
          CUSTOMER: 'عميلك',
          ASSISTANT: 'مساعدك',
          WORDING:
            'يكتب مساعدك كلماته بنفسه وبلغة عميلك. هذه صورة لشكل المحادثة، لا لنصّها بالحرف.',
          EMPTY: 'لا توجد محادثة لعرضها هنا.',
        },
        POSITION:
          'الشاشة {number} من {total} | الشاشة {number} من {total} | الشاشة {number} من {total} | الشاشة {number} من {total} | الشاشة {number} من {total} | الشاشة {number} من {total}',
        NEXT: 'التالي',
        PREVIOUS: 'رجوع',
        NEEDS: 'أكمل ما هو مُعلَّم أعلاه لتكمل.',
        KEPT: 'عندك تعديلات غير محفوظة على هذه المهمة من قبل {minutes} دقيقة.',
        KEPT_TAKE: 'أكمل من حيث توقفت',
        KEPT_DISCARD: 'تجاهلها',
        SAVE_BLOCKED:
          'بعض الخطوات ما زالت ناقصة، وكل واحدة مُعلَّمة على خطوتها.',
        SAVE_READY: 'هذه التغييرات غير محفوظة بعد، فمساعدك لا يستخدمها.',
        LIVE: {
          NONE: 'هذه المهمة لم تُنشأ بعد. لا شيء هنا يصل إلى عميل حتى تشغّلها في الشاشة الأخيرة.',
          OFF: 'هذه المهمة مطفأة. هي محفوظة، وهي لا ترد على أحد. تشغّلها من الشاشة الأخيرة.',
          ON: 'هذه المهمة مشغّلة. مساعدك يستخدم النسخة المحفوظة الآن، وتغييراتك لا تصل إلى العملاء إلا بعد حفظها.',
          CHIP_NONE: 'ليست شغّالة بعد',
          CHIP_OFF: 'مطفأة — لا ترد على أحد',
          CHIP_ON: 'شغّالة — ترد على العملاء',
        },
        UNREADABLE:
          'هذه المهمة لم تُكتب هنا، والقائمة المرتبة لا تستطيع عرض شكلها. كتابة خطواتها بالأسفل تحلّ محل المحفوظ.',
        RAIL: {
          LABEL: 'كتابة هذه المهمة',
          JOB: 'ماذا تفعل',
          STEPS: 'الخطوات',
          WHATIF: 'ماذا لو',
          ARABIC: 'كلمات النموذج',
          CHECK: 'راجعها',
        },
        JOB: {
          HEADING: 'ماذا تفعل هذه المهمة؟',
          DESCRIPTION: 'اسم يراه فريقك فقط، واللحظة التي يبدأها فيها مساعدك.',
          TITLE: 'شنو تسميها؟',
          TITLE_HINT: 'عملاؤك لا يرون هذا الاسم أبدًا.',
          TITLE_PLACEHOLDER: 'حجز موعد',
          TITLE_REQUIRED: 'أعطِ هذه المهمة اسمًا.',
          TITLE_LONG: 'هذا الاسم طويل.',
          STARTERS: 'ابدأ من واحدة من هذه',
          STARTERS_HINT:
            'شوف ما سيقرأه عميلك قبل أن تختار. لا شيء يُنشأ لمجرد النظر.',
          TRIGGER: 'متى يبدأها واصلو؟',
          TRIGGER_HINT:
            'صف اللحظة لا الكلمات بالحرف. مساعدك يفهم المعنى بأي لغة.',
          TRIGGER_PLACEHOLDER: 'العميل يبي يحجز موعد أو يغيّر موعده.',
          TRIGGER_REQUIRED: 'قل متى تبدأ هذه المهمة.',
          TRIGGER_LONG: 'هذا طويل.',
          TRIGGER_NOTE:
            'مساعدك يقرأ ما كتبه العميل ويقرر. أنت لا تعدّد الكلمات التي قد يستخدمها.',
          START: 'كيف تبدأ؟',
          START_ASSISTANT: 'عندما يطلبها العميل (يقرّر مساعدك)',
          START_ASSISTANT_BODY:
            'يقرأ مساعدك ما كتبه العميل ويبدأها عندما تناسب.',
          START_WORDS: 'فقط عندما يكتب العميل كلمات معيّنة',
          START_WORDS_BODY:
            'تبدأ عندما تحتوي الرسالة على إحدى كلماتك، ولا تبدأ غير ذلك.',
          START_MENU: 'فقط من زر في القائمة',
          START_MENU_BODY:
            'تبدأ فقط عندما يضغط العميل خيارًا في القائمة يبدأها.',
          WORDS: 'الكلمات',
          WORDS_HINT: 'يتجاهل واصلو التشكيل والأرقام العربية أو الإنجليزية.',
          WORDS_PLACEHOLDER: 'كود١',
          WORDS_ADD: 'أضف',
          WORDS_REMOVE: 'احذف {word}',
          WORDS_REQUIRED: 'أضف كلمة واحدة على الأقل.',
          WORDS_MANY: 'عشر كلمات على الأكثر.',
          WORD_LONG: 'كل كلمة {limit} على الأكثر.',
          MENUS_STARTING: 'تبدأها هذه القوائم: {menus}',
          NO_MENU: 'لا توجد قائمة تبدأ هذه المهمة بعد.',
          OPENING: 'أول ما يُقال (اختياري)',
          OPENING_HINT: 'يُرسل كما هو عند بدء هذه المهمة.',
          OPENING_LONG: 'هذا طويل.',
          CHANNELS: 'هذه المهمة تعمل على',
          CHANNELS_EVERY: 'كل القنوات',
          CHANNELS_NONE: 'اختر قناة واحدة على الأقل.',
          ENOUGH: 'جملة واحدة تكفي. لا يوجد شيء تملؤه هنا.',
          EXAMPLES: 'أو اضغط على واحدة من هذه:',
          EXAMPLE_1: 'العميل يبي يحجز موعد، أو يغيّر موعدًا عنده.',
          EXAMPLE_2: 'العميل يسأل عن سعر شيء أو يطلب عرض سعر.',
          EXAMPLE_3: 'العميل يبي يعرف وين وصل طلبه.',
          MAP_HEADING: 'ما ستكتبه الآن',
          MAP: {
            JOB: 'اسمها، واللحظة التي يبدأها فيها مساعدك. أنت هنا.',
            STEPS:
              'كل سؤال ورسالة وإجراء، بالترتيب الذي يحدث فيه. والخطوة التي تتحقق من تقويمك أو تسجّل حجزًا هي المكان الذي يُربط فيه أحد أنظمتك — أما النظام نفسه فتجهّزه من «للمطوّرين» ثم «أنظمتك».',
            WHATIF:
              'ماذا يفعل مساعدك حين لا يجد أحد أنظمتك شيئًا يعرضه، أو حين لا يرد.',
            ARABIC:
              'الكلمات بالحرف، باللغتين، لكل ما يظهر في نموذج داخل واتساب.',
            CHECK:
              'تقرأ المهمة كاملة على شكل خريطة. وهذه هي الشاشة الوحيدة التي تشغّل المهمة وتطفئها.',
          },
          PROMISE:
            'واصلو يحفظ موضعك في هذه المهمة ومساعدك هو من يتكلم. يسأل كل خطوة بكلماته، بلغة عميلك أيًا كانت، ويكتب الإجابة حيث حدّدت، ثم ينتقل. لا يستطيع تخطي خطوة ولا اختراع واحدة.',
        },
        LIST: {
          HEADING: 'ماذا يحدث، بالترتيب',
          DESCRIPTION:
            'واصلو ينزل في هذه القائمة خطوة خطوة. وكل ما ليس "أكمل" مكتوب تحت خطوته.',
          EMPTY: 'لا شيء هنا بعد. أضف أول شيء يجب أن يحدث.',
          ADD: 'أضف خطوة',
          UNWRITTEN: 'لم تُكتب بعد',
          THEN_GO: 'ثم اذهب إلى: {name}',
          THEN_STOP: 'ثم تنتهي المهمة',
          THEN_HANDOVER: 'ثم انقل المحادثة إلى شخص',
          UP: 'حرّكها لأعلى',
          DOWN: 'حرّكها لأسفل',
          UP_MEANS: 'حرّكها لأعلى، فيسأل واصلو "{moved}" قبل "{other}"',
          DOWN_MEANS: 'حرّكها لأسفل، فيسأل واصلو "{other}" قبل "{moved}"',
          MOVE_REFUSED:
            'هذه الحركة غير ممكنة: خطوة لاحقة تحتاج "{detail}"، وتحريك هذه يضعها بعد الخطوة التي تجيب عليها.',
          KIND_ASK: 'تسأل عن شيء',
          KIND_CHOOSE: 'تعرض خيارات',
          KIND_CHOOSE_LIVE: 'تعرض خيارات من نظامك',
          KIND_TELL: 'تقول شيئًا',
          KIND_CALL: 'تنفّذ شيئًا في نظامك',
          KIND_HANDOFF: 'تنقل المحادثة إلى شخص',
          KIND_END: 'تُنهي المهمة',
        },
        KIND: {
          HEADING: 'ماذا يحدث هنا؟',
          CANCEL: 'ليس الآن',
          QUESTION_LABEL: 'اسأل العميل عن شيء',
          QUESTION_ABOUT:
            'سؤال، والإجابة تُدوَّن. ونوع الإجابة تختاره في الشاشة التالية.',
          TELL_LABEL: 'قل شيئًا',
          TELL_ABOUT: 'يخبر مساعدك العميل بشيء ثم يكمل. ولا يُدوَّن شيء.',
          CALL_LABEL: 'نفّذ شيئًا في نظامك',
          CALL_ABOUT:
            'يثبّت الحجز، يسجّل الطلب، يستعلم عن شيء. واصلو يتصل بأحد أنظمتك.',
          HANDOFF_LABEL: 'انقل المحادثة إلى شخص',
          HANDOFF_ABOUT:
            'تتوقف المهمة ويستلمها أحد من فريقك، مع ملاحظة عن أين وصلت.',
        },
        SCREEN: {
          BACK: 'رجوع إلى الخطوات',
          POSITION:
            'الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total}',
          UNWRITTEN: 'لم تُكتب بعد',
          PREVIOUS: 'الخطوة السابقة',
          NEXT: 'الخطوة التالية',
          MORE: 'المزيد لهذه الخطوة',
          UP: 'حرّكها لأعلى',
          DOWN: 'حرّكها لأسفل',
          UP_MEANS: 'حرّكها لأعلى، فيسأل واصلو "{moved}" قبل "{other}"',
          DOWN_MEANS: 'حرّكها لأسفل، فيسأل واصلو "{other}" قبل "{moved}"',
          MOVE_REFUSED:
            'هذه الحركة غير ممكنة: خطوة لاحقة تحتاج "{detail}"، وتحريك هذه يضعها بعد الخطوة التي تجيب عليها.',
          REMOVE: 'احذف هذه الخطوة',
          REMOVE_CONFIRM:
            'تحذفها؟ كل ما يشير إليها سيقول إن الخطوة لم تعد موجودة.',
          REMOVE_YES: 'نعم، احذفها',
          REMOVE_NO: 'أبقِها',
          DONE: 'تم',
        },
        STEP: {
          NEED: 'ماذا تحتاج من العميل؟',
          NEED_LIVE: 'ماذا تحتاج من العميل؟',
          NEED_HINT: 'اكتبها كما تقولها بصوتك.',
          NEED_PLACEHOLDER: 'أي يوم تحب تجي؟',
          NEED_NOTE:
            'واصلو يصوغها بنفسه بلغة عميلك. وهذه الكلمات تُرسل كما هي في النموذج فقط.',
          NEED_NOTE_LIVE:
            'واصلو يصوغها بنفسه بلغة عميلك. وهذه الكلمات تُرسل كما هي في النموذج إذا عرضت هذه القائمة فيه.',
          ON_FORM: 'اعرضها في النموذج',
          ON_FORM_HINT:
            'يجلب النموذج هذه الخيارات من نظامك أثناء تعبئة العميل له، بحسب ما اختاره قبلها. وإذا تأخر نظامك تكمل المحادثة من هذا السؤال. لا يُستهلك أي رصيد.',
          OWN_WORDING: 'ما زلنا نستخدم صياغتك أنت.',
          OWN_WORDING_REPLACE: 'استخدم السؤال الجديد بدلًا منها',
          OPTIONS: 'الخيارات',
          OPTION_LABEL: 'الخيار {number}',
          OPTION_LONG: 'طويل — حتى {limit} حرفًا.',
          OPTION_REMOVE: 'احذف هذا الخيار',
          OPTION_ADD: 'أضف خيارًا',
          PHONE_LANDLINES: 'اقبل الأرقام الأرضية أيضًا',
          PHONE_LANDLINES_HINT:
            'عند الإيقاف: أرقام الجوال فقط، التي تصلها رسائل واتساب والرسائل النصية.',
          PHONE_COUNTRIES: 'من أي دول؟',
          PHONE_COUNTRY_KW: 'الكويت',
          PHONE_COUNTRY_SA: 'السعودية',
          PHONE_COUNTRY_AE: 'الإمارات',
          PHONE_COUNTRY_QA: 'قطر',
          PHONE_COUNTRY_BH: 'البحرين',
          PHONE_COUNTRY_OM: 'عُمان',
          PHONE_COUNTRY_ANY: 'أي دولة (مع رمز الدولة)',
          UPLOAD_COUNT: 'العدد الأقصى',
          UPLOAD_SIZE: 'أكبر حجم للملف',
          UPLOAD_SIZE_OPTION: '{size} ميغابايت',
          UPLOAD_NOTE:
            'في نموذج واتساب يأتي هذا السؤال في الصفحة الأخيرة، ويطلب النموذج الصور أو الملف مرة واحدة.',
          SHAPE_BUTTONS:
            'لا خيارات بعد. | خيار واحد، يعرضه واتساب زرًّا، حتى {limit} حرفًا. | خياران، يعرضهما واتساب زرّين، حتى {limit} حرفًا لكل واحد. | {count} خيارات، يعرضها واتساب أزرارًا، حتى {limit} حرفًا لكل واحد. | {count} خيارًا، يعرضها واتساب أزرارًا، حتى {limit} حرفًا لكل واحد. | {count} خيار، يعرضها واتساب أزرارًا، حتى {limit} حرفًا لكل واحد.',
          SHAPE_LIST:
            'لا خيارات بعد. | خيار واحد، يعرضه واتساب في قائمة، حتى {limit} حرفًا. | خياران، يعرضهما واتساب في قائمة، حتى {limit} حرفًا لكل واحد. | {count} خيارات، يعرضها واتساب في قائمة، حتى {limit} حرفًا لكل واحد. | {count} خيارًا، يعرضها واتساب في قائمة، حتى {limit} حرفًا لكل واحد. | {count} خيار، يعرضها واتساب في قائمة، حتى {limit} حرفًا لكل واحد.',
          SHAPE_MANY:
            'لا خيارات بعد. | خيار واحد، يعرضه واتساب في قائمة مرقّمة، حتى {limit} حرفًا. | خياران، يعرضهما واتساب في قائمة مرقّمة، حتى {limit} حرفًا لكل واحد. | {count} خيارات، يعرضها واتساب في قائمة مرقّمة، حتى {limit} حرفًا لكل واحد. | {count} خيارًا، يعرضها واتساب في قائمة مرقّمة، حتى {limit} حرفًا لكل واحد. | {count} خيار، يعرضها واتساب في قائمة مرقّمة، حتى {limit} حرفًا لكل واحد.',
          MANY: 'يمكن للعميل اختيار أكثر من خيار',
          MANY_HINT:
            'تعرض المحادثة الخيارات بأرقام، ويرد العميل بأرقام الخيارات التي يريدها. وفي النموذج يضع علامة على المربعات.',
          MANY_MIN: 'على الأقل',
          MANY_MAX: 'على الأكثر',
          MANY_BLOCKED: 'غير متاح لسؤال تقود خياراته إلى خطوات مختلفة.',
          MANY_LOCKED:
            'احذف الخيارات حتى تصبح {limit} أولًا: السؤال بإجابة واحدة يعرض {limit} على الأكثر.',
          SYSTEM: 'أي أنظمتك يرسل الخيارات؟',
          SYSTEM_PLACEHOLDER: 'اختر واحدًا',
          SYSTEM_UNAVAILABLE: 'ما قدرنا نعرض أنظمتك الآن.',
          SYSTEM_NONE:
            'لا يوجد نظام موصول بعد. أوصل واحدًا من "للمطورين"، أو اختر "أي شيء يكتبونه" وانقل المحادثة إلى شخص.',
          WIRING: 'ماذا يرسل وماذا يرجع منه',
          WIRING_OPEN: 'اضبطه',
          SENDS:
            'لا يرسل شيئًا من تفاصيله. | يرسل تفصيلًا واحدًا من تفاصيله. | يرسل تفصيلين من تفاصيله. | يرسل {count} من تفاصيله. | يرسل {count} من تفاصيله. | يرسل {count} من تفاصيله.',
          SENDS_NONE: 'لا يرسل شيئًا بعد.',
          GETS: 'عميلك يقرأ: {field}',
          GETS_NOTHING: 'لم يُختَر شيء من الجواب بعد.',
          IFNONE: 'وإذا لم يرجع شيء؟',
          IFNONE_LABEL: 'عندها',
          IFNONE_HANDOVER: 'انقل المحادثة إلى شخص',
          SAVED_AS: 'تُدوَّن باسم "{name}"',
          SAVED_BOTH: 'تُدوَّن باسم "{name}"، وعلى العميل في {field}',
          SAVED_NOWHERE: 'لا تُدوَّن في أي مكان بعد',
          ADVANCED: 'كيف يسأل واصلو',
          INSTRUCTION: 'ما نقوله لواصلو',
          INSTRUCTION_HINT:
            'مكتوب لمساعدك ولا يصل عميلًا أبدًا. واصلو يكتبه من سؤالك؛ غيّره فقط إذا احتجت.',
          ASKED: 'كيف يُطرح السؤال',
          AS_WRITTEN: 'أرسل كلماتي كما هي',
          AS_WRITTEN_HINT:
            'يُرسل كما كتبته. قد يضيف المساعد سطراً قصيراً قبله، مثل التحية أو توضيح سبب عدم قبول الإجابة.',
          AS_WRITTEN_OFF: 'دع المساعد يصيغه',
          OPTION_VALUE: 'ما يستلمه نظامك',
          OPTION_VALUE_HINT: 'اتركه فارغاً لإرسال الكلمات الإنجليزية.',
          IFSTUCK: 'إذا لم يستطع الإجابة',
          IFSTUCK_HANDOFF: 'حوّل المحادثة لفريقك',
          IFSTUCK_SKIP: 'تخطَّ هذا السؤال',
          IFSTUCK_GOTO: 'اسأل شيئاً آخر بدلاً منه',
          IFSTUCK_GOTO_LABEL: 'اسأل هذا بدلاً منه',
          OPTIONAL: 'اختياري',
          OPTIONAL_HINT:
            'يمكن للعميل تخطيه. إذا قال لا أو إنه لا يعرف، يُطرح السؤال التالي.',
          SKIP_BLOCKED:
            'هذه الإجابة تُرسل إلى «{connection}»، لذلك لا يمكن تخطيها.',
          READ_BACK: 'اعرض على العميل ما سيُرسل',
          READ_BACK_HINT:
            'قبل أن يجيب، يرى كل تفصيل سيستلمه نظامك، مكتوبًا مما يُرسل.',
          SUMMARY_NAME: 'الاسم في الملخّص',
          SUMMARY_NAME_EN: 'الاسم بالإنجليزية',
          SUMMARY_NAME_AR: 'الاسم بالعربية',
          READ_BACK_KEEP: 'اعرض أيضًا ما أعاده نظامك سابقًا',
          READ_BACK_KEEP_HINT:
            'حتى {limit}، كل واحد في سطر تحت التفاصيل، مثل المبلغ بالدينار.',
          READ_BACK_KEEP_NAME_EN: 'اسمه بالإنجليزية',
          READ_BACK_KEEP_NAME_AR: 'اسمه بالعربية',
          READ_BACK_KEEP_SUFFIX_EN: 'بعده، بالإنجليزية (اختياري)',
          READ_BACK_KEEP_SUFFIX_AR: 'بعده، بالعربية (اختياري)',
        },
        MESSAGE: {
          SAY: 'ماذا يقول؟',
          SAY_HINT: 'صِف المعنى. ومساعدك يكتب الكلمات.',
          SAY_PLACEHOLDER: 'خبّره أن الحجز تم، مع الفرع والوقت.',
          SAY_NOTE: 'يقولها مساعدك بلغة عميلك.',
          EXACT: 'قُلها بكلماتي بالحرف',
          EXACT_ABOUT:
            'لسعر أو سياسة أو جملة يجب ألا تُعاد صياغتها. تكتب اللغتين بنفسك.',
          EXACT_LABEL: 'النص بالحرف',
          EXACT_NOTE: 'يُرسَل تمامًا كما تكتبه.',
          SUMMARY: 'أضف ملخصًا لما أُرسل',
          SUMMARY_ABOUT:
            'يظهر تحت الرسالة كل تفصيل استلمه نظامك، مكتوبًا مما أُرسل.',
          SUMMARY_KEEP: 'اعرض أيضًا ما أعاده نظامك',
          SEND_WITH: 'أرسلها مع',
          SEND_ABOUT:
            'على واتساب يظهر الزر أو الموقع تحت رسالتك. وعلى القنوات الأخرى يُكتب الرابط أو المكان.',
          SEND_TEXT: 'نص فقط',
          SEND_LINK: 'زر رابط',
          SEND_LOCATION: 'موقع',
          LINK_LABEL_EN: 'كلمات الزر بالإنجليزية',
          LINK_LABEL_AR: 'كلمات الزر بالعربية',
          LINK_LABEL_EMPTY: 'زرك',
          LINK_FROM: 'من أين يأتي الرابط',
          LINK_FIXED: 'رابط أكتبه بنفسي',
          LINK_SYSTEM: 'رابط يرجعه نظامي',
          LINK_URL: 'الرابط',
          LINK_URL_INVALID: 'لازم يبدأ الرابط بـ https:// وبدون مسافات.',
          LINK_KEY: 'أي جواب فيه الرابط',
          LINK_KEY_PLACEHOLDER: 'اختر الجواب',
          LINK_KEY_NONE: 'لا توجد خطوة فوق تحفظ جوابًا من نظامك بعد.',
          PREVIEW: 'ما يراه عميلك',
        },
        PLACES: {
          PICK: 'أي مكان',
          PICK_PLACEHOLDER: 'اختر مكانًا',
          ADD: 'أضف مكانًا',
          EDIT: 'عدّل هذا المكان',
          NAME_EN: 'الاسم بالإنجليزية',
          NAME_AR: 'الاسم بالعربية',
          ADDRESS_EN: 'العنوان بالإنجليزية',
          ADDRESS_AR: 'العنوان بالعربية',
          WHERE: 'مكانه على الخريطة',
          WHERE_HINT: 'الصق رابط خرائط جوجل كاملًا، أو اكتب رقمي النقطة.',
          POINT: 'يوضع الدبوس عند {lat}، {lng}.',
          WHERE_SHORT_LINK:
            'هذا رابط مختصر. افتحه، ثم الصق الرابط الكامل من شريط العنوان.',
          WHERE_NO_POINT:
            'ما لقينا نقطة. الصق رابط خرائط جوجل كاملًا، أو اكتب الرقمين.',
          WHERE_OUT_OF_RANGE: 'هذه النقطة ليست على الخريطة.',
          NAME_MISSING: 'اكتب اسمًا لهذا المكان.',
          SAVE: 'احفظ المكان',
          CANCEL: 'إلغاء',
          REMOVE: 'احذف المكان',
          IN_USE: '{tasks} ما زالت ترسل هذا المكان. أزله من تلك المهمة أولًا.',
          LIST_JOIN: '، ',
          LIMIT:
            'وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا. | وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا. | وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا. | وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا. | وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا. | وصلت للحد: {count} من الأماكن. احذف مكانًا لا تستخدمه أولًا.',
          SAVE_FAILED: 'تعذّر الحفظ. حاول مرة أخرى.',
          LOAD_FAILED: 'تعذّر تحميل أماكنك. حاول مرة أخرى.',
        },
        ACTION: {
          NAME: 'ماذا تفعل هذه الخطوة؟',
          NAME_HINT: 'عملاؤك لا يرون هذا، وفريقك يراه.',
          NAME_PLACEHOLDER: 'تحقق من التقويم',
          NAME_LONG: 'طويل — حتى {limit} حرفًا.',
          SYSTEM: 'أي أنظمتك؟',
          SYSTEM_PLACEHOLDER: 'اختر واحدًا',
          SYSTEM_UNAVAILABLE: 'ما قدرنا نعرض أنظمتك الآن.',
          SYSTEM_NONE: 'لا يوجد نظام موصول بعد. أوصل واحدًا من "للمطورين".',
          WIRING: 'ماذا يرسل وماذا يرجع منه',
          WIRING_OPEN: 'اضبطه',
          SENDS:
            'لا يرسل شيئًا من تفاصيله. | يرسل تفصيلًا واحدًا من تفاصيله. | يرسل تفصيلين من تفاصيله. | يرسل {count} من تفاصيله. | يرسل {count} من تفاصيله. | يرسل {count} من تفاصيله.',
          SENDS_NONE: 'لا يرسل شيئًا بعد.',
          KEEPS:
            'لا يحتفظ بشيء من الجواب. | يحتفظ بتفصيل واحد من الجواب. | يحتفظ بتفصيلين من الجواب. | يحتفظ بـ{count} من الجواب. | يحتفظ بـ{count} من الجواب. | يحتفظ بـ{count} من الجواب.',
          KEEPS_NOTHING: 'لا يحتفظ بشيء من الجواب.',
          FAILS_HANDOVER: 'إذا لم تنجح، ينقل واصلو المحادثة إلى شخص.',
          FAILS_STEP:
            'إذا لم تنجح، يذهب واصلو إلى الخطوة التي اخترتها في "ماذا لو".',
          CHANGE_NOTE:
            'هذا يغيّر شيئًا في نظامك، لذلك يعمل فقط بعد أن يوافق العميل على سؤال.',
        },
        HANDOFF: {
          NOTE: 'ماذا يحتاج أن يعرف من يستلم المحادثة؟',
          NOTE_HINT: 'ملاحظة خاصة لفريقك. عميلك لا يراها أبدًا.',
          NOTE_PLACEHOLDER: 'العميل يبي يحجز وما قدرنا نعرض له وقتًا.',
          LAST_WORD: 'هل من كلمة أخيرة قبل أن تنتهي؟',
          LAST_WORD_HINT: 'اتركها فارغة لتنتهي بلا رسالة أخيرة.',
          AFTER: 'لا شيء يعمل بعد هذه. المحادثة صارت عند فريقك.',
          ENDS: 'تنتهي المهمة هنا.',
          // Task review item 11: who gets the chats from this step.
          HAND_TO: 'حوّلها إلى',
          HAND_TO_ACCOUNT: 'اختيارك المعتاد (حوّل المحادثات إلى)',
          HAND_TO_PERSON: 'شخص محدد',
          HAND_TO_TEAM: 'فريق',
          HAND_TO_ACCOUNT_HINT:
            'المحادثات من هذه الخطوة تذهب حيث يرسلها «حوّل المحادثات إلى» في صفحة الشخصية.',
          WHICH_PERSON: 'منو',
          WHICH_TEAM: 'أي فريق',
          PERSON_HINT:
            'كل محادثة تصل لهذه الخطوة تذهب لهذا الشخص، حتى لو كانت عند غيره.',
          TEAM_HINT:
            'كل محادثة تصل لهذه الخطوة تذهب لهذا الفريق. إذا لم يُختر أحد منه، تبقى عند الفريق كله.',
          PERSON_GONE: 'هذا الشخص لم يعد في حسابك. اختر غيره.',
          TEAM_GONE: 'هذا الفريق لم يعد موجودًا. اختر غيره.',
        },
        WIRING: {
          HEADING: 'ماذا يرسل وماذا يرجع منه',
          ANSWER_PICKED: 'اختيار العميل: {answer} ({step})',
          ANSWER_GAVE: 'ما كتبه العميل: {answer} ({step})',
          ANSWER_SYSTEM: 'من نظامك: {answer} ({step})',
          VALUES: 'شغّله بهذه القيم',
          VALUES_HINT:
            'لهذه التجربة فقط. في محادثة حقيقية تأتي كل واحدة مما اخترته فوق.',
          TRY: 'جرّبه الآن',
          RUNNING: 'يعمل…',
          TRY_NOTE: 'هذا اتصال حقيقي بنظامك.',
          LOCKED_HEADING: 'يملؤها واصلو',
          LOCKED_PHONE: 'رقم واتساب لهذه المحادثة (مقفل)',
          LOCKED_PHONE_NOTE:
            'يُؤخذ من المحادثة. الرقم الذي يكتبه العميل لا يحلّ محله أبدًا.',
          LOCKED_NAME: 'اسم العميل من ملفه',
          LOCKED_EMAIL: 'بريد العميل من ملفه',
          LOCKED_CHAT: 'الرقم المرجعي لهذه المحادثة',
          PREVIEW_NOTE:
            'هذا يغيّر شيئًا في نظامك، لذلك تعرض التجربة هنا ما سيُرسل فقط. لإرساله فعلًا، افتحه من أنظمتك.',
          OK: 'رجع بجواب. ({status})',
          FAILED: 'نظامك رفضه. ({status})',
          NO_STATUS: 'نظامك ما رد.',
          SHOWN_AS: 'يظهر للعميل كـ',
          NAME_EN_FROM: 'الاسم الإنجليزي يؤخذ من',
          NAME_AR_FROM: 'الاسم العربي يؤخذ من',
          NAME_FROM_SAME: 'الحقل نفسه للاثنين',
          SENDS_VALUES: '«{name}» يرسل: {values}',
          SHAPE_BUTTONS:
            'لا خيارات. | خيار واحد، يعرضه واتساب زرًّا. | خياران، يعرضهما واتساب زرّين. | {count} خيارات، يعرضها واتساب أزرارًا. | {count} خيارًا، يعرضها واتساب أزرارًا. | {count} خيار، يعرضها واتساب أزرارًا.',
          SHAPE_LIST:
            'لا خيارات. | خيار واحد، يعرضه واتساب في قائمة. | خياران، يعرضهما واتساب في قائمة. | {count} خيارات، يعرضها واتساب في قائمة. | {count} خيارًا، يعرضها واتساب في قائمة. | {count} خيار، يعرضها واتساب في قائمة.',
          DONE: 'تم',
        },
        RESPONSE: {
          WAITING: 'اضغط "جرّبه الآن" لترى ما يرجعه نظامك.',
          HEADING: 'رجع بجواب. اضغط على ما تقصده.',
          NO_LIST: 'لا شيء في هذا الجواب قائمةٌ يختار منها العميل.',
          IS_LIST: 'هذه القائمة',
          WHOLE_LIST: 'الجواب كله هو القائمة',
          FIELDS: 'أول واحد رجع به:',
          THEY_READ: 'هذا ما يقرأونه',
          WE_KEEP: 'هذا ما نحتفظ به',
          READS_AS: 'عميلك يقرأ: {text}',
          KEEP_HEADING: 'رجع بجواب. اضغط على ما تريد الاحتفاظ به.',
          NO_FIELDS: 'لا يوجد في هذا الجواب ما يُحتفظ به.',
          KEEP_AS: 'احتفظ به باسم',
        },
        WHATIF: {
          HEADING: 'ماذا لو حصل خطأ؟',
          DESCRIPTION: 'لكل واحدة من هذه إجابة جاهزة. غيّر ما لا يعجبك منها.',
          NOTHING: 'لا شيء في هذه المهمة قد يخطئ بطريقة تقررها أنت.',
          STEPS_HEADING: 'الخطوات التي تخصّها هذه الأسئلة',
          AT_STEP: 'الخطوة {number}',
          LIVE_EMPTY: 'نظامك ليس عنده ما يعرضه لـ "{name}"',
          CALL_FAILED: '"{name}" لم تنجح',
          ANSWER: 'عندها',
          HANDOVER: 'انقل المحادثة إلى شخص',
          FORGETS: 'حين ترجع المحادثة إلى "{name}"، ينسى واصلو: {keys}',
          PINNED: 'قائمتك',
          UNPIN: 'استخدم ما يستنتجه واصلو',
          ENGINE: 'هذه لا تضبطها أنت، فواصلو يفعلها أصلًا:',
          ENGINE_REFUSED:
            'إذا لم يجب العميل على السؤال نفسه ثلاث مرات، ينقل واصلو المحادثة إلى شخص.',
          ENGINE_UNREAD:
            'إذا لم يفهم واصلو دورين متتاليين، ينقل المحادثة إلى شخص.',
          WORKS_GOES_ON: 'عند النجاح، تنتقل المحادثة إلى «{step}».',
          WORKS_ENDS: 'عند النجاح، تنتهي المهمة هنا.',
          SYSTEM: {
            SAMPLE_PICK: 'من آخر ردّ لنظامك:',
            NO_SAMPLE: 'اختبر هذا الاتصال مرة وستظهر حقوله هنا لتختار منها.',
            TOGGLE: 'ماذا يجيب نظامك',
            REASON: 'سبب نظامك',
            REASON_HINT:
              'المكان الذي يكتب فيه نظامك سبب الرفض. نرسله للعميل كما هو.',
            SAY: 'أرسل رسالة نظامك للعميل كما هي',
            SAY_PATH: 'مكان الرسالة',
            SAY_PATH_AR: 'مكان الرسالة العربية، إن وُجدت',
            SAY_ON: 'أرسلها عندما',
            SAY_WORKED: 'ينجح الطلب',
            SAY_FAILED: 'يرفض نظامك',
            SAY_BOTH: 'في الحالتين',
            ANSWERS: 'إذا أجاب نظامك',
            ANSWERS_FIELD: 'أي جزء من الإجابة',
            FIELD_NAME: 'هو',
            CASE_IS: '{field} يساوي',
            CASE_HINT: 'افصل القيم بفواصل، حتى {limit}.',
            CASE_GO: 'انتقل إلى',
            ADD_CASE: 'أضف إجابة',
            REMOVE_CASE: 'احذف',
            ELSE: 'أي إجابة أخرى تكمل كما عند النجاح.',
          },
        },
        CHECK: {
          TRY: 'جرّبها قبل عملائك',
          TRY_HEADING: 'جرّب هذه المهمة',
          TRY_NOTE:
            'هذه المهمة كما هي على هذه الشاشة، محفوظة أو لا. لا يصل شيء إلى أي عميل.',
          TRY_START_NOW: 'ابدأ المهمة مع أول رسالة مني',
          TRY_NEEDS_NAME: 'سمِّ المهمة أولًا.',
          TRIED: 'جرّبتها {when}',
          NOT_TRIED: 'لم تُجرَّب منذ آخر تعديل',
          STARTS_WORDS: 'تبدأ عندما تحتوي الرسالة على: {words}',
          STARTS_MENU: 'تبدأ فقط من زر في القائمة',
          NO_MENU:
            'لا توجد قائمة تبدأ هذه المهمة بعد، فلا يصل إليها العملاء. أضفها إلى قائمة أولًا.',
          HEADING: 'هذا ما كتبته',
          DESCRIPTION: 'اضغط أي خطوة على الخريطة للرجوع إليها.',
          UNTITLED: 'هذه المهمة بلا اسم بعد',
          STARTS: 'تبدأ حين: {trigger}',
          STEPS:
            'الخطوات: {count} | الخطوات: {count} | الخطوات: {count} | الخطوات: {count} | الخطوات: {count} | الخطوات: {count}',
          JUMPS:
            'هذه القائمة تُقرأ من فوق لتحت. | هذه القائمة لا تُقرأ من فوق لتحت، فخطوة واحدة ترسل المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك. | هذه القائمة لا تُقرأ من فوق لتحت، فخطوتان ترسلان المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك. | هذه القائمة لا تُقرأ من فوق لتحت، فـ{count} خطوات ترسل المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك. | هذه القائمة لا تُقرأ من فوق لتحت، فـ{count} خطوةً ترسل المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك. | هذه القائمة لا تُقرأ من فوق لتحت، فـ{count} خطوة ترسل المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك.',
          JUMPS_ONE:
            'هذه القائمة لا تُقرأ من فوق لتحت — خطوة واحدة ترسل المحادثة إلى مكان آخر. والخريطة أسهل مكان لقراءة ذلك.',
          MAP_NOTE: 'الخريطة مرسومة من قائمتك. أنت لا ترسمها بنفسك أبدًا.',
          LIVE_OFF: 'هذه المهمة ليست شغّالة بعد.',
          LIVE_OFF_BODY:
            'لا شيء مما كتبته يصل إلى عميل حتى تشغّلها من هنا. وتقدر تطفيها متى ما شئت، من هذه الشاشة أو من قائمة المهام.',
          LIVE_ON: 'هذه المهمة شغّالة.',
          LIVE_ON_BODY:
            'مساعدك يستخدم النسخة المحفوظة مع العملاء الآن. وما غيّرته أعلاه لا يصلهم إلا بعد حفظه.',
          TURN_ON: 'شغّل هذه المهمة',
          TURN_OFF: 'أطفئ هذه المهمة',
          SAVE_OFF: 'احفظها وهي متوقفة',
          SAVE_CHANGES: 'احفظ هذه التغييرات',
          BLOCKED: 'أكمل الخطوات المُعلَّمة أولًا.',
          CHANGES_AFTER: 'يغيّر نظامك فقط بعد أن يجيب العميل: {question}',
          NEEDS_CONFIRM: 'قبل أن تعمل هذه المهمة',
          WARNINGS: 'يستحق نظرة قبل التشغيل',
          CTA: 'زر إرسال النموذج',
          CTA_HINT:
            'يُستخدم فقط عندما تُرسل هذه المهمة كنموذج داخل واتساب. اتركه فارغًا وسيقول الزر «إرسال».',
          WARN: {
            FAILURE_PATH:
              'عندما تنجح «{call}»، تكمل المحادثة إلى «{step}» التي جهّزتها لحالة الفشل.',
            ENDS_SILENTLY:
              'تنتهي المهمة بعد «{step}» مباشرة دون أن تخبر العميل بشيء.',
            KEPT_FOR_CHAT:
              '«{step}» محفوظ لهذه المحادثة فقط، فيُسأل العملاء العائدون مرة أخرى.',
            CONNECTION_OFF:
              '«{connection}» مطفأ. العملاء الذين يصلون إلى هذه الخطوة يُحوَّلون لفريقك.',
            SAVES_OVER:
              '«{step}» يحفظ إجابته في مكان يحفظ فيه سؤال سابق، لذلك لا يُسأل العملاء عنه أبدًا.',
          },
          SHOW_ME: 'أرني',
          FIX: {
            SKIP_WHEN_WORKS: 'تخطّها عند النجاح',
            SAVE_TO_PROFILE: 'احفظه في ملفهم أيضاً',
            OPEN_CONNECTION: 'افتحه',
          },
        },
      },
      BILINGUAL: {
        WRITTEN_ARIA: 'مكتوبة بـ{language}',
        MISSING: '{language}: غير مكتوبة',
        EDIT: 'تحرير',
        SUGGEST: 'اقترح',
        SUGGEST_FAILED:
          'لم ينجح هذا. لم يتغيّر أي شيء. اكتبها بنفسك أو حاول مرة أخرى.',
        SAVED_AS: 'حُفظت في خانة {language}.',
        LOOKS_LIKE:
          'تبدو هذه الكلمات باللغة {language}، لكنها محفوظة في خانة {other}.',
        CHANGE: 'غيّر',
        WRITE: 'اكتبها',
        BACK: 'عودة إلى {language}',
        TOO_LONG:
          'عدد الحروف: {count} من {max} | عدد الحروف: {count} من {max} | عدد الحروف: {count} من {max} | عدد الحروف: {count} من {max} | عدد الحروف: {count} من {max} | عدد الحروف: {count} من {max}',
        NOTE: 'مساعدك يردّ بلغة العميل من تلقاء نفسه. هذه الكلمات مختلفة: تظهر في نموذج داخل واتساب، والنموذج يرسلها كما كتبتها تمامًا.',
      },
      ARABIC: {
        TAB: 'كلمات النموذج',
        TITLE: 'كلمات النموذج',
        DESCRIPTION:
          'مساعدك يصوغ كل سؤال بنفسه داخل المحادثة. أما النموذج داخل واتساب فليس فيه من يصوغ شيئًا، فلا يُبنى إلا بلغة كتبت كلماتها بنفسك.',
        PROGRESS:
          'كُتب {written} من {total} | كُتب {written} من {total} | كُتب {written} من {total} | كُتب {written} من {total} | كُتب {written} من {total} | كُتب {written} من {total}',
        LEFT: 'الباقي: {count} | الباقي: {count} | الباقي: {count} | الباقي: {count} | الباقي: {count} | الباقي: {count}',
        ALL_DONE: 'كل كلمات هذا النموذج مكتوبة.',
        EMPTY: 'لا شيء لكتابته بعد',
        EMPTY_BODY:
          'لا يصلح للنموذج إلا سؤال تكتبه بنفسك. أضف خطوة سؤال وستظهر هنا.',
        OFF_SCREEN:
          'خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل. | خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل. | خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل. | خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل. | خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل. | خطوات ليست في هذه الشاشة: {count}. القائمة الحيّة والإجراء والرسالة والتحويل إلى شخص والنهاية وسؤال الوقت لا يمكن أن تكون في نموذج مهما كُتبت، فيسألها مساعدك داخل المحادثة بلغة العميل.',
        STEP: 'الخطوة {position}',
        OPTION_LABEL: 'خيار',
        STATE: {
          MISSING:
            'غير مكتوبة. لن تصل هذه المهمة كنموذج لعملاء {language} أصلًا.',
          FALLS_BACK:
            'غير مكتوبة. سيقرأ عملاء {language} هذا السطر وحده باللغة الأخرى.',
          NO_LABEL: 'غير مكتوبة بأي من اللغتين، فلا شيء يُعرض لهذا الخيار.',
          FROM_NAME: 'يُستخدم اسم الخطوة نفسه: «{name}»',
        },
        TRANSLATE:
          'ترجم الكلمات الناقصة ({count}) | ترجم الكلمات الناقصة ({count}) | ترجم الكلمات الناقصة ({count}) | ترجم الكلمات الناقصة ({count}) | ترجم الكلمات الناقصة ({count}) | ترجم الكلمات الناقصة ({count})',
        TRANSLATE_WORKING: 'جارٍ كتابتها…',
        TRANSLATE_FAILED:
          'لم ينجح هذا. لم يتغيّر أي شيء — اكتبها بنفسك أو حاول مرة أخرى.',
        TRANSLATE_NONE: 'لا شيء لاقتراحه.',
        SEE_WARNING: 'انظر ما يصل إلى العملاء',
        PROPOSED: 'اقتراح',
        PROPOSED_NOTE:
          'لا يُكتب شيء حتى تضغط «استخدم هذا». اقرأ كل واحدة أولًا — تصل هذه الكلمات إلى العميل كما هي.',
        ACCEPT: 'استخدم هذا',
        DISCARD: 'تجاهل',
        DISCARD_ALL: 'تجاهل كل الاقتراحات',
      },
    },
    MENUS: {
      TITLE: 'القوائم',
      ADD: 'أضف قائمة',
      SPOTLIGHT_TITLE: 'ما هي القائمة',
      SPOTLIGHT:
        'القائمة مجموعة قصيرة من الخيارات يلمسها عميلك بدل أن يكتب — حجز موعد، الأسعار، أوقات الدوام. يضعها مساعدك تحت ردّه حين تناسب المحادثة. في واتساب تظهر أزرارًا أو قائمة، وفي إنستغرام تصل قائمةً مرقّمة يردّ عليها العميل بالرقم. ويمكن للخيار أيضًا أن يبدأ إحدى مهامك.',
      EXAMPLE: {
        HEADING: 'مثال',
        BODY: 'أهلًا بك! كيف نقدر نساعدك؟',
        BUTTON: 'عرض الخيارات',
        OPTION_1: 'حجز موعد',
        OPTION_2: 'الأسعار',
        OPTION_3: 'أوقات الدوام',
        OPTION_4: 'تواصل معنا',
        NOTE: 'ثلاثة خيارات أو أقل تظهر أزرارًا، وأربعة فأكثر تظهر قائمةً خلف زر واحد. غيّر اللغة لترى ما يصل إلى عميل يكتب بالإنجليزية.',
      },
      LIST: {
        HEADING: 'قوائمك',
        DESCRIPTION:
          'التشغيل يعني أن مساعدك قد يعرضها. والإيقاف يبقيها هنا دون استخدامها.',
        WHEN: 'تظهر حين: {when}',
        OPTIONS:
          'الخيارات: {count} | الخيارات: {count} | الخيارات: {count} | الخيارات: {count} | الخيارات: {count} | الخيارات: {count}',
        USAGE_SHOWN:
          'لم تُعرض خلال الأيام السبعة الأخيرة. | عُرضت على عميل واحد خلال الأيام السبعة الأخيرة. | عُرضت على عميلين خلال الأيام السبعة الأخيرة. | عُرضت على {count} عملاء خلال الأيام السبعة الأخيرة. | عُرضت على {count} عميلًا خلال الأيام السبعة الأخيرة. | عُرضت على {count} عميل خلال الأيام السبعة الأخيرة.',
        UPDATED: 'عُدّلت {when}',
        UPDATED_JUST_NOW: 'الآن',
        EDIT: 'تعديل',
        ON: 'مشغّلة',
        OFF: 'مطفأة',
        SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
      },
      EMPTY: {
        TITLE: 'لا توجد قوائم بعد',
        BODY: 'مساعدك يجيب من دونها. أضف قائمة للأسئلة الأكثر تكرارًا، ليلمس العميل الإجابة بدل أن يكتبها.',
        ACTION: 'أضف قائمتك الأولى',
      },
      EDITOR: {
        NEW_TITLE: 'قائمة جديدة',
        SPOTLIGHT:
          'يكتب مساعدك ردّه كالمعتاد ويضع هذه القائمة تحته، ولا يسرد الخيارات بكلماته أبدًا.',
        ABOUT: {
          HEADING: 'ما هذه القائمة',
          DESCRIPTION: 'اسم لها، ومتى يعرضها مساعدك.',
        },
        NAME: {
          LABEL: 'ماذا تسمّيها؟',
          PLACEHOLDER: 'القائمة الرئيسية',
          HINT: 'يختار مساعدك القوائم بهذا الاسم، ولا يراه عملاؤك أبدًا.',
        },
        WHEN: {
          LABEL: 'متى تُعرض؟',
          PLACEHOLDER: 'حين يسلّم العميل أو يسأل عمّا نقدّمه.',
          HINT: 'صِف اللحظة لا الكلمات بعينها. يطابق مساعدك المعنى بأي لغة.',
        },
        OPTIONS: {
          HEADING: 'الخيارات',
          DESCRIPTION:
            'حتى {limit}، بالترتيب الذي تظهر به. اكتب كلًّا منها بالإنجليزية والعربية، فمساعدك يستخدم اللغة التي يكتب بها العميل.',
          LABEL: 'الخيار {number}',
          TITLE: 'ما يقوله الخيار',
          COUNT:
            '{count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit} | {count} من {limit}',
          TASK: 'عند اللمس',
          TASK_HINT:
            'إذا كانت هذه المهمة قيد التنفيذ، تستمر المحادثة كما هي. وإذا كانت مهمة أخرى قيد التنفيذ، تحل هذه محلها، ويبقى ما قاله العميل في المحادثة.',
          NO_TASK: 'الإجابة عن السؤال فقط',
          OPEN_TASK: 'افتح هذه المهمة',
          TASK_OFF: '{title} (متوقفة)',
          TASKS_UNAVAILABLE:
            'تعذّر عرض مهامك الآن، لذا لا يمكن تغيير ما يبدؤه الخيار هنا. ولا يتأثر شيء آخر.',
          STOP_WORD:
            'هذه كلمة يرسلها العملاء لإيقاف الرسائل أو استئنافها. اختر عنوانًا آخر.',
          ADD: 'أضف خيارًا',
          REMOVE: 'احذف هذا الخيار',
          UP: 'تحريك لأعلى',
          DOWN: 'تحريك لأسفل',
          FULL: 'يمكن أن تضم القائمة حتى {limit}.',
          BUTTONS_NOTE:
            'حين تكون الخيارات ثلاثة أو أقل يعرضها واتساب أزرارًا، فلا يتجاوز كلٌّ منها {limit}.',
          LIST_NOTE:
            'حين تكون أربعة فأكثر يعرضها واتساب قائمةً، فلا يتجاوز كلٌّ منها {limit}.',
        },
        MESSAGE: {
          HEADING: 'الكلمات المحيطة بها',
          DESCRIPTION:
            'عادةً يكون ردّ مساعدك نفسه فوق القائمة. ولا تُستخدم هذه إلا حين يطول ردّه عن حمل القائمة.',
          BODY: 'النص فوق القائمة',
          BODY_HINT: 'اتركهما فارغين لاستخدام «اختار من الخيارات:».',
          BUTTON: 'زر القائمة',
          BUTTON_HINT:
            'الزر الذي يفتح قائمة من أربعة خيارات فأكثر، حتى {limit}. اتركه فارغًا لاستخدام «اختار من القائمة».',
        },
        PREVIEW: {
          HEADING: 'ما يراه عميلك',
          DESCRIPTION:
            'معاينة لواتساب تتبع ما تكتبه. وكل ما هو أطول مما يسمح به واتساب يظهر باللون الأحمر.',
          LANGUAGE: 'لغة المعاينة',
          ENGLISH: 'الإنجليزية',
          ARABIC: 'العربية',
          SAMPLE_BODY: 'يظهر ردّ مساعدك هنا.',
          OVER: '{count} من {limit}، أطول من المسموح | {count} من {limit}، أطول من المسموح | {count} من {limit}، أطول من المسموح | {count} من {limit}، أطول من المسموح | {count} من {limit}، أطول من المسموح | {count} من {limit}، أطول من المسموح',
          TOO_LONG:
            'أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر. | أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر. | أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر. | أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر. | أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر. | أطول مما يسمح به واتساب: {count}. اختصر ما يظهر باللون الأحمر.',
          BORROWED:
            'بعض الخيارات بلا عنوان بهذه اللغة بعد، فيظهر عنوانها باللغة الأخرى — تمامًا كما سيصل إلى عميلك.',
        },
        STATE: {
          HEADING: 'استخدام هذه القائمة',
          DESCRIPTION:
            'يمكنك إبقاء القائمة هنا دون أن تسمح لمساعدك بعرضها بعد.',
          LABEL: 'يمكن لمساعدك عرض هذه القائمة',
          ON: 'سيعرض مساعدك هذه القائمة حين تناسب المحادثة.',
          OFF: 'لن يعرض مساعدك هذه القائمة أبدًا.',
        },
        REMOVE: {
          HEADING: 'حذف هذه القائمة',
          DESCRIPTION:
            'يتوقف مساعدك عن عرضها فورًا. ويُجاب عن لمسات القوائم المرسلة سابقًا كرسائل عادية.',
          ACTION: 'احذف هذه القائمة',
          CONFIRM: 'حذف هذه القائمة؟',
          CONFIRM_BODY:
            'يمكنك إرجاعها من «حُذف مؤخرًا» في القوائم خلال {period}. ويمكنك إطفاؤها بدلًا من ذلك.',
          CONFIRM_ACTION: 'نعم، احذفها',
          CANCEL: 'أبقِها',
          FAILED: 'تعذّر حذفها. لم يتغيّر شيء — حاول مرة أخرى.',
          REMOVED: 'حُذفت.',
        },
        SAVE: 'حفظ التغييرات',
        CREATE: 'أنشئ هذه القائمة',
        SAVED: 'حُفظت. يستخدمها مساعدك خلال دقيقة.',
        CREATED: 'أُنشئت. يستخدمها مساعدك خلال دقيقة.',
        SAVE_FAILED: 'تعذّر حفظ ذلك. لم يتغيّر شيء — حاول مرة أخرى.',
        NOT_FOUND: 'تعذّر العثور على هذه القائمة. ربما حُذفت.',
        STATUS: {
          IDLE: 'كل شيء محفوظ',
          IDLE_DETAIL: 'مساعدك يستخدم هذا الآن.',
          NEW: 'لم تُنشأ بعد',
          NEW_DETAIL: 'املأ الاسم، ومتى تُعرض، والخيار الأول.',
          DIRTY: 'لم تُحفظ بعد',
          DIRTY_DETAIL: 'بانتظار الحفظ: {sections}',
          SAVING: 'جارٍ الحفظ…',
          SAVED: 'حُفظت عند {time}',
          SAVED_DETAIL: '{sections} — يستخدمها مساعدك خلال دقيقة.',
          ERROR: 'تعذّر حفظ ذلك',
          ERROR_DETAIL:
            'لم يتغيّر شيء، وما كتبته ما زال في الصفحة. حاول مرة أخرى.',
        },
        ERRORS: {
          TITLE: 'أعطِ هذه القائمة اسمًا.',
          TITLE_LONG: 'هذا الاسم طويل جدًا. أبقِه دون {limit}.',
          WHEN: 'قل متى تُعرض هذه القائمة.',
          WHEN_LONG: 'هذا طويل جدًا. أبقِه دون {limit}.',
          OPTIONS_EMPTY: 'أضف خيارًا واحدًا على الأقل.',
          OPTIONS_MANY: 'يمكن أن تضم القائمة حتى {limit}.',
          OPTION_BLANK:
            'الخيار {number} بلا عنوان. اكتبه بالإنجليزية أو العربية أو كليهما.',
          OPTION_LONG:
            'الخيار {number} أطول مما يسمح به واتساب. أبقِه في حدود {limit} حرفًا.',
          OPTION_DUPLICATE: 'للخيار {number} العنوان نفسه لخيار سابق.',
          OPTION_MISSING_AR:
            'الخيار {number} يحتاج كلماته العربية: عملاؤك يقرؤون العربية فقط.',
          OPTION_MISSING_EN:
            'الخيار {number} يحتاج كلماته الإنجليزية: عملاؤك يقرؤون الإنجليزية فقط.',
          BODY_LONG: 'هذا أطول مما يسمح به واتساب. أبقِه دون {limit}.',
          BUTTON_LONG: 'هذا أطول مما يسمح به واتساب. أبقِه في حدود {limit}.',
        },
      },
    },
    TEMPLATES: {
      TITLE: 'الرسائل المعتمدة',
      ADD: 'اكتب رسالة جديدة',
      TABS: {
        LABEL: 'الرسائل المعتمدة والنماذج',
      },
      SPOTLIGHT_TITLE: 'واتساب يعتمد صياغتك قبل الإرسال',
      SPOTLIGHT:
        'واتساب لا يسمح للنشاط التجاري ببدء محادثة إلا بصياغة اعتمدها مسبقًا. اكتبها هنا ونرسلها إلى واتساب بالنيابة عنك — الاعتماد يستغرق دقائق عادةً، وقد يصل إلى يوم. أما الردود داخل محادثة مفتوحة فلا تحتاج إلى شيء من هذا.',
      SENDER_LABEL: 'أي رقم',
      REFRESH: 'اسأل واتساب عن آخر تحديث',
      REFRESHING: 'نسأل واتساب…',
      REQUESTED: 'سألنا واتساب عن آخر تحديث، وقرأنا الردّ في الأسفل.',
      REFRESH_FAILED:
        'لم نتمكّن من سؤال واتساب الآن. لم يتغيّر شيء — حاول مرة أخرى.',
      UNAVAILABLE: 'لم نتمكّن من قراءة رسائل هذا الرقم الآن.',
      LAST_CHECKED: 'آخر سؤال لواتساب: {when}',
      SUMMARY: 'استُخدمت {used} رسالة من {limit} على هذا الرقم.',
      STORED:
        'لم نتمكّن من الوصول إلى واتساب الآن، فهذه هي النسخة التي حفظناها في المرة السابقة. قد تكون قديمة، وتقييمات الجودة غير موجودة فيها.',
      BASIC_FIELDS:
        'ردّ واتساب لكنه لم يرسل التفاصيل الإضافية هذه المرة، لذلك تظهر تقييمات الجودة ومدد المحاولة فارغة في الأسفل. لا خطأ في رسائلك.',
      TRUNCATED:
        'لديك رسائل معتمدة أكثر مما تعرضه هذه الصفحة. ابحث بالاسم أو بنص الرسالة للوصول إلى البقية.',
      NO_SENDER: {
        TITLE: 'لا يوجد رقم واتساب في مساحة العمل بعد',
        BODY: 'الصياغة المعتمدة تنتمي إلى رقم واتساب. وبمجرد ربط رقم، تصبح هذه الصفحة هي المكان الذي تكتبها فيه.',
      },
      LIST: {
        HEADING: 'ما وافق عليه واتساب',
        DESCRIPTION:
          'كل ما يستطيع هذا الرقم استخدامه لبدء محادثة، وبجانبه حكم واتساب نفسه.',
      },
      EMPTY: {
        TITLE: 'لا شيء معتمد لهذا الرقم بعد',
        BODY: 'اكتب أول رسالة وسنرسلها إلى واتساب. ستظهر هنا مع ردّه بمجرد وصوله.',
      },
      STATUS: {
        APPROVED: 'معتمدة',
        PENDING: 'في انتظار واتساب',
        IN_APPEAL: 'قيد الاعتراض',
        PAUSED: 'أوقفها واتساب مؤقتًا',
        PENDING_DELETION: 'قيد الإزالة',
        REJECTED: 'مرفوضة',
        DISABLED: 'أوقفها واتساب نهائيًا',
        ARCHIVED: 'نحّاها واتساب جانبًا',
        DELETED: 'أُزيلت',
        LIMIT_EXCEEDED: 'تجاوزت الحد المسموح',
        UNTOLD: 'واتساب لم يخبرنا',
      },
      QUALITY: {
        HIGH: 'جودة عالية',
        MEDIUM: 'جودة متوسطة',
        LOW: 'جودة منخفضة',
        UNKNOWN: 'مبكّر على التقييم',
        NONE: 'لا تقييم بعد',
      },
      QUALITY_NOTE: {
        MEDIUM:
          'حظر هذه الرسالة أو أبلغ عنها عدد من العملاء بما يكفي لينتبه واتساب. وهذا هو التنبيه الوحيد الذي يسبق الإيقاف المؤقت.',
        LOW: 'عند هذا التقييم يوقف واتساب الرسالة {first}، ثم {second}، ثم يوقفها نهائيًا. غيّر الصياغة أو توقّف عن إرسالها.',
      },
      REASON: {
        INVALID_FORMAT: 'الصياغة أو الفراغات ليست بالشكل الذي يطلبه واتساب',
        TAG_CONTENT_MISMATCH: 'الصياغة لا تطابق نوع الرسالة المحتسب عليها',
        SCAM: 'قرأها واتساب على أنها نصب',
        ABUSIVE_CONTENT: 'قرأها واتساب على أنها مسيئة',
        INCORRECT_CATEGORY: 'محتسبة على نوع خاطئ من الرسائل',
        PROMOTIONAL: 'قرأها واتساب كإعلان لا كرسالة خدمة',
        CATEGORY_NOT_AVAILABLE: 'هذا النوع من الرسائل غير متاح على هذا الرقم',
      },
      SUB_CATEGORY: {
        ORDER_DETAILS: 'تفاصيل طلب',
        ORDER_STATUS: 'حالة طلب',
        RICH_ORDER_STATUS: 'حالة طلب مفصّلة',
      },
      BUTTON_TYPE: {
        QUICK_REPLY: 'زرّ ردّ',
        URL: 'رابط',
        PHONE_NUMBER: 'زرّ اتصال',
        COPY_CODE: 'زرّ نسخ الرمز',
        OTP: 'زرّ رمز الدخول',
        FLOW: 'زرّ نموذج',
        MPM: 'زرّ عدّة منتجات',
        SPM: 'زرّ منتج واحد',
        CATALOG: 'زرّ الكتالوج',
        VOICE_CALL: 'زرّ مكالمة واتساب',
        ORDER_DETAILS: 'زرّ دفع الطلب',
        APP: 'زرّ فتح التطبيق',
        REMINDER: 'زرّ تذكيري لاحقًا',
        EXTENSION: 'زرّ إضافي',
        POSTBACK: 'زرّ ردّ',
        SEND_LOCATION: 'زرّ إرسال الموقع',
      },
      EDIT_BLOCKED: {
        JOIN: '، ',
        MEDIA_HEADER:
          'هذه الرسالة فيها {part} في أعلاها. وواتساب يستبدل كل أجزاء الرسالة عند تعديلها، ولا نستطيع نقل هذا الجزء بعد — فتعديلها هنا سيزيله. عدّل هذه من إدارة واتساب.',
        CAROUSEL:
          'هذه الرسالة فيها مجموعة بطاقات تُسحب بالإصبع. وواتساب يستبدل كل أجزاء الرسالة عند تعديلها، ولا نستطيع نقل البطاقات بعد — فتعديلها هنا سيزيلها. عدّل هذه من إدارة واتساب.',
        OFFER:
          'هذه الرسالة فيها عرض لفترة محدودة. وواتساب يستبدل كل أجزاء الرسالة عند تعديلها، ولا نستطيع نقل العرض بعد — فتعديلها هنا سيزيله. عدّل هذه من إدارة واتساب.',
        BUTTON:
          'هذه الرسالة فيها {part}، ولا نستطيع نقله بعد. وواتساب يستبدل كل أجزاء الرسالة عند تعديلها، فتعديلها هنا سيزيل ذلك الزرّ. عدّل هذه من إدارة واتساب.',
        PART: 'هذه الرسالة فيها جزء لا نستطيع نقله بعد ({part}). وواتساب يستبدل كل أجزاء الرسالة عند تعديلها، فتعديلها هنا سيزيله. عدّل هذه من إدارة واتساب.',
        CHANGING:
          'تغيير نوع الاحتساب سيزيل {part} من هذه الرسالة. فواتساب يستبدل كل أجزاء الرسالة عند تعديلها، والنوعان لا يُبنيان بالطريقة نفسها. اتركها كما هي، أو اكتب رسالة جديدة بالنوع الآخر.',
        WORDING:
          'واتساب يكتب صياغة رسالة رمز الدخول بنفسه، فصياغتك أنت لا يمكن نقلها. عدّل هذه من إدارة واتساب.',
        PARTS: {
          CAROUSEL: 'البطاقات',
          OFFER: 'العرض',
          WORDING: 'صياغتك أنت',
          PART: 'ذلك الجزء',
        },
      },
      ROW: {
        NAME: 'اسمها عند واتساب',
        LANGUAGE: 'اللغة',
        CATEGORY: 'محتسبة كـ',
        CATEGORY_UNTOLD: 'واتساب لم يخبرنا',
        BLANKS:
          'فراغات تُملأ: {count} | فراغات تُملأ: {count} | فراغات تُملأ: {count} | فراغات تُملأ: {count} | فراغات تُملأ: {count} | فراغات تُملأ: {count}',
        NO_BLANKS: 'لا شيء يُملأ.',
        REASON: 'رفضها واتساب لأنّ {reason}.',
        EDIT: 'عدّل الصياغة',
        SHOW_FACTS: 'ما يعرفه واتساب',
        HIDE_FACTS: 'أخفِ التفاصيل',
      },
      CATEGORY: {
        UTILITY: 'شيء طلبه العميل',
        MARKETING: 'تسويق',
        AUTHENTICATION: 'رمز دخول',
        FREE_SERVICE: 'رسالة خدمة مجانية',
      },
      CATEGORY_NOTE: {
        UTILITY:
          'طلب أو حجز أو توصيل — أي شيء بدأه العميل بنفسه. وهي الأرخص في الإرسال.',
        MARKETING:
          'عرض أو دعوة أو أي شيء تبدأه أنت. تكلفتها أعلى لكل شخص، وواتساب يقرأ صياغتها بتدقيق.',
        AUTHENTICATION:
          'رمز يُستخدم مرة واحدة. واتساب يكتب نصّ هذا النوع بنفسه — وأنت تختار فقط مدّة صلاحية الرمز.',
      },
      FACTS: {
        UNTOLD: 'واتساب لم يخبرنا',
        NOT_SENT: 'واتساب لم يرسل هذه المعلومة',
        ID: 'رقمها عند واتساب',
        QUALITY: 'كيف يتفاعل العملاء',
        QUALITY_AS_OF: '{quality} — حتى {when}',
        CATEGORY: 'محتسبة كـ',
        SUB_CATEGORY: 'نوع الرسالة',
        MOVED_FROM: 'نقلها واتساب من {from}، وهذا غيّر تكلفة كل إرسال.',
        SHOULD_BE: 'يرى واتساب أنّ المفروض احتسابها كـ {should}.',
        BLANKS_FORMAT: 'طريقة كتابة الفراغات',
        FORMAT_NUMBERED: 'مرقّمة',
        FORMAT_NAMED: 'مُسمّاة',
        WINDOW_LABEL: 'إذا كان الهاتف مغلقًا',
        WINDOW_DEFAULT: 'المدة الافتراضية عند واتساب',
        WINDOW: {
          MINUTES:
            'يحاول أقل من دقيقة | يحاول لمدة دقيقة واحدة | يحاول لمدة دقيقتين | يحاول لمدة {count} دقائق | يحاول لمدة {count} دقيقة | يحاول لمدة {count} دقيقة',
          HOURS:
            'يحاول أقل من ساعة | يحاول لمدة ساعة واحدة | يحاول لمدة ساعتين | يحاول لمدة {count} ساعات | يحاول لمدة {count} ساعة | يحاول لمدة {count} ساعة',
          DAYS: 'يحاول أقل من يوم | يحاول لمدة يوم واحد | يحاول لمدة يومين | يحاول لمدة {count} أيام | يحاول لمدة {count} يومًا | يحاول لمدة {count} يوم',
          MAX: 'يحاول أطول مدة ممكنة | يحاول أطول مدة ممكنة (يوم واحد) | يحاول أطول مدة ممكنة (يومان) | يحاول أطول مدة ممكنة ({count} أيام) | يحاول أطول مدة ممكنة ({count} يومًا) | يحاول أطول مدة ممكنة ({count} يوم)',
        },
        TRACKING: 'احتساب النقرات على الرابط',
        TRACKING_ON: 'واتساب يحتسب النقرات على الرابط',
        TRACKING_OFF: 'واتساب لا يحتسب النقرات على الرابط',
        FROM_LIBRARY: 'منسوخة من رسالة جاهزة عند واتساب',
        PARTS: 'مِمَّ تتكوّن الرسالة',
        HEADER_LABEL: 'السطر الأعلى: ',
        FOOTER_LABEL: 'السطر الأسفل: ',
        NO_HEADER: 'لا سطر أعلى',
        NO_BUTTONS: 'لا أزرار.',
        SECURITY_LINE: 'يضيف واتساب سطره الخاص «لا تشارك هذا الرمز».',
        CODE_EXPIRY:
          'يخبر واتساب العميل أنّ الرمز ينتهي فورًا. | يخبر واتساب العميل أنّ الرمز يصلح دقيقة واحدة. | يخبر واتساب العميل أنّ الرمز يصلح دقيقتين. | يخبر واتساب العميل أنّ الرمز يصلح {count} دقائق. | يخبر واتساب العميل أنّ الرمز يصلح {count} دقيقة. | يخبر واتساب العميل أنّ الرمز يصلح {count} دقيقة.',
        CARDS:
          'مجموعة بطاقات تُسحب بالإصبع. | بطاقة واحدة تُسحب بالإصبع. | بطاقتان تُسحبان بالإصبع. | مجموعة من {count} بطاقات تُسحب بالإصبع. | مجموعة من {count} بطاقة تُسحب بالإصبع. | مجموعة من {count} بطاقة تُسحب بالإصبع.',
        OFFER: 'عرض لفترة محدودة: {text}',
        HEADER: {
          IMAGE: 'صورة',
          VIDEO: 'مقطع مرئي',
          GIF: 'صورة متحركة',
          DOCUMENT: 'ملف',
          LOCATION: 'موقع على الخريطة',
          PRODUCT: 'منتج',
          TEXT: 'سطر نصّي',
        },
      },
      LIFECYCLE: {
        TITLE: 'ما يفعله واتساب من تلقاء نفسه',
        REVIEW:
          'المراجعة: كل رسالة جديدة أو معدّلة يقرأها واتساب. دقائق عادةً، وقد تصل إلى يوم.',
        PACING:
          'مراقبة الرسالة الجديدة: قد يحتجز واتساب أول إرسالات رسالة جديدة تمامًا حتى يرى تفاعل العملاء، ثم يطلقها.',
        PAUSING:
          'الإيقاف المؤقت: إذا حجبها أو أبلغ عنها عدد كافٍ من العملاء، يوقفها واتساب {first}، ثم {second} إن تكرّر الأمر، ويوقفها نهائيًا في المرة الثالثة.',
        ARCHIVAL:
          'التنحية جانبًا: الرسالة التي لم يستخدمها أحد لمدة سنة ينحّيها واتساب جانبًا، ويحذفها بعد {period} إلا إذا أعدتها من إدارة واتساب نفسها.',
        PAUSED:
          'الإيقاف لمدة {first} أو {second} ينتهي وحده. أما إذا أوقفها واتساب أثناء مراقبته لرسالة جديدة، فعليك رفع الإيقاف بنفسك.',
        DISABLED:
          'أوقفها واتساب بعد شكاوى متكرّرة. لا يمكن إرسالها، ولا يسمح لنا واتساب بإزالتها. وإرشادات واتساب نفسها تتعارض حول ما يليها: صفحة تقول إنّ الرسالة الموقوفة يمكن تعديلها وإعادتها للمراجعة، وأخرى تقول إنّ التعديل متاح للمعتمدة والمرفوضة والموقوفة مؤقتًا فقط. جرّبها من إدارة واتساب قبل أن تتخلّى عن الاسم.',
        ARCHIVED:
          'ينحّي واتساب الرسالة التي لم يستخدمها أحد سنة كاملة، ثم يحذفها بعد {period} نهائيًا. أعدها من إدارة واتساب قبل ذلك.',
        PENDING_DELETION:
          'في طريقها إلى الخارج. يظلّ واتساب يحاول توصيل ما أُرسل فعلًا حتى {period}، ثم تنتهي.',
      },
      ACTIONS: {
        UNPAUSE: 'ارفع الإيقاف',
        UNPAUSING: 'نسأل واتساب…',
        UNPAUSED: 'طلبنا من واتساب رفع الإيقاف وأعدنا قراءة القائمة.',
        UNPAUSE_FAILED:
          'لم يرفع واتساب الإيقاف. لم يتغيّر شيء — حاول بعد دقائق.',
        APPEAL: 'اطلب من واتساب إعادة النظر',
        UNARCHIVE: 'أعدها من عند واتساب',
        REMOVE: 'أزلها من واتساب',
      },
      REMOVE: {
        TITLE: 'هل تريد إزالة هذه الرسالة من واتساب؟',
        BODY: 'اقرأ هذا قبل أن تفعل. الإزالة تحدث عند واتساب ولا نستطيع التراجع عنها.',
        FOREVER: 'لا تراجع عنها — لا منّا ولا من واتساب ولا من الدعم.',
        NAME_LOCKED: 'لن تستطيع استخدام هذا الاسم مرة أخرى لمدة {period}.',
        IN_FLIGHT: 'ما أُرسل فعلًا يظلّ يحاول الوصول حتى {period}.',
        BREAKS_SENDS:
          'أي إرسال أو حملة مبنية عليها تتوقّف عن العمل، عميلًا بعد عميل.',
        SCOPE_LABEL: 'ما مدى الإزالة',
        SCOPE_LANGUAGE: 'نسخة {language} فقط',
        SCOPE_NAME:
          'كل لغات هذا الاسم ({count}) | كل لغات هذا الاسم ({count}) | كل لغات هذا الاسم ({count}) | كل لغات هذا الاسم ({count}) | كل لغات هذا الاسم ({count}) | كل لغات هذا الاسم ({count})',
        SCOPE_NAME_WARNING:
          'ستُزال معها أيضًا: {languages}. واتساب يزيل بالاسم، فتذهب كلها معًا.',
        TYPE_LABEL: 'اكتب {name} للتأكيد',
        TYPE_HINT: 'بالحرف نفسه كما هو مكتوب أعلاه.',
        CONFIRM: 'أزلها من واتساب',
        REMOVING: 'جارٍ الإزالة…',
        CANCEL: 'أبقِها',
        DONE: 'أُزيلت {name} من واتساب.',
        FAILED: 'لم يُزلها واتساب. لم يتغيّر شيء — حاول بعد دقائق.',
      },
      ANALYTICS: {
        HEADING: 'كيف كان أداؤها',
        LOAD: 'اعرض أداءها',
        SENT: 'أُرسلت',
        DELIVERED: 'وصلت',
        READ: 'قُرئت',
        WINDOW:
          'اليوم. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا. | آخر يوم. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا. | آخر يومين. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا. | آخر {count} أيام. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا. | آخر {count} يومًا. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا. | آخر {count} يوم. واتساب يحتفظ بعدد القراءات والنقرات سبعة أيام من كل إرسال ثم يصفّرها، فالمدة الأطول ستعرض أصفارًا لرسائل قُرئت فعلًا.',
        EMPTY:
          'لم تُرسل هذه اليوم، فلا شيء نعرضه بعد. | لم تُرسل هذه خلال آخر يوم، فلا شيء نعرضه بعد. | لم تُرسل هذه خلال آخر يومين، فلا شيء نعرضه بعد. | لم تُرسل هذه خلال آخر {count} أيام، فلا شيء نعرضه بعد. | لم تُرسل هذه خلال آخر {count} يومًا، فلا شيء نعرضه بعد. | لم تُرسل هذه خلال آخر {count} يوم، فلا شيء نعرضه بعد.',
        CLICK: {
          URL_BUTTON: 'نقرات على الرابط',
          UNIQUE_URL_BUTTON: 'أشخاص نقروا على الرابط',
          QUICK_REPLY_BUTTON: 'نقرات على أزرار الردّ',
        },
        NO_CLICKS_AUTH: 'واتساب لا يحتسب نقرات الأزرار في رسالة رمز الدخول.',
        TRACKING_OFF:
          'النقرات على الرابط غير محتسبة في هذه، لأنّ تتبّع الروابط موقوف لها.',
        OFF: 'واتساب لم يبدأ العدّ لهذا النشاط بعد. وهو يعدّ من اليوم الذي تطلبه فيه فقط، ولا يمكن إيقافه بعد تشغيله.',
        CONSENT:
          'بكلمات واتساب: تشغيل هذا يوجّه ميتا إلى إضافة التحليلات إلى حساب واتساب للأعمال، بما فيها تتبّع الروابط لعدّ النقرات على موقعك، وإلى جمع بيانات من محادثاتك مع العملاء وإخفاء هويّتها لتحسين خدماتها. ويمكنك إيقاف تتبّع الروابط في كل رسالة على حدة.',
        ENABLE: 'ابدأ العدّ',
        ENABLING: 'نسأل واتساب…',
        ENABLE_FAILED: 'لم يبدأ واتساب العدّ. لم يتغيّر شيء — حاول بعد دقائق.',
        UNAVAILABLE:
          'واتساب لا يوفّر هذه الأرقام لهذا الحساب. فهو يستثني الحسابات في الاتحاد الأوروبي واليابان.',
        FAILED: 'لم نتمكّن من قراءة هذه الأرقام الآن.',
      },
      FILTER: {
        SEARCH_LABEL: 'ابحث',
        SEARCH_HINT: 'يبحث في الاسم وفي نصّ الرسالة، بأي لغة.',
        SEARCH_PLACEHOLDER: 'الطلب جاهز',
        STATUS: 'حكم واتساب',
        CATEGORY: 'محتسبة كـ',
        LANGUAGE: 'اللغة',
        QUALITY: 'تفاعل العملاء',
        ANY_STATUS: 'أي حكم',
        ANY_CATEGORY: 'أي نوع',
        ANY_LANGUAGE: 'أي لغة',
        ANY_QUALITY: 'أي تفاعل',
        ATTENTION:
          'تحتاج إليك ({count}) | تحتاج إليك ({count}) | تحتاج إليك ({count}) | تحتاج إليك ({count}) | تحتاج إليك ({count}) | تحتاج إليك ({count})',
        SHOWING:
          'نعرض {showing} من {total}. | نعرض {showing} من {total}. | نعرض {showing} من {total}. | نعرض {showing} من {total}. | نعرض {showing} من {total}. | نعرض {showing} من {total}.',
        CLEAR: 'اعرض الكل',
        MORE: 'اعرض المزيد | اعرض واحدة أخرى | اعرض اثنتين أخريين | اعرض {count} أخرى | اعرض {count} أخرى | اعرض {count} أخرى',
        NONE_TITLE: 'لا شيء يطابق',
        NONE_BODY:
          'جرّب كلمات أقل، أو امسح عوامل التصفية لرؤية كل شيء على هذا الرقم.',
      },
      EDITOR: {
        TITLE: 'اكتب رسالة ليعتمدها واتساب',
        TITLE_EDIT: 'عدّل رسالة معتمدة',
        NO_SENDER:
          'افتح هذه الصفحة من صفحة الرسائل المعتمدة حتى نعرف لأي رقم هي.',
        SUBMIT: 'أرسلها إلى واتساب',
        SAVE: 'أرسل التعديل إلى واتساب',
        SUBMITTING: 'جارٍ الإرسال…',
        SUBMIT_NOTE:
          'القرار لواتساب. نُمرّر صياغتك كما كتبتها بالحرف ونعرض لك ردّه.',
        EDIT_NOTE:
          'الحفظ يعيد إرسالها إلى واتساب ليقرأها من جديد. الرسالة المعتمدة يمكن تعديلها {times} في {period}، ومرة واحدة في اليوم.',
        REFUSED:
          'لم يقبل واتساب هذه الصياغة ولم يوضّح السبب. غيّر الصياغة وحاول مرة أخرى — ولم تُرسل إلى أي عميل.',
        CURRENT: 'ما يقوله واتساب الآن:',
        NOT_EDITABLE:
          'واتساب يسمح بتعديل الرسالة المعتمدة أو المرفوضة أو الموقوفة مؤقتًا فقط. وهذه لا يمكن تعديلها في حالتها الحالية.',
        GONE: 'هذه الرسالة لم تعد موجودة عند واتساب.',
        LOAD_FAILED: 'لم نتمكّن من قراءة هذه الرسالة من واتساب الآن.',
        FORMAT_LABEL: 'كيف تُكتب الفراغات',
        FORMAT_LOCKED: 'لا يمكن تغيير هذا بعد أن يعتمد واتساب الرسالة.',
        FORMAT: {
          POSITIONAL: 'مرقّمة',
          NAMED: 'مُسمّاة',
        },
        FORMAT_READ: {
          POSITIONAL:
            'فراغاتك مرقّمة، مثل {blank}. وأنظمتك تملأها بحسب ترتيبها.',
          NAMED: 'فراغاتك مُسمّاة، مثل {named}. وأنظمتك تملأها بالاسم.',
        },
        SWITCH: {
          TITLE: 'احتسابها كـ«{category}» يغيّر هذه الصفحة',
          HIDES: 'هذه تختفي من الصفحة: {parts}.',
          SHOWS: 'وتظهر بدلًا منها: {parts}.',
          KEPT: 'لا يضيع شيء ممّا كتبته — يبقى هنا، وباختيار «{from}» مرة أخرى يعود كما هو.',
          CLEARS:
            'هذه وحدها تُمسح فعلًا، لأن واتساب يسمح لها بمدى مختلف: {parts}.',
          CONFIRM: 'احتسبها كـ«{category}»',
          CANCEL: 'أبقِها «{from}»',
        },
        PART: {
          HEADER: 'السطر الأعلى',
          BODY: 'الرسالة',
          FOOTER: 'السطر الأسفل',
          BUTTONS: 'الأزرار',
          BUTTON: 'زرّ',
          SECURITY: 'سطر الأمان من واتساب',
          EXPIRY: 'مدة صلاحية الرمز',
          WINDOW: 'مدة محاولة واتساب التسليم',
        },
        ABOUT: {
          HEADING: 'بماذا تسمّيها',
          DESCRIPTION:
            'فريقك وأنظمتك تطلب الرسالة بهذا الاسم، فاختر اسمًا تعرفه بعد سنة.',
          NAME_LABEL: 'الاسم',
          NAME_HINT:
            'حروف إنجليزية صغيرة وأرقام وشرطة سفلية فقط — مثل order_ready_v1. بلا مسافات.',
          NAME_LOCKED:
            'الاسم لا يتغيّر. واتساب يعدّه هويّة الرسالة — والاسم الجديد يعني رسالة جديدة.',
          NAME_FIX: 'استخدم {name}',
          LANGUAGE_LABEL: 'اللغة',
          LANGUAGE_HINT:
            'اكتبها كما يكتبها واتساب: ar للعربية، en للإنجليزية، en_US للإنجليزية الأمريكية. وحالة الحروف مهمّة.',
          LANGUAGE_LOCKED: 'اللغة لا تتغيّر. اكتب رسالة منفصلة للغة أخرى.',
          CATEGORY_LABEL: 'محتسبة كـ',
          CATEGORY_LOCKED: 'واتساب لا يسمح بتغيير هذا بعد اعتماد الرسالة.',
          CATEGORY_CHANGING:
            'أنت تغيّر نوع الاحتساب، وهذا يغيّر سعر كل إرسال. وسيقرأ واتساب الصياغة من جديد بحسب النوع الجديد.',
        },
        MESSAGE: {
          HEADING: 'ما يقرأه العميل',
          DESCRIPTION:
            'اكتبها كما تقولها، واضع فراغًا في موضع الاسم أو الرقم أو التاريخ.',
          HEADER_LABEL: 'سطر أعلاها (اختياري)',
          HEADER_HINT:
            'حتى {limit}. يُسمح بفراغ واحد هنا، ويطلب واتساب مثالًا له.',
          HEADER_SAMPLE: 'مثال لفراغ السطر الأعلى',
          BODY_LABEL: 'الرسالة',
          BODY_HINT_NUMBERED:
            'حتى {limit}. اكتب الفراغ هكذا {blank}، ثم {next}، ولا تتجاوز رقمًا.',
          BODY_HINT_NAMED:
            'حتى {limit}. اكتب الفراغ هكذا {named}، بحروف صغيرة وشرطة سفلية.',
          FOOTER_LABEL: 'سطر أسفلها (اختياري)',
          FOOTER_HINT: 'حتى {limit}، وغالبًا اسم متجرك.',
          RENUMBER: 'رقّم الفراغات بالترتيب',
        },
        BLANKS: {
          HEADING: 'مثال لكل فراغ',
          DESCRIPTION:
            'لن يعتمد واتساب صياغةً فيها فراغات قبل أن يرى ما يوضع فيها. وهذه الأمثلة لواتساب وللمعاينة فقط — ولا يراها أي عميل.',
          LABEL: 'مثال للفراغ {number}',
        },
        BUTTONS: {
          HEADING: 'الأزرار (اختياري)',
          DESCRIPTION:
            'حتى عشرة في المجموع: عشرة أزرار ردّ، ورابطان، وزرّ اتصال واحد، وزرّ نسخ رمز واحد. وزرّ الردّ يعيد العميل إلى صندوقك مباشرة.',
          TYPE_LABEL: 'ماذا يفعل',
          TEXT_LABEL: 'ماذا يقول',
          TEXT_HINT: 'حتى {limit}. والأقصر أوضح على الهاتف.',
          TEXT_PLACEHOLDER: 'تابع طلبي',
          URL_LABEL: 'إلى أين يذهب',
          URL_HINT:
            'عنوان كامل يبدأ بـ https://. ويمكنك وضع فراغ واحد في آخره فقط، ليتغيّر الرابط حسب كل عميل.',
          URL_SAMPLE_LABEL: 'مثال لفراغ نهاية الرابط',
          URL_SAMPLE_HINT:
            'الجزء المتغيّر فقط — ونحن نضعه داخل العنوان قبل إرساله إلى واتساب.',
          PHONE_LABEL: 'أي رقم يتصل به',
          PHONE_HINT: 'مع رمز الدولة، مثل +96522251188.',
          CODE_LABEL: 'الرمز الذي يُنسخ',
          CODE_HINT:
            'حتى {limit}. وواتساب يكتب نصّ الزرّ بنفسه، ولا يمكنك تغييره.',
          COPY_CODE_LABEL: 'انسخ الرمز',
          ADD: 'أضف {type}',
          REMOVE: 'أزل',
          FULL: 'هذا أقصى عدد أزرار يسمح به واتساب.',
          GROUPING:
            'يطلب واتساب أن تكون أزرار الردّ متجاورة، غير مفصولة برابط أو بزرّ اتصال.',
          REGROUP: 'اجمع أزرار الردّ معًا',
          SHAPE:
            'لا أزرار أسفل هذه الرسالة. | يعرض واتساب هذا الزرّ أسفل الرسالة. | يعرض واتساب الزرّين كليهما أسفل الرسالة. | يعرض واتساب هذه الأزرار الـ{count} كلها أسفل الرسالة. | يعرض واتساب هذه الأزرار الـ{count} كلها أسفل الرسالة. | يعرض واتساب هذه الأزرار الـ{count} كلها أسفل الرسالة.',
          CROWDED:
            'لا أزرار. | زرّ واحد. | زرّان. | {count} أزرار: يعرض واتساب {shown} ويخفي الـ{hidden} الباقية خلف «عرض كل الخيارات»، ولا يمكن فتح الرسالة على واتساب للكمبيوتر. | {count} زرًّا: يعرض واتساب {shown} ويخفي الـ{hidden} الباقية خلف «عرض كل الخيارات»، ولا يمكن فتح الرسالة على واتساب للكمبيوتر. | {count} زرّ: يعرض واتساب {shown} ويخفي الـ{hidden} الباقية خلف «عرض كل الخيارات»، ولا يمكن فتح الرسالة على واتساب للكمبيوتر.',
          TYPES: {
            QUICK_REPLY: 'زرّ ردّ',
            URL: 'رابطًا',
            PHONE_NUMBER: 'زرّ اتصال',
            COPY_CODE: 'زرّ نسخ رمز',
          },
        },
        AUTH: {
          HEADING: 'واتساب يكتب هذه بنفسه',
          DESCRIPTION:
            'في رسالة رمز الدخول، يكتب واتساب الصياغة بنفسه وبكل اللغات، ويضيف زرًّا ينسخ الرمز. وأنت تختار أمرين فقط.',
          SECURITY_LABEL: 'أضف سطر السلامة من واتساب',
          SECURITY_NOTE:
            'يضيف واتساب في النهاية: «لأمانك، لا تشارك هذا الرمز».',
          EXPIRY_LABEL: 'مدّة صلاحية الرمز (بالدقائق)',
          EXPIRY_HINT:
            'بين {min} و{max}. يخبر واتساب العميل بها بلغته. اتركها فارغة إن لم ترد ذكر شيء.',
          PREVIEW_BODY: '‏123456 هو رمز التحقق الخاص بك.',
          PREVIEW_SECURITY: 'لأمانك، لا تشارك هذا الرمز.',
          PREVIEW_EXPIRY:
            'ينتهي هذا الرمز فورًا. | ينتهي هذا الرمز بعد دقيقة واحدة. | ينتهي هذا الرمز بعد دقيقتين. | ينتهي هذا الرمز بعد {count} دقائق. | ينتهي هذا الرمز بعد {count} دقيقة. | ينتهي هذا الرمز بعد {count} دقيقة.',
          PREVIEW_NOTE:
            'واتساب يترجم هذه الصياغة بنفسه، فالعميل الذي يقرأ العربية يرى العربية.',
        },
        WINDOW: {
          HEADING: 'إذا كان الهاتف مغلقًا',
          DESCRIPTION:
            'كم يظلّ واتساب يحاول التوصيل قبل أن يتوقّف. المدة القصيرة تناسب رمزًا ينتهي، والطويلة تناسب تحديث طلب.',
          LABEL: 'استمر بالمحاولة',
          DEFAULT: 'المدة الافتراضية عند واتساب',
          MARKETING:
            'واتساب لا يسمح بتحديد هذه المدة لرسائل التسويق عبر الاتصال الذي نستخدمه. ويطبّق مدّته الخاصة وهي {period}.',
        },
        GOAL: {
          HEADING: 'ابدأ من الهدف',
          DESCRIPTION:
            'اختر الغرض من الرسالة وأضف جملة واحدة. نكتب لك مسودة أولى بالعربية والإنجليزية لتراجعها وترسلها إلى واتساب. لا شيء يرسل الرسالة المعتمدة من تلقاء نفسه: ترسلها أنت في رسالة واتساب جماعية، أو يرسلها المطوّر من قسم «للمطوّرين».',
          CHIPS: {
            APPOINTMENT_REMINDER: 'تذكير بموعد',
            ORDER_READY: 'الطلب جاهز',
            BACK_IN_STOCK: 'عاد إلى المخزون',
            OFFER: 'عرض',
            CUSTOM: 'صِفها بنفسك',
          },
          SENTENCE_LABEL: 'في جملة واحدة، ماذا تقول الرسالة؟',
          SENTENCE_HINT: 'مثلًا: ذكّرهم بزيارتهم مع الوقت والفرع.',
          SENTENCE_NEEDED: 'اكتب في جملة واحدة الغرض من الرسالة.',
          WRITE: 'اكتبها لي',
          WRITING: 'نكتبها…',
          WRITTEN: 'كُتبت لك. راجع كل كلمة قبل أن ترسلها إلى واتساب.',
          UNUSABLE:
            'عادت المسودة بشيء لا يمكننا استخدامه، مثل سعر أو رابط لم تعطنا إياه. لم يُخصم شيء. حاول مرة أخرى أو اكتبها بنفسك.',
          FAILED:
            'تعذّر كتابة مسودة الآن. لم يُخصم شيء. حاول مرة أخرى أو اكتبها بنفسك.',
          OTHER: {
            AR: 'اكتب النسخة العربية أيضًا',
            EN: 'اكتب النسخة الإنجليزية أيضًا',
          },
          OTHER_NOTE: 'تستخدم الإجابة نفسها، فلا تكلّف شيئًا إضافيًا.',
          SUGGESTED: 'المقترح: {category}. {reason}',
        },
        PREVIEW: {
          HEADING: 'ما سيراه عميلك',
          DESCRIPTION:
            'فقاعة واتساب الحقيقية، وأمثلتك مملوءة فيها. والفراغ الذي لم تعطه مثالًا يظهر برقمه.',
          EMPTY: 'اكتب الرسالة فتظهر هنا كما سيقرأها عميلك.',
          OVER: '{part} فيه {length} حرفًا. وواتساب يسمح بـ{limit}.',
          MEDIA_HEADER: 'هنا تظهر صورة أو فيديو أو ملف',
          SEE_ALL: 'عرض كل الخيارات',
        },
        SENT: {
          TITLE: 'أُرسلت إلى واتساب',
          BODY: 'هذا ردّ واتساب حتى الآن. وتظهر الرسالة في صفحة الرسائل المعتمدة، ويتحدّث الحكم هناك أثناء مراجعته لها.',
          BODY_UNTOLD:
            'استلمها واتساب ولم يصدر حكمه بعد. اسأله عن آخر تحديث في صفحة الرسائل المعتمدة بعد دقائق.',
          RECATEGORISED:
            'نقلها واتساب من {asked} إلى {given}، وهذا يغيّر تكلفة كل إرسال. تحقّق من السعر قبل استخدامها.',
          BACK: 'رجوع إلى الرسائل المعتمدة',
        },
        SAVED: {
          TITLE: 'أُرسل التعديل إلى واتساب',
          BODY: 'استلم واتساب التعديل ولم يذكر ما قرّره. اسأله عن آخر تحديث في صفحة الرسائل المعتمدة بعد دقائق.',
        },
        ERRORS: {
          NAME_BLANK: 'أعطها اسمًا لتستطيع طلبها لاحقًا.',
          NAME_INVALID:
            'واتساب يسمح هنا بالحروف الإنجليزية الصغيرة والأرقام والشرطة السفلية فقط.',
          NAME_LONG: 'هذا الاسم أطول مما يقبله واتساب.',
          LANGUAGE_BLANK: 'حدّد لغة هذه الصياغة.',
          LANGUAGE_INVALID:
            'اكتبها كما يكتبها واتساب — ar أو en أو en_US. والشرطة لا تصلح.',
          CATEGORY_INVALID: 'اختر نوع احتساب هذه الرسالة.',
          BODY_BLANK: 'اكتب ما سيقرأه العميل.',
          BODY_LONG: 'هذه أطول مما يقبله واتساب. اجعلها أقل من {limit}.',
          BODY_NAMED_BLANK:
            'أنت تستخدم فراغات مرقّمة، فاكتب {blank} ثم {next}. أو حوّل إلى الفراغات المُسمّاة أعلاه إن كنت تفضّل الكلمات.',
          BLANK_SYMBOL:
            'الفراغ هنا يحمل رقمًا ولا شيء غيره — اكتب {blank} ثم {next}. والعلامات مثل # أو $ أو % لا توضع داخل القوسين، فاتركها في الصياغة حول الفراغ.',
          BODY_NOT_A_NAME:
            'أنت تستخدم فراغات مُسمّاة، فاكتب {named} — حروف صغيرة وشرطة سفلية، بلا مسافات.',
          /**
           * تنبيه لا رفض — انظر `nameWithDigit`. واتساب يذكر الحروف المسموحة ولا يذكر الرقم بين
           * أسباب رفضه للصياغة، فهذه الجملة تقول ما يذكره وما قد يكلّفها، وزرّ الحفظ لا يقرأها.
           */
          NAME_DIGIT:
            'واتساب يصف الفراغ المُسمّى بأنه حروف صغيرة وشرطة سفلية، و{name} فيه رقم. وقد يعود مرفوضًا بعد أن يقرأ واتساب هذه الصياغة — يمكنك حفظها كما هي، أو تسميته بالحروف فقط احتياطًا.',
          BODY_SEQUENCE:
            'ابدأ من {blank} ولا تتجاوز رقمًا. واتساب يرفض الفجوة.',
          BLANK_BRACES:
            'الفراغ يُكتب بقوسين على كل جانب، هكذا {blank}. وأحدها هنا ينقصه قوسه، فيرسل واتساب الأقواس ضمن نص الرسالة.',
          BODY_DANGLING:
            'لا يصحّ أن تبدأ الرسالة بفراغ ولا أن تنتهي به. اكتب كلمة قبل الفراغ الأول وبعد الأخير — واتساب يرفض الصياغة التي تبدأ بفراغ أو تنتهي به.',
          DUPLICATE:
            'هذه الصياغة مطابقة حرفًا بحرف لـ {name}، في الرسالة وفي السطر الأسفل. وواتساب يرفض المكرّر — غيّر كلمة، أو استعمل تلك بدلًا منها.',
          SAMPLES_MISSING:
            'أعطِ مثالًا لكل فراغ. واتساب لا يعتمد الصياغة بدونها.',
          SAMPLES_LONG: 'اجعل كل مثال قصيرًا — كلمات قليلة تكفي.',
          HEADER_LONG: 'السطر الأعلى يجب أن يكون أقل من {limit}.',
          HEADER_ONE_BLANK:
            'السطر الأعلى يقبل فراغًا واحدًا على الأكثر. انقل الباقي إلى الرسالة.',
          HEADER_SAMPLE_MISSING: 'أعطِ مثالًا لفراغ السطر الأعلى.',
          FOOTER_LONG: 'السطر الأسفل يجب أن يكون أقل من {limit}.',
          FOOTER_NO_BLANKS:
            'السطر الأسفل لا يقبل فراغًا. انقله إلى داخل الرسالة.',
          TTL_NOT_FOR_CATEGORY:
            'واتساب لا يسمح بتحديد هذه المدة لهذا النوع من الرسائل.',
          TTL_RANGE: 'اختر إحدى المدد التي يسمح بها واتساب.',
          EXPIRY_RANGE: 'بين {min} و{max}.',
          BUTTON_AT: 'الزرّ {number}: {reason}',
          BUTTON_TEXT_BLANK: 'حدّد ما يقوله.',
          BUTTON_TEXT_LONG: 'هذه الصياغة أطول من أن تصلح لزرّ، فالحد {limit}.',
          BUTTON_URL_BLANK: 'حدّد إلى أين يذهب الرابط.',
          BUTTON_URL_LONG: 'هذا العنوان طويل جدًا.',
          BUTTON_URL_INVALID: 'هذا ليس عنوانًا كاملًا.',
          BUTTON_URL_ONE_BLANK:
            'الرابط يقبل فراغًا واحدًا على الأكثر، في آخره تمامًا.',
          BUTTON_URL_AT_END: 'يجب أن يكون الفراغ في آخر العنوان تمامًا.',
          BUTTON_URL_SAMPLE: 'أعطِ مثالًا لما يوضع في فراغ النهاية.',
          BUTTON_PHONE_BLANK: 'حدّد أي رقم يتصل به.',
          BUTTON_PHONE_INVALID: 'هذا لا يشبه رقم هاتف مع رمز دولة.',
          BUTTON_CODE_BLANK: 'حدّد الرمز الذي ينسخه العميل.',
          BUTTON_CODE_LONG: 'هذا الرمز أطول من {limit}.',
          BUTTONS_MANY: 'واتساب يسمح بعشرة أزرار في المجموع.',
          BUTTONS_MANY_QUICK_REPLY: 'واتساب يسمح بعشرة أزرار ردّ على الأكثر.',
          BUTTONS_MANY_URL: 'واتساب يسمح برابطين على الأكثر.',
          BUTTONS_MANY_PHONE_NUMBER: 'واتساب يسمح بزرّ اتصال واحد على الأكثر.',
          BUTTONS_MANY_COPY_CODE: 'واتساب يسمح بزرّ نسخ رمز واحد على الأكثر.',
          BUTTONS_GROUPING:
            'اجعل أزرار الردّ متجاورة — واتساب يرفضها مفصولة برابط أو بزرّ اتصال.',
          WIRE_BLANK: 'واتساب يحتاج إلى تعبئة هذا.',
          WIRE_INVALID: 'واتساب لن يقبل هذا.',
          WIRE_TOO_LONG: 'هذا أطول مما يسمح به واتساب هنا.',
          WIRE_VARIABLE_NOT_A_NUMBER:
            'أنت تستخدم فراغات مرقّمة، فاكتب {blank} ثم {next}.',
          WIRE_VARIABLE_HAS_SYMBOL:
            'الفراغ هنا يحمل رقمًا ولا شيء غيره. والعلامات مثل # أو $ أو % لا توضع داخل القوسين.',
          WIRE_VARIABLE_NOT_A_NAME:
            'أنت تستخدم فراغات مُسمّاة، فاكتب {named} — حروف صغيرة وشرطة سفلية.',
          WIRE_VARIABLE_SEQUENCE: 'ابدأ من {blank} ولا تتجاوز رقمًا.',
          WIRE_BRACES: 'فراغ هنا ينقصه أحد قوسيه. اكتبه هكذا {blank}.',
          WIRE_DANGLING_VARIABLE:
            'لا يصحّ أن تبدأ الرسالة بفراغ ولا أن تنتهي به. اكتب كلمة قبل الفراغ الأول وبعد الأخير.',
          WIRE_MISSING: 'أعطِ مثالًا لكل فراغ.',
          WIRE_NO_VARIABLES: 'هذا السطر لا يقبل فراغًا.',
          WIRE_ONE_VARIABLE: 'هذا السطر يقبل فراغًا واحدًا على الأكثر.',
          WIRE_OUT_OF_RANGE: 'هذا الرقم خارج ما يسمح به واتساب.',
          WIRE_NOT_FOR_THIS_CATEGORY:
            'واتساب لا يسمح بتحديد هذا لهذا النوع من الرسائل.',
          WIRE_TOO_MANY: 'هذه أزرار أكثر مما يسمح به واتساب.',
          WIRE_GROUPING: 'اجعل أزرار الردّ متجاورة، غير مفصولة بزرّ آخر.',
          WIRE_UNKNOWN_TYPE: 'لا نستطيع إرسال هذا النوع من الأزرار بعد.',
          WIRE_TEXT_BLANK: 'حدّد ما يقوله.',
          WIRE_TEXT_TOO_LONG:
            'هذه الصياغة أطول من أن تصلح لزرّ، فالحد {limit}.',
          WIRE_URL_BLANK: 'حدّد إلى أين يذهب الرابط.',
          WIRE_URL_TOO_LONG: 'هذا العنوان طويل جدًا.',
          WIRE_URL_INVALID: 'هذا ليس عنوانًا كاملًا.',
          WIRE_URL_ONE_VARIABLE: 'الرابط يقبل فراغًا واحدًا على الأكثر.',
          WIRE_URL_VARIABLE_AT_END: 'يجب أن يكون الفراغ في آخر العنوان تمامًا.',
          WIRE_URL_SAMPLE_MISSING: 'أعطِ مثالًا لما يوضع في فراغ النهاية.',
          WIRE_PHONE_BLANK: 'حدّد أي رقم يتصل به.',
          WIRE_PHONE_INVALID: 'هذا لا يشبه رقم هاتف مع رمز دولة.',
          WIRE_PHONE_TOO_LONG: 'هذا الرقم طويل جدًا.',
          WIRE_CODE_BLANK: 'حدّد الرمز الذي ينسخه العميل.',
          WIRE_CODE_TOO_LONG: 'هذا الرمز طويل جدًا.',
          WIRE_TOO_MANY_QUICK_REPLY: 'واتساب يسمح بعشرة أزرار ردّ على الأكثر.',
          WIRE_TOO_MANY_URL: 'واتساب يسمح برابطين على الأكثر.',
          WIRE_TOO_MANY_PHONE_NUMBER: 'واتساب يسمح بزرّ اتصال واحد على الأكثر.',
          WIRE_TOO_MANY_COPY_CODE: 'واتساب يسمح بزرّ نسخ رمز واحد على الأكثر.',
          WIRE_NOT_ROUND_TRIPPABLE:
            'هذه الرسالة فيها جزء لا نستطيع نقله بعد — صورة أو مجموعة بطاقات أو عرض أو زرّ لا نبنيه. وتعديلها هنا سيزيل ذلك الجزء، فعدّلها من إدارة واتساب بدلًا من ذلك.',
          WIRE_REJECTED: 'رفض واتساب هذه الصياغة.',
          WIRE_NOT_FOUND: 'هذه الرسالة لم تعد موجودة عند واتساب.',
          WIRE_SUBMISSION_FAILED:
            'لم نتمكّن من الوصول إلى واتساب الآن. لم يُرسل شيء — حاول مرة أخرى.',
          WIRE_NOT_A_WHATSAPP_CLOUD_INBOX:
            'لا يمكن اعتماد صياغة لهذا الرقم من خلالنا. تواصل معنا وسننظر في الأمر.',
          WIRE_UNKNOWN:
            'واتساب لن يقبل هذا بصيغته الحالية. غيّره وحاول مرة أخرى.',
        },
      },
    },
    TEST: {
      TITLE: 'جرّبه',
      RESTART: 'ابدأ محادثة جديدة',
      SPOTLIGHT:
        'تحدّث إلى مساعدك كما يفعل أحد عملائك، قبل أن يفعلها عميل حقيقي. ما تكتبه هنا لا يصل إلى أحد، ولا يراه فريقك، ويمكنك تجربة صياغة لم تحفظها بعد. وكل رسالة تستهلك من رصيدك مثل أي ردّ حقيقي.',
      SETUP: {
        WORDING: 'جرّب صياغة لم تحفظها',
        CUSTOMER: 'تظاهر بأنك عميل بعينه',
      },
      LIMITS: {
        TEST_BUDGET: 'ميزانية التجربة لهذا اليوم: استُهلك منها {percent}',
      },
      REFUSED: {
        TEST_BUDGET_USED:
          'نفدت ميزانية التجربة لهذا اليوم. عملاؤك لم يتأثروا — وتعود التجربة غدًا.',
        SPEND_LIMIT_DAY:
          'بلغ مساعدك حدّ الإنفاق لهذا اليوم. تواصل معنا لرفع الحد.',
        SPEND_LIMIT_MONTH:
          'بلغ مساعدك حدّ الإنفاق لهذا الشهر. تواصل معنا لرفع الحد.',
        SPEND_LIMIT_PLATFORM:
          'نحن في واصلو نحدّ من الردود الذكية الآن. نعالج الأمر — حاول بعد قليل.',
        OUT_OF_CREDITS: 'نفد رصيدك. ويعود للعمل فور إضافة رصيد جديد.',
      },
      WORDING: {
        NOTE: 'هذه هي الصياغة التي يستخدمها مساعدك اليوم. غيّرها هنا فتردّ هذه المحادثة بها — لا يُحفظ شيء، ولا يراها أي عميل.',
        LABEL: 'الصياغة التي تريد تجربتها',
        HINT: 'تُستخدم في هذه المحادثة فقط، وما حفظته يبقى كما هو تمامًا.',
        DEFAULT:
          'ما زال مساعدك يستخدم الصياغة التي جاء بها. جرّب صياغتك هنا قبل حفظها في صفحة الشخصية.',
        CHANGED: 'تردّ هذه المحادثة بالصياغة أدناه، لا بما حفظته.',
        RESET: 'أعد ما هو محفوظ',
        UNAVAILABLE:
          'تعذّرت قراءة صياغتك المحفوظة الآن، لذلك تستخدمها هذه المحادثة كما هي.',
        EMPTY: 'هذه الخانة فارغة. اكتب شيئًا لتجربته، أو أعد ما هو محفوظ.',
      },
      CUSTOMER: {
        NOTE: 'اختياري. املأ ما تشاء، فيردّ مساعدك وكأن الرسالة وصلت من ذلك الشخص.',
        NAME: 'الاسم',
        NAME_PLACEHOLDER: 'نورة العجمي',
        PHONE: 'الهاتف',
        PHONE_PLACEHOLDER: '+965 9988 7744',
        EMAIL: 'البريد الإلكتروني',
        // `@` starts vue-i18n's linked-message syntax, so a bare address fails to
        // compile and the whole placeholder renders as an error. `{'@'}` is the literal.
        EMAIL_PLACEHOLDER: "noura{'@'}example.com",
        ATTRS: 'أي معلومة أخرى تحتفظ بها عنه',
        ATTRS_HINT:
          'ما تحتفظ به عادةً — منطقته، آخر طلب له، أو إن كان يشتري بالجملة.',
        ATTR_KEY: 'ما هي',
        ATTR_KEY_PLACEHOLDER: 'المنطقة',
        ATTR_VALUE: 'ماذا تقول',
        ATTR_VALUE_PLACEHOLDER: 'السالمية',
        TOO_LONG: 'إحدى هذه المعلومات طويلة جدًا. اختصرها وحاول مرة أخرى.',
      },
      CARD: {
        HEADING: 'العميل',
        RESET: 'إعادة ضبط العميل',
        NOTE: 'لا شيء هنا يصل إلى عميل حقيقي.',
        JUST_CHANGED: 'تغيّر للتو',
        REFUSED_EMAIL: 'لن يُحفظ: في الملف بريد إلكتروني مختلف',
        REFUSED_PHONE: 'لن يُحفظ: في الملف رقم هاتف مختلف',
        REFUSED_OTHER: 'لن يُحفظ',
        REMOVE: 'إزالة',
        ADD_DETAIL: 'أضف معلومة',
        LABELS: 'الوسوم',
        REMOVE_LABEL: 'أزل {label}',
        LABEL_PLACEHOLDER: 'اكتب وسمًا واضغط Enter',
        ANSWERS: 'الإجابات حتى الآن',
        SHOW: 'اعرض العميل',
        HIDE: 'أخفِ العميل',
      },
      CHANNEL: {
        LABEL: 'اعرضها كما في',
        WEBSITE_NOTE:
          'تظهر الخيارات في دردشة الموقع كقائمة مرقّمة. نافذة الدردشة في موقعك لا ترسل الضغطة، لذلك يكتب العميل الرقم.',
      },
      THREAD: {
        EMPTY_TITLE: 'جرّبه قبل أن يجرّبه عملاؤك',
        EMPTY_BODY:
          'أرسل إلى مساعدك رسالة بالطريقة التي يكتب بها أحد عملائك. ستحصل على ردّه، وعلى كل ما استند إليه في كتابته.',
        STARTERS: 'أو ابدأ بإحدى هذه',
        FALLBACK:
          'ظاهر باللغة الأخرى: لا توجد كلمات لهذا الخيار بهذه اللغة بعد.',
        STARTER_ONE: 'شنو أوقات دوامكم؟',
        STARTER_TWO: 'أقدر أحجز لبكرة؟',
        STARTER_THREE: 'شنو الأسعار عندكم؟',
        WRITING: 'مساعدك يكتب…',
        NO_REPLY: 'لم يجب على هذه بنفسه.',
        HANDOFF: 'توقّف هنا وطلب أحدًا من فريقك.',
        FAILED: 'لم يُرسَل شيء إلى أحد، وإعداداتك الحيّة كما هي.',
        MENU_TAP: 'المس خيارًا لتجيب كما يفعل العميل.',
      },
      COMPOSER: {
        LABEL: 'رسالة من عميل',
        PLACEHOLDER: 'اكتب ما قد يقوله عميل…',
        SEND: 'إرسال',
        HINT: 'Enter يرسل. وShift مع Enter يبدأ سطرًا جديدًا.',
        EMPTY: 'اكتب رسالة أولًا.',
      },
      LIVE: {
        LABEL: 'دعه يستخدم الأنظمة التي ربطتها فعليًا',
        ON: 'سيتصل بها فعلًا. أي طلب ينشئه أو حجز يثبّته أو رسالة يرسلها ستكون حقيقية.',
        OFF: 'تمرين فقط. يُخبَر بما كانت أنظمتك ستردّ به، ولا يحدث أي شيء خارج هذه المحادثة.',
      },
      TRACE: {
        SOURCE_SAMPLE: 'نستخدم العيّنة التي أرسلها نظامك في {date}',
        SOURCE_SAMPLE_UNDATED: 'نستخدم العيّنة التي أرسلها نظامك',
        SOURCE_NO_SAMPLE:
          'لا توجد عيّنة من "{name}" بعد. اختبره مرة من قسم المطوّرين، أو شغّل الإجراءات الحقيقية.',
        FETCH_SAMPLE: 'اجلب عيّنة الآن',
        FETCHED: 'وصلت العيّنة. أرسل رسالتك مرة أخرى لاستخدامها.',
        FETCH_FAILED: 'لم يرسل نظامك عيّنة. اختبره مرة من قسم المطوّرين.',
        STARTED_BY: {
          ASSISTANT: 'رأى مساعدك أن الرسالة تطلب هذه المهمة.',
          WORDS: 'بدأت لأن في الرسالة إحدى كلماتها.',
          MENU: 'بدأت من زر في القائمة.',
          SWITCH: 'انتقل إليها العميل من مهمة أخرى.',
          REHEARSAL: 'بدأت لأنك تجرّبها.',
        },
        SHOW: 'كيف وصل إلى هذا الردّ',
        HIDE: 'إخفاء كيف وصل إلى الردّ',
        NOTHING: 'من صياغتك وحدها',
        NOTHING_BODY:
          'أجاب مما كتبته في صفحة الشخصية. لم يبحث عن شيء ولم يستخدم أيًّا من الأنظمة التي ربطتها.',
        CHIP_ACTIONS:
          'لم يستخدم أي اتصال | استخدم اتصالًا واحدًا | استخدم اتصالين | استخدم {count} اتصالات | استخدم {count} اتصالًا | استخدم {count} اتصال',
        CHIP_LOOKUPS:
          'لم يقرأ أي إجابة محفوظة | قرأ إجابة محفوظة واحدة | قرأ إجابتين محفوظتين | قرأ {count} إجابات محفوظة | قرأ {count} إجابة محفوظة | قرأ {count} إجابة محفوظة',
        CHIP_TASK: 'اتّبع مهمة',
        CHIP_MENU: 'عرض قائمة',
        ACTIONS_HEADING: 'الأنظمة التي لجأ إليها',
        ACTION_REHEARSED: 'تمرين',
        ACTION_LIVE: 'نُفّذ فعلًا',
        KIND: {
          LOOKUP: 'بحث في إجاباتك',
          HANDOVER: 'حوّل المحادثة إلى فريقك',
          MENU: 'عرض القائمة «{label}»',
          MENU_UNNAMED: 'عرض قائمة',
          CLOSE: 'أغلق المحادثة',
          ASSIGN: 'أسند المحادثة إلى أحد فريقك',
          ADD_LABELS: 'أضاف وسمًا إلى المحادثة',
          REMOVE_LABELS: 'أزال وسمًا من المحادثة',
          NOTE: 'ترك ملاحظة خاصة في المحادثة',
          SAVE_CONTACT: 'حفظ {label} لدى العميل',
          SAVE_CONTACT_UNNAMED: 'حفظ معلومة لدى العميل',
          CONNECTION: 'سأل «{label}»',
          OTHER: 'فعل شيئًا في هذه المحادثة',
        },
        INSIDE_HEADING: 'ما فعله داخل هذه المحادثة',
        TECHNICAL: 'التفاصيل التقنية',
        ACTION_SENT: 'ما أرسله',
        ACTION_GOT: 'ما عاد إليه',
        TRUNCATED: '… والبقية أطول من أن تُعرض هنا.',
        LOOKUPS_HEADING: 'الإجابات المحفوظة التي قرأها',
        LOOKUP_QUERY: 'بحث عن «{query}»',
        LOOKUP_NONE: 'لا شيء مما حفظته كان قريبًا بما يكفي.',
        TASK_HEADING: 'المهمة التي كان يتّبعها',
        TASK_STARTED: 'بدأ «{name}».',
        TASK_COMPLETED: 'أنهى المهمة التي كان عليها.',
        TASK_ACTIVE: 'هو الآن يعمل على «{name}».',
        MENU_HEADING: 'القائمة التي عرضها',
        MENU_SHOWN: 'وضع «{title}» تحت ردّه.',
        CHIP_STEP: 'سار في مهمة خطوة بخطوة',
        CHIP_WITHHELD: 'حجب ما كتبه',
        STEP_HEADING: 'المهمة التي يسير فيها',
        STEP_TASK: 'يسير الآن في «{name}».',
        STEP_ON: 'هو عند خطوة «{step}».',
        STEP_RULE_FAILED: 'ما أرسلوه لا يناسب «{step}»، فأعاد طرحها.',
        STEP_ON_ID: 'هو عند الخطوة المعلَّمة «{step}».',
        STEP_RULE_FAILED_ID:
          'ما أرسلوه لا يناسب الخطوة المعلَّمة «{step}»، فأعاد طرحها.',
        STEP_NEXT_ASK: 'ثم طرح السؤال التالي.',
        STEP_NEXT_SAY: 'ثم ردّ وبقي على الخطوة نفسها.',
        STEP_NEXT_SILENT: 'لم يرسل شيئًا على هذه الرسالة.',
        STEP_NEXT_DONE: 'وصل إلى نهاية المهمة.',
        STEP_NEXT_HANDOFF: 'أوقف المهمة وطلب أحدًا من فريقك.',
        STEP_NEXT_SYSTEM_REFUSED: 'رفض نظامك مرتين، فانتقلت المحادثة لفريقك.',
        STEP_SENT_AS_WRITTEN: 'أُرسلت كما كُتبت.',
        STEP_NEXT_NONE: 'هذه الرسالة لم تحرّك المهمة.',
        STEP_ANSWERS: 'ما دوّنه حتى الآن',
        STEP_ANSWERS_NONE: 'لا شيء بعد.',
        STEP_ANSWER_STORED: 'يُرسَل بصيغة {value}',
        STEP_CONNECTIONS: 'الأنظمة التي راجعها لهذه المهمة',
        STEP_CONNECTIONS_NOTE: 'هل ردّ كل منها أم لا — وليس ما ردّ به.',
        STEP_CONNECTION_OK: 'ردّ',
        STEP_CONNECTION_FAILED: 'لم يردّ',
        STEP_DROPPED: 'لم يُحفظ: لم يذكره العميل.',
        STEP_NOT_SENT_AGAIN:
          'تم في وقت سابق من هذه المحادثة، ولم يُرسل مرة أخرى.',
        GATE_HEADING: 'قبل أن يرسل',
        GATE_WITHHELD:
          'كان قد كتب ردًّا، ولم يرسله، وطلب أحدًا من فريقك بدلًا من ذلك.',
        GATE_RETRIED: 'أعاد كتابة ردّه مرة واحدة قبل إرساله.',
        GATE_PRICE: 'كان قد وضع السعر {value}، ولا شيء مما أعطيته يقول ذلك.',
        GATE_PRICE_PLAIN: 'كان قد وضع سعرًا لا يقوله شيء مما أعطيته.',
        GATE_URL: 'كان قد وضع الرابط {value}، ولا شيء مما أعطيته يقول ذلك.',
        GATE_URL_PLAIN: 'كان قد وضع رابطًا لا يقوله شيء مما أعطيته.',
        GATE_LENGTH: 'كان قد كتب أطول مما تحمله هذه القناة.',
        GATE_LANGUAGE: 'كان قد كتب بلغة غير لغة هذا العميل.',
        GATE_CLAIM: 'قال إن شيئًا تمّ وهو لم يتم، فحُذف ذلك السطر.',
        GATE_PROMPT_ECHO: 'كرّر تعليماته، فحُذف ذلك السطر.',
        GATE_TIME:
          'ذكر الوقت {value} ولا شيء مما أعطيته يقوله، فحُذف ذلك السطر.',
        GATE_TIME_PLAIN: 'ذكر وقتًا لا يقوله شيء مما أعطيته، فحُذف ذلك السطر.',
        GATE_LIST:
          'ذكر {value} في قائمة ولا شيء مما أعطيته يقوله، فحُذف ذلك السطر.',
        GATE_LIST_PLAIN:
          'ذكر في قائمة شيئًا لا يقوله شيء مما أعطيته، فحُذف ذلك السطر.',
        GATE_OTHER: 'شيء فيه لم يجتز الفحص.',
      },
    },
    PANEL: {
      TITLE: 'واصلو AI',
      STATUS: {
        QUIET: 'مساعدك لم يردّ في هذه المحادثة.',
        QUIET_BODY:
          'لم يُجب على أي شيء هنا تلقائيًا. ومع ذلك يمكنك تعليمه الإجابة الصحيحة للمرة القادمة.',
        WHEN: 'آخر رد {when}.',
        JUST_NOW: 'الآن',
      },
      CHAT: {
        ANSWERING: 'المساعد يرد',
        TAKE: 'أنا أتولاها',
        YOU: 'أنت تتابع هذي المحادثة',
        SOMEONE: '{name} يتابعها',
        TEAM: 'فريقك يتابع هذي المحادثة',
        GIVE_BACK: 'رجّعها للمساعد',
        BLOCKED: 'ما أحد يرد هنا',
        CLOSED: 'مقفلة. إذا كتب العميل مرة ثانية، المساعد يرد أول.',
        PRACTICE_NOTE: 'المساعد يتدرّب، فيرد هنا على أرقام التجربة بس.',
        WORKING: 'لحظة…',
        FAILED: 'ما قدرنا نغيّر هذا الحين. ما تغيّر شي. حاول مرة ثانية.',
      },
      FOOTER: {
        ALL_CHATS: 'لكل المحادثات',
      },
      SUGGEST: {
        OPEN: 'اقترح هذا الرد على صاحب الحساب',
        TITLE: 'اقترح هذا الرد على صاحب الحساب',
        BODY: 'صاحب الحساب يشوفه في الأجوبة ويقرر إذا المساعد يتعلمه.',
        SEND: 'أرسل الاقتراح',
        SENDING: 'جارٍ الإرسال…',
        SENT: 'انرسل. صاحب الحساب بيشوفه في الأجوبة.',
        FULL: 'ما عند صاحب الحساب اقتراحات بالانتظار. | صاحب الحساب عنده اقتراح واحد بالانتظار. | صاحب الحساب عنده اقتراحين بالانتظار. | صاحب الحساب عنده {count} اقتراحات بالانتظار. | صاحب الحساب عنده {count} اقتراح بالانتظار. | صاحب الحساب عنده {count} اقتراح بالانتظار.',
        TOO_MANY: 'أرسلت اقتراحات واجد هالساعة. حاول بعدين.',
        FAILED: 'ما قدرنا نرسله الحين. حاول مرة ثانية.',
      },
      LAST: {
        HEADING: 'آخر رد له',
        CUSTOMER: 'سألوا',
        ASSISTANT: 'أجاب',
        SILENT: 'لم يقل شيئًا وانتظر شخصًا من فريقك.',
      },
      TRACE: {
        SHOW: 'كيف أجاب',
        HIDE: 'إخفاء كيف أجاب',
        NOTHING: 'أجاب من صياغتك وحدها.',
        LOOKUP_QUERY: 'بحث عن «{query}»',
        LOOKUP_NONE: 'لا شيء مما حفظته كان قريبًا بما يكفي.',
        CLOSE: 'تطابق قريب',
        LOOSE: 'تطابق بعيد شوي',
        NONE: 'ما لقى شي قريب',
        ACTIONS_HEADING: 'الأنظمة التي استعان بها',
      },
      TEACH: {
        OPEN: 'علّمه الإجابة الصحيحة',
        TITLE: 'علّمه الإجابة الصحيحة',
        BODY: 'اكتبها كما تقولها لعميلك. سيستخدمها مساعدك من رسالته التالية مباشرة — هنا وفي كل محادثة أخرى.',
        QUESTION_LABEL: 'عندما يسأل العميل',
        QUESTION_PLACEHOLDER: 'ما الذي سألوا عنه فعلًا؟',
        QUESTION_HINT: 'كلماتهم هي الأفضل. اختصرها إن كتبوا فقرة كاملة.',
        ANSWER_LABEL: 'يجب أن يقول مساعدك',
        ANSWER_PLACEHOLDER: 'اكتب الرد الذي سترسله…',
        SAVE: 'احفظ هذه الإجابة',
        CANCEL: 'إلغاء',
        SAVING: 'جارٍ الحفظ…',
        SAVED: 'تم الحفظ. سيستخدمها مساعدك من رسالته التالية.',
        REPLACED:
          'تم الاستبدال. كانت لهذا السؤال إجابة محفوظة، وأصبحت الآن هذه.',
        TOTAL:
          'لم تُحفظ أي إجابة من المحادثات بعد. | إجابة واحدة محفوظة من المحادثات. | إجابتان محفوظتان من المحادثات. | {count} إجابات محفوظة من المحادثات. | {count} إجابة محفوظة من المحادثات. | {count} إجابة محفوظة من المحادثات.',
        FAILED: 'تعذّر الحفظ الآن. لم يتغيّر شيء — حاول مرة أخرى.',
        AGAIN: 'علّمه إجابة أخرى',
      },
      OPEN: 'افتح واصلو AI',
      ERROR: 'تعذّر علينا قراءة ما فعله مساعدك هنا.',
      BROKEN: 'تعذّر عرض هذا الجزء. وكل ما عداه في بريدك يعمل كالمعتاد.',
    },
    BROADCASTS: {
      TITLE: 'رسائل واتساب الجماعية',
      SPOTLIGHT:
        'الإرسال الجماعي يوصل رسالة واتساب واحدة معتمدة إلى عدد كبير من عملائك دفعة واحدة. ويحتسب واتساب رسومًا على كل رسالة تصل، لذلك لا يخرج شيء قبل أن تقرأ الرسالة وترى كم شخصًا ستصل إليه. وبعد أن يبدأ الإرسال لا يمكن التراجع عنه.',
      NEW: 'إرسال جماعي جديد',
      LIST: {
        HEADING: 'ما أرسلته',
        DESCRIPTION: 'افتح أيًّا منها لمتابعة الإرسال، أو لمعرفة أين توقّف.',
        UNAVAILABLE: 'تعذّر عرض عمليات الإرسال الآن.',
      },
      EMPTY: {
        TITLE: 'لم ترسل أي رسالة جماعية بعد',
        BODY: 'اختر إحدى رسائل واتساب المعتمدة لديك، وحدّد من يستلمها، واقرأها قبل الإرسال. سترى بالضبط كم شخصًا ستصل إليه قبل احتساب أي رسوم.',
        ACTION: 'أرسل أول رسالة جماعية',
      },
      NOT_SET_UP: {
        TITLE: 'لم يُجهَّز بعد',
        BODY: 'يجب تجهيز الإرسال الجماعي لهذه المساحة أولًا، ونحن نقوم بذلك عنك.',
        ASK: 'اطلب منّا إكمال التجهيز',
        ASKING: 'جارٍ الإرسال…',
        ASKED: 'وصلنا طلبك، سنجهّزه ونخبرك.',
        FAILED: 'تعذّر إرسال الطلب الآن. حاول مرة أخرى.',
      },
      STATUS: {
        DRAFT: 'لم تُرسل',
        RUNNING: 'قيد الإرسال',
        PAUSED: 'متوقفة مؤقتًا',
        COMPLETED: 'انتهت',
        CANCELLED: 'أُوقفت',
        FAILED: 'لم تُرسل',
        UNKNOWN: 'حالة غير معروفة',
      },
      UNKNOWN_NOTE:
        'هذه العملية في حالة لا تعرفها هذه الصفحة. لن يغيّرها أي إجراء هنا — تواصل معنا وسنتحقق.',
      ROW: {
        SENT: 'أُرسلت {sent} من {total} | أُرسلت {sent} من {total} | أُرسلت {sent} من {total} | أُرسلت {sent} من {total} | أُرسلت {sent} من {total} | أُرسلت {sent} من {total}',
        PENDING:
          '{count} في الطريق | {count} في الطريق | {count} في الطريق | {count} في الطريق | {count} في الطريق | {count} في الطريق',
        SKIPPED:
          'تم تخطّي {count} | تم تخطّي {count} | تم تخطّي {count} | تم تخطّي {count} | تم تخطّي {count} | تم تخطّي {count}',
        FAILED:
          'لم تصل {count} | لم تصل {count} | لم تصل {count} | لم تصل {count} | لم تصل {count} | لم تصل {count}',
        STARTED: 'بدأت {when}',
        NOT_STARTED: 'لم تبدأ',
        JUST_NOW: 'الآن',
        OPEN: 'افتحها',
      },
      COMPOSE: {
        TITLE: 'إرسال جماعي جديد',
        BACK: 'رسائل واتساب الجماعية',
        POSITION:
          'الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total} | الخطوة {number} من {total}',
        NEXT: 'التالي',
        PREVIOUS: 'رجوع',
        LEAVE: 'اتركها',
        NEEDS: {
          NAME: 'أعطِ هذه العملية اسمًا للمتابعة.',
          CHANNEL: 'اختر الرقم الذي تُرسل منه للمتابعة.',
          MESSAGE: 'اختر إحدى رسائلك المعتمدة للمتابعة.',
          FILL: 'املأ كل الفراغات للمتابعة.',
          AUDIENCE: 'أضف شرطًا واحدًا على الأقل للمتابعة.',
        },
        STEPS: {
          NAME: 'التسمية',
          CHANNEL: 'الرقم',
          MESSAGE: 'الرسالة',
          FILL: 'ملء الفراغات',
          AUDIENCE: 'من يستلمها',
          CHECK: 'المراجعة والإرسال',
        },
        NAME: {
          HEADING: 'بماذا تريد تسميتها؟',
          DESCRIPTION:
            'يراها فريقك فقط ولا يراها عملاؤك أبدًا — بها ستجد هذه العملية مرة أخرى الشهر القادم.',
          LABEL: 'سمِّها',
          PLACEHOLDER: 'عرض العيد — زبائن السالمية',
          REQUIRED: 'أعطِ هذه العملية اسمًا.',
        },
        CHANNEL: {
          HEADING: 'من أي رقم تُرسل؟',
          DESCRIPTION: 'يرى عملاؤك هذا الرقم كمُرسِل، ويستطيعون الرد عليه.',
          PLACEHOLDER: 'اختر رقمًا',
          NO_NUMBER: 'لا يوجد رقم بعد',
          UNUSABLE: 'غير مهيّأ للإرسال',
          NONE: 'لا يوجد رقم واتساب في هذه المساحة يستطيع الإرسال الجماعي بعد.',
          UNAVAILABLE: 'تعذّر عرض أرقامك الآن.',
        },
        MESSAGE: {
          HEADING: 'أي رسالة معتمدة سترسل؟',
          DESCRIPTION:
            'لا يسمح واتساب للأعمال ببدء محادثة إلا بصياغة اعتمدها مسبقًا. هذه صياغاتك كما اعتُمدت تمامًا.',
          BLANKS:
            'فراغات تحتاج تعبئة: {count} | فراغات تحتاج تعبئة: {count} | فراغات تحتاج تعبئة: {count} | فراغات تحتاج تعبئة: {count} | فراغات تحتاج تعبئة: {count} | فراغات تحتاج تعبئة: {count}',
          NO_BLANKS: 'لا توجد فراغات.',
          MARKETING: 'تُحتسب كرسالة تسويقية',
          NONE: 'لا توجد رسائل معتمدة لهذا الرقم بعد. أضف واحدة في واتساب وستظهر هنا بعد اعتمادها.',
          UNAVAILABLE: 'تعذّرت قراءة الرسائل المعتمدة لهذا الرقم.',
          CHOOSE: 'استخدم هذه',
          CHOSEN: 'مختارة',
        },
        FILL: {
          HEADING: 'ماذا يوضع في الفراغات؟',
          DESCRIPTION:
            'يُملأ كل فراغ لكل شخص على حدة، فيصل كل عميل نسخته الخاصة من الرسالة.',
          BLANK: 'الفراغ {name}',
          TITLE_BLANK: 'الفراغ {name} في العنوان',
          BUTTON_BLANK:
            'الزر {n}: آخر رابطه أو الكود | الزر {n}: آخر رابطه أو الكود | الزر {n}: آخر رابطه أو الكود | الزر {n}: آخر رابطه أو الكود | الزر {n}: آخر رابطه أو الكود | الزر {n}: آخر رابطه أو الكود',
          SOURCE: 'املأه من',
          SOURCES: {
            FIELD: 'معلومة تحفظها عنه',
            ATTR: 'إحدى معلوماتك الخاصة',
            LITERAL: 'الكلمات نفسها للجميع',
          },
          FIELDS: {
            NAME: 'اسمه',
            PHONENUMBER: 'رقم هاتفه',
            EMAIL: 'بريده الإلكتروني',
          },
          ATTR_LABEL: 'أي معلومة',
          ATTR_NONE: 'لا تحفظ هذه المساحة أي معلومات إضافية عن العملاء بعد.',
          LITERAL_LABEL: 'ماذا تكتب فيه',
          LITERAL_PLACEHOLDER: '٢٠٪',
          LITERAL_REQUIRED: 'اكتب ما يجب أن يظهر في هذا الفراغ.',
          EMPTY_WARNING:
            'العميل الذي لا تحفظ عنه هذه المعلومة يُتخطّى بدل أن تصله رسالة ناقصة.',
        },
        AUDIENCE: {
          HEADING: 'من يجب أن يستلمها؟',
          DESCRIPTION:
            'أضف شروطًا حتى تصبح القائمة كما تريد. ويجب أن ينطبق كل شرط على العميل حتى تصله الرسالة.',
          FIELD: 'المعلومة',
          FIELD_PLACEHOLDER: 'اختر معلومة',
          CONDITION: 'الشرط',
          CONDITION_PLACEHOLDER: 'اختر شرطًا',
          VALUE: 'القيمة',
          ADD: 'أضف شرطًا',
          REMOVE: 'احذف هذا الشرط',
          YES: 'نعم',
          NO: 'لا',
          DAYS_HINT: 'عدد أيام صحيح.',
          DATE_HINT: 'بصيغة سنة-شهر-يوم، مثل 2026-08-01.',
          REQUIRED: 'أضف شرطًا واحدًا على الأقل.',
          UNAVAILABLE: 'تعذّرت قراءة ما تحفظه عن عملائك.',
        },
        CHECK: {
          HEADING: 'اقرأها قبل أن تخرج',
          DESCRIPTION: 'هذه آخر شاشة قبل أن تصل الرسالة إلى عملائك.',
          MESSAGE_HEADING: 'ما يصل كل عميل',
          MESSAGE_NOTE: 'الكلمات بين « » تُملأ لكل شخص على حدة مما اخترته.',
          COUNT_ACTION: 'احسب من سيستلمها',
          COUNTING: 'جارٍ الحساب…',
          COUNT_NOTE:
            'نمرّ على عملائك ونخبرك بالضبط كم شخصًا سيستلمها. لا يُرسل شيء ولا تُحتسب أي رسوم.',
          WILL_RECEIVE:
            'لن تصل أي عميل. | ستصل عميلًا واحدًا. | ستصل عميلين. | ستصل {count} عملاء. | ستصل {count} عميلًا. | ستصل {count} عميل.',
          WILL_RECEIVE_ONE: 'ستصل عميلًا واحدًا.',
          SKIPPED:
            'سيتم تخطّي {count}. | سيتم تخطّي {count}. | سيتم تخطّي {count}. | سيتم تخطّي {count}. | سيتم تخطّي {count}. | سيتم تخطّي {count}.',
          SKIPPED_ONE: 'سيتم تخطّي عميل واحد.',
          SKIP_REASON:
            '{count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason} | {count} × {reason}',
          SAMPLE_NOTE:
            'الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم. | الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم. | الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم. | الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم. | الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم. | الرقم بالأعلى دقيق. وهذه الأسباب مأخوذة من أول {count} تم تخطّيهم.',
          NO_REASONS: 'لم يُذكر سبب لمن تم تخطّيهم.',
          SKIP_CODE: {
            NO_PHONE:
              'لا يوجد رقم هاتف (مثل العملاء الذين يراسلونك باسم مستخدم واتساب)',
            UNUSABLE_PHONE: 'رقم الهاتف غير صالح للإرسال',
            SUPPRESSED: 'طلبوا عدم استلام الرسائل',
            RENDER_FAILED: 'تفصيل تحتاجه الرسالة فارغ عندهم',
          },
          NOBODY_MATCHED:
            'لا ينطبق أي عميل على هذه الشروط، فلا يوجد من ترسل إليه. وسّع الشروط ثم احسب مرة أخرى.',
          NOBODY_LEFT:
            'كل من تنطبق عليه هذه الشروط تم تخطّيه، فلا يوجد من ترسل إليه. اقرأ الأسباب بالأعلى ثم احسب مرة أخرى.',
          ACK: 'أفهم أن هذه الرسالة لن تصل إلى أحد. | أفهم أن عميلًا واحدًا لن تصله هذه الرسالة. | أفهم أن عميلين لن تصلهما هذه الرسالة. | أفهم أن {count} عملاء لن تصلهم هذه الرسالة. | أفهم أن {count} عميلًا لن تصلهم هذه الرسالة. | أفهم أن {count} عميل لن تصلهم هذه الرسالة.',
          ACK_ONE: 'أفهم أن عميلًا واحدًا لن تصله هذه الرسالة.',
          STALE: 'تغيّر شيء منذ آخر حساب. احسب مرة أخرى ثم أرسل.',
          RESOLVE_NOTE:
            'نمرّ على عملائك مرة أخرى عند الإرسال، لذلك قد يختلف الرقم النهائي قليلًا.',
          SEND: 'أرسلها الآن',
          SENDING: 'جارٍ الإرسال…',
          CONFIRM_TITLE:
            'إرسالها الآن؟ | إرسالها إلى عميل واحد الآن؟ | إرسالها إلى عميلين الآن؟ | إرسالها إلى {count} عملاء الآن؟ | إرسالها إلى {count} عميلًا الآن؟ | إرسالها إلى {count} عميل الآن؟',
          CONFIRM_TITLE_ONE: 'إرسالها إلى عميل واحد الآن؟',
          CONFIRM_BODY:
            'لا يمكن التراجع عن هذا، ويحتسب واتساب رسومًا على كل رسالة تصل.',
          CONFIRM_SEND: 'نعم، أرسلها',
          CONFIRM_CANCEL: 'ليس الآن',
          FAILED: 'تعذّر الوصول إلى الخدمة. لم يُرسل شيء — حاول مرة أخرى.',
        },
      },
      DETAIL: {
        BACK: 'رسائل واتساب الجماعية',
        NOT_FOUND: 'تعذّر العثور على هذه العملية. ربما حُذفت.',
        UNAVAILABLE: 'تعذّر فتح هذه العملية الآن.',
        SENT_MESSAGE: 'ما خرج فعلًا',
        NUMBERS: {
          RECIPIENTS: 'ستصل إلى',
          SKIPPED: 'تم تخطّيهم',
          SENT: 'أُرسلت',
          PENDING: 'في الطريق',
          DELIVERED: 'وصلت',
          FAILED: 'لم تصل',
        },
        PROGRESS: {
          HEADING: 'إلى أين وصلت كل رسالة',
          DESCRIPTION:
            'يتحدّث هذا من تلقاء نفسه أثناء الإرسال، ولا يحتاج منك شيئًا.',
          UNAVAILABLE:
            'تعذّرت قراءة إلى أين وصلت كل رسالة. أما الإرسال نفسه فلم يتأثر.',
          NOTHING: 'لم تخرج أي رسالة بعد.',
          QUEUED: 'بالانتظار',
          SENDING: 'قيد الخروج',
          ACCEPTED: 'في الطريق',
          SENT: 'أُرسلت',
          DELIVERED: 'وصلت',
          READ: 'قُرئت',
          FAILED: 'لم تصل',
          SKIPPED: 'تم تخطّيها',
          UNCONFIRMED: 'غير مؤكدة',
          UNCONFIRMED_NOTE:
            'ربما خرجت واحتُسبت رسومها ولم يصلنا تأكيد. لذلك تُستثنى عمدًا من «أعد الإرسال».',
        },
        REFRESH: 'تحديث',
        STOPPED_BECAUSE: 'توقّفت بسبب: {reason}',
        STOPPED: {
          SEND_AUTH_FAILED:
            'فقدنا صلاحية الإرسال من رقم واتساب لديك. تواصل معنا ونعيد ربطه — ولم يُحتسب شيء على الرسائل التي لم تُرسَل.',
          LAUNCH_FAILED:
            'حدث خطأ قبل أن يبدأ الإرسال، فلم تصل أي رسالة إلى أحد. يمكنك المحاولة مرة أخرى، وتواصل معنا إذا تكرر ذلك.',
          OUTSIDE_SENDING_HOURS:
            'أوقات الإرسال لديك مغلقة، لذلك ينتظر. ويكمل وحده عندما تبدأ، أو يمكنك أن تبدأه الآن.',
          UNKNOWN: 'حدث خطأ فأوقفنا الإرسال. تواصل معنا لنطّلع على ما جرى.',
        },
        ACTIONS: {
          HEADING: 'ماذا يمكنك أن تفعل',
          CANCEL: 'أوقفها',
          CANCEL_BODY:
            'يُتخطّى كل من ما زال بالانتظار. أما ما أُرسل فلا يمكن التراجع عنه.',
          CANCEL_CONFIRM: 'إيقاف هذا الإرسال؟',
          RESUME: 'تابع الإرسال',
          RESUME_BODY: 'يعود من توقّف عنده الإرسال ليستلم الرسالة من جديد.',
          RESUME_CONFIRM: 'متابعة الإرسال؟',
          RETRY: 'أعد الإرسال لمن لم تصلهم',
          RETRY_BODY:
            'كل واحدة رسالة واتساب جديدة وتُحتسب رسومها من جديد. ويُستثنى كل من يُحتمل أن رسالته خرجت فعلًا، لذلك قد يكون العدد أقل من ذلك.',
          RETRY_CONFIRM:
            'إعادة الإرسال؟ | إعادة الإرسال إلى عميل واحد على الأكثر؟ | إعادة الإرسال إلى عميلين على الأكثر؟ | إعادة الإرسال إلى {count} عملاء على الأكثر؟ | إعادة الإرسال إلى {count} عميلًا على الأكثر؟ | إعادة الإرسال إلى {count} عميل على الأكثر؟',
          NOTHING_TO_RETRY:
            'لا يمكن إعادة إرسال أي منها — فربما خرجت فعلًا واحتُسبت رسومها. ولم يُرسل شيء.',
          CONFIRM_YES: 'نعم، نفّذ',
          CONFIRM_NO: 'اتركها',
          NONE: 'لا يوجد إجراء متاح لهذه العملية.',
          FAILED: 'تعذّر تنفيذ ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
        },
      },
    },
    SETTINGS: {
      CONNECTIONS: {
        TITLE: 'أنظمتك',
        SPOTLIGHT:
          'هذه هي الزاوية المتقدّمة. الاتصال يتيح لمساعدك أن يقرأ شيئًا من نظام آخر وهو يتحدث — هل العطر متوفر، أين وصل الطلب، أي أيام التوصيل مفتوحة. أغلب المحلات لا تضيف أي اتصال، وكل ما عدا ذلك يعمل تمامًا من دونه.',
        HEADING: 'الأنظمة التي يستطيع مساعدك الوصول إليها',
        DESCRIPTION: 'كل واحد منها مكان يقرأ منه شيئًا في وسط المحادثة.',
        ADD: 'أضف اتصالًا',
        LIST: {
          ON: 'مشغّل',
          OFF: 'مطفأ',
          SIGN_IN_SAVED: 'بيانات الدخول محفوظة',
          NAME: 'مهامك تسمّي هذا {name}',
          UPDATED: 'تغيّر {when}',
          UPDATED_JUST_NOW: 'للتو',
          OPEN: 'فتح',
          PURPOSE_LOOKUP: 'يبحث عن معلومة',
          PURPOSE_CHANGE: 'يغيّر شيئًا',
          TRY_FIRST: 'جرّبه أولًا',
          NOT_TRIED: 'لم يُجرَّب منذ آخر تغيير',
          LEGACY: 'يُستخدم في المحادثة المفتوحة حاليًا. أضفه إلى مهمة.',
          LEGACY_STOP: 'أوقف استخدامه في المحادثة المفتوحة',
          TASK_ONLY: 'داخل المهام فقط',
          TASK_ONLY_HINT: 'لا يستخدمه المساعد في المحادثة المفتوحة.',
          SIGN_IN_NEEDED: 'يحتاج تسجيل الدخول',
          SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر شيء — حاول مرة أخرى.',
          OFF_CONFIRM:
            'لا مهام مشغّلة تستخدم هذا. | مهمة مشغّلة واحدة تستخدم هذا، والعملاء الذين يصلون إليه سيُحوَّلون لفريقك. | مهمتان مشغّلتان تستخدمان هذا، والعملاء الذين يصلون إليه سيُحوَّلون لفريقك. | {count} مهام مشغّلة تستخدم هذا، والعملاء الذين يصلون إليه سيُحوَّلون لفريقك. | {count} مهمة مشغّلة تستخدم هذا، والعملاء الذين يصلون إليه سيُحوَّلون لفريقك. | {count} مهمة مشغّلة تستخدم هذا، والعملاء الذين يصلون إليه سيُحوَّلون لفريقك.',
          OFF_YES: 'أطفئه',
          OFF_NO: 'أبقه مشغّلًا',
        },
        HEALTH: {
          LAST_USED: 'آخر استخدام {when}',
          JUST_NOW: 'للتو',
          WEEK: '{worked}، {failed} هذا الأسبوع',
          WORKED:
            'لم ينجح أي طلب | نجح طلب واحد | نجح طلبان | نجح {count} طلبات | نجح {count} طلبًا | نجح {count} طلب',
          FAILED:
            'لم يفشل أي طلب | فشل طلب واحد | فشل طلبان | فشل {count} طلبات | فشل {count} طلبًا | فشل {count} طلب',
          IDLE: 'لم يُستخدم هذا الأسبوع',
          REASON: {
            SIGN_IN: 'رفض نظامك تسجيل الدخول.',
            NOT_FOUND: 'يقول نظامك إن هذا العنوان غير موجود.',
            SERVER: 'حدثت مشكلة في نظامك.',
            HTTP: 'ردّ نظامك بخطأ ({status}).',
            TIMEOUT: 'تأخّر نظامك في الرد.',
            NETWORK: 'لم نتمكن من الوصول إلى نظامك.',
            BLOCKED: 'لا نستطيع الاتصال بهذا العنوان.',
            INVALID_INPUT: 'قيمة أُرسلت إليه لم تكن صالحة له.',
            MISSING_CONTACT_DETAIL: 'لم يكن لدى عميلٍ رقم هاتف أو بريد ليرسله.',
            NOT_GIVEN: 'لم يذكر العميل معلومة يحتاجها.',
            SWITCHED_OFF: 'هو مطفأ، وحاولت مهمة استخدامه.',
          },
        },
        EMPTY: {
          TITLE: 'لا يوجد لديك أي اتصال، وهذا هو الوضع الطبيعي',
          BODY: 'مساعدك يجيب أصلًا مما كتبته في صفحة الشخصية ومن الإجابات التي حفظتها. لا تضف اتصالًا إلا إذا احتاج أن يقرأ شيئًا من نظام آخر — وغالبًا بمساعدة من بنى ذلك النظام.',
          ACTION: 'أضف واحدًا على أي حال',
        },
        EDITOR: {
          NEW_TITLE: 'اتصال جديد',
          RETURN: 'عودة إلى الخطوة',
          LEAVE: {
            TITLE: 'لديك تغييرات غير محفوظة',
            BODY: 'الخروج الآن يفقدها. ولم يُرسل شيء إلى نظامك.',
            STAY: 'ابقَ هنا',
            CONFIRM: 'اخرج دون حفظ',
          },
          SPOTLIGHT:
            'من بنى النظام الذي تربطه يستطيع ملء هذا في دقائق. ولا شيء هنا يُعرض على عميل: مساعدك يقرأ الجواب ثم يقوله بكلماته.',
          ABOUT: {
            HEADING: 'ما هو',
            DESCRIPTION:
              'اسم قصير لك، وجملة واحدة يقرأها مساعدك ليعرف متى يستخدمه.',
          },
          NAME: {
            LABEL: 'الاسم القصير',
            PLACEHOLDER: 'مواعيد التوصيل',
            HINT: 'مهامك تشير إليه بهذا الاسم، فلا يمكن تغييره بعد الحفظ.',
            FIXED: 'مهامك تسمّي هذا {name}. وهذا لا يمكن تغييره.',
          },
          WHAT: {
            LABEL: 'ماذا يفعل',
            PLACEHOLDER: 'أي أيام التوصيل ما زالت مفتوحة في منطقة معيّنة.',
            HINT: 'مساعدك يقرأ هذه الجملة وحدها ليقرّر متى يستخدمه. جملة واحدة واضحة.',
          },
          CALL: {
            HEADING: 'ماذا يستدعي',
            DESCRIPTION: 'عنوان النظام وطريقة استدعائه. من بناه يعرف الاثنين.',
          },
          METHOD: { LABEL: 'طريقة الاستدعاء' },
          PURPOSE: {
            LABEL: 'ماذا يفعل هذا؟',
            LOOKUP: 'يبحث عن معلومة',
            LOOKUP_HINT: 'يتحقق من نظامك ويقرأ الجواب. لا يتغيّر شيء.',
            CHANGE: 'يغيّر شيئًا',
            CHANGE_HINT: 'يحجز أو يطلب أو يلغي أو يحفظ شيئًا في نظامك.',
          },
          ADVANCED: 'متقدّم',
          IDENTITY_NOTE:
            'هذا يبحث في بيانات عملائك برقم يكتبه العميل. يستطيع أي شخص كتابة رقم غيره. استخدم «رقم واتساب لهذه المحادثة» بدلًا منه.',
          URL: {
            LABEL: 'عنوان الويب',
            PLACEHOLDER: 'https://api.yourshop.com/slots?area=',
            HINT: 'لا بد أن يبدأ بـ https://. ضع {token} في موضع القيمة — بعد اسم الموقع، لا داخله أبدًا.',
          },
          DETAILS: {
            HEADING: 'التفاصيل التي يملؤها',
            DESCRIPTION:
              'كل {token} كتبته أعلاه يحتاج سطرًا هنا، ليعرف مساعدك ماذا يضع مكانه.',
            NONE: 'لا شيء بعد. أضف سطرًا لكل قيمة على مساعدك أن يملأها.',
            CONTACT_HEADING: 'هذه نملؤها لك من بيانات عميلك',
            CONTACT_DESCRIPTION:
              'استخدم أيًّا منها أعلاه ونضع نحن ما نعرفه أصلًا عن الشخص الذي يراسلك. مساعدك لا يختارها، فلا يمكن أن تخرج خطأ — ولا تحتاج سطرًا هنا.',
            ADD: 'أضف تفصيلًا',
            FULL: 'هذا أقصى ما نستطيع حفظه.',
            NAME: 'الاسم',
            NAME_PLACEHOLDER: 'area',
            TYPE: 'النوع',
            NOTE: 'ما هو',
            NOTE_PLACEHOLDER: 'المحافظة التي يقيم فيها العميل.',
            REQUIRED: 'لا بد منه',
            REMOVE: 'حذف هذا التفصيل',
            TYPES: {
              STRING: 'نص',
              NUMBER: 'رقم',
              INTEGER: 'رقم صحيح',
              BOOLEAN: 'نعم أو لا',
            },
          },
          BODY: {
            HEADING: 'ماذا يرسل',
            DESCRIPTION: 'فقط للاستدعاءات التي ترسل شيئًا مع العنوان.',
            LABEL: 'ما يُرسَل',
            HINT: 'مكتوب بصيغة JSON. استخدم {token} في أي موضع تأتي فيه قيمة.',
          },
          SIGN_IN: {
            HEADING: 'بيانات الدخول',
            DESCRIPTION: 'كيف يعرف النظام الآخر أن الاستدعاء منك فعلًا.',
            LABEL: 'نوع الدخول',
            TYPES: {
              NONE: 'بلا',
              BEARER: 'رمز',
              HEADER: 'مفتاح يُرسل في ترويسة',
              BASIC: 'اسم مستخدم وكلمة مرور',
            },
            HEADER_NAME: 'اسم الترويسة',
            HEADER_NAME_PLACEHOLDER: 'X-Api-Key',
            SECRET: 'القيمة',
            SECRET_HINT_NEW:
              'نحفظها مقفلة ولا نعرضها مرة أخرى — لا لك ولا لمساعدك.',
            SECRET_HINT_SAVED:
              'توجد بيانات دخول محفوظة. اترك هذه الخانة فارغة لإبقائها.',
            SECRET_HINT_HOST:
              'هذا العنوان لموقع غير الموقع المحفوظ. ولا تُنقَل بيانات دخول محفوظة إلى موقع جديد أبدًا، فأدخلها من جديد قبل الحفظ.',
          },
          KEEP: {
            HEADING: 'ما يُبقى من الجواب',
            DESCRIPTION:
              'بعض الأنظمة تردّ بأكثر بكثير مما يحتاجه مساعدك. هذا يُبقيه على الجزء المفيد.',
            LABEL: 'أبقِ هذه فقط',
            PLACEHOLDER: 'slots\nfee',
            HINT: 'اسم واحد في كل سطر. اتركها فارغة لإبقاء الجواب كله.',
          },
          STATE: {
            HEADING: 'هل هو قيد الاستخدام',
            DESCRIPTION:
              'أطفئه ليتوقف مساعدك عن استخدامه، دون أن تفقد شيئًا مما أعددته هنا.',
            LABEL: 'يُسمح لمساعدك باستخدام هذا',
            ON: 'يستطيع مساعدك استخدامه وهو يتحدث إلى عميل.',
            OFF: 'لن يستخدمه مساعدك. ومهامك تبقى مشيرة إليه.',
            UNTESTED: 'جرّبه أولًا. يمكن تشغيله بعد أن تنجح تجربة.',
            NOT_TRIED: 'لم يُجرَّب منذ آخر تغيير.',
            CHANGED:
              'احفظ هذا التغيير، ثم جرّبه مرة أخرى. يمكن تشغيله بعد أن تنجح تلك التجربة.',
            ON_CHANGE:
              'تستخدمه مهامك بعد موافقة العميل. لا يستخدمه مساعدك من تلقاء نفسه في المحادثة.',
          },
          TRY: {
            KEPT_SAMPLE: {
              SAVED:
                'يحتفظ واصلو بالردّ من آخر اختبار في {date}، ليستخدمه «جرّبه».',
              FORGET: 'احذف العيّنة',
              FORGOTTEN: 'حُذفت العيّنة. اختبره مرة أخرى لحفظ عيّنة جديدة.',
              FORGET_FAILED: 'تعذّر حذف العيّنة. حاول مرة أخرى.',
            },
            HEADING: 'جرّبه',
            DESCRIPTION:
              'استدعِه مرة واحدة الآن بقيم تختارها. مساعدك ليس طرفًا في هذا، ولا يرى عميل شيئًا.',
            NEEDS_SAVE: 'احفظه أولًا، ثم تستطيع تجربته هنا.',
            NEEDS_SIGN_IN: 'أضف بيانات الدخول واحفظها، ثم تستطيع تجربته هنا.',
            SAMPLE_HEADING: 'عميل تجريبي',
            SAMPLE_HINT:
              'في المحادثة الحقيقية نملؤها من بيانات العميل. اكتب هنا قيمًا تجريبية.',
            SAMPLE: {
              CONTACT_NAME: 'الاسم',
              CONTACT_EMAIL: 'البريد الإلكتروني',
              CONTACT_PHONE_NUMBER: 'رقم الهاتف',
              CONVERSATION_ID: 'رقم المحادثة',
            },
            VALUES: 'قيم للتجربة',
            ACTION: 'جرّبه',
            RUNNING: 'يُستدعى…',
            OK: 'ردّ.',
            OK_STATUS: 'ردّ بـ {status}.',
            FAILED: 'لم يردّ.',
            FAILED_STATUS: 'ردّ بـ {status}، وهذا خطأ.',
            NO_STATUS: 'لم يعد شيء إطلاقًا.',
            PREVIEW_ACTION: 'اعرض ما سيُرسل',
            PREVIEW_TITLE: 'هذا ما سيُرسل. لم يُرسل شيء بعد.',
            PREVIEW_ADDRESS: 'العنوان',
            PREVIEW_HEADERS: 'يُرسل معه أيضًا (القيم مخفية)',
            PREVIEW_BODY: 'ما يرسله',
            SEND_CONFIRM: 'هذا يرسل فعلًا إلى نظامك. استخدم بيانات تجريبية.',
            SEND_ACTION: 'أرسله فعلًا',
            RESULT: 'ما عاد إلينا',
            ERROR_TITLE: 'ماذا قال',
            DETAIL_LABEL: 'ما ردّ به النظام',
            UNAVAILABLE: 'تعذّر تشغيل ذلك الآن. لم يُستدعَ شيء ولم يتغيّر شيء.',
          },
          REMOVE: {
            HEADING: 'حذف هذا الاتصال',
            DESCRIPTION:
              'أي مهمة تشير إليه تفقد قدرتها على قراءة أي شيء. وإطفاؤه يفعل الشيء نفسه ويحفظ إعداداتك.',
            ACTION: 'احذف هذا الاتصال',
            CONFIRM: 'حذف هذا الاتصال؟',
            CONFIRM_BODY:
              'يمكنك إرجاعه من «حُذف مؤخرًا» في «أنظمتك» خلال {period}. يعود مطفأً، وتضيف بيانات الدخول من جديد. ويمكنك إطفاؤه بدلًا من ذلك.',
            CONFIRM_ACTION: 'نعم، احذفه',
            CANCEL: 'أبقِه',
            REMOVED: 'حُذف.',
            FAILED: 'تعذّر الحذف. لم يتغيّر شيء — حاول مرة أخرى.',
          },
          SAVE: 'حفظ التغييرات',
          CREATE: 'أنشئ هذا الاتصال',
          SAVED: 'تم الحفظ. يستخدم مساعدك هذا من الرسالة القادمة.',
          CREATED: 'أُنشئ. يستخدم مساعدك هذا من الرسالة القادمة.',
          SAVE_FAILED: 'تعذّر الحفظ. لم يتغيّر شيء — حاول مرة أخرى.',
          NOT_FOUND: 'لم نجد هذا الاتصال. ربما حُذف.',
          STATUS: {
            IDLE: 'كل شيء محفوظ',
            IDLE_DETAIL: 'مساعدك يستخدم هذا الآن.',
            NEW: 'لم يُنشأ بعد',
            NEW_DETAIL: 'املأ الاسم، وماذا يفعل، وعنوان الويب.',
            DIRTY: 'لم يُحفظ بعد',
            DIRTY_DETAIL: 'بانتظار الحفظ: {sections}',
            SAVING: 'يُحفظ…',
            SAVED: 'حُفظ الساعة {time}',
            SAVED_DETAIL: '{sections} — يستخدمها مساعدك من الرسالة القادمة.',
            ERROR: 'تعذّر الحفظ',
            ERROR_DETAIL:
              'لم يتغيّر شيء، وما كتبته ما زال على الصفحة. حاول مرة أخرى.',
          },
          ERRORS: {
            PURPOSE: 'اختر ما يفعله هذا.',
            NAME: 'أعطِ هذا الاتصال اسمًا قصيرًا.',
            NAME_SHAPE:
              'استخدم ثلاثة أحرف أو أرقام إنجليزية على الأقل — نحوّل ما تكتبه إلى الاسم القصير الذي تستخدمه مهامك.',
            DESCRIPTION: 'اكتب ماذا يفعل، ليعرف مساعدك متى يستخدمه.',
            DESCRIPTION_LONG: 'هذا طويل. أبقِه دون {limit}.',
            URL: 'اكتب عنوان الويب الذي يُستدعى.',
            URL_LONG: 'هذا العنوان طويل جدًا.',
            URL_HTTPS: 'لا بد أن يبدأ العنوان بـ https://.',
            URL_HOST:
              'اسم الموقع في العنوان لا بد أن يكون ثابتًا — والقيمة لا تأتي إلا بعده.',
            BODY_JSON:
              'هذا ليس JSON صحيحًا بعد، فلا نستطيع حفظه. راجع الأقواس وعلامات التنصيص.',
            PARAMS_FULL: 'هذا أكثر من التفاصيل التي نستطيع حفظها.',
            PARAM_BLANK: 'التفصيل {number} بلا اسم.',
            PARAM_SHAPE:
              'التفصيل {number} لا يقبل إلا حروفًا إنجليزية وأرقامًا وشرطة سفلية، ويبدأ بحرف.',
            PARAM_DUPLICATE: 'تفصيلان يحملان الاسم نفسه.',
            PARAM_UNDECLARED: 'لا شيء يخبرنا ما «{name}». أضفه تفصيلًا أدناه.',
            PARAM_UNKNOWN_CONTACT:
              '«{name}» ليس من التفاصيل التي نملؤها لك من بيانات عميلك. وهي: {known}.',
            HEADER_NAME: 'اكتب في أي ترويسة يُرسل المفتاح.',
            SECRET: 'بيانات الدخول هذه تحتاج قيمة.',
            KEEP_FULL: 'هذا أكثر من الأسطر التي نستطيع حفظها.',
          },
        },
      },
    },
    FORMS: {
      TITLE: 'النماذج',
      SPOTLIGHT_TITLE:
        'دع العميل يملأ نموذجًا واحدًا بدلًا من الإجابة سؤالًا بعد سؤال',
      SPOTLIGHT:
        'يفتح النموذج داخل واتساب: بضعة حقول وزر واحد، وينتهي الأمر بخطوة واحدة. يناسب أجزاء المهمة التي تسأل فيها الأشياء نفسها دائمًا — تاريخ، عدد أشخاص، رقم هاتف. وأي خطوة تعتمد على ما قاله العميل قبل قليل تبقى في المحادثة، ويتعامل معها المساعد هناك كما يفعل اليوم.',
      SENDER_LABEL: 'أي رقم',
      UNAVAILABLE: 'لم نتمكن من قراءة نماذج هذا الرقم الآن.',
      NO_SENDER: {
        TITLE: 'لا يوجد رقم واتساب في مساحة العمل بعد',
        BODY: 'النموذج يتبع رقم واتساب. وبمجرد ربط رقم، تصبح هذه الصفحة هي مكان نشر النماذج.',
      },
      READY: {
        TITLE: 'هذا الرقم يستطيع استخدام النماذج',
        BODY: 'لا يذكر واتساب عن هذا الرقم ما يمنعه من إرسال نموذج.',
      },
      NOT_READY: {
        TITLE: 'هذا الرقم لا يستطيع استخدام النماذج بعد',
      },
      WHY: {
        NO_VERIFIED_NAME:
          'لم نتمكن من قراءة الاسم الذي يراه العملاء على هذا الرقم، فلا نستطيع أن نؤكد لك أن النموذج سيصل إليهم. أعطِ الأمر ساعة ثم عد إلى هنا.',
        QUALITY_RISKY:
          'خفّض واتساب تصنيف هذا الرقم، غالبًا لأن عملاء حجبوه أو أبلغوا عنه. ويطلب واتساب جودة رسائل جيدة قبل إرسال النموذج، فتعود النماذج وحدها بعد تحسّن التصنيف — لا شيء تضغطه هنا.',
        BUSINESS_NOT_VERIFIED:
          'النماذج تحتاج أن يكون نشاطك التجاري موثّقًا لدى ميتا. ومهامك تظل شغّالة في المحادثة إلى أن يتم التوثيق. للتوثيق: افتح إعدادات الأعمال في ميتا (Meta Business Settings)، ثم مركز الأمان (Security Center)، ثم بدء التوثيق (Start verification)، واتبع الخطوات بمستندات شركتك.',
        UNKNOWN_SIGNALS:
          'لم يصلنا رد من واتساب عن هذا الرقم بعد، فلا نستطيع تحديد ما إذا كان يصلح للنماذج. أعطِ الأمر ساعة ثم عد إلى هنا — لا يوجد خطأ.',
        UNTOLD:
          'هذا الرقم لا يستطيع استخدام النماذج بعد. والسبب الذي أُبلغنا به: {reason}',
        UNTOLD_SILENT:
          'هذا الرقم لا يستطيع استخدام النماذج بعد، ولم نُبلَّغ بالسبب. لا شيء تفعله هنا يغيّر ذلك — أخبرنا إن ظهرت لك هذه الرسالة.',
      },
      VERDICT_UNAVAILABLE: {
        TITLE: 'لم نتمكن من فحص هذا الرقم الآن',
        BODY: 'النشر متوقف حتى نتمكن من ذلك. ولا نعلم بوجود أي خطأ في الرقم، والنماذج أدناه غير متأثرة.',
      },
      SIGNALS: {
        NAME: 'الاسم الذي يراه العملاء',
        NAME_UNTOLD: 'لم يخبرنا واتساب',
        RATING: 'تصنيف واتساب',
        RATING_UNTOLD: 'غير مصنّف بعد',
        STANDING: 'حالة الرقم لدى واتساب',
        STANDING_UNTOLD: 'لم يخبرنا واتساب',
        TIER: 'عدد من يمكنك بدء محادثة معهم يوميًا',
        TIER_UNTOLD: 'لم يخبرنا واتساب',
        CHECKED: 'آخر رد من واتساب: {when}',
      },
      PUBLISHED: {
        HEADING: 'النماذج الموجودة على هذا الرقم',
        DESCRIPTION: 'كل ما يحفظه واتساب لهذا الرقم، وبجانبه حكمه هو.',
        EMPTY_TITLE: 'لا نماذج على هذا الرقم بعد',
        EMPTY_BODY:
          'اختر مهمة أدناه وانشر نموذجها. سيظهر هنا مع رد واتساب حين يصل.',
        INVALID: 'أبلغ واتساب عن مشكلة في هذا النموذج: {detail}',
        COUNT:
          'النماذج على هذا الرقم: {count} | النماذج على هذا الرقم: {count} | النماذج على هذا الرقم: {count} | النماذج على هذا الرقم: {count} | النماذج على هذا الرقم: {count} | النماذج على هذا الرقم: {count}',
        MORE: 'لدى واتساب نماذج أكثر مما تعرضه هذه القائمة.',
        VERSION: 'مبني على النسخة {version} من المهمة',
        LEGACY:
          'نُشر قبل أن تصبح النماذج بلغتين. ما زال يعمل، وواتساب لا يسمح بحذف نموذج منشور. انشر المهمة مرة أخرى لتحصل على نموذج إنجليزي وآخر عربي يستطيع المساعد إرسالهما.',
      },
      LANG: {
        EN: 'الإنجليزية',
        AR: 'العربية',
      },
      STATUS: {
        DRAFT: 'غير مُفعّل بعد',
        PUBLISHED: 'مُفعّل',
        DEPRECATED: 'متوقف',
        BLOCKED: 'محجوب من واتساب',
        THROTTLED: 'مُبطّأ من واتساب',
        UNTOLD: 'لم يخبرنا واتساب',
      },
      TASKS: {
        HEADING: 'مهام يمكن أن تصبح نموذجًا',
        DESCRIPTION:
          'المهمة المكتوبة على هيئة خطوات يمكن نقل أسئلتها المباشرة إلى نموذج. افتح إحداها لترى بالضبط ما سيسأله النموذج وما سيبقى في المحادثة.',
        EMPTY_TITLE: 'لا توجد مهمة مكتوبة على هيئة خطوات بعد',
        EMPTY_BODY:
          'النموذج مبني من خطوات مهمة. اكتب مهمة على هيئة خطوات أولًا ثم عد إلى هنا.',
        PREVIEW: 'اعرض ما سيسأله',
        PREVIEWING: 'نقرأ المهمة…',
        HIDE: 'إغلاق',
        PUBLISH: 'انشر هذا النموذج',
        PUBLISHING: 'نرسله إلى واتساب…',
        PUBLISH_BLOCKED_BUSINESS:
          'النشر متوقف إلى أن توثّق ميتا نشاطك التجاري. طريقة التوثيق موضّحة في الأعلى.',
        PUBLISHED_NOTE:
          'نُشر باسم {name}. واتساب يقرر موعد تفعيله — أعد تحميل الصفحة لترى رده.',
        NO_FORM_IN_LANG:
          'لا يوجد نموذج {language}: لا شيء في هذه المهمة يمكن أن يصبح حقلًا فيه بعد. اكتب أسئلتها بـ{language} ثم انشر مرة أخرى.',
        NOT_SENDABLE:
          'واتساب يحفظ {name}، لكن المساعد لم يتمكن من تسجيله ولن يرسله. انشر مرة أخرى.',
        FIELDS_HEADING: 'ما سيسأله النموذج',
        FIELD_COUNT:
          'عدد الحقول في النموذج: {count} | عدد الحقول في النموذج: {count} | عدد الحقول في النموذج: {count} | عدد الحقول في النموذج: {count} | عدد الحقول في النموذج: {count} | عدد الحقول في النموذج: {count}',
        FIELD_COUNT_CAPPED:
          'عدد الحقول في النموذج: {count} من {max} | عدد الحقول في النموذج: {count} من {max} | عدد الحقول في النموذج: {count} من {max} | عدد الحقول في النموذج: {count} من {max} | عدد الحقول في النموذج: {count} من {max} | عدد الحقول في النموذج: {count} من {max}',
        NO_FIELDS: 'لا شيء في هذه المهمة يمكن أن يصبح حقلًا في نموذج.',
        FIELD_CUT: 'العنوان مختصر ليتّسع',
        STAYS_HEADING: 'ما سيبقى في المحادثة',
        STAYS_NOTE:
          'هذه الخطوات تعتمد على اللحظة، فيستمر المساعد في سؤالها كما يفعل اليوم.',
        STEP_SHAPE: '{name} · {shape}',
        STATE: {
          LINE: '{language}: {state}',
          LIVE: 'يعمل',
          TRY: 'جرّبه',
          TRYING: 'نفتحه…',
          TRY_FAILED: 'لم يعطنا واتساب معاينة الآن. حاول بعد قليل.',
          WORDS_CHANGED: 'يعمل. صياغتك الأخيرة ليست في النموذج بعد.',
          QUESTIONS_CHANGED:
            'غيّرت أسئلة النموذج. يُسأل العملاء عنها في المحادثة حتى تحدّث النموذج.',
          NOT_PUBLISHED: 'غير منشور',
          UPDATE: 'حدّثه',
          PUBLISH: 'انشره',
          UNKNOWN:
            'تعذّر التحقق من نماذج هذه المهمة التي تعمل الآن. حاول بعد قليل.',
        },
      },
      SHAPE: {
        TEXT: 'كتابة',
        NUMBER: 'رقم',
        DATE: 'تاريخ',
        EMAIL: 'بريد إلكتروني',
        PHONE: 'رقم هاتف',
        OPTION: 'اختيار من قائمة',
        LIVE: 'اختيار من نظامك',
        PHOTO: 'صور',
        FILE: 'ملف',
        UNTOLD: 'حقل',
      },
      STAY: {
        RUNTIME_ROWS: 'خياراته غير معروفة حتى لحظة السؤال',
        RUNS_A_CONNECTION: 'يستدعي أحد أنظمتك',
        HANDS_OFF: 'يحوّل المحادثة إلى أحد فريقك',
        SAID_IN_CHAT: 'يقول شيئًا ولا يسأل',
        ENDS_THE_TASK: 'هو موضع انتهاء المهمة',
        TIME_OF_DAY: 'يسأل عن وقت من اليوم، والنموذج لا يجمع ذلك',
        NO_LABEL: 'أحد خياراته بلا كلمات مكتوبة بعد',
        NO_QUESTION_IN_LANGUAGE:
          'سؤاله غير مكتوب باللغة التي يُكتب بها هذا النموذج — اكتبه على تلك الخطوة',
        AFTER_A_BRANCH: 'يأتي بعد خطوة تُرسل المحادثة في مسارات مختلفة',
        AFTER_THE_FORM: 'يأتي بعد تسليم النموذج',
        FORM_FULL: 'النموذج وصل أصلًا إلى الطول الذي يسمح به واتساب',
        UNREACHABLE: 'لا شيء في المهمة يؤدي إليه',
        IN_CHAT: 'اخترتَ أن يُسأل في المحادثة',
        UPLOAD_PLACE: 'النموذج يأخذ الصور أو الملف مرة واحدة، في صفحته الأخيرة',
        UNTOLD: 'لم يذكر مُنشئ النماذج سببًا',
      },
      ARRANGE: {
        OPEN: 'ترتيب النموذج',
        HEADER: 'ترتيب نموذج «{task}»',
        INTRO_NOTE:
          'كل خانة في النموذج سؤال من أسئلة هذه المهمة، لذلك تُحفظ كل إجابة حيث كانت المحادثة ستحفظها.',
        NEW_NOTE:
          'نموذج هذه المهمة ما زال صفحة واحدة. احفظ لتبقى الصفحات كما تراها.',
        FORM_TITLE: 'عنوان النموذج',
        INTRO: 'مقدمة النموذج',
        PAGE: 'الصفحة {n} | الصفحة {n} | الصفحة {n} | الصفحة {n} | الصفحة {n} | الصفحة {n}',
        PAGE_OF:
          'الصفحة {n} من {total} | الصفحة {n} من {total} | الصفحة {n} من {total} | الصفحة {n} من {total} | الصفحة {n} من {total} | الصفحة {n} من {total}',
        PAGE_TITLE: 'عنوان الصفحة',
        BUTTON: 'كلمات الزر',
        NEXT_DEFAULT: 'التالي',
        ADD_PAGE: 'إضافة صفحة',
        REMOVE_PAGE: 'إزالة هذه الصفحة',
        MOVE_UP: 'نقل للأعلى',
        MOVE_DOWN: 'نقل للأسفل',
        MOVE_TO: 'نقل إلى صفحة',
        SKIPPABLE: 'يمكن للعميل تخطيه',
        SKIP_NOTE: 'إذا تخطاه العميل، يسأله المساعد في المحادثة بعد النموذج.',
        TO_CHAT: 'اسأله في المحادثة بدلًا من ذلك',
        TO_FORM: 'أضفه إلى النموذج',
        IN_CHAT: 'يُسأل في المحادثة',
        AUTO: 'قسّمه إلى صفحات تلقائيًا',
        SUMMARY: '{questions} على {pages}',
        QUESTIONS:
          'لا أسئلة | سؤال واحد | سؤالان | {count} أسئلة | {count} سؤالًا | {count} سؤال',
        PAGES:
          'لا صفحات | صفحة واحدة | صفحتان | {count} صفحات | {count} صفحةً | {count} صفحة',
        CUT_SHORT:
          'يظهر هذا مختصرًا على الهاتف، والسؤال كاملًا يظهر فوق الخانة.',
        MANY: 'يمكن وضع علامة على أكثر من واحد.',
        LIVE_CHOICES: 'تأتي الخيارات من نظامك عندما يفتح العميل هذه الصفحة.',
        ERR_EMPTY: 'كل صفحة تحتاج إلى سؤال واحد على الأقل.',
        ERR_PAGES:
          'يمكن أن يحتوي النموذج على {count} صفحة كحد أقصى. | يمكن أن يحتوي النموذج على صفحة واحدة كحد أقصى. | يمكن أن يحتوي النموذج على صفحتين كحد أقصى. | يمكن أن يحتوي النموذج على {count} صفحات كحد أقصى. | يمكن أن يحتوي النموذج على {count} صفحة كحد أقصى. | يمكن أن يحتوي النموذج على {count} صفحة كحد أقصى.',
        ERR_QUESTIONS:
          'يمكن أن يحتوي النموذج على {count} سؤال كحد أقصى. | يمكن أن يحتوي النموذج على سؤال واحد كحد أقصى. | يمكن أن يحتوي النموذج على سؤالين كحد أقصى. | يمكن أن يحتوي النموذج على {count} أسئلة كحد أقصى. | يمكن أن يحتوي النموذج على {count} سؤالًا كحد أقصى. | يمكن أن يحتوي النموذج على {count} سؤال كحد أقصى.',
        ERR_LONG:
          'كلمات الزر حتى {count} حرف. | كلمات الزر حتى حرف واحد. | كلمات الزر حتى حرفين. | كلمات الزر حتى {count} أحرف. | كلمات الزر حتى {count} حرفًا. | كلمات الزر حتى {count} حرف.',
        ERR_LONG_TITLE:
          'العنوان حتى {count} حرف. | العنوان حتى حرف واحد. | العنوان حتى حرفين. | العنوان حتى {count} أحرف. | العنوان حتى {count} حرفًا. | العنوان حتى {count} حرف.',
        ERR_LONG_INTRO:
          'مقدمة النموذج حتى {count} حرف. | مقدمة النموذج حتى حرف واحد. | مقدمة النموذج حتى حرفين. | مقدمة النموذج حتى {count} أحرف. | مقدمة النموذج حتى {count} حرفًا. | مقدمة النموذج حتى {count} حرف.',
        ERR_BRANCH: 'السؤال الذي يحدد الخطوة التالية يجب أن يكون الأخير.',
        ERR_LIVE:
          'خيارات هذا السؤال تأتي من نظامك وتحتاج إجابة من صفحة سابقة. انقله إلى صفحة لاحقة.',
        ERR_TWICE: 'هذا السؤال موجود في النموذج مرتين.',
        ERR_UPLOAD_LAST: 'سؤال الصور أو الملف يجب أن يكون في الصفحة الأخيرة.',
        ERR_UPLOAD_TWICE:
          'النموذج يطلب الصور أو الملف مرة واحدة فقط. اترك الآخر في المحادثة.',
        WARN_GONE: 'حُذف «{question}» من المهمة، لذلك لم يعد في النموذج.',
        WARN_GONE_UNNAMED: 'حُذف سؤال من المهمة، لذلك لم يعد في النموذج.',
        WARN_NOT_ELIGIBLE:
          'لم يعد «{question}» يصلح للنموذج، لذلك يسأله المساعد في المحادثة.',
        WARN_PAGE_EMPTIED:
          'لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت. | لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت. | لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت. | لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت. | لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت. | لم يبقَ في الصفحة {n} أي سؤال، لذلك أُزيلت.',
        WARN_WORDS:
          'بعض كلمات النموذج غير مكتوبة بـ{language} بعد، لذلك يعرض النموذج كلمات واصلو في مكانها.',
        WARN_QUESTION:
          'السؤال «{question}» غير مكتوب بـ{language}، لذلك يسأله المساعد في المحادثة بتلك اللغة.',
        PREVIEW: 'ما يراه عميلك',
        PREVIEW_LOADING: 'جارٍ رسم النموذج…',
        PREVIEW_FAILED: 'تعذّر رسم المعاينة. تغييراتك ما زالت هنا.',
        PREVIEW_EMPTY: 'لا شيء في النموذج بهذه اللغة بعد.',
        PREVIEW_PHOTOS: 'أضف صورًا',
        PREVIEW_FILE: 'أضف ملفًا',
        SAVE: 'حفظ النموذج',
        SAVED:
          'حُفظ النموذج. تبويب النماذج يبيّن إن كانت آخر كلماتك على واتساب.',
        SAVE_FAILED: 'لم يُحفظ النموذج. حاول مرة أخرى.',
        STATUS_DIRTY: 'لم يُحفظ بعد',
        STATUS_SAVED: 'محفوظ',
        CHECK_LINE:
          'على واتساب، يمكن أن تكون أسئلة هذه المهمة نموذجًا واحدًا بصفحات.',
      },
      ERROR: {
        BUILDER_UNREACHABLE:
          'لم نتمكن من الوصول إلى الجزء الذي يبني النموذج في واصلو. لم يُرسل شيء إلى واتساب، فحاول بعد دقيقة.',
        BUILDER_REFUSED:
          'بناء النماذج من المهام غير مُفعّل لمساحة العمل هذه بعد. لم يُرسل شيء إلى واتساب.',
        BUILDER_MALFORMED:
          'عاد النموذج بشكل لا نعرفه، فلم يُرسل شيء إلى واتساب. هذا خطؤنا نحن.',
        NOT_ELIGIBLE:
          'هذا الرقم لا يستطيع استخدام النماذج بعد، فلم يُرسل شيء إلى واتساب.',
        NO_FORM:
          'كل خطوة في هذه المهمة تعتمد على اللحظة، فلا يوجد ما يسأله النموذج.',
        NO_SUCH_TASK:
          'لم نجد هذه المهمة في الجهة التي تبني النماذج. لم يُرسل شيء إلى واتساب، وهذا خطؤنا نحن لا نتيجة لشيء فعلته.',
        REJECTED: 'لم يقبل واتساب هذا النموذج.',
        STAGE: {
          CREATE: 'رفض واتساب إنشاءه، فلم يتبقَّ شيء.',
          UPLOAD:
            'أنشأه واتساب لكنه لم يقبل محتواه. هو قائم على حساب واتساب الخاص بك غير مكتمل، باسم {name}.',
          PUBLISH:
            'قبل واتساب النموذج لكنه لم يفعّله. هو على حساب واتساب الخاص بك باسم {name}.',
        },
        META_SAID: 'قال واتساب: {detail}',
        GENERIC: 'حدث خطأ ولم يُنشر شيء. حاول مرة أخرى.',
        ENDPOINT_UNAVAILABLE:
          'هذا النموذج يجلب خيارات من نظامك، ولم نتمكن من تجهيز هذا الرقم لذلك بعد. لم يُنشر شيء. حاول مرة أخرى بعد دقيقة.',
      },
    },
    OUTBOUND: {
      TITLE: 'واجهة الإرسال البرمجية',
      COMMON: {
        SECTION: 'واجهة الإرسال البرمجية',
        BACK: 'واجهة الإرسال البرمجية',
        ON: 'مشغّل',
        OFF: 'مطفأ',
        SAVING: 'جارٍ الحفظ…',
        SAVED: 'تم الحفظ.',
        SAVE_FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر أي شيء — حاول مرة أخرى.',
        UNAVAILABLE: 'تعذّر تحميل هذا الآن.',
        NOT_SET_UP_TITLE: 'الإرسال من أنظمتك غير مفعّل في مساحة العمل هذه',
        NOT_SET_UP_BODY:
          'لا يوجد رقم واتساب موصول بهذه الميزة بعد — تواصل معنا وسنوصل رقمًا. وسيظهر كل ما في هذه الصفحات بمجرد تشغيله.',
        COPY: 'نسخ',
        COPIED: 'تم النسخ',
        COPY_FAILED: 'تعذّر النسخ. حدّده وانسخه يدويًا.',
        CLOSE: 'إغلاق',
        CANCEL: 'إلغاء',
        NONE: 'لا شيء',
        NOT_YET: 'ليس بعد',
        UNKNOWN: 'غير معروف',
        JUST_NOW: 'قبل قليل',
        SENDER: 'الرقم الذي يُرسل منه',
        SENDER_ANY: 'أي رقم من أرقامك',
        SENDER_UNKNOWN: 'رقم لم يعد موجودًا',
        NO_NUMBER: 'لا يوجد رقم بعد',
        REFRESH: 'تحديث',
        PAGER: {
          SHOWING:
            'عرض {from}–{to} من {total} | عرض {from}–{to} من {total} | عرض {from}–{to} من {total} | عرض {from}–{to} من {total} | عرض {from}–{to} من {total} | عرض {from}–{to} من {total}',
          PREVIOUS: 'السابق',
          NEXT: 'التالي',
          NONE: 'لا شيء لعرضه',
        },
      },
      STATUS: {
        ALL: 'الكل',
        QUEUED: 'في الانتظار',
        SENDING: 'قيد الإرسال',
        ACCEPTED: 'تسلّمها واتساب',
        SENT: 'أُرسلت',
        DELIVERED: 'وصلت',
        READ: 'قُرئت',
        FAILED: 'لم تُرسل بسبب خطأ',
        UNCONFIRMED: 'غير مؤكدة',
        SUPPRESSED: 'لم تُرسل عمدًا',
        EXPLAIN: {
          QUEUED: 'وصلتنا ولم تخرج بعد.',
          SENDING: 'تخرج الآن.',
          ACCEPTED: 'تسلّمها واتساب، ولم يخبرنا بعد أنها وصلت إلى عميلك.',
          SENT: 'أرسلها واتساب إلى عميلك.',
          DELIVERED: 'وصلت إلى هاتف عميلك.',
          READ: 'فتحها عميلك.',
          FAILED: 'لم تخرج.',
          UNCONFIRMED:
            'قد تكون خرجت واحتُسبت تكلفتها — لم يصلنا خبر لا بهذا ولا بذاك.',
          SUPPRESSED: 'لم نرسلها، عن قصد.',
        },
      },
      REASON: {
        LIST: {
          MANUAL: 'أنت أضفته',
          MANUAL_BODY:
            'أحد أفراد فريقك أضاف هذا الرقم إلى القائمة من هذه الصفحة.',
          API: 'أحد أنظمتك أضافه',
          API_BODY: 'طلب منّا أحد أنظمتك التوقف عن مراسلة هذا الرقم.',
          KEYWORD: 'العميل أوقفها',
          KEYWORD_BODY:
            'ردّ عميلك بكلمة STOP. ولا يستطيع التراجع عنها غيره، بإرسال START إلى رقمك.',
          PROVIDER: 'واتساب أوقفها',
          PROVIDER_BODY:
            'واتساب نفسه أوقف الرسائل إلى هذا الرقم. ولا يستطيع التراجع عنها غير عميلك، بإرسال START إلى رقمك.',
          UNKNOWN: 'غير معروف',
        },
        MESSAGE: {
          OPT_OUT: 'هذا الرقم مدرج في قائمة «أرقام لا تُراسَل» لديك.',
          TEST_MODE:
            'كان وضع التجربة مشغّلًا، وهذا الرقم ليس من أرقام التجربة لديك.',
          RECIPIENT_CAP:
            'كان هذا الرقم قد استلم أقصى ما يستلمه رقم واحد من رسائل في اليوم.',
        },
      },
      SETUP: {
        TITLE: 'الإعداد',
        SPOTLIGHT:
          'من هنا ترسل أنظمتك — موقعك، أو نموذج الحجز، أو متجرك — رسالة واتساب عبر رقمك. الميزة مطفأة في كل مساحة عمل حتى يشغّلها أحد من هنا، ولا يستطيع أي نظام الإرسال قبل أن تنشئ مفتاحًا.',
        ENABLE: {
          HEADING: 'الإرسال من أنظمتك',
          LABEL: 'اسمح لأنظمتي بإرسال الرسائل',
          ON_BODY: 'تستطيع أنظمتك إرسال رسائل واتساب عبر رقمك.',
          OFF_BODY:
            'لا تستطيع أنظمتك إرسال أي رسالة بعد. شغّل هذا حين تصبح جاهزًا لذلك.',
          SAVING: 'جارٍ الحفظ…',
          FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر أي شيء — حاول مرة أخرى.',
        },
        PAUSE: {
          HEADING: 'إيقاف مؤقت للكل',
          LABEL: 'إيقاف الإرسال مؤقتًا',
          ON_BODY:
            'لا يخرج شيء. ونخبر أنظمتك أن تعيد المحاولة لاحقًا، فلا يضيع شيء.',
          OFF_BODY: 'تخرج الرسائل التي تطلبها أنظمتك كالمعتاد.',
          PAUSED_BY: 'أوقفها {who}، {when}.',
          PAUSED_BY_UNKNOWN: 'أُوقفت {when}. وليس لدينا سجل بمن أوقفها.',
          PAUSED_WHEN_UNKNOWN: 'أوقفها {who}.',
          PAUSED_BARE: 'الإرسال متوقف مؤقتًا.',
          RESUME: 'استئناف الإرسال',
          FAILED: 'تعذّر تغيير ذلك الآن. لم يتغيّر أي شيء — حاول مرة أخرى.',
        },
        TEST: {
          HEADING: 'وضع التجربة',
          LABEL: 'أرسل إلى أرقام التجربة فقط',
          ON_BODY:
            'لا يستلم شيئًا غير الأرقام أدناه. وكل ما عداها يُسجَّل ولا يُرسل، فتستطيع تجربة شيفرة حقيقية بأمان.',
          OFF_BODY: 'تذهب الرسائل إلى أي رقم يطلبه نظامك.',
          NUMBERS_HEADING: 'أرقام التجربة',
          NUMBERS_DESCRIPTION:
            'ما دام وضع التجربة مشغّلًا، فهذه هي الأرقام الوحيدة التي تستلم شيئًا. ضع هاتفك أنت أولًا.',
          NUMBER_LABEL: 'رقم الهاتف',
          ADD: 'إضافة رقم',
          PLACEHOLDER: '+96550001234',
          HINT: 'مع رمز الدولة، مثل ‎+96550001234.',
          REMOVE: 'إزالة هذا الرقم',
          EMPTY: 'لا يوجد أي رقم، فوضع التجربة لا يرسل إلى أحد إطلاقًا.',
          FULL: 'هذا أقصى ما نستطيع حفظه.',
          INVALID: 'هذا لا يبدو رقم هاتف مع رمز دولة.',
          DUPLICATE: 'هذا الرقم موجود في القائمة أصلًا.',
          SAVING: 'جارٍ الحفظ…',
          FAILED: 'تعذّر حفظ ذلك. لم يتغيّر أي شيء — حاول مرة أخرى.',
        },
        TEMPLATES: {
          HEADING: 'الرسائل التي تستطيع إرسالها',
          DESCRIPTION:
            'لا يسمح واتساب للنشاط التجاري ببدء محادثة إلا بصياغة وافق عليها مسبقًا. وأنظمتك تطلب واحدة منها بالاسم.',
          SENDER_LABEL: 'أي رقم',
          REFRESH: 'تحديث من واتساب',
          REFRESHING: 'جارٍ سؤال واتساب…',
          REQUESTED: 'طلبنا من واتساب قائمة محدّثة.',
          REFRESH_FAILED:
            'تعذّر طلب التحديث الآن. لم يتغيّر أي شيء — حاول مرة أخرى.',
          NAME: 'اطلبها بهذا الاسم',
          LANGUAGE: 'اللغة',
          LANGUAGE_HINT: 'أرسل اللغة كما هي مكتوبة هنا تمامًا.',
          CATEGORY: 'تُحتسب على أنها',
          STATUS: 'واتساب يقول',
          BLANKS:
            'الفراغات المطلوب ملؤها: {count} | الفراغات المطلوب ملؤها: {count} | الفراغات المطلوب ملؤها: {count} | الفراغات المطلوب ملؤها: {count} | الفراغات المطلوب ملؤها: {count} | الفراغات المطلوب ملؤها: {count}',
          NO_BLANKS: 'لا شيء يُملأ.',
          BUTTONS: 'الأزرار: {list}',
          BUTTON_NEEDS_VALUE: '{text} (أرسل قيمة)',
          LIST_JOIN: '، ',
          EMPTY:
            'لا توجد رسائل معتمدة لهذا الرقم بعد. أضف واحدة في واتساب، وستظهر هنا بمجرد اعتمادها.',
          UNAVAILABLE: 'تعذّر علينا قراءة الرسائل المعتمدة لهذا الرقم.',
          NOT_SET_UP:
            'الرسائل المعتمدة غير مُعدّة لمساحة العمل هذه بعد. تواصل معنا وسنُعدّها لك.',
        },
        QUICKSTART: {
          HEADING: 'رسالتك الأولى',
          DESCRIPTION:
            'أعطِ هذا لمن يتولّى أنظمتك. تشغيله كما هو يرسل رسالة واتساب حقيقية واحدة.',
          ADDRESS: 'إلى أين تُرسل',
          ADDRESS_NOTE:
            'إذا لم يصل أول طلب إلى هذا العنوان، تواصل معنا — نفعّله لكل مساحة عمل على حدة.',
          KEY_PLACEHOLDER: 'PUT-YOUR-KEY-HERE',
          KEY_NOTE:
            'ضع المفتاح الذي أنشأته أعلاه في الموضع المشار إليه. لا نستطيع ملأه عنك — فنحن لا نحتفظ بنسخة منه.',
          NEEDS_KEY: 'أنشئ مفتاحًا أعلاه، ثم يصبح هذا جاهزًا للتشغيل.',
          NEEDS_TEMPLATE: 'ما إن تُعتمد إحدى رسائلك حتى يوضع اسمها هنا.',
          COPY: 'نسخ الأمر',
        },
        LIMITS: {
          HEADING: 'ما هو مسموح لمساحة عملك',
          DESCRIPTION:
            'هذه تمنع نظامًا واحدًا خرج عن السيطرة من إنفاق شهرك كله في عصر يوم. ولا يُغيَّر منها شيء من هذه الصفحة.',
          SET_BY_WASLO: 'حدّدته واصلو',
          SET_BY_WASLO_HINT: 'نحن حدّدنا هذا لمساحة عملك. تواصل معنا لتغييره.',
          RATE_PER_MIN: 'رسائل في الدقيقة',
          RATE_PER_DAY: 'رسائل في اليوم',
          READ_RATE_PER_DAY: 'استعلامات في اليوم',
          READ_RATE_PER_DAY_HINT:
            'كم مرة تستطيع أنظمتك سؤالنا عمّا حدث لرسالة.',
          RECIPIENT_CAP_PER_DAY: 'رسائل لشخص واحد في اليوم',
          MONTHLY_MESSAGES: 'رسائل في الشهر',
          MONTHLY_MESSAGES_NONE: 'لا حدّ شهري من عندنا',
          MONTHLY_MESSAGES_ZERO: 'لا شيء — الإرسال متوقف هذا الشهر.',
          COUNTRIES: 'الدول التي يُسمح بمراسلتها',
          COUNTRIES_ANY: 'أي مكان',
          COUNTRIES_NONE:
            'لا شيء، فلا يمكن الإرسال إلى أي مكان. تواصل معنا إن لم يكن هذا ما تتوقعه.',
          STOP_WORDS: 'كلمات توقف الرسائل',
          START_WORDS: 'كلمات تعيدها',
          RETENTION: 'كم يُحفظ سجل الرسائل',
          DAYS: 'لا أيام | يوم واحد | يومان | {count} أيام | {count} يومًا | {count} يوم',
          PER_MINUTE:
            '{count} في الدقيقة | {count} في الدقيقة | {count} في الدقيقة | {count} في الدقيقة | {count} في الدقيقة | {count} في الدقيقة',
          PER_DAY:
            '{count} في اليوم | {count} في اليوم | {count} في اليوم | {count} في اليوم | {count} في اليوم | {count} في اليوم',
          PER_MONTH:
            '{count} في الشهر | {count} في الشهر | {count} في الشهر | {count} في الشهر | {count} في الشهر | {count} في الشهر',
        },
      },
      KEYS: {
        HEADING: 'المفاتيح',
        DESCRIPTION:
          'المفتاح هو ما يتيح لأحد أنظمتك الإرسال عبر رقمك. أعطِ كل نظام مفتاحه، لتستطيع إطفاء واحد دون المساس بالبقية.',
        CREATE: 'إنشاء مفتاح',
        EMPTY: {
          TITLE: 'لا توجد مفاتيح بعد',
          BODY: 'لا يستطيع شيء الإرسال قبل أن تنشئ مفتاحًا. وتراه مرة واحدة، لحظة إنشائه، ولا تراه بعدها أبدًا.',
          ACTION: 'أنشئ مفتاحك الأول',
        },
        ROW: {
          NO_LABEL: 'بلا اسم',
          PREFIX: 'يبدأ بـ',
          CREATED: 'أُنشئ {when}',
          LAST_USED: 'آخر استخدام {when}',
          NEVER_USED: 'لم يُستخدم قط',
          REVOKED: 'أُطفئ {when}',
          REVOKED_BADGE: 'مطفأ',
          SENDER: 'يُرسل من {number}',
          SENDER_ANY: 'يُرسل من أي رقم من أرقامك',
          REFUSED_ONE: 'رُفض مرة واحدة · آخرها {when}: {reason}',
          REFUSED_MANY:
            'لم يُرفض · آخرها {when}: {reason} | رُفض مرة واحدة · آخرها {when}: {reason} | رُفض مرتين إجمالًا · آخرها {when}: {reason} | رُفض {count} مرات إجمالًا · آخرها {when}: {reason} | رُفض {count} مرة إجمالًا · آخرها {when}: {reason} | رُفض {count} مرة إجمالًا · آخرها {when}: {reason}',
        },
        REFUSAL: {
          SENDER_REMOVED: 'الرقم الذي يُرسل منه هذا المفتاح أُزيل',
          NOT_SET_UP: 'لا يوجد رقم من أرقامك جاهز للإرسال بعد',
          AMBIGUOUS_SENDER: 'لم يُحدَّد من أي أرقامك يُرسل',
          RATE_LIMITED: 'كان الإرسال أسرع مما يسمح به حدّك',
          MONTHLY_ALLOWANCE: 'نفدت حصة رسائل هذا الشهر',
          DISABLED: 'الإرسال من أنظمتك مُطفأ',
          PAUSED: 'الإرسال من أنظمتك متوقف مؤقتًا',
          PLAN_REQUIRED: 'الإرسال من أنظمتك ليس ضمن باقتك',
          INVALID_REQUEST: 'كان الطلب ناقصًا شيئًا نحتاجه',
          COUNTRY_NOT_ALLOWED: 'حاول مراسلة دولة لا يُسمح لك بمراسلتها',
          CIRCUIT_OPEN: 'أوقفنا الإرسال بعد عدة إخفاقات متتالية',
          BUSY: 'كنّا مشغولين ولم نستقبله — يمكن إرساله مرة أخرى',
          IDEMPOTENCY_CONFLICT:
            'أعاد استخدام مرجع سبق أن استخدمناه لرسالة أخرى',
          TEMPLATE: 'الرسالة التي طلب إرسالها ليست مما يمكنك إرساله',
          UNKNOWN: 'تواصل معنا ونخبرك بالسبب',
        },
        UNAVAILABLE: 'تعذّر سرد مفاتيحك الآن.',
        NEW: {
          TITLE: 'إنشاء مفتاح',
          BODY: 'ترى المفتاح مرة واحدة في الشاشة التالية، ولا تراه بعدها أبدًا. جهّز مكانًا تلصقه فيه قبل أن تضغط «أنشئه».',
          LABEL: 'لأي شيء هو؟',
          LABEL_PLACEHOLDER: 'نموذج الحجز في الموقع',
          LABEL_HINT:
            'لا يراه غير فريقك. وهو ما ستعرف به أي نظام تطفئه لاحقًا.',
          SENDER: 'من أي رقم يُرسل؟',
          SENDER_PLACEHOLDER: 'اختر رقمًا',
          SENDER_HINT:
            'لا يستطيع هذا المفتاح الإرسال إلا من الرقم الذي تختاره.',
          SENDER_REQUIRED:
            'في مساحة العمل هذه أكثر من رقم واتساب — اختر الرقم الذي يُرسل منه هذا المفتاح.',
          SENDER_NONE:
            'لا يوجد رقم واتساب في مساحة العمل هذه يستطيع الإرسال بعد.',
          CREATE: 'أنشئه',
          CREATING: 'جارٍ الإنشاء…',
          CANCEL: 'ليس الآن',
          FAILED: 'تعذّر إنشاء المفتاح. لم يُنشأ شيء — حاول مرة أخرى.',
        },
        SECRET: {
          TITLE: 'مفتاحك الجديد',
          ONCE: 'هذه هي المرة الوحيدة التي تراه فيها. انسخه الآن والصقه في نظامك — فنحن لا نحتفظ بنسخة، ولا يستطيع أحد أن يعرضه عليك مرة أخرى.',
          LABEL: 'المفتاح',
          REVEAL: 'إظهار المفتاح',
          HIDE: 'إخفاؤه',
          COPY: 'نسخ المفتاح',
          COPIED: 'تم النسخ',
          DONE: 'حفظته',
          LOST: 'إن فقدته، أطفئ هذا المفتاح وأنشئ غيره. ولا يتأثر شيء آخر.',
        },
        REVOKE: {
          ACTION: 'إطفاء هذا المفتاح',
          CONFIRM_TITLE: 'إطفاء هذا المفتاح؟',
          CONFIRM_BODY:
            'أي نظام ما زال يستخدمه يتوقف عن الإرسال فورًا. ولا يمكن التراجع — تنشئ مفتاحًا جديدًا بدلًا منه.',
          CONFIRM_YES: 'نعم، أطفئه',
          CANCEL: 'أبقِه',
          DONE: 'أُطفئ. ولم يعد ما يستخدمه قادرًا على الإرسال.',
          FAILED: 'تعذّر إطفاء المفتاح. لم يتغيّر أي شيء — حاول مرة أخرى.',
        },
      },
      LOG: {
        TITLE: 'سجل الرسائل',
        SPOTLIGHT:
          'كل رسالة طلبت أنظمتك منّا إرسالها، الأحدث أولًا — بما فيها ما لم نرسله عن قصد، ولماذا.',
        HEADING: 'ما أرسلته أنظمتك',
        FILTER: {
          LABEL: 'اعرض',
          ALL: 'الكل',
        },
        ROW: {
          TO: 'إلى',
          TEMPLATE: 'الرسالة',
          WHEN: 'متى',
          UPDATED: 'آخر تغيير {when}',
          OPEN: 'فتح',
          JUST_NOW: 'قبل قليل',
        },
        RETENTION:
          'لا تُحفظ الرسائل هنا. | تُحفظ الرسائل هنا يومًا واحدًا ثم تُزال. | تُحفظ الرسائل هنا يومين ثم تُزال. | تُحفظ الرسائل هنا {days} أيام ثم تُزال. | تُحفظ الرسائل هنا {days} يومًا ثم تُزال. | تُحفظ الرسائل هنا {days} يوم ثم تُزال.',
        RETENTION_UNKNOWN:
          'تعذّر معرفة مدة حفظ الرسائل. وتُزال الأقدم بعد فترة.',
        EMPTY: {
          TITLE: 'لم يُرسل شيء بعد',
          BODY: 'أول رسالة يرسلها أحد أنظمتك تظهر هنا، وصلت أو لم تصل.',
          FILTERED_TITLE: 'لا شيء يطابق هذه التصفية',
          FILTERED_BODY: 'توجد رسائل هنا، لكن لا شيء منها في هذه الحالة.',
          CLEAR_FILTER: 'اعرض الكل',
        },
        UNAVAILABLE: 'تعذّر قراءة سجل الرسائل الآن.',
        DETAIL: {
          BACK: 'سجل الرسائل',
          TITLE: 'رسالة واحدة',
          NOT_FOUND: 'تعذّر العثور على هذه الرسالة. قد تكون أقدم مما نحفظه.',
          UNAVAILABLE: 'تعذّر فتح هذه الرسالة الآن.',
          WHAT_HAPPENED: 'ماذا حدث',
          WHY_NOT_SENT: 'لماذا لم تُرسل',
          WHY_FAILED: 'ما الذي حدث من خطأ',
          FIELDS: {
            TO: 'أُرسلت إلى',
            TEMPLATE: 'الرسالة المستخدمة',
            LANGUAGE: 'اللغة',
            SENDER: 'أُرسلت من',
            STATUS: 'إلى أين وصلت',
            PROVIDER_STATUS: 'ماذا سمّاها واتساب',
            ERROR_CODE: 'رمز الخطأ',
            ERROR: 'ما الذي حدث من خطأ',
            ATTEMPTS: 'المحاولات',
            ATTEMPTS_VALUE:
              'لم تُجرَّب بعد | محاولة واحدة | محاولتان | {count} محاولات | {count} محاولة | {count} محاولة',
            REFERENCE: 'مرجعك',
            REFERENCE_HINT:
              'المرجع الذي أرسله نظامك معها، ليطابق هذه الرسالة بسجله هو.',
            METADATA: 'ما أرفقه نظامك',
            NO_METADATA: 'لم يرفق نظامك شيئًا.',
            CONVERSATION: 'المحادثة التي دخلت فيها',
            CONVERSATION_REUSED: 'دخلت في محادثة قائمة أصلًا مع هذا العميل.',
            CONVERSATION_NEW: 'بدأت محادثة جديدة مع هذا العميل.',
            CONVERSATION_NONE: 'لم تصل إلى محادثة أصلًا.',
            OPEN_CONVERSATION: 'فتح المحادثة',
          },
          TIMES: {
            HEADING: 'متى حدث كل شيء',
            CREATED: 'طلبها نظامك',
            ENQUEUED: 'استلمناها',
            SENT: 'خرجت',
            TERMINAL: 'انتهت',
            UPDATED: 'آخر تغيير',
            NOT_YET: 'ليس بعد',
          },
        },
      },
      NUMBERS: {
        TITLE: 'أرقام لا تُراسَل',
        SPOTLIGHT:
          'أرقام يجب ألّا تراسلها أنظمتك أبدًا. العميل الذي يردّ بـ STOP يُضاف هنا من تلقاء نفسه، وكل ما في هذه القائمة يُرفض قبل أن يكلّفك شيئًا.',
        HEADING: 'أرقام لن نراسلها',
        DESCRIPTION: 'كل رسالة تطلبها أنظمتك تُقارن بهذه القائمة أولًا.',
        ADD: {
          HEADING: 'إضافة رقم',
          LABEL: 'رقم الهاتف',
          PLACEHOLDER: '+96550001234',
          HINT: 'مع رمز الدولة، مثل ‎+96550001234.',
          ACTION: 'أضفه',
          ADDING: 'جارٍ الإضافة…',
          ADDED: 'أُضيف. ولن يُرسل شيء إلى هذا الرقم.',
          INVALID: 'هذا لا يبدو رقم هاتف مع رمز دولة.',
          DUPLICATE: 'هذا الرقم موجود في القائمة أصلًا.',
          FAILED: 'تعذّرت إضافة الرقم. لم يتغيّر أي شيء — حاول مرة أخرى.',
        },
        ROW: {
          WHY: 'لماذا',
          ADDED_WHEN: 'أُضيف {when}',
          REMOVED_WHEN: 'أُزيل {when}',
          REMOVED_BY: 'أزاله {who}',
          REMOVED_BADGE: 'لم يعد في القائمة',
        },
        LOCKED: {
          HEADING: 'هذا ليس لك أن ترفعه',
          KEYWORD: 'لا يستطيع التراجع عنه غير عميلك، بإرسال START إلى رقمك.',
          PROVIDER: 'لا يستطيع التراجع عنه غير عميلك، بإرسال START إلى رقمك.',
        },
        REMOVE: {
          ACTION: 'إزالته من القائمة',
          CONFIRM_TITLE: 'مراسلة هذا الرقم من جديد؟',
          CONFIRM_BODY:
            'ستستطيع أنظمتك الإرسال إليه من الرسالة التالية. لا تفعل هذا إلا إذا طلب منك العميل ذلك.',
          CONFIRM_YES: 'نعم، أزله',
          CANCEL: 'اتركه',
          DONE: 'أُزيل. وتستطيع أنظمتك مراسلة هذا الرقم من جديد.',
          FAILED: 'تعذّرت إزالة الرقم. لم يتغيّر أي شيء — حاول مرة أخرى.',
          REFUSED:
            'هذا الرقم انسحب بنفسه، فلا يستطيع التراجع غير العميل — يرسل START إلى رقمك.',
        },
        SHOW_REMOVED: 'أظهر الأرقام التي أُزيلت من القائمة',
        EMPTY: {
          TITLE: 'لا أحد في القائمة',
          BODY: 'العميل الذي يردّ بـ STOP يُضاف هنا من تلقاء نفسه. وتستطيع أنت أيضًا إضافة رقم.',
          FILTERED_TITLE: 'لا شيء لعرضه',
          FILTERED_BODY: 'لم يُزل أي رقم من هذه القائمة بعد.',
        },
        UNAVAILABLE: 'تعذّر قراءة القائمة الآن.',
      },
    },
    // ── SET UP WITH AI (spec §8 P-8.1 to P-8.3) ─────────────────────────────────────
    SETUP_AI: {
      UNDO: 'تراجع',
      SOURCE: {
        PROFILE: 'من ملفك في واتساب',
        WEBSITE: 'من موقعك',
        ACCOUNT_NAME: 'من اسم حسابك',
        WASLO: 'اقتراح من واصلو',
      },
      VOICE: {
        RECORD: 'سجّل ملاحظة صوتية',
        STOP: 'إيقاف {time}',
        TURN: 'حوّلها إلى نص',
        TURNING: 'نستمع…',
        DISCARD: 'احذف التسجيل',
        DENIED:
          'لم يُسمح للمتصفح باستخدام الميكروفون. يمكنك الكتابة بدلًا من ذلك.',
        NOTHING_HEARD: 'لم نسمع أي كلام في هذا التسجيل. لم يُخصم شيء.',
        FAILED: 'لم نتمكن من تحويله إلى نص. حاول مرة أخرى.',
      },
      REVIEW: {
        KEEP: 'احتفظ',
        EDIT: 'عدّل',
        REMOVE: 'احذف',
        ANSWERS: 'إجابات لعملائك',
        QUESTION: 'السؤال',
        ANSWER: 'الإجابة',
        QUESTIONS: 'أنت وحدك تعرف إجابة هذه',
        QUESTIONS_HINT:
          'لم يجد واصلو هذه المعلومات في أي مكان، فلم يخمّنها. أجب عمّا تعرفه، واترك الباقي فارغًا.',
        SKIP_PLACEHOLDER: 'اتركه فارغًا للتخطي',
      },
      FIRST_DRAFT: {
        TITLE: 'اكتبها عنّي',
        OFFER:
          'ما زال مساعدك يستخدم كلمات البداية من واصلو. يمكن لواصلو كتابة مسودة أولى مما يذكره ملفك في واتساب.',
        OPEN: 'اكتبها عنّي',
        ALREADY_WRITTEN:
          'شخصية مساعدك مكتوبة بكلماتك، فلا شيء لصياغته. يمكنك تعديلها من صفحة الشخصية.',
        FOUND_INTRO:
          'هذا ما وجده واصلو عن نشاطك. المسودة تعتمد عليه فقط، وما لا يجده يصبح سؤالًا لك.',
        ACCOUNT_NAME: 'اسم حسابك: {name}',
        NOTHING_FOUND:
          'لم يجد واصلو شيئًا عن نشاطك بعد. أضف موقعك بالأسفل، أو اكتب الشخصية بنفسك.',
        WEBSITE: 'موقعك',
        WEBSITE_HINT:
          'يمكن لواصلو قراءته أولًا كمصدر للإجابات تراه وتستطيع إيقافه.',
        READ_WEBSITE: 'اقرأ الموقع أولًا',
        SKIP_WEBSITE: 'تخطَّ الموقع',
        READING: 'نقرأ موقعك الآن. تبدأ المسودة عندما ننتهي.',
        WEBSITE_READ: 'تمت قراءة موقعك. يمكنك كتابة المسودة الآن.',
        WEBSITE_FAILED:
          'لم نتمكن من قراءة هذا الموقع. يمكنك تخطيه والكتابة من الباقي.',
        DRAFT: 'اكتب المسودة',
        DRAFTING: 'نكتب مسودتك…',
        UNUSABLE:
          'رجعت المسودة بلا شيء يمكن استخدامه. لم يُخصم شيء. حاول مرة أخرى.',
        FAILED: 'حدث خطأ. لم يتغير شيء. حاول مرة أخرى.',
        REVIEW_INTRO:
          'احتفظ بالصحيح، وعدّل القريب، واحذف الباقي. لا يتغير شيء حتى تضغط احفظ هذه.',
        TEACH_NOTE: 'حفظ الإجابات يعلّمها لمساعدك، ويخصم الرصيد مرة واحدة.',
        SAVE: 'احفظ هذه',
        START_AGAIN: 'ابدأ من جديد',
        SAVED:
          'تم الحفظ. يستخدمها مساعدك من الرسالة القادمة. يمكنك التراجع عنها كلها مرة واحدة.',
        UNDONE: 'تم التراجع. كل شيء كما كان قبل الحفظ.',
        OPEN_PERSONALITY: 'افتح الشخصية',
      },
      WHAT_CHANGED: {
        HEADING: 'تغيّر شيء؟ أخبر واصلو.',
        DESCRIPTION:
          'سعر جديد، أو فرع مغلق، أو أوقات العطلة. قلها مرة واحدة، ويعرض لك واصلو كل مكان تغيّره قبل أن يتغير أي شيء.',
        PLACEHOLDER:
          'من الأسبوع القادم غسيل السيارة الصغيرة ٤ دنانير، وفرع السالمية مغلق حتى ١ نوفمبر.',
        PROPOSE: 'اقترح التغييرات',
        PROPOSING: 'نقرأ ما تغيّر…',
        UNUSABLE:
          'لم نتمكن من تحويل ذلك إلى تغييرات. لم يُخصم شيء. جرّب قولها بطريقة أخرى.',
        FAILED: 'حدث خطأ. لم يتغير شيء. حاول مرة أخرى.',
        NOTHING: 'لم يجد واصلو شيئًا في إعداداتك يغيّره هذا. لم يُخصم شيء.',
      },
      CHANGES: {
        INTRO: 'يقترح واصلو هذه التغييرات بخصوص:',
        APPLY_ALL: 'طبّقها كلها',
        APPLY_SELECTED: 'طبّق ما اخترته',
        NOT_NOW: 'ليس الآن',
        DONE: 'تم التطبيق. يمكنك التراجع عنها كلها مرة واحدة.',
        UNDONE: 'تم التراجع. كل شيء كما كان.',
        FAILED: 'لم يتغير شيء. حاول مرة أخرى.',
        OPEN_PAGE: 'افتح مكانه',
        UNTIL: 'حتى {day}',
        ACTIONS: {
          ANSWER_ADD: 'أضف إجابة',
          ANSWER_EDIT: 'غيّر إجابة',
          ANSWER_OFF: 'أوقف استخدام إجابة',
          LINE_EDIT: 'غيّر سطرًا من الشخصية',
          OPTION_LABEL: 'غيّر كلمات خيار في مهمة',
          OPTION_REMOVE: 'احذف خيارًا من مهمة',
          TASK_OFF: 'أوقف مهمة',
          ROW_LABEL: 'غيّر كلمات خيار في قائمة',
          ROW_REMOVE: 'احذف خيارًا من قائمة',
          HOURS_SET: 'غيّر أوقات العمل',
          NOTICE_ADD: 'أضف ملاحظة مؤقتة',
        },
        PAGES: {
          ANSWERS: 'الإجابات',
          PERSONALITY: 'الشخصية',
          TASKS: 'المهام',
          MENUS: 'القوائم',
          HOURS: 'أوقات العمل',
          NOTICES: 'الملاحظات المؤقتة',
        },
      },
      DAYS: {
        SUN: 'الأحد',
        MON: 'الاثنين',
        TUE: 'الثلاثاء',
        WED: 'الأربعاء',
        THU: 'الخميس',
        FRI: 'الجمعة',
        SAT: 'السبت',
        CLOSED: 'مغلق',
      },
      DESCRIBE: {
        TITLE: 'ابدأ من وصف',
        HINT: 'قل ما يفعله العملاء في هذه المهمة بكلماتك. يكتب واصلو الخطوات، وتراجعها أنت قبل حفظ أي شيء.',
        PLACEHOLDER:
          'يحجز العملاء غسيل سيارة: يختارون الفرع وحجم السيارة واليوم والساعة من نظام الحجز عندنا، ثم نؤكد.',
        DRAFT: 'اكتب مسودة المهمة',
        DRAFTING: 'نكتب مسودة المهمة…',
        UNUSABLE:
          'رجعت المسودة بلا شيء يمكن استخدامه. لم يُخصم شيء. جرّب وصفها بطريقة أخرى.',
        FAILED: 'حدث خطأ. لم يُخصم شيء. حاول مرة أخرى.',
        NEEDS_SYSTEM:
          'تحتاج نظامًا متصلًا: {what}. حتى تربطه، تنتقل المحادثة إلى فريقك عند هذه الخطوة.',
        LEFT_OUT:
          'أوقف واصلو المسودة عند هذه الخطوة. أضف الخطوات التي بعدها عندما يتصل النظام.',
        PROBLEMS_HEADING:
          'لم يتمكن واصلو من إكمال هذه. أصلحها قبل تشغيل المهمة:',
        MENU_ADD: 'عند تشغيل هذه المهمة، أضف «{option}» إلى قائمتي أيضًا',
        MENU_WHICH: 'أي قائمة',
        MENU_FAILED:
          'المهمة تعمل، لكن لم نتمكن من إضافتها إلى القائمة. أضفها من صفحة القوائم.',
        MESSAGE: 'اكتب أيضًا رسالة معتمدة لهذه المهمة',
        PROBLEMS: {
          NO_STEPS: 'المسودة بلا خطوات بعد. أضف السؤال الأول.',
          STEPS_MANY:
            'كان في المسودة خطوات أكثر مما تتسع له المهمة، فتُركت الأخيرة.',
          OPTIONS_MANY:
            'كان لأحد الأسئلة أكثر من عشرة خيارات. احتفظ بالعشرة المهمة.',
          NO_OPTIONS:
            'جاء اختيار بلا خيارات، فصار سؤالًا. أضف الخيارات إن احتجتها.',
          OPTION_LABEL_LONG:
            'كلمات أحد الخيارات أطول من زر واتساب، فتُركت فارغة. اكتبها أقصر.',
          QUESTION_LONG:
            'كان أحد الأسئلة طويلًا جدًا، فتُرك فارغًا. اكتبه أقصر.',
          QUESTION_MISSING:
            'أحد الأسئلة غير مكتوب بإحدى لغتي عملائك. اكتبه باللغتين.',
          MESSAGE_LONG:
            'كانت إحدى الرسائل طويلة جدًا، فتُركت فارغة. اكتبها أقصر.',
          SHAPE: 'تعذّرت قراءة شيء في المسودة. راجع كل خطوة.',
        },
      },
    },
    CLAUDE: {
      CHIP: 'اقتراح من Claude',
      CONNECT: {
        TITLE: 'ربط Claude',
        ASK: 'هل تسمح لـ {app} بقراءة إعداداتك وكتابة مسودات تغييرات لـ {account}؟',
        RETURNS_TO: 'بعد ردّك تعود إلى {host}.',
        ACCOUNT: 'مساحة العمل',
        CAN: 'يمكنه',
        CAN_READ: 'قراءة الشخصية والإجابات وأوقات العمل والرصيد وأداء مساعدك.',
        CAN_CHATS:
          'قراءة محادثاتك الأخيرة مع العملاء وسبب تحويلها إلى موظف. وتصل إلى حسابك في Claude.',
        CAN_DRAFT:
          'كتابة مسودات إجابات ومهام وقوائم وربط بأنظمتك وملاحظات مؤقتة ورسائل معتمدة وأرقام تجربة. وكلها تنتظرك وهي متوقفة.',
        CANNOT: 'لا يمكنه',
        CANNOT_SWITCH: 'تشغيل أي شيء أو البدء بالرد على العملاء.',
        CANNOT_SEND:
          'إرسال رسائل إلى العملاء أو إرسال أي شيء إلى واتساب للموافقة.',
        CANNOT_SECRETS: 'رؤية كلمات المرور والمفاتيح أو إضافتها.',
        CANNOT_REMOVE: 'حذف أي شيء نهائيًا.',
        CREDITS:
          'يعمل Claude بحسابك الخاص في Claude ولا يخصم أبدًا من رصيدك في واصلو. قد يخصم اعتماد ما يقترحه من رصيدك، وتعرض لك واصلو السعر أولًا.',
        ALLOW: 'السماح',
        CANCEL: 'إلغاء',
        EXPIRED: 'انتهت صلاحية هذا الطلب. ابدأ من جديد من Claude.',
        FAILED: 'تعذّر ربط Claude. حاول مرة أخرى.',
        LEAVING: 'نعيدك إلى Claude.',
      },
      APPS: {
        HEADING: 'تطبيقات الذكاء الاصطناعي المرتبطة',
        DESCRIPTION:
          'تطبيقات سمحت لها بقراءة إعداداتك وكتابة مسودات تغييرات لك.',
        NONE: 'لا يوجد تطبيق مرتبط بعد.',
        UNAVAILABLE: 'تعذّر التحقق من التطبيقات المرتبطة الآن.',
        CONNECTED_BY: 'ربطه {who}، {when}',
        LAST_USED: 'آخر استخدام {when}',
        NEVER_USED: 'لم يُستخدم بعد',
        JUST_NOW: 'الآن',
        DISCONNECT: 'فصل',
        DISCONNECTED:
          'تم الفصل. لم يعد بإمكانه قراءة أي شيء أو كتابة مسودات هنا.',
        FAILED: 'تعذّر ذلك. حاول مرة أخرى.',
        HOW_HEADING: 'استخدم واصلو من Claude',
        HOW_ADD:
          'في Claude افتح الإعدادات ثم الموصلات، وأضف موصلًا مخصصًا بهذا العنوان:',
        HOW_ALLOW: 'يعيدك Claude إلى هنا لتسمح له. ولن تنسخ أي مفتاح.',
        COPY: 'نسخ',
        COPIED: 'تم النسخ',
      },
      BATCHES: {
        LINE: 'لم يقترح Claude أي تغيير | اقترح Claude تغييرًا واحدًا | اقترح Claude تغييرين | اقترح Claude {count} تغييرات | اقترح Claude {count} تغييرًا | اقترح Claude {count} تغيير',
        REVIEW: 'مراجعة',
        HIDE: 'إخفاء',
        UNDO_ALL: 'تراجع عن الكل',
        UNDONE: 'تم التراجع. عاد كل ما فعله Claude في تلك الجلسة كما كان.',
        FAILED: 'تعذّر التراجع. حاول مرة أخرى.',
        DRAFTED: 'حُفظ بعضه متوقفًا وعليه علامة اقتراح من Claude:',
        OPEN_TASKS: 'المهام والقوائم',
        OPEN_CONNECTIONS: 'أنظمتك',
      },
      REVIEW: {
        CHANGES_INTRO: 'يقترح Claude هذه التغييرات.',
        PROMPT: 'شخصية جديدة',
        PROMPT_USE: 'استخدم هذه الشخصية',
        STALE: 'تغيّرت الشخصية بعد أن قرأها Claude. اطلب منه مرة أخرى.',
        MESSAGE: 'رسالة معتمدة',
        MESSAGE_OPEN: 'افتحها في المحرر',
        MESSAGE_SENDER: 'للرقم',
        MESSAGE_NO_NUMBER: 'اربط رقم واتساب أولًا.',
        NUMBERS: 'أرقام تجربة لموقعك',
        NUMBERS_USE: 'استخدم هذه الأرقام',
        WHY: 'السبب: {why}',
        NOT_NOW: 'ليس الآن',
        DONE: 'تم.',
        FAILED: 'لم يتغيّر شيء. حاول مرة أخرى.',
      },
      ANSWERS: {
        HEADING: 'اقتراح من Claude',
        DESCRIPTION: 'إجابات كتبها Claude. لا يستخدمها مساعدك حتى تعتمدها.',
        KEEP: 'اعتماد',
        KEEP_ALL: 'اعتماد الكل',
        REMOVE: 'حذف',
        KEPT: 'تم الاعتماد. يستخدمها مساعدك من الآن.',
        CONFLICTS: 'بعضها يخالف إجابات لديك. راجعها في كل الإجابات.',
        FAILED: 'لم يُعتمد شيء. حاول مرة أخرى.',
      },
    },
    NOT_READY: {
      TITLE: 'هذا الجزء قيد الإنشاء',
      BODY: 'سيصل في التحديث القادم. وكل ما عداه هنا يعمل كالمعتاد.',
    },
  },
};

export const wasloMessages = { en, ar };

/**
 * Component-facing translator. Local scope, so these keys never collide with Chatwoot's
 * and never need to be registered anywhere. `inheritLocale` is the default and is what
 * makes an Arabic account render Arabic here.
 */
export const useWasloI18n = () => {
  const composer = useI18n({
    useScope: 'local',
    messages: wasloMessages,
    fallbackLocale: 'en',
    // Six-form Arabic messages only (`helpers/plural.js`); every other message keeps
    // vue-i18n's own choice rule, so nothing already on screen changes.
    pluralRules: { ar: arabicChoice },
  });
  // ONE `t` for the section (spec §7 P-7.3): every number a message is given goes through the
  // formatter, and the plural choice is explicit (`helpers/plural.js` `localisedArgs`). A new
  // object over the composer, never the composer edited: `locale` and the rest stay live.
  const phrase = (unit, count) =>
    composer.t(
      `WASLO.COMMON.COUNT.${unit}`,
      { count: formatCount(count, composer.locale.value) },
      count
    );
  const t = (key, ...args) =>
    composer.t(
      key,
      ...localisedArgs(args, composer.locale.value, COPY_FACTS[key], phrase)
    );
  return Object.assign(Object.create(composer), { t });
};

// `level`, not `node`: `waslo-lint` greps THIS FILE for banned vocabulary over its whole
// text, not only its string values, and `node` joined that list with the step editor. The
// lint is right to be blunt — the alternative is teaching it to parse JavaScript — so the
// one identifier in here that collided was renamed rather than excused.
const readPath = (tree, key) =>
  key.split('.').reduce((level, part) => {
    if (level && typeof level === 'object' && part in level) return level[part];
    return undefined;
  }, tree);

/**
 * Which of the tables above a locale reads from.
 *
 * Chatwoot's locale values are its own directory names, and four of them are not BCP-47
 * (`pt_BR`, `zh_CN`, `zh_TW`, `ur_IN` — see `helpers/intl.js`). So an exact hit is tried
 * first and the base subtag second, which is what makes a hypothetical `ar_KW` read
 * Arabic instead of silently reading English. Anything we have no table for is English,
 * because a nav row is not the place to render a key.
 *
 * Exported for `specs/nav-registration.spec.js`.
 */
export const wasloLocaleTable = locale => {
  if (typeof locale !== 'string' || locale === '') return en;
  if (wasloMessages[locale]) return wasloMessages[locale];
  const base = locale.split(/[-_]/)[0];
  return wasloMessages[base] ?? en;
};

/**
 * Module-facing translator for code that has no setup context (i.e. `nav.js`).
 *
 * Prefers the caller's global `t`; otherwise reads the table for `locale`, and English
 * for a key that table happens not to carry. Never returns a raw key, and never throws —
 * it is called from inside the sidebar's `menuItems` computed, where a throw does not
 * hide a row, it blanks the whole primary nav.
 *
 * @param {(key: string) => string} t       The caller's global translator.
 * @param {string} key                      A `WASLO.…` path into the tables above.
 * @param {string} [locale]                 The locale the rest of the screen is using.
 *                                          Absent → English, which is the honest answer
 *                                          for a caller that does not know.
 */
export const wasloT = (t, key, locale) => {
  const translated = typeof t === 'function' ? t(key) : undefined;
  if (typeof translated === 'string' && translated && translated !== key) {
    return translated;
  }
  const localised = readPath(wasloLocaleTable(locale), key);
  if (typeof localised === 'string') return localised;
  const fallback = readPath(en, key);
  return typeof fallback === 'string' ? fallback : key;
};
